var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { jsxs as j, jsx as P, Fragment as Mi } from "react/jsx-runtime";
import * as A from "react";
import $, { createContext as Ne, useSyncExternalStore as ri, useMemo as pe, useState as ge, useRef as O, useEffect as le, useCallback as pt, useContext as G, useLayoutEffect as Pa, useId as Pt, useInsertionEffect as qo, Children as ff, isValidElement as df, Fragment as wa, createElement as hf, forwardRef as mf, Component as pf } from "react";
import * as qa from "react-dom";
import gf from "react-dom";
let ee, I, L, XP, jP, Ae, LP, MP, Ra, fo, IP, ZP, Pe, GP, UP, qf, OP, HP, QP, zP, kP, YP, KP, JP, DP, Zn;
let __tla = (async () => {
  var _a2;
  Ra = Ne(null);
  async function Af(e) {
    if (!navigator.gpu) throw new Error("WebGPU is not supported in this browser. Use Chrome 113+ or Edge 113+.");
    const t = (await import("./engine-DiWhFmMQ.js")).default, n = await t({
      externalDeviceMode: true
    }), i = n.WebGPU.importJsDevice(e);
    return n.initWithExternalDevice(i), n;
  }
  function ai(e) {
    const t = e.getSceneTextureHandle();
    return t ? e.WebGPU.getJsObject(t) : null;
  }
  const yf = `
struct GlassUniforms {
    rect: vec4f,              // x, y, w, h in normalized [0,1] UV coords
    cornerRadius: f32,        // in pixels
    blurIntensity: f32,       // 0.0 = sharp, 1.0 = max frosted
    opacity: f32,             // tint mix: 0.0 = transparent, 1.0 = solid tint
    refractionStrength: f32,  // lens magnification at edges (0.0\u20130.3)
    tint: vec3f,              // RGB tint color
    aberration: f32,          // chromatic aberration intensity (pixels)
    resolution: vec2f,        // canvas pixel dimensions
    specularIntensity: f32,   // specular highlight brightness (0\u20131)
    rimIntensity: f32,        // rim lighting intensity (0\u20131)
    mode: f32,                // 0.0 = standard, 1.0 = prominent
    _pad4: f32,
    _pad5: f32,
    _pad6: f32,
    // --- New block at byte offset 80 ---
    contrast: f32,               // offset 80
    saturation: f32,             // offset 84
    fresnelIOR: f32,             // offset 88
    fresnelExponent: f32,        // offset 92
    // --- New block at byte offset 96 ---
    envReflectionStrength: f32,  // offset 96
    glareAngle: f32,             // offset 100
    blurRadius: f32,             // offset 104
    dpr: f32,                    // offset 108: device pixel ratio
};

@group(0) @binding(0) var texSampler: sampler;
@group(0) @binding(1) var texBackground: texture_2d<f32>;
@group(1) @binding(0) var<uniform> glass: GlassUniforms;

struct VertexOutput {
    @builtin(position) position: vec4f,
    @location(0) uv: vec2f,
};

@vertex
fn vs_main(@builtin(vertex_index) vertexIndex: u32) -> VertexOutput {
    let pos = array<vec2f, 3>(
        vec2f(-1.0, -1.0),
        vec2f( 3.0, -1.0),
        vec2f(-1.0,  3.0),
    );
    var output: VertexOutput;
    output.position = vec4f(pos[vertexIndex], 0.0, 1.0);
    output.uv = vec2f(output.position.x * 0.5 + 0.5, 1.0 - (output.position.y * 0.5 + 0.5));
    return output;
}

fn sdRoundedBox(p: vec2f, b: vec2f, r: f32) -> f32 {
    let q = abs(p) - b + r;
    return min(max(q.x, q.y), 0.0) + length(max(q, vec2f(0.0))) - r;
}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
    let bgColor = textureSample(texBackground, texSampler, uv);

    // --- Blit pass fast path (rect.z = 0 sentinel) ---
    if (glass.rect.z <= 0.0) {
        return vec4f(bgColor.rgb, 1.0);
    }

    // --- SDF mask (pixel space) ---
    let pixelPos = uv * glass.resolution;
    let rectCenter = (glass.rect.xy + glass.rect.zw * 0.5) * glass.resolution;
    let rectHalf = glass.rect.zw * 0.5 * glass.resolution;
    let dpr = max(glass.dpr, 1.0);
    let clampedRadius = min(glass.cornerRadius * dpr, min(rectHalf.x, rectHalf.y));
    let dist = sdRoundedBox(pixelPos - rectCenter, rectHalf, clampedRadius);

    let fw = fwidth(dist);
    let mask = smoothstep(fw, -fw, dist);

    // Mode-dependent multipliers (0.0 = standard, 1.0 = prominent)
    let modeF = glass.mode;
    let refractionMul = mix(1.0, 1.8, modeF);
    let specularMul = mix(1.0, 1.5, modeF);
    let rimSpread = mix(3.0, 6.0, modeF);
    let aberrationMul = mix(1.0, 1.5, modeF);

    // --- Plano-convex dome refraction (Apple Liquid Glass model) ---
    // Simulates a glass dome: flat back, curved front like a water droplet.
    // SDF depth = glass thickness. Center is deep (thick, flat surface = no
    // refraction). Edges are shallow (steep surface = maximum refraction).
    // Uses Snell's law: offset = -surfaceNormal * tan(\u03B8t - \u03B8i)
    let glassCenter = glass.rect.xy + glass.rect.zw * 0.5;
    let localPos = uv - glassCenter;

    let glassMinHalf = min(rectHalf.x, rectHalf.y);

    // SDF gradient = surface normal direction (via central differences)
    let gradX = sdRoundedBox((pixelPos + vec2f(1.0, 0.0)) - rectCenter, rectHalf, clampedRadius)
              - sdRoundedBox((pixelPos - vec2f(1.0, 0.0)) - rectCenter, rectHalf, clampedRadius);
    let gradY = sdRoundedBox((pixelPos + vec2f(0.0, 1.0)) - rectCenter, rectHalf, clampedRadius)
              - sdRoundedBox((pixelPos - vec2f(0.0, 1.0)) - rectCenter, rectHalf, clampedRadius);
    let surfaceNormal = vec2f(gradX, gradY) * 0.5;  // central diff \u2192 divide by 2
    let normalLen = length(surfaceNormal);

    // Glass dome thickness: 0 at edge (SDF=0), increases toward center
    let glassThickness = glassMinHalf * glass.refractionStrength;
    let normalizedDepth = clamp(-dist / glassThickness, 0.0, 1.0);

    // Snell's law on the dome surface:
    // depthRatio^4 = steep edge, very flat center (water droplet profile).
    // Higher power concentrates refraction closer to the boundary.
    let depthRatio = 1.0 - normalizedDepth;  // 1 at edge, 0 at center
    let edgeCurve = depthRatio * depthRatio * depthRatio * depthRatio;  // pow4
    let incidentAngle = asin(clamp(edgeCurve, 0.0, 0.999));
    let ior = glass.fresnelIOR;
    let transmittedAngle = asin(clamp(sin(incidentAngle) / ior, -0.999, 0.999));

    // Edge shift: tangent of angle difference (how much the ray bends)
    let edgeShift = -tan(transmittedAngle - incidentAngle);

    // UV offset: surface normal \xD7 shift \xD7 dome thickness
    // 3.0 multiplier calibrated to match iOS edge displacement (~40-50px)
    let refractPixels = surfaceNormal * edgeShift * glassThickness * 3.0;
    let refractOffset = refractPixels / glass.resolution;

    // --- Reflection UV: mirror of refraction, extended reach ---
    // Reflection bends opposite to refraction; scale > 1 samples beyond panel bounds
    // (Apple samples content from area larger than the glass element)
    let reflectionScale = 2.5;
    let reflectUV = uv - refractOffset * reflectionScale;

    // Chromatic aberration: different IOR per channel (dispersion)
    let aberrationNorm = glass.aberration * 0.002 * aberrationMul;
    let rUV = uv + refractOffset * (1.0 + aberrationNorm);
    let gUV = uv + refractOffset;
    let bUV = uv + refractOffset * (1.0 - aberrationNorm);

    // displacementFactor for specular/aberration blend (1=center, 0=edge)
    let displacementFactor = normalizedDepth;

    // --- 81-tap (9x9) Gaussian blur at REFRACTED UV ---
    // Blur is applied at the refracted position so the frosted glass effect
    // shows through the dome displacement. Each blur sample is offset by
    // refractOffset so the entire blurred patch is displaced, not averaged away.
    let bgDims = vec2f(textureDimensions(texBackground));
    let texelSize = 1.0 / bgDims;
    let sampleStep = glass.blurRadius / 4.0;

    var blurColor = vec4f(0.0);
    var totalWeight = 0.0;

    // Blur centered on the refracted UV (gUV = uv + refractOffset)
    for (var x = -4; x <= 4; x++) {
        for (var y = -4; y <= 4; y++) {
            let r2 = f32(x * x + y * y);
            let weight = exp(-r2 * 0.125);  // sigma=2 matches old Gaussian profile
            let sampleUV = gUV + vec2f(f32(x), f32(y)) * texelSize * sampleStep;
            blurColor += textureSample(texBackground, texSampler, sampleUV) * weight;
            totalWeight += weight;
        }
    }
    blurColor /= totalWeight;

    // Chromatic aberration: sample sharp (unblurred) at dispersed UVs
    let rSample = textureSample(texBackground, texSampler, rUV);
    let bSample = textureSample(texBackground, texSampler, bUV);

    let aberrationEdge = 1.0 - displacementFactor;
    let aberrationBlend = aberrationEdge * aberrationEdge * 0.6;

    let aberratedColor = vec3f(
        mix(blurColor.r, rSample.r, aberrationBlend),
        blurColor.g,
        mix(blurColor.b, bSample.b, aberrationBlend)
    );

    // --- Contrast reduction + saturation boost ---
    // Reference: backdrop-filter: contrast(85%) saturate(140%)
    let luminance = dot(aberratedColor, vec3f(0.299, 0.587, 0.114));
    let saturated = mix(vec3f(luminance), aberratedColor, glass.saturation);
    let contrasted = mix(vec3f(0.5), saturated, glass.contrast);

    // --- Tint ---
    let tinted = mix(contrasted, glass.tint, glass.opacity);

    // --- Directional Fresnel specular ---
    // Reference uses paired inset box-shadows: cool blue top-left, warm bottom-right
    // Use CSS-pixel-space distance so effects look the same at any DPR
    let cssDist = dist / dpr;
    let innerDist = max(-cssDist, 0.0);
    let broadGlow = exp(-innerDist * 0.08);

    let halfSize = glass.rect.zw * 0.5;
    let normPos = localPos / halfSize;
    let normLen = max(length(normPos), 0.001);
    let normDir = normPos / normLen;

    // Light direction from glareAngle uniform (replaces hardcoded upper-left)
    let lightDir = vec2f(cos(glass.glareAngle), sin(glass.glareAngle));
    let lightDot = dot(normDir, lightDir);
    let topLeftFactor = clamp(lightDot * 0.5 + 0.5, 0.0, 1.0);

    let coolSpec = broadGlow * topLeftFactor * glass.specularIntensity * specularMul;
    let coolColor = vec3f(0.6, 0.75, 1.0);

    let warmSpec = broadGlow * (1.0 - topLeftFactor) * glass.specularIntensity * 0.5 * specularMul;
    let warmColor = vec3f(0.95, 1.0, 0.75);

    // --- Physically-based Fresnel reflection (Schlick's approximation) ---
    // cosTheta \u2248 normalizedDepth in our dome model (1=center/head-on, 0=edge/grazing)
    let cosTheta = clamp(normalizedDepth, 0.0, 1.0);
    let r0 = pow((1.0 - glass.fresnelIOR) / (1.0 + glass.fresnelIOR), 2.0);
    let fresnelTerm = r0 + (1.0 - r0) * pow(1.0 - cosTheta, glass.fresnelExponent);

    // --- Soft environment reflection (9-tap disc blur at reflected UV) ---
    // Samples actual scene content at reflected position \u2014 not flat white.
    // Disc pattern: center + 8 neighbors at 45\xB0 intervals for soft/frosted look.
    let reflBlurRad = 5.0; // texels \u2014 wider than refraction for softer reflection
    let rt = texelSize;    // reuse from blur section
    let rc = reflectUV;
    let reflectionColor = (
        textureSample(texBackground, texSampler, rc).rgb
      + textureSample(texBackground, texSampler, rc + vec2f( reflBlurRad, 0.0) * rt).rgb
      + textureSample(texBackground, texSampler, rc + vec2f(-reflBlurRad, 0.0) * rt).rgb
      + textureSample(texBackground, texSampler, rc + vec2f(0.0,  reflBlurRad) * rt).rgb
      + textureSample(texBackground, texSampler, rc + vec2f(0.0, -reflBlurRad) * rt).rgb
      + textureSample(texBackground, texSampler, rc + vec2f( 0.707, 0.707) * reflBlurRad * rt).rgb
      + textureSample(texBackground, texSampler, rc + vec2f(-0.707, 0.707) * reflBlurRad * rt).rgb
      + textureSample(texBackground, texSampler, rc + vec2f( 0.707,-0.707) * reflBlurRad * rt).rgb
      + textureSample(texBackground, texSampler, rc + vec2f(-0.707,-0.707) * reflBlurRad * rt).rgb
    ) / 9.0;

    // Environment reflection: Fresnel-weighted background sample
    let envRef = reflectionColor * fresnelTerm * glass.envReflectionStrength;

    // Sharp rim at glass boundary
    let rimGlow = exp(-cssDist * cssDist / rimSpread) * glass.rimIntensity;

    let specular = coolSpec * coolColor + warmSpec * warmColor + vec3f(rimGlow);

    // --- Final composite ---
    // Glass regions: output glass color with alpha=mask so alpha blending
    // composites only the glass area over the previously drawn background.
    let glassRgb = clamp(tinted + specular + envRef, vec3f(0.0), vec3f(1.0));
    return vec4f(glassRgb, mask);
}
`, en = {
    rect: {
      x: 0,
      y: 0,
      w: 0,
      h: 0
    },
    cornerRadius: 12,
    blurIntensity: 0.5,
    opacity: 0.15,
    refractionStrength: 0.08,
    tint: {
      r: 1,
      g: 1,
      b: 1
    },
    aberration: 1,
    resolution: {
      x: 1920,
      y: 1080
    },
    specularIntensity: 0.8,
    rimIntensity: 0.6,
    mode: 0,
    contrast: 1,
    saturation: 1,
    fresnelIOR: 1.45,
    fresnelExponent: 2.5,
    envReflectionStrength: 0.4,
    glareAngle: 45,
    blurRadius: 9,
    dpr: 1
  };
  function xf(e, t) {
    const n = t ?? new Float32Array(28);
    return n[0] = e.rect.x, n[1] = e.rect.y, n[2] = e.rect.w, n[3] = e.rect.h, n[4] = e.cornerRadius, n[5] = e.blurIntensity, n[6] = e.opacity, n[7] = e.refractionStrength, n[8] = e.tint.r, n[9] = e.tint.g, n[10] = e.tint.b, n[11] = e.aberration, n[12] = e.resolution.x, n[13] = e.resolution.y, n[14] = e.specularIntensity, n[15] = e.rimIntensity, n[16] = e.mode, n[17] = 0, n[18] = 0, n[19] = 0, n[20] = e.contrast, n[21] = e.saturation, n[22] = e.fresnelIOR, n[23] = e.fresnelExponent, n[24] = e.envReflectionStrength, n[25] = e.glareAngle, n[26] = e.blurRadius, n[27] = e.dpr, n;
  }
  function Pf(e, t, n, i) {
    if (i === 0 || n === 0) {
      e.cornerRadius = t.cornerRadius, e.blurIntensity = t.blurIntensity, e.opacity = t.opacity, e.refractionStrength = t.refractionStrength, e.tint.r = t.tint.r, e.tint.g = t.tint.g, e.tint.b = t.tint.b, e.aberration = t.aberration, e.specularIntensity = t.specularIntensity, e.rimIntensity = t.rimIntensity, e.mode = t.mode, e.contrast = t.contrast, e.saturation = t.saturation, e.fresnelIOR = t.fresnelIOR, e.fresnelExponent = t.fresnelExponent, e.envReflectionStrength = t.envReflectionStrength, e.glareAngle = t.glareAngle, e.blurRadius = t.blurRadius;
      return;
    }
    const s = 1 - Math.exp(-i * n);
    e.cornerRadius += (t.cornerRadius - e.cornerRadius) * s, e.blurIntensity += (t.blurIntensity - e.blurIntensity) * s, e.opacity += (t.opacity - e.opacity) * s, e.refractionStrength += (t.refractionStrength - e.refractionStrength) * s, e.tint.r += (t.tint.r - e.tint.r) * s, e.tint.g += (t.tint.g - e.tint.g) * s, e.tint.b += (t.tint.b - e.tint.b) * s, e.aberration += (t.aberration - e.aberration) * s, e.specularIntensity += (t.specularIntensity - e.specularIntensity) * s, e.rimIntensity += (t.rimIntensity - e.rimIntensity) * s, e.mode += (t.mode - e.mode) * s, e.contrast += (t.contrast - e.contrast) * s, e.saturation += (t.saturation - e.saturation) * s, e.fresnelIOR += (t.fresnelIOR - e.fresnelIOR) * s, e.fresnelExponent += (t.fresnelExponent - e.fresnelExponent) * s, e.envReflectionStrength += (t.envReflectionStrength - e.envReflectionStrength) * s, e.glareAngle += (t.glareAngle - e.glareAngle) * s, e.blurRadius += (t.blurRadius - e.blurRadius) * s;
  }
  const tn = 32, Ct = 256, wf = 28;
  qf = class {
    constructor() {
      __publicField(this, "device");
      __publicField(this, "pipeline");
      __publicField(this, "uniformBuffer");
      __publicField(this, "sampler");
      __publicField(this, "perFrameLayout");
      __publicField(this, "perRegionLayout");
      __publicField(this, "perFrameBindGroup", null);
      __publicField(this, "perRegionBindGroup");
      __publicField(this, "canvasFormat");
      __publicField(this, "regions", /* @__PURE__ */ new Map());
      __publicField(this, "nextId", 1);
      __publicField(this, "stagingBuffer");
      __publicField(this, "regionScratch");
      __publicField(this, "cachedRegionList", []);
      __publicField(this, "regionListDirty", true);
      __publicField(this, "rectCacheDirty", true);
      __publicField(this, "scrollX", 0);
      __publicField(this, "scrollY", 0);
      __publicField(this, "scrollListenerBound", false);
      __publicField(this, "_frameCount", 0);
      __publicField(this, "_frameTimes", []);
      __publicField(this, "_lastMetricsLog", 0);
      const t = Ct / 4;
      this.stagingBuffer = new Float32Array((tn + 1) * t), this.regionScratch = new Float32Array(wf);
    }
    async init(t, n) {
      this.device = t, this.canvasFormat = n, this.perFrameLayout = t.createBindGroupLayout({
        label: "GlassRenderer perFrame",
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.FRAGMENT,
            sampler: {
              type: "filtering"
            }
          },
          {
            binding: 1,
            visibility: GPUShaderStage.FRAGMENT,
            texture: {
              sampleType: "float"
            }
          }
        ]
      }), this.perRegionLayout = t.createBindGroupLayout({
        label: "GlassRenderer perRegion",
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
            buffer: {
              type: "uniform",
              hasDynamicOffset: true,
              minBindingSize: 112
            }
          }
        ]
      });
      const i = t.createPipelineLayout({
        label: "GlassRenderer pipeline layout",
        bindGroupLayouts: [
          this.perFrameLayout,
          this.perRegionLayout
        ]
      }), o = t.createShaderModule({
        label: "GlassRenderer shader",
        code: yf
      });
      this.pipeline = await t.createRenderPipelineAsync({
        label: "GlassRenderer pipeline",
        layout: i,
        vertex: {
          module: o,
          entryPoint: "vs_main"
        },
        fragment: {
          module: o,
          entryPoint: "fs_main",
          targets: [
            {
              format: n,
              blend: {
                color: {
                  srcFactor: "src-alpha",
                  dstFactor: "one-minus-src-alpha",
                  operation: "add"
                },
                alpha: {
                  srcFactor: "one",
                  dstFactor: "one-minus-src-alpha",
                  operation: "add"
                }
              }
            }
          ]
        },
        primitive: {
          topology: "triangle-list"
        }
      }), this.sampler = t.createSampler({
        label: "GlassRenderer sampler",
        magFilter: "linear",
        minFilter: "linear"
      }), this.uniformBuffer = t.createBuffer({
        label: "GlassRenderer uniforms",
        size: (tn + 1) * Ct,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
      }), this.perRegionBindGroup = t.createBindGroup({
        label: "GlassRenderer perRegion bindGroup",
        layout: this.perRegionLayout,
        entries: [
          {
            binding: 0,
            resource: {
              buffer: this.uniformBuffer,
              size: 112
            }
          }
        ]
      }), this.bindScrollListener();
    }
    setSceneTexture(t) {
      this.perFrameBindGroup = this.device.createBindGroup({
        label: "GlassRenderer perFrame bindGroup",
        layout: this.perFrameLayout,
        entries: [
          {
            binding: 0,
            resource: this.sampler
          },
          {
            binding: 1,
            resource: t.createView()
          }
        ]
      });
    }
    bindScrollListener() {
      if (this.scrollListenerBound) return;
      const t = () => {
        this.rectCacheDirty = true;
      };
      window.addEventListener("scroll", t, {
        passive: true
      }), window.addEventListener("resize", t, {
        passive: true
      }), this.scrollListenerBound = true;
    }
    addRegion(t, n) {
      if (this.regions.size >= tn) throw new Error(`GlassRenderer: MAX_GLASS_REGIONS (${tn}) exceeded. Currently ${this.regions.size} active regions. Unmount off-screen glass components or increase MAX_GLASS_REGIONS.`);
      const i = this.nextId++, o = {
        ...en,
        ...n,
        tint: {
          ...en.tint,
          ...(n == null ? void 0 : n.tint) ?? {}
        },
        rect: {
          ...en.rect,
          ...(n == null ? void 0 : n.rect) ?? {}
        },
        resolution: {
          ...en.resolution,
          ...(n == null ? void 0 : n.resolution) ?? {}
        }
      }, s = {
        id: i,
        element: t,
        current: {
          ...o,
          tint: {
            ...o.tint
          },
          rect: {
            ...o.rect
          },
          resolution: {
            ...o.resolution
          }
        },
        target: {
          ...o,
          tint: {
            ...o.tint
          },
          rect: {
            ...o.rect
          },
          resolution: {
            ...o.resolution
          }
        },
        morphSpeed: 8,
        cachedRect: {
          left: 0,
          top: 0,
          width: 0,
          height: 0
        },
        rectDirty: true
      };
      return this.regions.set(i, s), this.regionListDirty = true, this.rectCacheDirty = true, i;
    }
    removeRegion(t) {
      this.regions.delete(t), this.regionListDirty = true;
    }
    getRegion(t) {
      return this.regions.get(t);
    }
    setRegionParams(t, n, i, o, s) {
      const r = this.regions.get(t);
      r && (r.target.cornerRadius = n, r.target.blurIntensity = i, r.target.opacity = o, r.target.refractionStrength = s);
    }
    setRegionTint(t, n, i, o) {
      const s = this.regions.get(t);
      s && (s.target.tint = {
        r: n,
        g: i,
        b: o
      });
    }
    setRegionAberration(t, n) {
      const i = this.regions.get(t);
      i && (i.target.aberration = n);
    }
    setRegionSpecular(t, n) {
      const i = this.regions.get(t);
      i && (i.target.specularIntensity = n);
    }
    setRegionRim(t, n) {
      const i = this.regions.get(t);
      i && (i.target.rimIntensity = n);
    }
    setRegionMode(t, n) {
      const i = this.regions.get(t);
      i && (i.target.mode = n);
    }
    setRegionMorphSpeed(t, n) {
      const i = this.regions.get(t);
      i && (i.morphSpeed = n);
    }
    setRegionContrast(t, n) {
      const i = this.regions.get(t);
      i && (i.target.contrast = n);
    }
    setRegionSaturation(t, n) {
      const i = this.regions.get(t);
      i && (i.target.saturation = n);
    }
    setRegionBlurRadius(t, n) {
      const i = this.regions.get(t);
      i && (i.target.blurRadius = n);
    }
    setRegionFresnelIOR(t, n) {
      const i = this.regions.get(t);
      i && (i.target.fresnelIOR = n);
    }
    setRegionFresnelExponent(t, n) {
      const i = this.regions.get(t);
      i && (i.target.fresnelExponent = n);
    }
    setRegionEnvReflectionStrength(t, n) {
      const i = this.regions.get(t);
      i && (i.target.envReflectionStrength = n);
    }
    setRegionGlareAngle(t, n) {
      const i = this.regions.get(t);
      i && (i.target.glareAngle = n);
    }
    render(t, n, i, o, s) {
      if (!this.perFrameBindGroup) return;
      const r = performance.now(), a = this.device;
      this.regionListDirty && (this.cachedRegionList = Array.from(this.regions.values()), this.regionListDirty = false);
      const l = this.cachedRegionList, c = l.length, u = Ct / 4, f = this.stagingBuffer;
      f.fill(0, 0, u), f[12] = n, f[13] = i, f[27] = s;
      for (let y = 0; y < c; y++) {
        const x = l[y];
        Pf(x.current, x.target, o, x.morphSpeed), x.current.resolution.x = n, x.current.resolution.y = i, x.current.dpr = s;
        const q = x.element.getBoundingClientRect(), R = x.cachedRect;
        R.left = q.left, R.top = q.top, R.width = q.width, R.height = q.height, x.current.rect.x = R.left / n * s, x.current.rect.y = R.top / i * s, x.current.rect.w = R.width / n * s, x.current.rect.h = R.height / i * s, xf(x.current, this.regionScratch);
        const v = (y + 1) * u;
        f.set(this.regionScratch, v);
      }
      const d = (c + 1) * Ct;
      a.queue.writeBuffer(this.uniformBuffer, 0, f.buffer, 0, d);
      const m = t.getCurrentTexture(), p = a.createCommandEncoder({
        label: "GlassRenderer frame"
      }), h = p.beginRenderPass({
        colorAttachments: [
          {
            view: m.createView(),
            loadOp: "clear",
            storeOp: "store",
            clearValue: {
              r: 0,
              g: 0,
              b: 0,
              a: 1
            }
          }
        ]
      });
      h.setPipeline(this.pipeline), h.setBindGroup(0, this.perFrameBindGroup), h.setBindGroup(1, this.perRegionBindGroup, [
        0
      ]), h.draw(3);
      for (let y = 0; y < c; y++) {
        const x = l[y].cachedRect, w = 40, q = Math.max(0, Math.floor(x.left * s) - w), R = Math.max(0, Math.floor(x.top * s) - w), v = Math.min(n, Math.ceil((x.left + x.width) * s) + w), S = Math.min(i, Math.ceil((x.top + x.height) * s) + w), V = v - q, b = S - R;
        V > 0 && b > 0 && (h.setScissorRect(q, R, V, b), h.setBindGroup(1, this.perRegionBindGroup, [
          (y + 1) * Ct
        ]), h.draw(3));
      }
      h.end(), a.queue.submit([
        p.finish()
      ]);
      const g = performance.now() - r;
      this._frameCount++, this._frameTimes.push(g), this._frameTimes.length > 60 && this._frameTimes.shift();
    }
    destroy() {
      var _a3;
      (_a3 = this.uniformBuffer) == null ? void 0 : _a3.destroy(), this.regions.clear(), this.cachedRegionList = [], this.perFrameBindGroup = null;
    }
  };
  function Ro(e) {
    return {
      subscribe(t) {
        const n = window.matchMedia(e);
        return n.addEventListener("change", t), () => n.removeEventListener("change", t);
      },
      getSnapshot() {
        return window.matchMedia(e).matches;
      },
      getServerSnapshot() {
        return false;
      }
    };
  }
  const li = Ro("(prefers-reduced-motion: reduce)"), ci = Ro("(prefers-reduced-transparency: reduce)"), ui = Ro("(prefers-color-scheme: dark)");
  function Rf() {
    const e = ri(li.subscribe, li.getSnapshot, li.getServerSnapshot), t = ri(ci.subscribe, ci.getSnapshot, ci.getServerSnapshot), n = ri(ui.subscribe, ui.getSnapshot, ui.getServerSnapshot);
    return pe(() => ({
      reducedMotion: e,
      reducedTransparency: t,
      darkMode: n
    }), [
      e,
      t,
      n
    ]);
  }
  const vf = "data:image/jpeg;base64,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";
  async function Vf(e) {
    const t = await fetch(vf);
    if (!t.ok) {
      console.error("GlassProvider: failed to load wallpaper:", t.status);
      return;
    }
    const n = await t.blob(), i = await createImageBitmap(n, {
      colorSpaceConversion: "none"
    }), o = i.width, s = i.height, a = new OffscreenCanvas(o, s).getContext("2d");
    a.drawImage(i, 0, 0);
    const l = a.getImageData(0, 0, o, s);
    i.close();
    const c = new Uint8Array(l.data.buffer), u = e._malloc(c.byteLength);
    e.HEAPU8.set(c, u), e.uploadImageData(u, o, s), e._free(u);
  }
  UP = function({ children: e, backgroundMode: t = "image", engineRef: n }) {
    const [i, o] = ge(false), s = O(null), r = O(null), a = O(null), l = O(null), c = O(null), u = Rf();
    le(() => {
      let p = false;
      return (async () => {
        if (!c.current) return;
        if (!navigator.gpu) {
          console.error("GlassProvider: WebGPU not supported");
          return;
        }
        const h = await navigator.gpu.requestAdapter();
        if (!h || p) return;
        const g = await h.requestDevice();
        if (p) {
          g.destroy();
          return;
        }
        const y = await Af(g);
        if (p) {
          y.destroyEngine(), g.destroy();
          return;
        }
        for (; !y.getEngine(); ) {
          if (p) {
            y.destroyEngine(), g.destroy();
            return;
          }
          await new Promise((S) => setTimeout(S, 50));
        }
        const x = navigator.gpu.getPreferredCanvasFormat(), w = new qf();
        if (await w.init(g, x), p) {
          w.destroy(), y.destroyEngine(), g.destroy();
          return;
        }
        const q = ai(y);
        if (!q) {
          console.error("GlassProvider: getSceneTexture returned null"), w.destroy(), y.destroyEngine(), g.destroy();
          return;
        }
        w.setSceneTexture(q);
        const v = c.current.getContext("webgpu");
        v.configure({
          device: g,
          format: x,
          alphaMode: "opaque"
        }), s.current = y, r.current = g, a.current = w, l.current = v, n && (n.current = y), o(true);
      })().catch((h) => {
        console.error("GlassProvider: engine init failed", h);
      }), () => {
        p = true, n && (n.current = null);
        const h = l.current;
        if (h) try {
          h.unconfigure();
        } catch {
        }
        l.current = null;
        const g = a.current;
        g && (g.destroy(), a.current = null), s.current && (s.current.destroyEngine(), s.current = null);
        const y = r.current;
        y && (y.destroy(), r.current = null);
      };
    }, []), le(() => {
      if (!i) return;
      let p, h = 0;
      const g = (y) => {
        var _a3;
        const x = h === 0 ? 0 : Math.min((y - h) / 1e3, 0.1);
        h = y;
        const w = (_a3 = s.current) == null ? void 0 : _a3.getEngine(), q = a.current, R = l.current, v = c.current;
        w && q && R && v && (w.update(x), w.renderBackground(), q.render(R, v.width, v.height, x, devicePixelRatio)), p = requestAnimationFrame(g);
      };
      return p = requestAnimationFrame(g), () => cancelAnimationFrame(p);
    }, [
      i
    ]), le(() => {
      var _a3;
      if (!i) return;
      const p = c.current;
      if (!p) return;
      const h = (_a3 = s.current) == null ? void 0 : _a3.getEngine();
      h && h.setDpr(devicePixelRatio);
      const g = new ResizeObserver((y) => {
        var _a4, _b, _c2;
        const x = (_a4 = s.current) == null ? void 0 : _a4.getEngine();
        if (x) {
          for (const w of y) {
            const q = ((_b = w.devicePixelContentBoxSize) == null ? void 0 : _b[0].inlineSize) ?? Math.round(w.contentBoxSize[0].inlineSize * devicePixelRatio), R = ((_c2 = w.devicePixelContentBoxSize) == null ? void 0 : _c2[0].blockSize) ?? Math.round(w.contentBoxSize[0].blockSize * devicePixelRatio), v = 4096, S = Math.max(1, Math.min(q, v)), V = Math.max(1, Math.min(R, v));
            if (p.width !== S || p.height !== V) {
              p.width = S, p.height = V, x.resize(S, V);
              const b = ai(s.current);
              b && a.current && a.current.setSceneTexture(b);
            }
          }
          x.setDpr(devicePixelRatio);
        }
      });
      try {
        g.observe(p, {
          box: "device-pixel-content-box"
        });
      } catch {
        g.observe(p, {
          box: "content-box"
        });
      }
      return () => g.disconnect();
    }, [
      i
    ]), le(() => {
      var _a3;
      const p = (_a3 = s.current) == null ? void 0 : _a3.getEngine();
      p && p.setPaused(u.reducedMotion);
    }, [
      u.reducedMotion,
      i
    ]), le(() => {
      !i || !s.current || Vf(s.current).catch((p) => {
        console.error("GlassProvider: wallpaper upload failed", p);
      });
    }, [
      i
    ]), le(() => {
      if (!i || !s.current) return;
      s.current.setBackgroundMode(t === "image" ? 0 : 1);
      const p = ai(s.current);
      p && a.current && a.current.setSceneTexture(p);
    }, [
      t,
      i
    ]);
    const f = pt((p) => {
      const h = a.current;
      if (!h) return null;
      const g = h.addRegion(p);
      return {
        id: g,
        updateRect: (x, w, q, R) => {
        },
        updateParams: (x, w, q, R) => h.setRegionParams(g, x, w, q, R),
        updateTint: (x, w, q) => h.setRegionTint(g, x, w, q),
        updateAberration: (x) => h.setRegionAberration(g, x),
        updateSpecular: (x) => h.setRegionSpecular(g, x),
        updateRim: (x) => h.setRegionRim(g, x),
        updateMode: (x) => h.setRegionMode(g, x),
        updateMorphSpeed: (x) => h.setRegionMorphSpeed(g, x),
        updateContrast: (x) => h.setRegionContrast(g, x),
        updateSaturation: (x) => h.setRegionSaturation(g, x),
        updateBlurRadius: (x) => h.setRegionBlurRadius(g, x),
        updateFresnelIOR: (x) => h.setRegionFresnelIOR(g, x),
        updateFresnelExponent: (x) => h.setRegionFresnelExponent(g, x),
        updateEnvReflectionStrength: (x) => h.setRegionEnvReflectionStrength(g, x),
        updateGlareAngle: (x) => h.setRegionGlareAngle(g, x),
        remove: () => h.removeRegion(g)
      };
    }, [
      i
    ]), d = pt((p) => {
      const h = a.current;
      h && h.removeRegion(p);
    }, []), m = pe(() => ({
      registerRegion: f,
      unregisterRegion: d,
      ready: i,
      preferences: u
    }), [
      f,
      d,
      i,
      u
    ]);
    return j(Ra, {
      value: m,
      children: [
        P("canvas", {
          id: "gpu-canvas",
          ref: c,
          style: {
            position: "fixed",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: -1,
            display: "block"
          }
        }),
        e
      ]
    });
  };
  Zn = function() {
    const e = G(Ra);
    if (!e) throw new Error("useGlassEngine must be used within a <GlassProvider>");
    return e;
  };
  const Sf = {
    tint: [
      0.7,
      0.75,
      0.85
    ],
    opacity: 0.08
  }, Tf = {
    tint: [
      0.15,
      0.15,
      0.2
    ],
    opacity: 0.25
  }, bf = {
    tint: [
      0.2,
      0.2,
      0.22
    ],
    opacity: 0.92
  }, Ff = {
    tint: [
      0.92,
      0.92,
      0.94
    ],
    opacity: 0.9
  };
  function vo(e, t) {
    const n = Zn(), i = O(null), { preferences: o, registerRegion: s, unregisterRegion: r, ready: a } = n;
    le(() => {
      if (!a || !e.current) return;
      const l = s(e.current);
      return i.current = l, () => {
        l && (l.remove(), r(l.id)), i.current = null;
      };
    }, [
      a,
      s,
      r,
      e
    ]), le(() => {
      const l = i.current;
      if (!l) return;
      let c, u, f, d;
      const m = t.cornerRadius ?? 24;
      if (o.reducedTransparency) {
        l.updateMorphSpeed(0);
        const p = o.darkMode ? bf : Ff;
        c = 0, u = p.opacity, f = t.tint ?? p.tint, d = 0, l.updateAberration(0), l.updateSpecular(0), l.updateRim(0), l.updateMode(0), l.updateContrast(1), l.updateSaturation(1), l.updateBlurRadius(0), l.updateFresnelIOR(1), l.updateFresnelExponent(1), l.updateEnvReflectionStrength(0), l.updateGlareAngle(0);
      } else {
        const p = o.darkMode ? Sf : Tf;
        c = t.blur ?? 0.3, u = t.opacity ?? p.opacity, f = t.tint ?? p.tint, d = t.refraction ?? 0.5, l.updateAberration(t.aberration ?? 7.2), l.updateSpecular(t.specular ?? 0.08), l.updateRim(t.rim ?? 0.15), l.updateMode(t.refractionMode === "prominent" ? 1 : 0), l.updateContrast(t.contrast ?? 1.24), l.updateSaturation(t.saturation ?? 1.05), l.updateFresnelIOR(t.fresnelIOR ?? 1.5), l.updateFresnelExponent(t.fresnelExponent ?? 5), l.updateEnvReflectionStrength(t.envReflectionStrength ?? 0.15), l.updateGlareAngle((t.glareDirection ?? 420) * Math.PI / 180), l.updateBlurRadius(t.blurRadius ?? (t.blur ?? 0.3) * 30);
      }
      l.updateParams(m, c, u, d), l.updateTint(f[0], f[1], f[2]), l.updateMorphSpeed(t.morphSpeed ?? 8);
    }, [
      t.cornerRadius,
      t.blur,
      t.opacity,
      t.refraction,
      t.tint,
      t.aberration,
      t.specular,
      t.rim,
      t.refractionMode,
      t.morphSpeed,
      t.contrast,
      t.saturation,
      t.blurRadius,
      t.fresnelIOR,
      t.fresnelExponent,
      t.envReflectionStrength,
      t.glareDirection,
      o.reducedTransparency,
      o.darkMode,
      a
    ]);
  }
  function Vo(...e) {
    return (t) => {
      for (const n of e) typeof n == "function" ? n(t) : n && typeof n == "object" && (n.current = t);
    };
  }
  Pe = function({ children: e, className: t, style: n, ref: i, blur: o, opacity: s, cornerRadius: r, tint: a, refraction: l, aberration: c, specular: u, rim: f, refractionMode: d, morphSpeed: m, contrast: p, saturation: h, blurRadius: g, fresnelIOR: y, fresnelExponent: x, envReflectionStrength: w, glareDirection: q, ...R }) {
    const v = O(null), S = Vo(v, i), { preferences: V } = Zn();
    vo(v, {
      blur: o,
      opacity: s,
      cornerRadius: r,
      tint: a,
      refraction: l,
      aberration: c,
      specular: u,
      rim: f,
      refractionMode: d,
      morphSpeed: m,
      contrast: p,
      saturation: h,
      blurRadius: g,
      fresnelIOR: y,
      fresnelExponent: x,
      envReflectionStrength: w,
      glareDirection: q
    });
    const b = (V == null ? void 0 : V.darkMode) ?? true ? {
      color: "rgba(255, 255, 255, 0.95)",
      textShadow: "0 1px 3px rgba(0, 0, 0, 0.6), 0 0 8px rgba(0, 0, 0, 0.3)"
    } : {
      color: "rgba(0, 0, 0, 0.87)",
      textShadow: "0 1px 2px rgba(255, 255, 255, 0.8), 0 0 6px rgba(255, 255, 255, 0.4)"
    };
    return P("div", {
      ref: S,
      "data-testid": "glass-panel",
      className: t,
      style: {
        position: "relative",
        background: "transparent",
        borderRadius: `${r ?? 24}px`,
        ...b,
        ...n
      },
      ...R,
      children: e
    });
  };
  Ae = function({ children: e, className: t, style: n, ref: i, blur: o, opacity: s, cornerRadius: r = 16, tint: a, refraction: l, aberration: c, specular: u, rim: f, refractionMode: d, morphSpeed: m, contrast: p, saturation: h, blurRadius: g, fresnelIOR: y, fresnelExponent: x, envReflectionStrength: w, glareDirection: q, onClick: R, disabled: v, type: S = "button", ...V }) {
    const b = O(null), T = Vo(b, i), { preferences: F } = Zn(), [U, D] = ge(false), [K, z] = ge(false), N = K ? (o ?? 0.5) * 0.3 : U ? (o ?? 0.5) * 0.8 : o, H = U ? (u ?? 0.2) * 1.8 : u, C = U ? (f ?? 0.15) * 2 : f, W = U ? (c ?? 3) * 1.5 : c;
    vo(b, {
      blur: N,
      opacity: s,
      cornerRadius: r,
      tint: a,
      refraction: l,
      aberration: W,
      specular: H,
      rim: C,
      refractionMode: d,
      morphSpeed: m,
      contrast: p,
      saturation: h,
      blurRadius: g,
      fresnelIOR: y,
      fresnelExponent: x,
      envReflectionStrength: w,
      glareDirection: q
    });
    const X = (F == null ? void 0 : F.darkMode) ?? true ? {
      color: "rgba(255, 255, 255, 0.95)",
      textShadow: "0 1px 3px rgba(0, 0, 0, 0.6), 0 0 8px rgba(0, 0, 0, 0.3)"
    } : {
      color: "rgba(0, 0, 0, 0.87)",
      textShadow: "0 1px 2px rgba(255, 255, 255, 0.8), 0 0 6px rgba(255, 255, 255, 0.4)"
    };
    return P("button", {
      ref: T,
      className: t,
      style: {
        position: "relative",
        background: "transparent",
        border: "none",
        borderRadius: `${r}px`,
        cursor: v ? "default" : "pointer",
        ...X,
        ...n
      },
      onMouseEnter: () => {
        v || D(true);
      },
      onMouseLeave: () => {
        D(false), z(false);
      },
      onMouseDown: () => {
        v || z(true);
      },
      onMouseUp: () => z(false),
      onClick: R,
      disabled: v,
      type: S,
      ...V,
      children: e
    });
  };
  LP = function({ children: e, className: t, style: n, ref: i, blur: o, opacity: s, cornerRadius: r, tint: a, refraction: l, aberration: c, specular: u, rim: f, refractionMode: d, morphSpeed: m, contrast: p, saturation: h, blurRadius: g, fresnelIOR: y, fresnelExponent: x, envReflectionStrength: w, glareDirection: q, ...R }) {
    const v = O(null), S = Vo(v, i), { preferences: V } = Zn();
    vo(v, {
      blur: o,
      opacity: s,
      cornerRadius: r,
      tint: a,
      refraction: l,
      aberration: c,
      specular: u,
      rim: f,
      refractionMode: d,
      morphSpeed: m,
      contrast: p,
      saturation: h,
      blurRadius: g,
      fresnelIOR: y,
      fresnelExponent: x,
      envReflectionStrength: w,
      glareDirection: q
    });
    const b = (V == null ? void 0 : V.darkMode) ?? true ? {
      color: "rgba(255, 255, 255, 0.95)",
      textShadow: "0 1px 3px rgba(0, 0, 0, 0.6), 0 0 8px rgba(0, 0, 0, 0.3)"
    } : {
      color: "rgba(0, 0, 0, 0.87)",
      textShadow: "0 1px 2px rgba(255, 255, 255, 0.8), 0 0 6px rgba(255, 255, 255, 0.4)"
    };
    return P("article", {
      ref: S,
      className: t,
      style: {
        position: "relative",
        background: "transparent",
        borderRadius: `${r ?? 24}px`,
        ...b,
        ...n
      },
      ...R,
      children: e
    });
  };
  let zt;
  L = Object.freeze({
    xs: 4,
    sm: 8,
    md: 16,
    lg: 20,
    xl: 24,
    xxl: 32,
    xxxl: 48
  });
  I = Object.freeze({
    sm: 8,
    md: 14,
    lg: 20,
    xl: 28,
    pill: 9999
  });
  ee = Object.freeze({
    toggleWidth: 51,
    toggleHeight: 31,
    toggleThumbSize: 27,
    toggleThumbInset: 2,
    sliderTrackHeight: 4,
    sliderThumbSize: 28,
    segmentedHeight: 32,
    segmentedPadding: 2,
    minTapTarget: 44
  });
  zt = Ne({});
  function So(e) {
    const t = O(null);
    return t.current === null && (t.current = e()), t.current;
  }
  const Ef = typeof window < "u", To = Ef ? Pa : le, Jn = Ne(null);
  function bo(e, t) {
    e.indexOf(t) === -1 && e.push(t);
  }
  function Sn(e, t) {
    const n = e.indexOf(t);
    n > -1 && e.splice(n, 1);
  }
  const be = (e, t, n) => n > t ? t : n < e ? e : n;
  function ki(e, t) {
    return t ? `${e}. For more information and steps for solving, visit https://motion.dev/troubleshooting/${t}` : e;
  }
  let wt = () => {
  }, Ce = () => {
  };
  typeof process < "u" && ((_a2 = process.env) == null ? void 0 : _a2.NODE_ENV) !== "production" && (wt = (e, t, n) => {
    !e && typeof console < "u" && console.warn(ki(t, n));
  }, Ce = (e, t, n) => {
    if (!e) throw new Error(ki(t, n));
  });
  const He = {}, va = (e) => /^-?(?:\d+(?:\.\d+)?|\.\d+)$/u.test(e);
  function Va(e) {
    return typeof e == "object" && e !== null;
  }
  const Sa = (e) => /^0[^.\s]+$/u.test(e);
  function Ta(e) {
    let t;
    return () => (t === void 0 && (t = e()), t);
  }
  const ue = (e) => e, Cf = (e, t) => (n) => t(e(n)), Yt = (...e) => e.reduce(Cf), Ht = (e, t, n) => {
    const i = t - e;
    return i === 0 ? 1 : (n - e) / i;
  };
  class Fo {
    constructor() {
      this.subscriptions = [];
    }
    add(t) {
      return bo(this.subscriptions, t), () => Sn(this.subscriptions, t);
    }
    notify(t, n, i) {
      const o = this.subscriptions.length;
      if (o) if (o === 1) this.subscriptions[0](t, n, i);
      else for (let s = 0; s < o; s++) {
        const r = this.subscriptions[s];
        r && r(t, n, i);
      }
    }
    getSize() {
      return this.subscriptions.length;
    }
    clear() {
      this.subscriptions.length = 0;
    }
  }
  const se = (e) => e * 1e3, ce = (e) => e / 1e3;
  function ba(e, t) {
    return t ? e * (1e3 / t) : 0;
  }
  const Ts = /* @__PURE__ */ new Set();
  function On(e, t, n) {
    e || Ts.has(t) || (console.warn(ki(t, n)), Ts.add(t));
  }
  const Fa = (e, t, n) => (((1 - 3 * n + 3 * t) * e + (3 * n - 6 * t)) * e + 3 * t) * e, Uf = 1e-7, Lf = 12;
  function Nf(e, t, n, i, o) {
    let s, r, a = 0;
    do
      r = t + (n - t) / 2, s = Fa(r, i, o) - e, s > 0 ? n = r : t = r;
    while (Math.abs(s) > Uf && ++a < Lf);
    return r;
  }
  function Bt(e, t, n, i) {
    if (e === t && n === i) return ue;
    const o = (s) => Nf(s, 0, 1, e, n);
    return (s) => s === 0 || s === 1 ? s : Fa(o(s), t, i);
  }
  const Ea = (e) => (t) => t <= 0.5 ? e(2 * t) / 2 : (2 - e(2 * (1 - t))) / 2, Ca = (e) => (t) => 1 - e(1 - t), Ua = Bt(0.33, 1.53, 0.69, 0.99), Eo = Ca(Ua), La = Ea(Eo), Na = (e) => e >= 1 ? 1 : (e *= 2) < 1 ? 0.5 * Eo(e) : 0.5 * (2 - Math.pow(2, -10 * (e - 1))), Co = (e) => 1 - Math.sin(Math.acos(e)), Da = Ca(Co), Ka = Ea(Co), Df = Bt(0.42, 0, 1, 1), Kf = Bt(0, 0, 0.58, 1), Wa = Bt(0.42, 0, 0.58, 1), Wf = (e) => Array.isArray(e) && typeof e[0] != "number", za = (e) => Array.isArray(e) && typeof e[0] == "number", bs = {
    linear: ue,
    easeIn: Df,
    easeInOut: Wa,
    easeOut: Kf,
    circIn: Co,
    circInOut: Ka,
    circOut: Da,
    backIn: Eo,
    backInOut: La,
    backOut: Ua,
    anticipate: Na
  }, zf = (e) => typeof e == "string", Fs = (e) => {
    if (za(e)) {
      Ce(e.length === 4, "Cubic bezier arrays must contain four numerical values.", "cubic-bezier-length");
      const [t, n, i, o] = e;
      return Bt(t, n, i, o);
    } else if (zf(e)) return Ce(bs[e] !== void 0, `Invalid easing type '${e}'`, "invalid-easing-type"), bs[e];
    return e;
  }, nn = [
    "setup",
    "read",
    "resolveKeyframes",
    "preUpdate",
    "update",
    "preRender",
    "render",
    "postRender"
  ];
  function Hf(e, t) {
    let n = /* @__PURE__ */ new Set(), i = /* @__PURE__ */ new Set(), o = false, s = false;
    const r = /* @__PURE__ */ new WeakSet();
    let a = {
      delta: 0,
      timestamp: 0,
      isProcessing: false
    };
    function l(u) {
      r.has(u) && (c.schedule(u), e()), u(a);
    }
    const c = {
      schedule: (u, f = false, d = false) => {
        const p = d && o ? n : i;
        return f && r.add(u), p.add(u), u;
      },
      cancel: (u) => {
        i.delete(u), r.delete(u);
      },
      process: (u) => {
        if (a = u, o) {
          s = true;
          return;
        }
        o = true;
        const f = n;
        n = i, i = f, n.forEach(l), n.clear(), o = false, s && (s = false, c.process(u));
      }
    };
    return c;
  }
  const Mf = 40;
  function Ha(e, t) {
    let n = false, i = true;
    const o = {
      delta: 0,
      timestamp: 0,
      isProcessing: false
    }, s = () => n = true, r = nn.reduce((w, q) => (w[q] = Hf(s), w), {}), { setup: a, read: l, resolveKeyframes: c, preUpdate: u, update: f, preRender: d, render: m, postRender: p } = r, h = () => {
      const w = He.useManualTiming, q = w ? o.timestamp : performance.now();
      n = false, w || (o.delta = i ? 1e3 / 60 : Math.max(Math.min(q - o.timestamp, Mf), 1)), o.timestamp = q, o.isProcessing = true, a.process(o), l.process(o), c.process(o), u.process(o), f.process(o), d.process(o), m.process(o), p.process(o), o.isProcessing = false, n && t && (i = false, e(h));
    }, g = () => {
      n = true, i = true, o.isProcessing || e(h);
    };
    return {
      schedule: nn.reduce((w, q) => {
        const R = r[q];
        return w[q] = (v, S = false, V = false) => (n || g(), R.schedule(v, S, V)), w;
      }, {}),
      cancel: (w) => {
        for (let q = 0; q < nn.length; q++) r[nn[q]].cancel(w);
      },
      state: o,
      steps: r
    };
  }
  const { schedule: M, cancel: Me, state: te, steps: fi } = Ha(typeof requestAnimationFrame < "u" ? requestAnimationFrame : ue, true);
  let mn;
  function kf() {
    mn = void 0;
  }
  const ie = {
    now: () => (mn === void 0 && ie.set(te.isProcessing || He.useManualTiming ? te.timestamp : performance.now()), mn),
    set: (e) => {
      mn = e, queueMicrotask(kf);
    }
  }, Ma = (e) => (t) => typeof t == "string" && t.startsWith(e), ka = Ma("--"), If = Ma("var(--"), Uo = (e) => If(e) ? Zf.test(e.split("/*")[0].trim()) : false, Zf = /var\(--(?:[\w-]+\s*|[\w-]+\s*,(?:\s*[^)(\s]|\s*\((?:[^)(]|\([^)(]*\))*\))+\s*)\)$/iu;
  function Es(e) {
    return typeof e != "string" ? false : e.split("/*")[0].includes("var(--");
  }
  const qt = {
    test: (e) => typeof e == "number",
    parse: parseFloat,
    transform: (e) => e
  }, Mt = {
    ...qt,
    transform: (e) => be(0, 1, e)
  }, on = {
    ...qt,
    default: 1
  }, Nt = (e) => Math.round(e * 1e5) / 1e5, Lo = /-?(?:\d+(?:\.\d+)?|\.\d+)/gu;
  function Jf(e) {
    return e == null;
  }
  const Of = /^(?:#[\da-f]{3,8}|(?:rgb|hsl)a?\((?:-?[\d.]+%?[,\s]+){2}-?[\d.]+%?\s*(?:[,/]\s*)?(?:\b\d+(?:\.\d+)?|\.\d+)?%?\))$/iu, No = (e, t) => (n) => !!(typeof n == "string" && Of.test(n) && n.startsWith(e) || t && !Jf(n) && Object.prototype.hasOwnProperty.call(n, t)), Ia = (e, t, n) => (i) => {
    if (typeof i != "string") return i;
    const [o, s, r, a] = i.match(Lo);
    return {
      [e]: parseFloat(o),
      [t]: parseFloat(s),
      [n]: parseFloat(r),
      alpha: a !== void 0 ? parseFloat(a) : 1
    };
  }, Yf = (e) => be(0, 255, e), di = {
    ...qt,
    transform: (e) => Math.round(Yf(e))
  }, _e = {
    test: No("rgb", "red"),
    parse: Ia("red", "green", "blue"),
    transform: ({ red: e, green: t, blue: n, alpha: i = 1 }) => "rgba(" + di.transform(e) + ", " + di.transform(t) + ", " + di.transform(n) + ", " + Nt(Mt.transform(i)) + ")"
  };
  function Bf(e) {
    let t = "", n = "", i = "", o = "";
    return e.length > 5 ? (t = e.substring(1, 3), n = e.substring(3, 5), i = e.substring(5, 7), o = e.substring(7, 9)) : (t = e.substring(1, 2), n = e.substring(2, 3), i = e.substring(3, 4), o = e.substring(4, 5), t += t, n += n, i += i, o += o), {
      red: parseInt(t, 16),
      green: parseInt(n, 16),
      blue: parseInt(i, 16),
      alpha: o ? parseInt(o, 16) / 255 : 1
    };
  }
  const Ii = {
    test: No("#"),
    parse: Bf,
    transform: _e.transform
  }, jt = (e) => ({
    test: (t) => typeof t == "string" && t.endsWith(e) && t.split(" ").length === 1,
    parse: parseFloat,
    transform: (t) => `${t}${e}`
  }), Ke = jt("deg"), Se = jt("%"), E = jt("px"), jf = jt("vh"), Xf = jt("vw"), Cs = {
    ...Se,
    parse: (e) => Se.parse(e) / 100,
    transform: (e) => Se.transform(e * 100)
  }, ct = {
    test: No("hsl", "hue"),
    parse: Ia("hue", "saturation", "lightness"),
    transform: ({ hue: e, saturation: t, lightness: n, alpha: i = 1 }) => "hsla(" + Math.round(e) + ", " + Se.transform(Nt(t)) + ", " + Se.transform(Nt(n)) + ", " + Nt(Mt.transform(i)) + ")"
  }, Q = {
    test: (e) => _e.test(e) || Ii.test(e) || ct.test(e),
    parse: (e) => _e.test(e) ? _e.parse(e) : ct.test(e) ? ct.parse(e) : Ii.parse(e),
    transform: (e) => typeof e == "string" ? e : e.hasOwnProperty("red") ? _e.transform(e) : ct.transform(e),
    getAnimatableNone: (e) => {
      const t = Q.parse(e);
      return t.alpha = 0, Q.transform(t);
    }
  }, Qf = /(?:#[\da-f]{3,8}|(?:rgb|hsl)a?\((?:-?[\d.]+%?[,\s]+){2}-?[\d.]+%?\s*(?:[,/]\s*)?(?:\b\d+(?:\.\d+)?|\.\d+)?%?\))/giu;
  function Gf(e) {
    var _a3, _b;
    return isNaN(e) && typeof e == "string" && (((_a3 = e.match(Lo)) == null ? void 0 : _a3.length) || 0) + (((_b = e.match(Qf)) == null ? void 0 : _b.length) || 0) > 0;
  }
  const Za = "number", Ja = "color", _f = "var", $f = "var(", Us = "${}", ed = /var\s*\(\s*--(?:[\w-]+\s*|[\w-]+\s*,(?:\s*[^)(\s]|\s*\((?:[^)(]|\([^)(]*\))*\))+\s*)\)|#[\da-f]{3,8}|(?:rgb|hsl)a?\((?:-?[\d.]+%?[,\s]+){2}-?[\d.]+%?\s*(?:[,/]\s*)?(?:\b\d+(?:\.\d+)?|\.\d+)?%?\)|-?(?:\d+(?:\.\d+)?|\.\d+)/giu;
  function gt(e) {
    const t = e.toString(), n = [], i = {
      color: [],
      number: [],
      var: []
    }, o = [];
    let s = 0;
    const a = t.replace(ed, (l) => (Q.test(l) ? (i.color.push(s), o.push(Ja), n.push(Q.parse(l))) : l.startsWith($f) ? (i.var.push(s), o.push(_f), n.push(l)) : (i.number.push(s), o.push(Za), n.push(parseFloat(l))), ++s, Us)).split(Us);
    return {
      values: n,
      split: a,
      indexes: i,
      types: o
    };
  }
  function td(e) {
    return gt(e).values;
  }
  function Oa({ split: e, types: t }) {
    const n = e.length;
    return (i) => {
      let o = "";
      for (let s = 0; s < n; s++) if (o += e[s], i[s] !== void 0) {
        const r = t[s];
        r === Za ? o += Nt(i[s]) : r === Ja ? o += Q.transform(i[s]) : o += i[s];
      }
      return o;
    };
  }
  function nd(e) {
    return Oa(gt(e));
  }
  const id = (e) => typeof e == "number" ? 0 : Q.test(e) ? Q.getAnimatableNone(e) : e, od = (e, t) => typeof e == "number" ? (t == null ? void 0 : t.trim().endsWith("/")) ? e : 0 : id(e);
  function sd(e) {
    const t = gt(e);
    return Oa(t)(t.values.map((i, o) => od(i, t.split[o])));
  }
  const he = {
    test: Gf,
    parse: td,
    createTransformer: nd,
    getAnimatableNone: sd
  };
  function hi(e, t, n) {
    return n < 0 && (n += 1), n > 1 && (n -= 1), n < 1 / 6 ? e + (t - e) * 6 * n : n < 1 / 2 ? t : n < 2 / 3 ? e + (t - e) * (2 / 3 - n) * 6 : e;
  }
  function rd({ hue: e, saturation: t, lightness: n, alpha: i }) {
    e /= 360, t /= 100, n /= 100;
    let o = 0, s = 0, r = 0;
    if (!t) o = s = r = n;
    else {
      const a = n < 0.5 ? n * (1 + t) : n + t - n * t, l = 2 * n - a;
      o = hi(l, a, e + 1 / 3), s = hi(l, a, e), r = hi(l, a, e - 1 / 3);
    }
    return {
      red: Math.round(o * 255),
      green: Math.round(s * 255),
      blue: Math.round(r * 255),
      alpha: i
    };
  }
  function Tn(e, t) {
    return (n) => n > 0 ? t : e;
  }
  const J = (e, t, n) => e + (t - e) * n, mi = (e, t, n) => {
    const i = e * e, o = n * (t * t - i) + i;
    return o < 0 ? 0 : Math.sqrt(o);
  }, ad = [
    Ii,
    _e,
    ct
  ], ld = (e) => ad.find((t) => t.test(e));
  function Ls(e) {
    const t = ld(e);
    if (wt(!!t, `'${e}' is not an animatable color. Use the equivalent color code instead.`, "color-not-animatable"), !t) return false;
    let n = t.parse(e);
    return t === ct && (n = rd(n)), n;
  }
  const Ns = (e, t) => {
    const n = Ls(e), i = Ls(t);
    if (!n || !i) return Tn(e, t);
    const o = {
      ...n
    };
    return (s) => (o.red = mi(n.red, i.red, s), o.green = mi(n.green, i.green, s), o.blue = mi(n.blue, i.blue, s), o.alpha = J(n.alpha, i.alpha, s), _e.transform(o));
  }, Zi = /* @__PURE__ */ new Set([
    "none",
    "hidden"
  ]);
  function cd(e, t) {
    return Zi.has(e) ? (n) => n <= 0 ? e : t : (n) => n >= 1 ? t : e;
  }
  function ud(e, t) {
    return (n) => J(e, t, n);
  }
  function Do(e) {
    return typeof e == "number" ? ud : typeof e == "string" ? Uo(e) ? Tn : Q.test(e) ? Ns : hd : Array.isArray(e) ? Ya : typeof e == "object" ? Q.test(e) ? Ns : fd : Tn;
  }
  function Ya(e, t) {
    const n = [
      ...e
    ], i = n.length, o = e.map((s, r) => Do(s)(s, t[r]));
    return (s) => {
      for (let r = 0; r < i; r++) n[r] = o[r](s);
      return n;
    };
  }
  function fd(e, t) {
    const n = {
      ...e,
      ...t
    }, i = {};
    for (const o in n) e[o] !== void 0 && t[o] !== void 0 && (i[o] = Do(e[o])(e[o], t[o]));
    return (o) => {
      for (const s in i) n[s] = i[s](o);
      return n;
    };
  }
  function dd(e, t) {
    const n = [], i = {
      color: 0,
      var: 0,
      number: 0
    };
    for (let o = 0; o < t.values.length; o++) {
      const s = t.types[o], r = e.indexes[s][i[s]], a = e.values[r] ?? 0;
      n[o] = a, i[s]++;
    }
    return n;
  }
  const hd = (e, t) => {
    const n = he.createTransformer(t), i = gt(e), o = gt(t);
    return i.indexes.var.length === o.indexes.var.length && i.indexes.color.length === o.indexes.color.length && i.indexes.number.length >= o.indexes.number.length ? Zi.has(e) && !o.values.length || Zi.has(t) && !i.values.length ? cd(e, t) : Yt(Ya(dd(i, o), o.values), n) : (wt(true, `Complex values '${e}' and '${t}' too different to mix. Ensure all colors are of the same type, and that each contains the same quantity of number and color values. Falling back to instant transition.`, "complex-values-different"), Tn(e, t));
  };
  function Ba(e, t, n) {
    return typeof e == "number" && typeof t == "number" && typeof n == "number" ? J(e, t, n) : Do(e)(e, t);
  }
  const md = (e) => {
    const t = ({ timestamp: n }) => e(n);
    return {
      start: (n = true) => M.update(t, n),
      stop: () => Me(t),
      now: () => te.isProcessing ? te.timestamp : ie.now()
    };
  }, ja = (e, t, n = 10) => {
    let i = "";
    const o = Math.max(Math.round(t / n), 2);
    for (let s = 0; s < o; s++) i += Math.round(e(s / (o - 1)) * 1e4) / 1e4 + ", ";
    return `linear(${i.substring(0, i.length - 2)})`;
  }, bn = 2e4;
  function Ko(e) {
    let t = 0;
    const n = 50;
    let i = e.next(t);
    for (; !i.done && t < bn; ) t += n, i = e.next(t);
    return t >= bn ? 1 / 0 : t;
  }
  function pd(e, t = 100, n) {
    const i = n({
      ...e,
      keyframes: [
        0,
        t
      ]
    }), o = Math.min(Ko(i), bn);
    return {
      type: "keyframes",
      ease: (s) => i.next(o * s).value / t,
      duration: ce(o)
    };
  }
  const Y = {
    stiffness: 100,
    damping: 10,
    mass: 1,
    velocity: 0,
    duration: 800,
    bounce: 0.3,
    visualDuration: 0.3,
    restSpeed: {
      granular: 0.01,
      default: 2
    },
    restDelta: {
      granular: 5e-3,
      default: 0.5
    },
    minDuration: 0.01,
    maxDuration: 10,
    minDamping: 0.05,
    maxDamping: 1
  };
  function Ji(e, t) {
    return e * Math.sqrt(1 - t * t);
  }
  const gd = 12;
  function Ad(e, t, n) {
    let i = n;
    for (let o = 1; o < gd; o++) i = i - e(i) / t(i);
    return i;
  }
  const pi = 1e-3;
  function yd({ duration: e = Y.duration, bounce: t = Y.bounce, velocity: n = Y.velocity, mass: i = Y.mass }) {
    let o, s;
    wt(e <= se(Y.maxDuration), "Spring duration must be 10 seconds or less", "spring-duration-limit");
    let r = 1 - t;
    r = be(Y.minDamping, Y.maxDamping, r), e = be(Y.minDuration, Y.maxDuration, ce(e)), r < 1 ? (o = (c) => {
      const u = c * r, f = u * e, d = u - n, m = Ji(c, r), p = Math.exp(-f);
      return pi - d / m * p;
    }, s = (c) => {
      const f = c * r * e, d = f * n + n, m = Math.pow(r, 2) * Math.pow(c, 2) * e, p = Math.exp(-f), h = Ji(Math.pow(c, 2), r);
      return (-o(c) + pi > 0 ? -1 : 1) * ((d - m) * p) / h;
    }) : (o = (c) => {
      const u = Math.exp(-c * e), f = (c - n) * e + 1;
      return -pi + u * f;
    }, s = (c) => {
      const u = Math.exp(-c * e), f = (n - c) * (e * e);
      return u * f;
    });
    const a = 5 / e, l = Ad(o, s, a);
    if (e = se(e), isNaN(l)) return {
      stiffness: Y.stiffness,
      damping: Y.damping,
      duration: e
    };
    {
      const c = Math.pow(l, 2) * i;
      return {
        stiffness: c,
        damping: r * 2 * Math.sqrt(i * c),
        duration: e
      };
    }
  }
  const xd = [
    "duration",
    "bounce"
  ], Pd = [
    "stiffness",
    "damping",
    "mass"
  ];
  function Ds(e, t) {
    return t.some((n) => e[n] !== void 0);
  }
  function wd(e) {
    let t = {
      velocity: Y.velocity,
      stiffness: Y.stiffness,
      damping: Y.damping,
      mass: Y.mass,
      isResolvedFromDuration: false,
      ...e
    };
    if (!Ds(e, Pd) && Ds(e, xd)) if (t.velocity = 0, e.visualDuration) {
      const n = e.visualDuration, i = 2 * Math.PI / (n * 1.2), o = i * i, s = 2 * be(0.05, 1, 1 - (e.bounce || 0)) * Math.sqrt(o);
      t = {
        ...t,
        mass: Y.mass,
        stiffness: o,
        damping: s
      };
    } else {
      const n = yd({
        ...e,
        velocity: 0
      });
      t = {
        ...t,
        ...n,
        mass: Y.mass
      }, t.isResolvedFromDuration = true;
    }
    return t;
  }
  function Fn(e = Y.visualDuration, t = Y.bounce) {
    const n = typeof e != "object" ? {
      visualDuration: e,
      keyframes: [
        0,
        1
      ],
      bounce: t
    } : e;
    let { restSpeed: i, restDelta: o } = n;
    const s = n.keyframes[0], r = n.keyframes[n.keyframes.length - 1], a = {
      done: false,
      value: s
    }, { stiffness: l, damping: c, mass: u, duration: f, velocity: d, isResolvedFromDuration: m } = wd({
      ...n,
      velocity: -ce(n.velocity || 0)
    }), p = d || 0, h = c / (2 * Math.sqrt(l * u)), g = r - s, y = ce(Math.sqrt(l / u)), x = Math.abs(g) < 5;
    i || (i = x ? Y.restSpeed.granular : Y.restSpeed.default), o || (o = x ? Y.restDelta.granular : Y.restDelta.default);
    let w, q, R, v, S, V;
    if (h < 1) R = Ji(y, h), v = (p + h * y * g) / R, w = (T) => {
      const F = Math.exp(-h * y * T);
      return r - F * (v * Math.sin(R * T) + g * Math.cos(R * T));
    }, S = h * y * v + g * R, V = h * y * g - v * R, q = (T) => Math.exp(-h * y * T) * (S * Math.sin(R * T) + V * Math.cos(R * T));
    else if (h === 1) {
      w = (F) => r - Math.exp(-y * F) * (g + (p + y * g) * F);
      const T = p + y * g;
      q = (F) => Math.exp(-y * F) * (y * T * F - p);
    } else {
      const T = y * Math.sqrt(h * h - 1);
      w = (K) => {
        const z = Math.exp(-h * y * K), N = Math.min(T * K, 300);
        return r - z * ((p + h * y * g) * Math.sinh(N) + T * g * Math.cosh(N)) / T;
      };
      const F = (p + h * y * g) / T, U = h * y * F - g * T, D = h * y * g - F * T;
      q = (K) => {
        const z = Math.exp(-h * y * K), N = Math.min(T * K, 300);
        return z * (U * Math.sinh(N) + D * Math.cosh(N));
      };
    }
    const b = {
      calculatedDuration: m && f || null,
      velocity: (T) => se(q(T)),
      next: (T) => {
        if (!m && h < 1) {
          const U = Math.exp(-h * y * T), D = Math.sin(R * T), K = Math.cos(R * T), z = r - U * (v * D + g * K), N = se(U * (S * D + V * K));
          return a.done = Math.abs(N) <= i && Math.abs(r - z) <= o, a.value = a.done ? r : z, a;
        }
        const F = w(T);
        if (m) a.done = T >= f;
        else {
          const U = se(q(T));
          a.done = Math.abs(U) <= i && Math.abs(r - F) <= o;
        }
        return a.value = a.done ? r : F, a;
      },
      toString: () => {
        const T = Math.min(Ko(b), bn), F = ja((U) => b.next(T * U).value, T, 30);
        return T + "ms " + F;
      },
      toTransition: () => {
      }
    };
    return b;
  }
  Fn.applyToOptions = (e) => {
    const t = pd(e, 100, Fn);
    return e.ease = t.ease, e.duration = se(t.duration), e.type = "keyframes", e;
  };
  const qd = 5;
  function Xa(e, t, n) {
    const i = Math.max(t - qd, 0);
    return ba(n - e(i), t - i);
  }
  function Oi({ keyframes: e, velocity: t = 0, power: n = 0.8, timeConstant: i = 325, bounceDamping: o = 10, bounceStiffness: s = 500, modifyTarget: r, min: a, max: l, restDelta: c = 0.5, restSpeed: u }) {
    const f = e[0], d = {
      done: false,
      value: f
    }, m = (V) => a !== void 0 && V < a || l !== void 0 && V > l, p = (V) => a === void 0 ? l : l === void 0 || Math.abs(a - V) < Math.abs(l - V) ? a : l;
    let h = n * t;
    const g = f + h, y = r === void 0 ? g : r(g);
    y !== g && (h = y - f);
    const x = (V) => -h * Math.exp(-V / i), w = (V) => y + x(V), q = (V) => {
      const b = x(V), T = w(V);
      d.done = Math.abs(b) <= c, d.value = d.done ? y : T;
    };
    let R, v;
    const S = (V) => {
      m(d.value) && (R = V, v = Fn({
        keyframes: [
          d.value,
          p(d.value)
        ],
        velocity: Xa(w, V, d.value),
        damping: o,
        stiffness: s,
        restDelta: c,
        restSpeed: u
      }));
    };
    return S(0), {
      calculatedDuration: null,
      next: (V) => {
        let b = false;
        return !v && R === void 0 && (b = true, q(V), S(V)), R !== void 0 && V >= R ? v.next(V - R) : (!b && q(V), d);
      }
    };
  }
  function Rd(e, t, n) {
    const i = [], o = n || He.mix || Ba, s = e.length - 1;
    for (let r = 0; r < s; r++) {
      let a = o(e[r], e[r + 1]);
      if (t) {
        const l = Array.isArray(t) ? t[r] || ue : t;
        a = Yt(l, a);
      }
      i.push(a);
    }
    return i;
  }
  function vd(e, t, { clamp: n = true, ease: i, mixer: o } = {}) {
    const s = e.length;
    if (Ce(s === t.length, "Both input and output ranges must be the same length", "range-length"), s === 1) return () => t[0];
    if (s === 2 && t[0] === t[1]) return () => t[1];
    const r = e[0] === e[1];
    e[0] > e[s - 1] && (e = [
      ...e
    ].reverse(), t = [
      ...t
    ].reverse());
    const a = Rd(t, i, o), l = a.length, c = (u) => {
      if (r && u < e[0]) return t[0];
      let f = 0;
      if (l > 1) for (; f < e.length - 2 && !(u < e[f + 1]); f++) ;
      const d = Ht(e[f], e[f + 1], u);
      return a[f](d);
    };
    return n ? (u) => c(be(e[0], e[s - 1], u)) : c;
  }
  function Vd(e, t) {
    const n = e[e.length - 1];
    for (let i = 1; i <= t; i++) {
      const o = Ht(0, t, i);
      e.push(J(n, 1, o));
    }
  }
  function Sd(e) {
    const t = [
      0
    ];
    return Vd(t, e.length - 1), t;
  }
  function Td(e, t) {
    return e.map((n) => n * t);
  }
  function bd(e, t) {
    return e.map(() => t || Wa).splice(0, e.length - 1);
  }
  function ut({ duration: e = 300, keyframes: t, times: n, ease: i = "easeInOut" }) {
    const o = Wf(i) ? i.map(Fs) : Fs(i), s = {
      done: false,
      value: t[0]
    }, r = Td(n && n.length === t.length ? n : Sd(t), e), a = vd(r, t, {
      ease: Array.isArray(o) ? o : bd(t, o)
    });
    return {
      calculatedDuration: e,
      next: (l) => (s.value = a(l), s.done = l >= e, s)
    };
  }
  const Fd = (e) => e !== null;
  function Yn(e, { repeat: t, repeatType: n = "loop" }, i, o = 1) {
    const s = e.filter(Fd), a = o < 0 || t && n !== "loop" && t % 2 === 1 ? 0 : s.length - 1;
    return !a || i === void 0 ? s[a] : i;
  }
  const Ed = {
    decay: Oi,
    inertia: Oi,
    tween: ut,
    keyframes: ut,
    spring: Fn
  };
  function Qa(e) {
    typeof e.type == "string" && (e.type = Ed[e.type]);
  }
  class Wo {
    constructor() {
      this.updateFinished();
    }
    get finished() {
      return this._finished;
    }
    updateFinished() {
      this._finished = new Promise((t) => {
        this.resolve = t;
      });
    }
    notifyFinished() {
      this.resolve();
    }
    then(t, n) {
      return this.finished.then(t, n);
    }
  }
  const Cd = (e) => e / 100;
  class En extends Wo {
    constructor(t) {
      super(), this.state = "idle", this.startTime = null, this.isStopped = false, this.currentTime = 0, this.holdTime = null, this.playbackSpeed = 1, this.delayState = {
        done: false,
        value: void 0
      }, this.stop = () => {
        var _a3, _b;
        const { motionValue: n } = this.options;
        n && n.updatedAt !== ie.now() && this.tick(ie.now()), this.isStopped = true, this.state !== "idle" && (this.teardown(), (_b = (_a3 = this.options).onStop) == null ? void 0 : _b.call(_a3));
      }, this.options = t, this.initAnimation(), this.play(), t.autoplay === false && this.pause();
    }
    initAnimation() {
      const { options: t } = this;
      Qa(t);
      const { type: n = ut, repeat: i = 0, repeatDelay: o = 0, repeatType: s, velocity: r = 0 } = t;
      let { keyframes: a } = t;
      const l = n || ut;
      process.env.NODE_ENV !== "production" && l !== ut && Ce(a.length <= 2, `Only two keyframes currently supported with spring and inertia animations. Trying to animate ${a}`, "spring-two-frames"), l !== ut && typeof a[0] != "number" && (this.mixKeyframes = Yt(Cd, Ba(a[0], a[1])), a = [
        0,
        100
      ]);
      const c = l({
        ...t,
        keyframes: a
      });
      s === "mirror" && (this.mirroredGenerator = l({
        ...t,
        keyframes: [
          ...a
        ].reverse(),
        velocity: -r
      })), c.calculatedDuration === null && (c.calculatedDuration = Ko(c));
      const { calculatedDuration: u } = c;
      this.calculatedDuration = u, this.resolvedDuration = u + o, this.totalDuration = this.resolvedDuration * (i + 1) - o, this.generator = c;
    }
    updateTime(t) {
      const n = Math.round(t - this.startTime) * this.playbackSpeed;
      this.holdTime !== null ? this.currentTime = this.holdTime : this.currentTime = n;
    }
    tick(t, n = false) {
      const { generator: i, totalDuration: o, mixKeyframes: s, mirroredGenerator: r, resolvedDuration: a, calculatedDuration: l } = this;
      if (this.startTime === null) return i.next(0);
      const { delay: c = 0, keyframes: u, repeat: f, repeatType: d, repeatDelay: m, type: p, onUpdate: h, finalKeyframe: g } = this.options;
      this.speed > 0 ? this.startTime = Math.min(this.startTime, t) : this.speed < 0 && (this.startTime = Math.min(t - o / this.speed, this.startTime)), n ? this.currentTime = t : this.updateTime(t);
      const y = this.currentTime - c * (this.playbackSpeed >= 0 ? 1 : -1), x = this.playbackSpeed >= 0 ? y < 0 : y > o;
      this.currentTime = Math.max(y, 0), this.state === "finished" && this.holdTime === null && (this.currentTime = o);
      let w = this.currentTime, q = i;
      if (f) {
        const V = Math.min(this.currentTime, o) / a;
        let b = Math.floor(V), T = V % 1;
        !T && V >= 1 && (T = 1), T === 1 && b--, b = Math.min(b, f + 1), !!(b % 2) && (d === "reverse" ? (T = 1 - T, m && (T -= m / a)) : d === "mirror" && (q = r)), w = be(0, 1, T) * a;
      }
      let R;
      x ? (this.delayState.value = u[0], R = this.delayState) : R = q.next(w), s && !x && (R.value = s(R.value));
      let { done: v } = R;
      !x && l !== null && (v = this.playbackSpeed >= 0 ? this.currentTime >= o : this.currentTime <= 0);
      const S = this.holdTime === null && (this.state === "finished" || this.state === "running" && v);
      return S && p !== Oi && (R.value = Yn(u, this.options, g, this.speed)), h && h(R.value), S && this.finish(), R;
    }
    then(t, n) {
      return this.finished.then(t, n);
    }
    get duration() {
      return ce(this.calculatedDuration);
    }
    get iterationDuration() {
      const { delay: t = 0 } = this.options || {};
      return this.duration + ce(t);
    }
    get time() {
      return ce(this.currentTime);
    }
    set time(t) {
      t = se(t), this.currentTime = t, this.startTime === null || this.holdTime !== null || this.playbackSpeed === 0 ? this.holdTime = t : this.driver && (this.startTime = this.driver.now() - t / this.playbackSpeed), this.driver ? this.driver.start(false) : (this.startTime = 0, this.state = "paused", this.holdTime = t, this.tick(t));
    }
    getGeneratorVelocity() {
      const t = this.currentTime;
      if (t <= 0) return this.options.velocity || 0;
      if (this.generator.velocity) return this.generator.velocity(t);
      const n = this.generator.next(t).value;
      return Xa((i) => this.generator.next(i).value, t, n);
    }
    get speed() {
      return this.playbackSpeed;
    }
    set speed(t) {
      const n = this.playbackSpeed !== t;
      n && this.driver && this.updateTime(ie.now()), this.playbackSpeed = t, n && this.driver && (this.time = ce(this.currentTime));
    }
    play() {
      var _a3, _b;
      if (this.isStopped) return;
      const { driver: t = md, startTime: n } = this.options;
      this.driver || (this.driver = t((o) => this.tick(o))), (_b = (_a3 = this.options).onPlay) == null ? void 0 : _b.call(_a3);
      const i = this.driver.now();
      this.state === "finished" ? (this.updateFinished(), this.startTime = i) : this.holdTime !== null ? this.startTime = i - this.holdTime : this.startTime || (this.startTime = n ?? i), this.state === "finished" && this.speed < 0 && (this.startTime += this.calculatedDuration), this.holdTime = null, this.state = "running", this.driver.start();
    }
    pause() {
      this.state = "paused", this.updateTime(ie.now()), this.holdTime = this.currentTime;
    }
    complete() {
      this.state !== "running" && this.play(), this.state = "finished", this.holdTime = null;
    }
    finish() {
      var _a3, _b;
      this.notifyFinished(), this.teardown(), this.state = "finished", (_b = (_a3 = this.options).onComplete) == null ? void 0 : _b.call(_a3);
    }
    cancel() {
      var _a3, _b;
      this.holdTime = null, this.startTime = 0, this.tick(0), this.teardown(), (_b = (_a3 = this.options).onCancel) == null ? void 0 : _b.call(_a3);
    }
    teardown() {
      this.state = "idle", this.stopDriver(), this.startTime = this.holdTime = null;
    }
    stopDriver() {
      this.driver && (this.driver.stop(), this.driver = void 0);
    }
    sample(t) {
      return this.startTime = 0, this.tick(t, true);
    }
    attachTimeline(t) {
      var _a3;
      return this.options.allowFlatten && (this.options.type = "keyframes", this.options.ease = "linear", this.initAnimation()), (_a3 = this.driver) == null ? void 0 : _a3.stop(), t.observe(this);
    }
  }
  function Ud(e) {
    for (let t = 1; t < e.length; t++) e[t] ?? (e[t] = e[t - 1]);
  }
  const $e = (e) => e * 180 / Math.PI, Yi = (e) => {
    const t = $e(Math.atan2(e[1], e[0]));
    return Bi(t);
  }, Ld = {
    x: 4,
    y: 5,
    translateX: 4,
    translateY: 5,
    scaleX: 0,
    scaleY: 3,
    scale: (e) => (Math.abs(e[0]) + Math.abs(e[3])) / 2,
    rotate: Yi,
    rotateZ: Yi,
    skewX: (e) => $e(Math.atan(e[1])),
    skewY: (e) => $e(Math.atan(e[2])),
    skew: (e) => (Math.abs(e[1]) + Math.abs(e[2])) / 2
  }, Bi = (e) => (e = e % 360, e < 0 && (e += 360), e), Ks = Yi, Ws = (e) => Math.sqrt(e[0] * e[0] + e[1] * e[1]), zs = (e) => Math.sqrt(e[4] * e[4] + e[5] * e[5]), Nd = {
    x: 12,
    y: 13,
    z: 14,
    translateX: 12,
    translateY: 13,
    translateZ: 14,
    scaleX: Ws,
    scaleY: zs,
    scale: (e) => (Ws(e) + zs(e)) / 2,
    rotateX: (e) => Bi($e(Math.atan2(e[6], e[5]))),
    rotateY: (e) => Bi($e(Math.atan2(-e[2], e[0]))),
    rotateZ: Ks,
    rotate: Ks,
    skewX: (e) => $e(Math.atan(e[4])),
    skewY: (e) => $e(Math.atan(e[1])),
    skew: (e) => (Math.abs(e[1]) + Math.abs(e[4])) / 2
  };
  function ji(e) {
    return e.includes("scale") ? 1 : 0;
  }
  function Xi(e, t) {
    if (!e || e === "none") return ji(t);
    const n = e.match(/^matrix3d\(([-\d.e\s,]+)\)$/u);
    let i, o;
    if (n) i = Nd, o = n;
    else {
      const a = e.match(/^matrix\(([-\d.e\s,]+)\)$/u);
      i = Ld, o = a;
    }
    if (!o) return ji(t);
    const s = i[t], r = o[1].split(",").map(Kd);
    return typeof s == "function" ? s(r) : r[s];
  }
  const Dd = (e, t) => {
    const { transform: n = "none" } = getComputedStyle(e);
    return Xi(n, t);
  };
  function Kd(e) {
    return parseFloat(e.trim());
  }
  const Rt = [
    "transformPerspective",
    "x",
    "y",
    "z",
    "translateX",
    "translateY",
    "translateZ",
    "scale",
    "scaleX",
    "scaleY",
    "rotate",
    "rotateX",
    "rotateY",
    "rotateZ",
    "skew",
    "skewX",
    "skewY"
  ], vt = new Set(Rt), Hs = (e) => e === qt || e === E, Wd = /* @__PURE__ */ new Set([
    "x",
    "y",
    "z"
  ]), zd = Rt.filter((e) => !Wd.has(e));
  function Hd(e) {
    const t = [];
    return zd.forEach((n) => {
      const i = e.getValue(n);
      i !== void 0 && (t.push([
        n,
        i.get()
      ]), i.set(n.startsWith("scale") ? 1 : 0));
    }), t;
  }
  const ze = {
    width: ({ x: e }, { paddingLeft: t = "0", paddingRight: n = "0", boxSizing: i }) => {
      const o = e.max - e.min;
      return i === "border-box" ? o : o - parseFloat(t) - parseFloat(n);
    },
    height: ({ y: e }, { paddingTop: t = "0", paddingBottom: n = "0", boxSizing: i }) => {
      const o = e.max - e.min;
      return i === "border-box" ? o : o - parseFloat(t) - parseFloat(n);
    },
    top: (e, { top: t }) => parseFloat(t),
    left: (e, { left: t }) => parseFloat(t),
    bottom: ({ y: e }, { top: t }) => parseFloat(t) + (e.max - e.min),
    right: ({ x: e }, { left: t }) => parseFloat(t) + (e.max - e.min),
    x: (e, { transform: t }) => Xi(t, "x"),
    y: (e, { transform: t }) => Xi(t, "y")
  };
  ze.translateX = ze.x;
  ze.translateY = ze.y;
  const et = /* @__PURE__ */ new Set();
  let Qi = false, Gi = false, _i = false;
  function Ga() {
    if (Gi) {
      const e = Array.from(et).filter((i) => i.needsMeasurement), t = new Set(e.map((i) => i.element)), n = /* @__PURE__ */ new Map();
      t.forEach((i) => {
        const o = Hd(i);
        o.length && (n.set(i, o), i.render());
      }), e.forEach((i) => i.measureInitialState()), t.forEach((i) => {
        i.render();
        const o = n.get(i);
        o && o.forEach(([s, r]) => {
          var _a3;
          (_a3 = i.getValue(s)) == null ? void 0 : _a3.set(r);
        });
      }), e.forEach((i) => i.measureEndState()), e.forEach((i) => {
        i.suspendedScrollY !== void 0 && window.scrollTo(0, i.suspendedScrollY);
      });
    }
    Gi = false, Qi = false, et.forEach((e) => e.complete(_i)), et.clear();
  }
  function _a() {
    et.forEach((e) => {
      e.readKeyframes(), e.needsMeasurement && (Gi = true);
    });
  }
  function Md() {
    _i = true, _a(), Ga(), _i = false;
  }
  class zo {
    constructor(t, n, i, o, s, r = false) {
      this.state = "pending", this.isAsync = false, this.needsMeasurement = false, this.unresolvedKeyframes = [
        ...t
      ], this.onComplete = n, this.name = i, this.motionValue = o, this.element = s, this.isAsync = r;
    }
    scheduleResolve() {
      this.state = "scheduled", this.isAsync ? (et.add(this), Qi || (Qi = true, M.read(_a), M.resolveKeyframes(Ga))) : (this.readKeyframes(), this.complete());
    }
    readKeyframes() {
      const { unresolvedKeyframes: t, name: n, element: i, motionValue: o } = this;
      if (t[0] === null) {
        const s = o == null ? void 0 : o.get(), r = t[t.length - 1];
        if (s !== void 0) t[0] = s;
        else if (i && n) {
          const a = i.readValue(n, r);
          a != null && (t[0] = a);
        }
        t[0] === void 0 && (t[0] = r), o && s === void 0 && o.set(t[0]);
      }
      Ud(t);
    }
    setFinalKeyframe() {
    }
    measureInitialState() {
    }
    renderEndStyles() {
    }
    measureEndState() {
    }
    complete(t = false) {
      this.state = "complete", this.onComplete(this.unresolvedKeyframes, this.finalKeyframe, t), et.delete(this);
    }
    cancel() {
      this.state === "scheduled" && (et.delete(this), this.state = "pending");
    }
    resume() {
      this.state === "pending" && this.scheduleResolve();
    }
  }
  const kd = (e) => e.startsWith("--");
  function $a(e, t, n) {
    kd(t) ? e.style.setProperty(t, n) : e.style[t] = n;
  }
  const Id = {};
  function el(e, t) {
    const n = Ta(e);
    return () => Id[t] ?? n();
  }
  const Zd = el(() => window.ScrollTimeline !== void 0, "scrollTimeline"), tl = el(() => {
    try {
      document.createElement("div").animate({
        opacity: 0
      }, {
        easing: "linear(0, 1)"
      });
    } catch {
      return false;
    }
    return true;
  }, "linearEasing"), Lt = ([e, t, n, i]) => `cubic-bezier(${e}, ${t}, ${n}, ${i})`, Ms = {
    linear: "linear",
    ease: "ease",
    easeIn: "ease-in",
    easeOut: "ease-out",
    easeInOut: "ease-in-out",
    circIn: Lt([
      0,
      0.65,
      0.55,
      1
    ]),
    circOut: Lt([
      0.55,
      0,
      1,
      0.45
    ]),
    backIn: Lt([
      0.31,
      0.01,
      0.66,
      -0.59
    ]),
    backOut: Lt([
      0.33,
      1.53,
      0.69,
      0.99
    ])
  };
  function nl(e, t) {
    if (e) return typeof e == "function" ? tl() ? ja(e, t) : "ease-out" : za(e) ? Lt(e) : Array.isArray(e) ? e.map((n) => nl(n, t) || Ms.easeOut) : Ms[e];
  }
  function Jd(e, t, n, { delay: i = 0, duration: o = 300, repeat: s = 0, repeatType: r = "loop", ease: a = "easeOut", times: l } = {}, c = void 0) {
    const u = {
      [t]: n
    };
    l && (u.offset = l);
    const f = nl(a, o);
    Array.isArray(f) && (u.easing = f);
    const d = {
      delay: i,
      duration: o,
      easing: Array.isArray(f) ? "linear" : f,
      fill: "both",
      iterations: s + 1,
      direction: r === "reverse" ? "alternate" : "normal"
    };
    return c && (d.pseudoElement = c), e.animate(u, d);
  }
  function il(e) {
    return typeof e == "function" && "applyToOptions" in e;
  }
  function Od({ type: e, ...t }) {
    return il(e) && tl() ? e.applyToOptions(t) : (t.duration ?? (t.duration = 300), t.ease ?? (t.ease = "easeOut"), t);
  }
  class ol extends Wo {
    constructor(t) {
      if (super(), this.finishedTime = null, this.isStopped = false, this.manualStartTime = null, !t) return;
      const { element: n, name: i, keyframes: o, pseudoElement: s, allowFlatten: r = false, finalKeyframe: a, onComplete: l } = t;
      this.isPseudoElement = !!s, this.allowFlatten = r, this.options = t, Ce(typeof t.type != "string", `Mini animate() doesn't support "type" as a string.`, "mini-spring");
      const c = Od(t);
      this.animation = Jd(n, i, o, c, s), c.autoplay === false && this.animation.pause(), this.animation.onfinish = () => {
        if (this.finishedTime = this.time, !s) {
          const u = Yn(o, this.options, a, this.speed);
          this.updateMotionValue && this.updateMotionValue(u), $a(n, i, u), this.animation.cancel();
        }
        l == null ? void 0 : l(), this.notifyFinished();
      };
    }
    play() {
      this.isStopped || (this.manualStartTime = null, this.animation.play(), this.state === "finished" && this.updateFinished());
    }
    pause() {
      this.animation.pause();
    }
    complete() {
      var _a3, _b;
      (_b = (_a3 = this.animation).finish) == null ? void 0 : _b.call(_a3);
    }
    cancel() {
      try {
        this.animation.cancel();
      } catch {
      }
    }
    stop() {
      if (this.isStopped) return;
      this.isStopped = true;
      const { state: t } = this;
      t === "idle" || t === "finished" || (this.updateMotionValue ? this.updateMotionValue() : this.commitStyles(), this.isPseudoElement || this.cancel());
    }
    commitStyles() {
      var _a3, _b, _c2;
      const t = (_a3 = this.options) == null ? void 0 : _a3.element;
      !this.isPseudoElement && (t == null ? void 0 : t.isConnected) && ((_c2 = (_b = this.animation).commitStyles) == null ? void 0 : _c2.call(_b));
    }
    get duration() {
      var _a3, _b;
      const t = ((_b = (_a3 = this.animation.effect) == null ? void 0 : _a3.getComputedTiming) == null ? void 0 : _b.call(_a3).duration) || 0;
      return ce(Number(t));
    }
    get iterationDuration() {
      const { delay: t = 0 } = this.options || {};
      return this.duration + ce(t);
    }
    get time() {
      return ce(Number(this.animation.currentTime) || 0);
    }
    set time(t) {
      const n = this.finishedTime !== null;
      this.manualStartTime = null, this.finishedTime = null, this.animation.currentTime = se(t), n && this.animation.pause();
    }
    get speed() {
      return this.animation.playbackRate;
    }
    set speed(t) {
      t < 0 && (this.finishedTime = null), this.animation.playbackRate = t;
    }
    get state() {
      return this.finishedTime !== null ? "finished" : this.animation.playState;
    }
    get startTime() {
      return this.manualStartTime ?? Number(this.animation.startTime);
    }
    set startTime(t) {
      this.manualStartTime = this.animation.startTime = t;
    }
    attachTimeline({ timeline: t, rangeStart: n, rangeEnd: i, observe: o }) {
      var _a3;
      return this.allowFlatten && ((_a3 = this.animation.effect) == null ? void 0 : _a3.updateTiming({
        easing: "linear"
      })), this.animation.onfinish = null, t && Zd() ? (this.animation.timeline = t, n && (this.animation.rangeStart = n), i && (this.animation.rangeEnd = i), ue) : o(this);
    }
  }
  const sl = {
    anticipate: Na,
    backInOut: La,
    circInOut: Ka
  };
  function Yd(e) {
    return e in sl;
  }
  function Bd(e) {
    typeof e.ease == "string" && Yd(e.ease) && (e.ease = sl[e.ease]);
  }
  const gi = 10;
  class jd extends ol {
    constructor(t) {
      Bd(t), Qa(t), super(t), t.startTime !== void 0 && t.autoplay !== false && (this.startTime = t.startTime), this.options = t;
    }
    updateMotionValue(t) {
      const { motionValue: n, onUpdate: i, onComplete: o, element: s, ...r } = this.options;
      if (!n) return;
      if (t !== void 0) {
        n.set(t);
        return;
      }
      const a = new En({
        ...r,
        autoplay: false
      }), l = Math.max(gi, ie.now() - this.startTime), c = be(0, gi, l - gi), u = a.sample(l).value, { name: f } = this.options;
      s && f && $a(s, f, u), n.setWithVelocity(a.sample(Math.max(0, l - c)).value, u, c), a.stop();
    }
  }
  const ks = (e, t) => t === "zIndex" ? false : !!(typeof e == "number" || Array.isArray(e) || typeof e == "string" && (he.test(e) || e === "0") && !e.startsWith("url("));
  function Xd(e) {
    const t = e[0];
    if (e.length === 1) return true;
    for (let n = 0; n < e.length; n++) if (e[n] !== t) return true;
  }
  function Qd(e, t, n, i) {
    const o = e[0];
    if (o === null) return false;
    if (t === "display" || t === "visibility") return true;
    const s = e[e.length - 1], r = ks(o, t), a = ks(s, t);
    return wt(r === a, `You are trying to animate ${t} from "${o}" to "${s}". "${r ? s : o}" is not an animatable value.`, "value-not-animatable"), !r || !a ? false : Xd(e) || (n === "spring" || il(n)) && i;
  }
  function $i(e) {
    e.duration = 0, e.type = "keyframes";
  }
  const rl = /* @__PURE__ */ new Set([
    "opacity",
    "clipPath",
    "filter",
    "transform"
  ]), Gd = /^(?:oklch|oklab|lab|lch|color|color-mix|light-dark)\(/;
  function _d(e) {
    for (let t = 0; t < e.length; t++) if (typeof e[t] == "string" && Gd.test(e[t])) return true;
    return false;
  }
  const $d = /* @__PURE__ */ new Set([
    "color",
    "backgroundColor",
    "outlineColor",
    "fill",
    "stroke",
    "borderColor",
    "borderTopColor",
    "borderRightColor",
    "borderBottomColor",
    "borderLeftColor"
  ]), eh = Ta(() => Object.hasOwnProperty.call(Element.prototype, "animate"));
  function th(e) {
    var _a3;
    const { motionValue: t, name: n, repeatDelay: i, repeatType: o, damping: s, type: r, keyframes: a } = e;
    if (!(((_a3 = t == null ? void 0 : t.owner) == null ? void 0 : _a3.current) instanceof HTMLElement)) return false;
    const { onUpdate: c, transformTemplate: u } = t.owner.getProps();
    return eh() && n && (rl.has(n) || $d.has(n) && _d(a)) && (n !== "transform" || !u) && !c && !i && o !== "mirror" && s !== 0 && r !== "inertia";
  }
  const nh = 40;
  class ih extends Wo {
    constructor({ autoplay: t = true, delay: n = 0, type: i = "keyframes", repeat: o = 0, repeatDelay: s = 0, repeatType: r = "loop", keyframes: a, name: l, motionValue: c, element: u, ...f }) {
      var _a3;
      super(), this.stop = () => {
        var _a4, _b;
        this._animation && (this._animation.stop(), (_a4 = this.stopTimeline) == null ? void 0 : _a4.call(this)), (_b = this.keyframeResolver) == null ? void 0 : _b.cancel();
      }, this.createdAt = ie.now();
      const d = {
        autoplay: t,
        delay: n,
        type: i,
        repeat: o,
        repeatDelay: s,
        repeatType: r,
        name: l,
        motionValue: c,
        element: u,
        ...f
      }, m = (u == null ? void 0 : u.KeyframeResolver) || zo;
      this.keyframeResolver = new m(a, (p, h, g) => this.onKeyframesResolved(p, h, d, !g), l, c, u), (_a3 = this.keyframeResolver) == null ? void 0 : _a3.scheduleResolve();
    }
    onKeyframesResolved(t, n, i, o) {
      var _a3, _b;
      this.keyframeResolver = void 0;
      const { name: s, type: r, velocity: a, delay: l, isHandoff: c, onUpdate: u } = i;
      this.resolvedAt = ie.now();
      let f = true;
      Qd(t, s, r, a) || (f = false, (He.instantAnimations || !l) && (u == null ? void 0 : u(Yn(t, i, n))), t[0] = t[t.length - 1], $i(i), i.repeat = 0);
      const m = {
        startTime: o ? this.resolvedAt ? this.resolvedAt - this.createdAt > nh ? this.resolvedAt : this.createdAt : this.createdAt : void 0,
        finalKeyframe: n,
        ...i,
        keyframes: t
      }, p = f && !c && th(m), h = (_b = (_a3 = m.motionValue) == null ? void 0 : _a3.owner) == null ? void 0 : _b.current;
      let g;
      if (p) try {
        g = new jd({
          ...m,
          element: h
        });
      } catch {
        g = new En(m);
      }
      else g = new En(m);
      g.finished.then(() => {
        this.notifyFinished();
      }).catch(ue), this.pendingTimeline && (this.stopTimeline = g.attachTimeline(this.pendingTimeline), this.pendingTimeline = void 0), this._animation = g;
    }
    get finished() {
      return this._animation ? this.animation.finished : this._finished;
    }
    then(t, n) {
      return this.finished.finally(t).then(() => {
      });
    }
    get animation() {
      var _a3;
      return this._animation || ((_a3 = this.keyframeResolver) == null ? void 0 : _a3.resume(), Md()), this._animation;
    }
    get duration() {
      return this.animation.duration;
    }
    get iterationDuration() {
      return this.animation.iterationDuration;
    }
    get time() {
      return this.animation.time;
    }
    set time(t) {
      this.animation.time = t;
    }
    get speed() {
      return this.animation.speed;
    }
    get state() {
      return this.animation.state;
    }
    set speed(t) {
      this.animation.speed = t;
    }
    get startTime() {
      return this.animation.startTime;
    }
    attachTimeline(t) {
      return this._animation ? this.stopTimeline = this.animation.attachTimeline(t) : this.pendingTimeline = t, () => this.stop();
    }
    play() {
      this.animation.play();
    }
    pause() {
      this.animation.pause();
    }
    complete() {
      this.animation.complete();
    }
    cancel() {
      var _a3;
      this._animation && this.animation.cancel(), (_a3 = this.keyframeResolver) == null ? void 0 : _a3.cancel();
    }
  }
  function al(e, t, n, i = 0, o = 1) {
    const s = Array.from(e).sort((c, u) => c.sortNodePosition(u)).indexOf(t), r = e.size, a = (r - 1) * i;
    return typeof n == "function" ? n(s, r) : o === 1 ? s * i : a - s * i;
  }
  const oh = /^var\(--(?:([\w-]+)|([\w-]+), ?([a-zA-Z\d ()%#.,-]+))\)/u;
  function sh(e) {
    const t = oh.exec(e);
    if (!t) return [
      ,
    ];
    const [, n, i, o] = t;
    return [
      `--${n ?? i}`,
      o
    ];
  }
  const rh = 4;
  function ll(e, t, n = 1) {
    Ce(n <= rh, `Max CSS variable fallback depth detected in property "${e}". This may indicate a circular fallback dependency.`, "max-css-var-depth");
    const [i, o] = sh(e);
    if (!i) return;
    const s = window.getComputedStyle(t).getPropertyValue(i);
    if (s) {
      const r = s.trim();
      return va(r) ? parseFloat(r) : r;
    }
    return Uo(o) ? ll(o, t, n + 1) : o;
  }
  const ah = {
    type: "spring",
    stiffness: 500,
    damping: 25,
    restSpeed: 10
  }, lh = (e) => ({
    type: "spring",
    stiffness: 550,
    damping: e === 0 ? 2 * Math.sqrt(550) : 30,
    restSpeed: 10
  }), ch = {
    type: "keyframes",
    duration: 0.8
  }, uh = {
    type: "keyframes",
    ease: [
      0.25,
      0.1,
      0.35,
      1
    ],
    duration: 0.3
  }, fh = (e, { keyframes: t }) => t.length > 2 ? ch : vt.has(e) ? e.startsWith("scale") ? lh(t[1]) : ah : uh;
  function cl(e, t) {
    if ((e == null ? void 0 : e.inherit) && t) {
      const { inherit: n, ...i } = e;
      return {
        ...t,
        ...i
      };
    }
    return e;
  }
  function Ho(e, t) {
    const n = (e == null ? void 0 : e[t]) ?? (e == null ? void 0 : e.default) ?? e;
    return n !== e ? cl(n, e) : n;
  }
  const dh = /* @__PURE__ */ new Set([
    "when",
    "delay",
    "delayChildren",
    "staggerChildren",
    "staggerDirection",
    "repeat",
    "repeatType",
    "repeatDelay",
    "from",
    "elapsed"
  ]);
  function hh(e) {
    for (const t in e) if (!dh.has(t)) return true;
    return false;
  }
  const Mo = (e, t, n, i = {}, o, s) => (r) => {
    const a = Ho(i, e) || {}, l = a.delay || i.delay || 0;
    let { elapsed: c = 0 } = i;
    c = c - se(l);
    const u = {
      keyframes: Array.isArray(n) ? n : [
        null,
        n
      ],
      ease: "easeOut",
      velocity: t.getVelocity(),
      ...a,
      delay: -c,
      onUpdate: (d) => {
        t.set(d), a.onUpdate && a.onUpdate(d);
      },
      onComplete: () => {
        r(), a.onComplete && a.onComplete();
      },
      name: e,
      motionValue: t,
      element: s ? void 0 : o
    };
    hh(a) || Object.assign(u, fh(e, u)), u.duration && (u.duration = se(u.duration)), u.repeatDelay && (u.repeatDelay = se(u.repeatDelay)), u.from !== void 0 && (u.keyframes[0] = u.from);
    let f = false;
    if ((u.type === false || u.duration === 0 && !u.repeatDelay) && ($i(u), u.delay === 0 && (f = true)), (He.instantAnimations || He.skipAnimations || (o == null ? void 0 : o.shouldSkipAnimations)) && (f = true, $i(u), u.delay = 0), u.allowFlatten = !a.type && !a.ease, f && !s && t.get() !== void 0) {
      const d = Yn(u.keyframes, a);
      if (d !== void 0) {
        M.update(() => {
          u.onUpdate(d), u.onComplete();
        });
        return;
      }
    }
    return a.isSync ? new En(u) : new ih(u);
  };
  function Is(e) {
    const t = [
      {},
      {}
    ];
    return e == null ? void 0 : e.values.forEach((n, i) => {
      t[0][i] = n.get(), t[1][i] = n.getVelocity();
    }), t;
  }
  function ko(e, t, n, i) {
    if (typeof t == "function") {
      const [o, s] = Is(i);
      t = t(n !== void 0 ? n : e.custom, o, s);
    }
    if (typeof t == "string" && (t = e.variants && e.variants[t]), typeof t == "function") {
      const [o, s] = Is(i);
      t = t(n !== void 0 ? n : e.custom, o, s);
    }
    return t;
  }
  function tt(e, t, n) {
    const i = e.getProps();
    return ko(i, t, n !== void 0 ? n : i.custom, e);
  }
  const ul = /* @__PURE__ */ new Set([
    "width",
    "height",
    "top",
    "left",
    "right",
    "bottom",
    ...Rt
  ]), Zs = 30, mh = (e) => !isNaN(parseFloat(e));
  class ph {
    constructor(t, n = {}) {
      this.canTrackVelocity = null, this.events = {}, this.updateAndNotify = (i) => {
        var _a3;
        const o = ie.now();
        if (this.updatedAt !== o && this.setPrevFrameValue(), this.prev = this.current, this.setCurrent(i), this.current !== this.prev && ((_a3 = this.events.change) == null ? void 0 : _a3.notify(this.current), this.dependents)) for (const s of this.dependents) s.dirty();
      }, this.hasAnimated = false, this.setCurrent(t), this.owner = n.owner;
    }
    setCurrent(t) {
      this.current = t, this.updatedAt = ie.now(), this.canTrackVelocity === null && t !== void 0 && (this.canTrackVelocity = mh(this.current));
    }
    setPrevFrameValue(t = this.current) {
      this.prevFrameValue = t, this.prevUpdatedAt = this.updatedAt;
    }
    onChange(t) {
      return process.env.NODE_ENV !== "production" && On(false, 'value.onChange(callback) is deprecated. Switch to value.on("change", callback).'), this.on("change", t);
    }
    on(t, n) {
      this.events[t] || (this.events[t] = new Fo());
      const i = this.events[t].add(n);
      return t === "change" ? () => {
        i(), M.read(() => {
          this.events.change.getSize() || this.stop();
        });
      } : i;
    }
    clearListeners() {
      for (const t in this.events) this.events[t].clear();
    }
    attach(t, n) {
      this.passiveEffect = t, this.stopPassiveEffect = n;
    }
    set(t) {
      this.passiveEffect ? this.passiveEffect(t, this.updateAndNotify) : this.updateAndNotify(t);
    }
    setWithVelocity(t, n, i) {
      this.set(n), this.prev = void 0, this.prevFrameValue = t, this.prevUpdatedAt = this.updatedAt - i;
    }
    jump(t, n = true) {
      this.updateAndNotify(t), this.prev = t, this.prevUpdatedAt = this.prevFrameValue = void 0, n && this.stop(), this.stopPassiveEffect && this.stopPassiveEffect();
    }
    dirty() {
      var _a3;
      (_a3 = this.events.change) == null ? void 0 : _a3.notify(this.current);
    }
    addDependent(t) {
      this.dependents || (this.dependents = /* @__PURE__ */ new Set()), this.dependents.add(t);
    }
    removeDependent(t) {
      this.dependents && this.dependents.delete(t);
    }
    get() {
      return this.current;
    }
    getPrevious() {
      return this.prev;
    }
    getVelocity() {
      const t = ie.now();
      if (!this.canTrackVelocity || this.prevFrameValue === void 0 || t - this.updatedAt > Zs) return 0;
      const n = Math.min(this.updatedAt - this.prevUpdatedAt, Zs);
      return ba(parseFloat(this.current) - parseFloat(this.prevFrameValue), n);
    }
    start(t) {
      return this.stop(), new Promise((n) => {
        this.hasAnimated = true, this.animation = t(n), this.events.animationStart && this.events.animationStart.notify();
      }).then(() => {
        this.events.animationComplete && this.events.animationComplete.notify(), this.clearAnimation();
      });
    }
    stop() {
      this.animation && (this.animation.stop(), this.events.animationCancel && this.events.animationCancel.notify()), this.clearAnimation();
    }
    isAnimating() {
      return !!this.animation;
    }
    clearAnimation() {
      delete this.animation;
    }
    destroy() {
      var _a3, _b;
      (_a3 = this.dependents) == null ? void 0 : _a3.clear(), (_b = this.events.destroy) == null ? void 0 : _b.notify(), this.clearListeners(), this.stop(), this.stopPassiveEffect && this.stopPassiveEffect();
    }
  }
  function At(e, t) {
    return new ph(e, t);
  }
  const eo = (e) => Array.isArray(e);
  function gh(e, t, n) {
    e.hasValue(t) ? e.getValue(t).set(n) : e.addValue(t, At(n));
  }
  function Ah(e) {
    return eo(e) ? e[e.length - 1] || 0 : e;
  }
  function yh(e, t) {
    const n = tt(e, t);
    let { transitionEnd: i = {}, transition: o = {}, ...s } = n || {};
    s = {
      ...s,
      ...i
    };
    for (const r in s) {
      const a = Ah(s[r]);
      gh(e, r, a);
    }
  }
  const ne = (e) => !!(e && e.getVelocity);
  function xh(e) {
    return !!(ne(e) && e.add);
  }
  function to(e, t) {
    const n = e.getValue("willChange");
    if (xh(n)) return n.add(t);
    if (!n && He.WillChange) {
      const i = new He.WillChange("auto");
      e.addValue("willChange", i), i.add(t);
    }
  }
  function Io(e) {
    return e.replace(/([A-Z])/g, (t) => `-${t.toLowerCase()}`);
  }
  const Ph = "framerAppearId", fl = "data-" + Io(Ph);
  function dl(e) {
    return e.props[fl];
  }
  function wh({ protectedKeys: e, needsAnimating: t }, n) {
    const i = e.hasOwnProperty(n) && t[n] !== true;
    return t[n] = false, i;
  }
  function hl(e, t, { delay: n = 0, transitionOverride: i, type: o } = {}) {
    let { transition: s, transitionEnd: r, ...a } = t;
    const l = e.getDefaultTransition();
    s = s ? cl(s, l) : l;
    const c = s == null ? void 0 : s.reduceMotion;
    i && (s = i);
    const u = [], f = o && e.animationState && e.animationState.getState()[o];
    for (const d in a) {
      const m = e.getValue(d, e.latestValues[d] ?? null), p = a[d];
      if (p === void 0 || f && wh(f, d)) continue;
      const h = {
        delay: n,
        ...Ho(s || {}, d)
      }, g = m.get();
      if (g !== void 0 && !m.isAnimating() && !Array.isArray(p) && p === g && !h.velocity) {
        M.update(() => m.set(p));
        continue;
      }
      let y = false;
      if (window.MotionHandoffAnimation) {
        const q = dl(e);
        if (q) {
          const R = window.MotionHandoffAnimation(q, d, M);
          R !== null && (h.startTime = R, y = true);
        }
      }
      to(e, d);
      const x = c ?? e.shouldReduceMotion;
      m.start(Mo(d, m, p, x && ul.has(d) ? {
        type: false
      } : h, e, y));
      const w = m.animation;
      w && u.push(w);
    }
    if (r) {
      const d = () => M.update(() => {
        r && yh(e, r);
      });
      u.length ? Promise.all(u).then(d) : d();
    }
    return u;
  }
  function no(e, t, n = {}) {
    var _a3;
    const i = tt(e, t, n.type === "exit" ? (_a3 = e.presenceContext) == null ? void 0 : _a3.custom : void 0);
    let { transition: o = e.getDefaultTransition() || {} } = i || {};
    n.transitionOverride && (o = n.transitionOverride);
    const s = i ? () => Promise.all(hl(e, i, n)) : () => Promise.resolve(), r = e.variantChildren && e.variantChildren.size ? (l = 0) => {
      const { delayChildren: c = 0, staggerChildren: u, staggerDirection: f } = o;
      return qh(e, t, l, c, u, f, n);
    } : () => Promise.resolve(), { when: a } = o;
    if (a) {
      const [l, c] = a === "beforeChildren" ? [
        s,
        r
      ] : [
        r,
        s
      ];
      return l().then(() => c());
    } else return Promise.all([
      s(),
      r(n.delay)
    ]);
  }
  function qh(e, t, n = 0, i = 0, o = 0, s = 1, r) {
    const a = [];
    for (const l of e.variantChildren) l.notify("AnimationStart", t), a.push(no(l, t, {
      ...r,
      delay: n + (typeof i == "function" ? 0 : i) + al(e.variantChildren, l, i, o, s)
    }).then(() => l.notify("AnimationComplete", t)));
    return Promise.all(a);
  }
  function Rh(e, t, n = {}) {
    e.notify("AnimationStart", t);
    let i;
    if (Array.isArray(t)) {
      const o = t.map((s) => no(e, s, n));
      i = Promise.all(o);
    } else if (typeof t == "string") i = no(e, t, n);
    else {
      const o = typeof t == "function" ? tt(e, t, n.custom) : t;
      i = Promise.all(hl(e, o, n));
    }
    return i.then(() => {
      e.notify("AnimationComplete", t);
    });
  }
  const vh = {
    test: (e) => e === "auto",
    parse: (e) => e
  }, ml = (e) => (t) => t.test(e), pl = [
    qt,
    E,
    Se,
    Ke,
    Xf,
    jf,
    vh
  ], Js = (e) => pl.find(ml(e));
  function Vh(e) {
    return typeof e == "number" ? e === 0 : e !== null ? e === "none" || e === "0" || Sa(e) : true;
  }
  const Sh = /* @__PURE__ */ new Set([
    "brightness",
    "contrast",
    "saturate",
    "opacity"
  ]);
  function Th(e) {
    const [t, n] = e.slice(0, -1).split("(");
    if (t === "drop-shadow") return e;
    const [i] = n.match(Lo) || [];
    if (!i) return e;
    const o = n.replace(i, "");
    let s = Sh.has(t) ? 1 : 0;
    return i !== n && (s *= 100), t + "(" + s + o + ")";
  }
  const bh = /\b([a-z-]*)\(.*?\)/gu, io = {
    ...he,
    getAnimatableNone: (e) => {
      const t = e.match(bh);
      return t ? t.map(Th).join(" ") : e;
    }
  }, oo = {
    ...he,
    getAnimatableNone: (e) => {
      const t = he.parse(e);
      return he.createTransformer(e)(t.map((i) => typeof i == "number" ? 0 : typeof i == "object" ? {
        ...i,
        alpha: 1
      } : i));
    }
  }, Os = {
    ...qt,
    transform: Math.round
  }, Fh = {
    rotate: Ke,
    rotateX: Ke,
    rotateY: Ke,
    rotateZ: Ke,
    scale: on,
    scaleX: on,
    scaleY: on,
    scaleZ: on,
    skew: Ke,
    skewX: Ke,
    skewY: Ke,
    distance: E,
    translateX: E,
    translateY: E,
    translateZ: E,
    x: E,
    y: E,
    z: E,
    perspective: E,
    transformPerspective: E,
    opacity: Mt,
    originX: Cs,
    originY: Cs,
    originZ: E
  }, Zo = {
    borderWidth: E,
    borderTopWidth: E,
    borderRightWidth: E,
    borderBottomWidth: E,
    borderLeftWidth: E,
    borderRadius: E,
    borderTopLeftRadius: E,
    borderTopRightRadius: E,
    borderBottomRightRadius: E,
    borderBottomLeftRadius: E,
    width: E,
    maxWidth: E,
    height: E,
    maxHeight: E,
    top: E,
    right: E,
    bottom: E,
    left: E,
    inset: E,
    insetBlock: E,
    insetBlockStart: E,
    insetBlockEnd: E,
    insetInline: E,
    insetInlineStart: E,
    insetInlineEnd: E,
    padding: E,
    paddingTop: E,
    paddingRight: E,
    paddingBottom: E,
    paddingLeft: E,
    paddingBlock: E,
    paddingBlockStart: E,
    paddingBlockEnd: E,
    paddingInline: E,
    paddingInlineStart: E,
    paddingInlineEnd: E,
    margin: E,
    marginTop: E,
    marginRight: E,
    marginBottom: E,
    marginLeft: E,
    marginBlock: E,
    marginBlockStart: E,
    marginBlockEnd: E,
    marginInline: E,
    marginInlineStart: E,
    marginInlineEnd: E,
    fontSize: E,
    backgroundPositionX: E,
    backgroundPositionY: E,
    ...Fh,
    zIndex: Os,
    fillOpacity: Mt,
    strokeOpacity: Mt,
    numOctaves: Os
  }, Eh = {
    ...Zo,
    color: Q,
    backgroundColor: Q,
    outlineColor: Q,
    fill: Q,
    stroke: Q,
    borderColor: Q,
    borderTopColor: Q,
    borderRightColor: Q,
    borderBottomColor: Q,
    borderLeftColor: Q,
    filter: io,
    WebkitFilter: io,
    mask: oo,
    WebkitMask: oo
  }, gl = (e) => Eh[e], Ch = /* @__PURE__ */ new Set([
    io,
    oo
  ]);
  function Al(e, t) {
    let n = gl(e);
    return Ch.has(n) || (n = he), n.getAnimatableNone ? n.getAnimatableNone(t) : void 0;
  }
  const Uh = /* @__PURE__ */ new Set([
    "auto",
    "none",
    "0"
  ]);
  function Lh(e, t, n) {
    let i = 0, o;
    for (; i < e.length && !o; ) {
      const s = e[i];
      typeof s == "string" && !Uh.has(s) && gt(s).values.length && (o = e[i]), i++;
    }
    if (o && n) for (const s of t) e[s] = Al(n, o);
  }
  class Nh extends zo {
    constructor(t, n, i, o, s) {
      super(t, n, i, o, s, true);
    }
    readKeyframes() {
      const { unresolvedKeyframes: t, element: n, name: i } = this;
      if (!n || !n.current) return;
      super.readKeyframes();
      for (let u = 0; u < t.length; u++) {
        let f = t[u];
        if (typeof f == "string" && (f = f.trim(), Uo(f))) {
          const d = ll(f, n.current);
          d !== void 0 && (t[u] = d), u === t.length - 1 && (this.finalKeyframe = f);
        }
      }
      if (this.resolveNoneKeyframes(), !ul.has(i) || t.length !== 2) return;
      const [o, s] = t, r = Js(o), a = Js(s), l = Es(o), c = Es(s);
      if (l !== c && ze[i]) {
        this.needsMeasurement = true;
        return;
      }
      if (r !== a) if (Hs(r) && Hs(a)) for (let u = 0; u < t.length; u++) {
        const f = t[u];
        typeof f == "string" && (t[u] = parseFloat(f));
      }
      else ze[i] && (this.needsMeasurement = true);
    }
    resolveNoneKeyframes() {
      const { unresolvedKeyframes: t, name: n } = this, i = [];
      for (let o = 0; o < t.length; o++) (t[o] === null || Vh(t[o])) && i.push(o);
      i.length && Lh(t, i, n);
    }
    measureInitialState() {
      const { element: t, unresolvedKeyframes: n, name: i } = this;
      if (!t || !t.current) return;
      i === "height" && (this.suspendedScrollY = window.pageYOffset), this.measuredOrigin = ze[i](t.measureViewportBox(), window.getComputedStyle(t.current)), n[0] = this.measuredOrigin;
      const o = n[n.length - 1];
      o !== void 0 && t.getValue(i, o).jump(o, false);
    }
    measureEndState() {
      var _a3;
      const { element: t, name: n, unresolvedKeyframes: i } = this;
      if (!t || !t.current) return;
      const o = t.getValue(n);
      o && o.jump(this.measuredOrigin, false);
      const s = i.length - 1, r = i[s];
      i[s] = ze[n](t.measureViewportBox(), window.getComputedStyle(t.current)), r !== null && this.finalKeyframe === void 0 && (this.finalKeyframe = r), ((_a3 = this.removedTransforms) == null ? void 0 : _a3.length) && this.removedTransforms.forEach(([a, l]) => {
        t.getValue(a).set(l);
      }), this.resolveNoneKeyframes();
    }
  }
  function yl(e, t, n) {
    if (e == null) return [];
    if (e instanceof EventTarget) return [
      e
    ];
    if (typeof e == "string") {
      let i = document;
      const o = (n == null ? void 0 : n[e]) ?? i.querySelectorAll(e);
      return o ? Array.from(o) : [];
    }
    return Array.from(e).filter((i) => i != null);
  }
  const xl = (e, t) => t && typeof e == "number" ? t.transform(e) : e;
  function pn(e) {
    return Va(e) && "offsetHeight" in e && !("ownerSVGElement" in e);
  }
  const { schedule: Jo } = Ha(queueMicrotask, false), de = {
    x: false,
    y: false
  };
  function Pl() {
    return de.x || de.y;
  }
  function Dh(e) {
    return e === "x" || e === "y" ? de[e] ? null : (de[e] = true, () => {
      de[e] = false;
    }) : de.x || de.y ? null : (de.x = de.y = true, () => {
      de.x = de.y = false;
    });
  }
  function wl(e, t) {
    const n = yl(e), i = new AbortController(), o = {
      passive: true,
      ...t,
      signal: i.signal
    };
    return [
      n,
      o,
      () => i.abort()
    ];
  }
  function Kh(e) {
    return !(e.pointerType === "touch" || Pl());
  }
  function Wh(e, t, n = {}) {
    const [i, o, s] = wl(e, n);
    return i.forEach((r) => {
      let a = false, l = false, c;
      const u = () => {
        r.removeEventListener("pointerleave", p);
      }, f = (g) => {
        c && (c(g), c = void 0), u();
      }, d = (g) => {
        a = false, window.removeEventListener("pointerup", d), window.removeEventListener("pointercancel", d), l && (l = false, f(g));
      }, m = () => {
        a = true, window.addEventListener("pointerup", d, o), window.addEventListener("pointercancel", d, o);
      }, p = (g) => {
        if (g.pointerType !== "touch") {
          if (a) {
            l = true;
            return;
          }
          f(g);
        }
      }, h = (g) => {
        if (!Kh(g)) return;
        l = false;
        const y = t(r, g);
        typeof y == "function" && (c = y, r.addEventListener("pointerleave", p, o));
      };
      r.addEventListener("pointerenter", h, o), r.addEventListener("pointerdown", m, o);
    }), s;
  }
  const ql = (e, t) => t ? e === t ? true : ql(e, t.parentElement) : false, Oo = (e) => e.pointerType === "mouse" ? typeof e.button != "number" || e.button <= 0 : e.isPrimary !== false, zh = /* @__PURE__ */ new Set([
    "BUTTON",
    "INPUT",
    "SELECT",
    "TEXTAREA",
    "A"
  ]);
  function Hh(e) {
    return zh.has(e.tagName) || e.isContentEditable === true;
  }
  const Mh = /* @__PURE__ */ new Set([
    "INPUT",
    "SELECT",
    "TEXTAREA"
  ]);
  function kh(e) {
    return Mh.has(e.tagName) || e.isContentEditable === true;
  }
  const gn = /* @__PURE__ */ new WeakSet();
  function Ys(e) {
    return (t) => {
      t.key === "Enter" && e(t);
    };
  }
  function Ai(e, t) {
    e.dispatchEvent(new PointerEvent("pointer" + t, {
      isPrimary: true,
      bubbles: true
    }));
  }
  const Ih = (e, t) => {
    const n = e.currentTarget;
    if (!n) return;
    const i = Ys(() => {
      if (gn.has(n)) return;
      Ai(n, "down");
      const o = Ys(() => {
        Ai(n, "up");
      }), s = () => Ai(n, "cancel");
      n.addEventListener("keyup", o, t), n.addEventListener("blur", s, t);
    });
    n.addEventListener("keydown", i, t), n.addEventListener("blur", () => n.removeEventListener("keydown", i), t);
  };
  function Bs(e) {
    return Oo(e) && !Pl();
  }
  const js = /* @__PURE__ */ new WeakSet();
  function Zh(e, t, n = {}) {
    const [i, o, s] = wl(e, n), r = (a) => {
      const l = a.currentTarget;
      if (!Bs(a) || js.has(a)) return;
      gn.add(l), n.stopPropagation && js.add(a);
      const c = t(l, a), u = (m, p) => {
        window.removeEventListener("pointerup", f), window.removeEventListener("pointercancel", d), gn.has(l) && gn.delete(l), Bs(m) && typeof c == "function" && c(m, {
          success: p
        });
      }, f = (m) => {
        u(m, l === window || l === document || n.useGlobalTarget || ql(l, m.target));
      }, d = (m) => {
        u(m, false);
      };
      window.addEventListener("pointerup", f, o), window.addEventListener("pointercancel", d, o);
    };
    return i.forEach((a) => {
      (n.useGlobalTarget ? window : a).addEventListener("pointerdown", r, o), pn(a) && (a.addEventListener("focus", (c) => Ih(c, o)), !Hh(a) && !a.hasAttribute("tabindex") && (a.tabIndex = 0));
    }), s;
  }
  function Yo(e) {
    return Va(e) && "ownerSVGElement" in e;
  }
  const An = /* @__PURE__ */ new WeakMap();
  let yn;
  const Rl = (e, t, n) => (i, o) => o && o[0] ? o[0][e + "Size"] : Yo(i) && "getBBox" in i ? i.getBBox()[t] : i[n], Jh = Rl("inline", "width", "offsetWidth"), Oh = Rl("block", "height", "offsetHeight");
  function Yh({ target: e, borderBoxSize: t }) {
    var _a3;
    (_a3 = An.get(e)) == null ? void 0 : _a3.forEach((n) => {
      n(e, {
        get width() {
          return Jh(e, t);
        },
        get height() {
          return Oh(e, t);
        }
      });
    });
  }
  function Bh(e) {
    e.forEach(Yh);
  }
  function jh() {
    typeof ResizeObserver > "u" || (yn = new ResizeObserver(Bh));
  }
  function Xh(e, t) {
    yn || jh();
    const n = yl(e);
    return n.forEach((i) => {
      let o = An.get(i);
      o || (o = /* @__PURE__ */ new Set(), An.set(i, o)), o.add(t), yn == null ? void 0 : yn.observe(i);
    }), () => {
      n.forEach((i) => {
        const o = An.get(i);
        o == null ? void 0 : o.delete(t), (o == null ? void 0 : o.size) || (yn == null ? void 0 : yn.unobserve(i));
      });
    };
  }
  const xn = /* @__PURE__ */ new Set();
  let ft;
  function Qh() {
    ft = () => {
      const e = {
        get width() {
          return window.innerWidth;
        },
        get height() {
          return window.innerHeight;
        }
      };
      xn.forEach((t) => t(e));
    }, window.addEventListener("resize", ft);
  }
  function Gh(e) {
    return xn.add(e), ft || Qh(), () => {
      xn.delete(e), !xn.size && typeof ft == "function" && (window.removeEventListener("resize", ft), ft = void 0);
    };
  }
  function Xs(e, t) {
    return typeof e == "function" ? Gh(e) : Xh(e, t);
  }
  function _h(e) {
    return Yo(e) && e.tagName === "svg";
  }
  const $h = [
    ...pl,
    Q,
    he
  ], em = (e) => $h.find(ml(e)), Qs = () => ({
    translate: 0,
    scale: 1,
    origin: 0,
    originPoint: 0
  }), dt = () => ({
    x: Qs(),
    y: Qs()
  }), Gs = () => ({
    min: 0,
    max: 0
  }), _ = () => ({
    x: Gs(),
    y: Gs()
  }), tm = /* @__PURE__ */ new WeakMap();
  function Bn(e) {
    return e !== null && typeof e == "object" && typeof e.start == "function";
  }
  function kt(e) {
    return typeof e == "string" || Array.isArray(e);
  }
  const Bo = [
    "animate",
    "whileInView",
    "whileFocus",
    "whileHover",
    "whileTap",
    "whileDrag",
    "exit"
  ], jo = [
    "initial",
    ...Bo
  ];
  function jn(e) {
    return Bn(e.animate) || jo.some((t) => kt(e[t]));
  }
  function vl(e) {
    return !!(jn(e) || e.variants);
  }
  function nm(e, t, n) {
    for (const i in t) {
      const o = t[i], s = n[i];
      if (ne(o)) e.addValue(i, o);
      else if (ne(s)) e.addValue(i, At(o, {
        owner: e
      }));
      else if (s !== o) if (e.hasValue(i)) {
        const r = e.getValue(i);
        r.liveStyle === true ? r.jump(o) : r.hasAnimated || r.set(o);
      } else {
        const r = e.getStaticValue(i);
        e.addValue(i, At(r !== void 0 ? r : o, {
          owner: e
        }));
      }
    }
    for (const i in n) t[i] === void 0 && e.removeValue(i);
    return t;
  }
  const Cn = {
    current: null
  }, Xo = {
    current: false
  }, im = typeof window < "u";
  function Vl() {
    if (Xo.current = true, !!im) if (window.matchMedia) {
      const e = window.matchMedia("(prefers-reduced-motion)"), t = () => Cn.current = e.matches;
      e.addEventListener("change", t), t();
    } else Cn.current = false;
  }
  const _s = [
    "AnimationStart",
    "AnimationComplete",
    "Update",
    "BeforeLayoutMeasure",
    "LayoutMeasure",
    "LayoutAnimationStart",
    "LayoutAnimationComplete"
  ];
  let Un = {};
  function Sl(e) {
    Un = e;
  }
  function om() {
    return Un;
  }
  class sm {
    scrapeMotionValuesFromProps(t, n, i) {
      return {};
    }
    constructor({ parent: t, props: n, presenceContext: i, reducedMotionConfig: o, skipAnimations: s, blockInitialAnimation: r, visualState: a }, l = {}) {
      this.current = null, this.children = /* @__PURE__ */ new Set(), this.isVariantNode = false, this.isControllingVariants = false, this.shouldReduceMotion = null, this.shouldSkipAnimations = false, this.values = /* @__PURE__ */ new Map(), this.KeyframeResolver = zo, this.features = {}, this.valueSubscriptions = /* @__PURE__ */ new Map(), this.prevMotionValues = {}, this.hasBeenMounted = false, this.events = {}, this.propEventSubscriptions = {}, this.notifyUpdate = () => this.notify("Update", this.latestValues), this.render = () => {
        this.current && (this.triggerBuild(), this.renderInstance(this.current, this.renderState, this.props.style, this.projection));
      }, this.renderScheduledAt = 0, this.scheduleRender = () => {
        const m = ie.now();
        this.renderScheduledAt < m && (this.renderScheduledAt = m, M.render(this.render, false, true));
      };
      const { latestValues: c, renderState: u } = a;
      this.latestValues = c, this.baseTarget = {
        ...c
      }, this.initialValues = n.initial ? {
        ...c
      } : {}, this.renderState = u, this.parent = t, this.props = n, this.presenceContext = i, this.depth = t ? t.depth + 1 : 0, this.reducedMotionConfig = o, this.skipAnimationsConfig = s, this.options = l, this.blockInitialAnimation = !!r, this.isControllingVariants = jn(n), this.isVariantNode = vl(n), this.isVariantNode && (this.variantChildren = /* @__PURE__ */ new Set()), this.manuallyAnimateOnMount = !!(t && t.current);
      const { willChange: f, ...d } = this.scrapeMotionValuesFromProps(n, {}, this);
      for (const m in d) {
        const p = d[m];
        c[m] !== void 0 && ne(p) && p.set(c[m]);
      }
    }
    mount(t) {
      var _a3, _b;
      if (this.hasBeenMounted) for (const n in this.initialValues) (_a3 = this.values.get(n)) == null ? void 0 : _a3.jump(this.initialValues[n]), this.latestValues[n] = this.initialValues[n];
      this.current = t, tm.set(t, this), this.projection && !this.projection.instance && this.projection.mount(t), this.parent && this.isVariantNode && !this.isControllingVariants && (this.removeFromVariantTree = this.parent.addVariantChild(this)), this.values.forEach((n, i) => this.bindToMotionValue(i, n)), this.reducedMotionConfig === "never" ? this.shouldReduceMotion = false : this.reducedMotionConfig === "always" ? this.shouldReduceMotion = true : (Xo.current || Vl(), this.shouldReduceMotion = Cn.current), process.env.NODE_ENV !== "production" && On(this.shouldReduceMotion !== true, "You have Reduced Motion enabled on your device. Animations may not appear as expected.", "reduced-motion-disabled"), this.shouldSkipAnimations = this.skipAnimationsConfig ?? false, (_b = this.parent) == null ? void 0 : _b.addChild(this), this.update(this.props, this.presenceContext), this.hasBeenMounted = true;
    }
    unmount() {
      var _a3;
      this.projection && this.projection.unmount(), Me(this.notifyUpdate), Me(this.render), this.valueSubscriptions.forEach((t) => t()), this.valueSubscriptions.clear(), this.removeFromVariantTree && this.removeFromVariantTree(), (_a3 = this.parent) == null ? void 0 : _a3.removeChild(this);
      for (const t in this.events) this.events[t].clear();
      for (const t in this.features) {
        const n = this.features[t];
        n && (n.unmount(), n.isMounted = false);
      }
      this.current = null;
    }
    addChild(t) {
      this.children.add(t), this.enteringChildren ?? (this.enteringChildren = /* @__PURE__ */ new Set()), this.enteringChildren.add(t);
    }
    removeChild(t) {
      this.children.delete(t), this.enteringChildren && this.enteringChildren.delete(t);
    }
    bindToMotionValue(t, n) {
      if (this.valueSubscriptions.has(t) && this.valueSubscriptions.get(t)(), n.accelerate && rl.has(t) && this.current instanceof HTMLElement) {
        const { factory: r, keyframes: a, times: l, ease: c, duration: u } = n.accelerate, f = new ol({
          element: this.current,
          name: t,
          keyframes: a,
          times: l,
          ease: c,
          duration: se(u)
        }), d = r(f);
        this.valueSubscriptions.set(t, () => {
          d(), f.cancel();
        });
        return;
      }
      const i = vt.has(t);
      i && this.onBindTransform && this.onBindTransform();
      const o = n.on("change", (r) => {
        this.latestValues[t] = r, this.props.onUpdate && M.preRender(this.notifyUpdate), i && this.projection && (this.projection.isTransformDirty = true), this.scheduleRender();
      });
      let s;
      typeof window < "u" && window.MotionCheckAppearSync && (s = window.MotionCheckAppearSync(this, t, n)), this.valueSubscriptions.set(t, () => {
        o(), s && s(), n.owner && n.stop();
      });
    }
    sortNodePosition(t) {
      return !this.current || !this.sortInstanceNodePosition || this.type !== t.type ? 0 : this.sortInstanceNodePosition(this.current, t.current);
    }
    updateFeatures() {
      let t = "animation";
      for (t in Un) {
        const n = Un[t];
        if (!n) continue;
        const { isEnabled: i, Feature: o } = n;
        if (!this.features[t] && o && i(this.props) && (this.features[t] = new o(this)), this.features[t]) {
          const s = this.features[t];
          s.isMounted ? s.update() : (s.mount(), s.isMounted = true);
        }
      }
    }
    triggerBuild() {
      this.build(this.renderState, this.latestValues, this.props);
    }
    measureViewportBox() {
      return this.current ? this.measureInstanceViewportBox(this.current, this.props) : _();
    }
    getStaticValue(t) {
      return this.latestValues[t];
    }
    setStaticValue(t, n) {
      this.latestValues[t] = n;
    }
    update(t, n) {
      (t.transformTemplate || this.props.transformTemplate) && this.scheduleRender(), this.prevProps = this.props, this.props = t, this.prevPresenceContext = this.presenceContext, this.presenceContext = n;
      for (let i = 0; i < _s.length; i++) {
        const o = _s[i];
        this.propEventSubscriptions[o] && (this.propEventSubscriptions[o](), delete this.propEventSubscriptions[o]);
        const s = "on" + o, r = t[s];
        r && (this.propEventSubscriptions[o] = this.on(o, r));
      }
      this.prevMotionValues = nm(this, this.scrapeMotionValuesFromProps(t, this.prevProps || {}, this), this.prevMotionValues), this.handleChildMotionValue && this.handleChildMotionValue();
    }
    getProps() {
      return this.props;
    }
    getVariant(t) {
      return this.props.variants ? this.props.variants[t] : void 0;
    }
    getDefaultTransition() {
      return this.props.transition;
    }
    getTransformPagePoint() {
      return this.props.transformPagePoint;
    }
    getClosestVariantNode() {
      return this.isVariantNode ? this : this.parent ? this.parent.getClosestVariantNode() : void 0;
    }
    addVariantChild(t) {
      const n = this.getClosestVariantNode();
      if (n) return n.variantChildren && n.variantChildren.add(t), () => n.variantChildren.delete(t);
    }
    addValue(t, n) {
      const i = this.values.get(t);
      n !== i && (i && this.removeValue(t), this.bindToMotionValue(t, n), this.values.set(t, n), this.latestValues[t] = n.get());
    }
    removeValue(t) {
      this.values.delete(t);
      const n = this.valueSubscriptions.get(t);
      n && (n(), this.valueSubscriptions.delete(t)), delete this.latestValues[t], this.removeValueFromRenderState(t, this.renderState);
    }
    hasValue(t) {
      return this.values.has(t);
    }
    getValue(t, n) {
      if (this.props.values && this.props.values[t]) return this.props.values[t];
      let i = this.values.get(t);
      return i === void 0 && n !== void 0 && (i = At(n === null ? void 0 : n, {
        owner: this
      }), this.addValue(t, i)), i;
    }
    readValue(t, n) {
      let i = this.latestValues[t] !== void 0 || !this.current ? this.latestValues[t] : this.getBaseTargetFromProps(this.props, t) ?? this.readValueFromInstance(this.current, t, this.options);
      return i != null && (typeof i == "string" && (va(i) || Sa(i)) ? i = parseFloat(i) : !em(i) && he.test(n) && (i = Al(t, n)), this.setBaseTarget(t, ne(i) ? i.get() : i)), ne(i) ? i.get() : i;
    }
    setBaseTarget(t, n) {
      this.baseTarget[t] = n;
    }
    getBaseTarget(t) {
      var _a3;
      const { initial: n } = this.props;
      let i;
      if (typeof n == "string" || typeof n == "object") {
        const s = ko(this.props, n, (_a3 = this.presenceContext) == null ? void 0 : _a3.custom);
        s && (i = s[t]);
      }
      if (n && i !== void 0) return i;
      const o = this.getBaseTargetFromProps(this.props, t);
      return o !== void 0 && !ne(o) ? o : this.initialValues[t] !== void 0 && i === void 0 ? void 0 : this.baseTarget[t];
    }
    on(t, n) {
      return this.events[t] || (this.events[t] = new Fo()), this.events[t].add(n);
    }
    notify(t, ...n) {
      this.events[t] && this.events[t].notify(...n);
    }
    scheduleRenderMicrotask() {
      Jo.render(this.render);
    }
  }
  class Tl extends sm {
    constructor() {
      super(...arguments), this.KeyframeResolver = Nh;
    }
    sortInstanceNodePosition(t, n) {
      return t.compareDocumentPosition(n) & 2 ? 1 : -1;
    }
    getBaseTargetFromProps(t, n) {
      const i = t.style;
      return i ? i[n] : void 0;
    }
    removeValueFromRenderState(t, { vars: n, style: i }) {
      delete n[t], delete i[t];
    }
    handleChildMotionValue() {
      this.childSubscription && (this.childSubscription(), delete this.childSubscription);
      const { children: t } = this.props;
      ne(t) && (this.childSubscription = t.on("change", (n) => {
        this.current && (this.current.textContent = `${n}`);
      }));
    }
  }
  class Je {
    constructor(t) {
      this.isMounted = false, this.node = t;
    }
    update() {
    }
  }
  function bl({ top: e, left: t, right: n, bottom: i }) {
    return {
      x: {
        min: t,
        max: n
      },
      y: {
        min: e,
        max: i
      }
    };
  }
  function rm({ x: e, y: t }) {
    return {
      top: t.min,
      right: e.max,
      bottom: t.max,
      left: e.min
    };
  }
  function am(e, t) {
    if (!t) return e;
    const n = t({
      x: e.left,
      y: e.top
    }), i = t({
      x: e.right,
      y: e.bottom
    });
    return {
      top: n.y,
      left: n.x,
      bottom: i.y,
      right: i.x
    };
  }
  function yi(e) {
    return e === void 0 || e === 1;
  }
  function so({ scale: e, scaleX: t, scaleY: n }) {
    return !yi(e) || !yi(t) || !yi(n);
  }
  function Ge(e) {
    return so(e) || Fl(e) || e.z || e.rotate || e.rotateX || e.rotateY || e.skewX || e.skewY;
  }
  function Fl(e) {
    return $s(e.x) || $s(e.y);
  }
  function $s(e) {
    return e && e !== "0%";
  }
  function Ln(e, t, n) {
    const i = e - n, o = t * i;
    return n + o;
  }
  function er(e, t, n, i, o) {
    return o !== void 0 && (e = Ln(e, o, i)), Ln(e, n, i) + t;
  }
  function ro(e, t = 0, n = 1, i, o) {
    e.min = er(e.min, t, n, i, o), e.max = er(e.max, t, n, i, o);
  }
  function El(e, { x: t, y: n }) {
    ro(e.x, t.translate, t.scale, t.originPoint), ro(e.y, n.translate, n.scale, n.originPoint);
  }
  const tr = 0.999999999999, nr = 1.0000000000001;
  function lm(e, t, n, i = false) {
    var _a3;
    const o = n.length;
    if (!o) return;
    t.x = t.y = 1;
    let s, r;
    for (let a = 0; a < o; a++) {
      s = n[a], r = s.projectionDelta;
      const { visualElement: l } = s.options;
      l && l.props.style && l.props.style.display === "contents" || (i && s.options.layoutScroll && s.scroll && s !== s.root && (Re(e.x, -s.scroll.offset.x), Re(e.y, -s.scroll.offset.y)), r && (t.x *= r.x.scale, t.y *= r.y.scale, El(e, r)), i && Ge(s.latestValues) && Pn(e, s.latestValues, (_a3 = s.layout) == null ? void 0 : _a3.layoutBox));
    }
    t.x < nr && t.x > tr && (t.x = 1), t.y < nr && t.y > tr && (t.y = 1);
  }
  function Re(e, t) {
    e.min += t, e.max += t;
  }
  function ir(e, t, n, i, o = 0.5) {
    const s = J(e.min, e.max, o);
    ro(e, t, n, s, i);
  }
  function or(e, t) {
    return typeof e == "string" ? parseFloat(e) / 100 * (t.max - t.min) : e;
  }
  function Pn(e, t, n) {
    const i = n ?? e;
    ir(e.x, or(t.x, i.x), t.scaleX, t.scale, t.originX), ir(e.y, or(t.y, i.y), t.scaleY, t.scale, t.originY);
  }
  function Cl(e, t) {
    return bl(am(e.getBoundingClientRect(), t));
  }
  function cm(e, t, n) {
    const i = Cl(e, n), { scroll: o } = t;
    return o && (Re(i.x, o.offset.x), Re(i.y, o.offset.y)), i;
  }
  const um = {
    x: "translateX",
    y: "translateY",
    z: "translateZ",
    transformPerspective: "perspective"
  }, fm = Rt.length;
  function dm(e, t, n) {
    let i = "", o = true;
    for (let s = 0; s < fm; s++) {
      const r = Rt[s], a = e[r];
      if (a === void 0) continue;
      let l = true;
      if (typeof a == "number") l = a === (r.startsWith("scale") ? 1 : 0);
      else {
        const c = parseFloat(a);
        l = r.startsWith("scale") ? c === 1 : c === 0;
      }
      if (!l || n) {
        const c = xl(a, Zo[r]);
        if (!l) {
          o = false;
          const u = um[r] || r;
          i += `${u}(${c}) `;
        }
        n && (t[r] = c);
      }
    }
    return i = i.trim(), n ? i = n(t, o ? "" : i) : o && (i = "none"), i;
  }
  function Qo(e, t, n) {
    const { style: i, vars: o, transformOrigin: s } = e;
    let r = false, a = false;
    for (const l in t) {
      const c = t[l];
      if (vt.has(l)) {
        r = true;
        continue;
      } else if (ka(l)) {
        o[l] = c;
        continue;
      } else {
        const u = xl(c, Zo[l]);
        l.startsWith("origin") ? (a = true, s[l] = u) : i[l] = u;
      }
    }
    if (t.transform || (r || n ? i.transform = dm(t, e.transform, n) : i.transform && (i.transform = "none")), a) {
      const { originX: l = "50%", originY: c = "50%", originZ: u = 0 } = s;
      i.transformOrigin = `${l} ${c} ${u}`;
    }
  }
  function Ul(e, { style: t, vars: n }, i, o) {
    const s = e.style;
    let r;
    for (r in t) s[r] = t[r];
    o == null ? void 0 : o.applyProjectionStyles(s, i);
    for (r in n) s.setProperty(r, n[r]);
  }
  function sr(e, t) {
    return t.max === t.min ? 0 : e / (t.max - t.min) * 100;
  }
  const Ut = {
    correct: (e, t) => {
      if (!t.target) return e;
      if (typeof e == "string") if (E.test(e)) e = parseFloat(e);
      else return e;
      const n = sr(e, t.target.x), i = sr(e, t.target.y);
      return `${n}% ${i}%`;
    }
  }, hm = {
    correct: (e, { treeScale: t, projectionDelta: n }) => {
      const i = e, o = he.parse(e);
      if (o.length > 5) return i;
      const s = he.createTransformer(e), r = typeof o[0] != "number" ? 1 : 0, a = n.x.scale * t.x, l = n.y.scale * t.y;
      o[0 + r] /= a, o[1 + r] /= l;
      const c = J(a, l, 0.5);
      return typeof o[2 + r] == "number" && (o[2 + r] /= c), typeof o[3 + r] == "number" && (o[3 + r] /= c), s(o);
    }
  }, ao = {
    borderRadius: {
      ...Ut,
      applyTo: [
        "borderTopLeftRadius",
        "borderTopRightRadius",
        "borderBottomLeftRadius",
        "borderBottomRightRadius"
      ]
    },
    borderTopLeftRadius: Ut,
    borderTopRightRadius: Ut,
    borderBottomLeftRadius: Ut,
    borderBottomRightRadius: Ut,
    boxShadow: hm
  };
  function Ll(e, { layout: t, layoutId: n }) {
    return vt.has(e) || e.startsWith("origin") || (t || n !== void 0) && (!!ao[e] || e === "opacity");
  }
  function Go(e, t, n) {
    var _a3;
    const i = e.style, o = t == null ? void 0 : t.style, s = {};
    if (!i) return s;
    for (const r in i) (ne(i[r]) || o && ne(o[r]) || Ll(r, e) || ((_a3 = n == null ? void 0 : n.getValue(r)) == null ? void 0 : _a3.liveStyle) !== void 0) && (s[r] = i[r]);
    return s;
  }
  function mm(e) {
    return window.getComputedStyle(e);
  }
  class pm extends Tl {
    constructor() {
      super(...arguments), this.type = "html", this.renderInstance = Ul;
    }
    readValueFromInstance(t, n) {
      var _a3;
      if (vt.has(n)) return ((_a3 = this.projection) == null ? void 0 : _a3.isProjecting) ? ji(n) : Dd(t, n);
      {
        const i = mm(t), o = (ka(n) ? i.getPropertyValue(n) : i[n]) || 0;
        return typeof o == "string" ? o.trim() : o;
      }
    }
    measureInstanceViewportBox(t, { transformPagePoint: n }) {
      return Cl(t, n);
    }
    build(t, n, i) {
      Qo(t, n, i.transformTemplate);
    }
    scrapeMotionValuesFromProps(t, n, i) {
      return Go(t, n, i);
    }
  }
  const gm = {
    offset: "stroke-dashoffset",
    array: "stroke-dasharray"
  }, Am = {
    offset: "strokeDashoffset",
    array: "strokeDasharray"
  };
  function ym(e, t, n = 1, i = 0, o = true) {
    e.pathLength = 1;
    const s = o ? gm : Am;
    e[s.offset] = `${-i}`, e[s.array] = `${t} ${n}`;
  }
  const xm = [
    "offsetDistance",
    "offsetPath",
    "offsetRotate",
    "offsetAnchor"
  ];
  function Nl(e, { attrX: t, attrY: n, attrScale: i, pathLength: o, pathSpacing: s = 1, pathOffset: r = 0, ...a }, l, c, u) {
    if (Qo(e, a, c), l) {
      e.style.viewBox && (e.attrs.viewBox = e.style.viewBox);
      return;
    }
    e.attrs = e.style, e.style = {};
    const { attrs: f, style: d } = e;
    f.transform && (d.transform = f.transform, delete f.transform), (d.transform || f.transformOrigin) && (d.transformOrigin = f.transformOrigin ?? "50% 50%", delete f.transformOrigin), d.transform && (d.transformBox = (u == null ? void 0 : u.transformBox) ?? "fill-box", delete f.transformBox);
    for (const m of xm) f[m] !== void 0 && (d[m] = f[m], delete f[m]);
    t !== void 0 && (f.x = t), n !== void 0 && (f.y = n), i !== void 0 && (f.scale = i), o !== void 0 && ym(f, o, s, r, false);
  }
  const Dl = /* @__PURE__ */ new Set([
    "baseFrequency",
    "diffuseConstant",
    "kernelMatrix",
    "kernelUnitLength",
    "keySplines",
    "keyTimes",
    "limitingConeAngle",
    "markerHeight",
    "markerWidth",
    "numOctaves",
    "targetX",
    "targetY",
    "surfaceScale",
    "specularConstant",
    "specularExponent",
    "stdDeviation",
    "tableValues",
    "viewBox",
    "gradientTransform",
    "pathLength",
    "startOffset",
    "textLength",
    "lengthAdjust"
  ]), Kl = (e) => typeof e == "string" && e.toLowerCase() === "svg";
  function Pm(e, t, n, i) {
    Ul(e, t, void 0, i);
    for (const o in t.attrs) e.setAttribute(Dl.has(o) ? o : Io(o), t.attrs[o]);
  }
  function Wl(e, t, n) {
    const i = Go(e, t, n);
    for (const o in e) if (ne(e[o]) || ne(t[o])) {
      const s = Rt.indexOf(o) !== -1 ? "attr" + o.charAt(0).toUpperCase() + o.substring(1) : o;
      i[s] = e[o];
    }
    return i;
  }
  class wm extends Tl {
    constructor() {
      super(...arguments), this.type = "svg", this.isSVGTag = false, this.measureInstanceViewportBox = _;
    }
    getBaseTargetFromProps(t, n) {
      return t[n];
    }
    readValueFromInstance(t, n) {
      if (vt.has(n)) {
        const i = gl(n);
        return i && i.default || 0;
      }
      return n = Dl.has(n) ? n : Io(n), t.getAttribute(n);
    }
    scrapeMotionValuesFromProps(t, n, i) {
      return Wl(t, n, i);
    }
    build(t, n, i) {
      Nl(t, n, this.isSVGTag, i.transformTemplate, i.style);
    }
    renderInstance(t, n, i, o) {
      Pm(t, n, i, o);
    }
    mount(t) {
      this.isSVGTag = Kl(t.tagName), super.mount(t);
    }
  }
  const qm = jo.length;
  function zl(e) {
    if (!e) return;
    if (!e.isControllingVariants) {
      const n = e.parent ? zl(e.parent) || {} : {};
      return e.props.initial !== void 0 && (n.initial = e.props.initial), n;
    }
    const t = {};
    for (let n = 0; n < qm; n++) {
      const i = jo[n], o = e.props[i];
      (kt(o) || o === false) && (t[i] = o);
    }
    return t;
  }
  function Hl(e, t) {
    if (!Array.isArray(t)) return false;
    const n = t.length;
    if (n !== e.length) return false;
    for (let i = 0; i < n; i++) if (t[i] !== e[i]) return false;
    return true;
  }
  const Rm = [
    ...Bo
  ].reverse(), vm = Bo.length;
  function Vm(e) {
    return (t) => Promise.all(t.map(({ animation: n, options: i }) => Rh(e, n, i)));
  }
  function Sm(e) {
    let t = Vm(e), n = rr(), i = true, o = false;
    const s = (c) => (u, f) => {
      var _a3;
      const d = tt(e, f, c === "exit" ? (_a3 = e.presenceContext) == null ? void 0 : _a3.custom : void 0);
      if (d) {
        const { transition: m, transitionEnd: p, ...h } = d;
        u = {
          ...u,
          ...h,
          ...p
        };
      }
      return u;
    };
    function r(c) {
      t = c(e);
    }
    function a(c) {
      const { props: u } = e, f = zl(e.parent) || {}, d = [], m = /* @__PURE__ */ new Set();
      let p = {}, h = 1 / 0;
      for (let y = 0; y < vm; y++) {
        const x = Rm[y], w = n[x], q = u[x] !== void 0 ? u[x] : f[x], R = kt(q), v = x === c ? w.isActive : null;
        v === false && (h = y);
        let S = q === f[x] && q !== u[x] && R;
        if (S && (i || o) && e.manuallyAnimateOnMount && (S = false), w.protectedKeys = {
          ...p
        }, !w.isActive && v === null || !q && !w.prevProp || Bn(q) || typeof q == "boolean") continue;
        if (x === "exit" && w.isActive && v !== true) {
          w.prevResolvedValues && (p = {
            ...p,
            ...w.prevResolvedValues
          });
          continue;
        }
        const V = Tm(w.prevProp, q);
        let b = V || x === c && w.isActive && !S && R || y > h && R, T = false;
        const F = Array.isArray(q) ? q : [
          q
        ];
        let U = F.reduce(s(x), {});
        v === false && (U = {});
        const { prevResolvedValues: D = {} } = w, K = {
          ...D,
          ...U
        }, z = (C) => {
          b = true, m.has(C) && (T = true, m.delete(C)), w.needsAnimating[C] = true;
          const W = e.getValue(C);
          W && (W.liveStyle = false);
        };
        for (const C in K) {
          const W = U[C], X = D[C];
          if (p.hasOwnProperty(C)) continue;
          let Ee = false;
          eo(W) && eo(X) ? Ee = !Hl(W, X) : Ee = W !== X, Ee ? W != null ? z(C) : m.add(C) : W !== void 0 && m.has(C) ? z(C) : w.protectedKeys[C] = true;
        }
        w.prevProp = q, w.prevResolvedValues = U, w.isActive && (p = {
          ...p,
          ...U
        }), (i || o) && e.blockInitialAnimation && (b = false);
        const N = S && V;
        b && (!N || T) && d.push(...F.map((C) => {
          const W = {
            type: x
          };
          if (typeof C == "string" && (i || o) && !N && e.manuallyAnimateOnMount && e.parent) {
            const { parent: X } = e, Ee = tt(X, C);
            if (X.enteringChildren && Ee) {
              const { delayChildren: si } = Ee.transition || {};
              W.delay = al(X.enteringChildren, e, si);
            }
          }
          return {
            animation: C,
            options: W
          };
        }));
      }
      if (m.size) {
        const y = {};
        if (typeof u.initial != "boolean") {
          const x = tt(e, Array.isArray(u.initial) ? u.initial[0] : u.initial);
          x && x.transition && (y.transition = x.transition);
        }
        m.forEach((x) => {
          const w = e.getBaseTarget(x), q = e.getValue(x);
          q && (q.liveStyle = true), y[x] = w ?? null;
        }), d.push({
          animation: y
        });
      }
      let g = !!d.length;
      return i && (u.initial === false || u.initial === u.animate) && !e.manuallyAnimateOnMount && (g = false), i = false, o = false, g ? t(d) : Promise.resolve();
    }
    function l(c, u) {
      var _a3;
      if (n[c].isActive === u) return Promise.resolve();
      (_a3 = e.variantChildren) == null ? void 0 : _a3.forEach((d) => {
        var _a4;
        return (_a4 = d.animationState) == null ? void 0 : _a4.setActive(c, u);
      }), n[c].isActive = u;
      const f = a(c);
      for (const d in n) n[d].protectedKeys = {};
      return f;
    }
    return {
      animateChanges: a,
      setActive: l,
      setAnimateFunction: r,
      getState: () => n,
      reset: () => {
        n = rr(), o = true;
      }
    };
  }
  function Tm(e, t) {
    return typeof t == "string" ? t !== e : Array.isArray(t) ? !Hl(t, e) : false;
  }
  function Xe(e = false) {
    return {
      isActive: e,
      protectedKeys: {},
      needsAnimating: {},
      prevResolvedValues: {}
    };
  }
  function rr() {
    return {
      animate: Xe(true),
      whileInView: Xe(),
      whileHover: Xe(),
      whileTap: Xe(),
      whileDrag: Xe(),
      whileFocus: Xe(),
      exit: Xe()
    };
  }
  function lo(e, t) {
    e.min = t.min, e.max = t.max;
  }
  function fe(e, t) {
    lo(e.x, t.x), lo(e.y, t.y);
  }
  function ar(e, t) {
    e.translate = t.translate, e.scale = t.scale, e.originPoint = t.originPoint, e.origin = t.origin;
  }
  const Ml = 1e-4, bm = 1 - Ml, Fm = 1 + Ml, kl = 0.01, Em = 0 - kl, Cm = 0 + kl;
  function oe(e) {
    return e.max - e.min;
  }
  function Um(e, t, n) {
    return Math.abs(e - t) <= n;
  }
  function lr(e, t, n, i = 0.5) {
    e.origin = i, e.originPoint = J(t.min, t.max, e.origin), e.scale = oe(n) / oe(t), e.translate = J(n.min, n.max, e.origin) - e.originPoint, (e.scale >= bm && e.scale <= Fm || isNaN(e.scale)) && (e.scale = 1), (e.translate >= Em && e.translate <= Cm || isNaN(e.translate)) && (e.translate = 0);
  }
  function Dt(e, t, n, i) {
    lr(e.x, t.x, n.x, i ? i.originX : void 0), lr(e.y, t.y, n.y, i ? i.originY : void 0);
  }
  function cr(e, t, n, i = 0) {
    const o = i ? J(n.min, n.max, i) : n.min;
    e.min = o + t.min, e.max = e.min + oe(t);
  }
  function Lm(e, t, n, i) {
    cr(e.x, t.x, n.x, i == null ? void 0 : i.x), cr(e.y, t.y, n.y, i == null ? void 0 : i.y);
  }
  function ur(e, t, n, i = 0) {
    const o = i ? J(n.min, n.max, i) : n.min;
    e.min = t.min - o, e.max = e.min + oe(t);
  }
  function Nn(e, t, n, i) {
    ur(e.x, t.x, n.x, i == null ? void 0 : i.x), ur(e.y, t.y, n.y, i == null ? void 0 : i.y);
  }
  function fr(e, t, n, i, o) {
    return e -= t, e = Ln(e, 1 / n, i), o !== void 0 && (e = Ln(e, 1 / o, i)), e;
  }
  function Nm(e, t = 0, n = 1, i = 0.5, o, s = e, r = e) {
    if (Se.test(t) && (t = parseFloat(t), t = J(r.min, r.max, t / 100) - r.min), typeof t != "number") return;
    let a = J(s.min, s.max, i);
    e === s && (a -= t), e.min = fr(e.min, t, n, a, o), e.max = fr(e.max, t, n, a, o);
  }
  function dr(e, t, [n, i, o], s, r) {
    Nm(e, t[n], t[i], t[o], t.scale, s, r);
  }
  const Dm = [
    "x",
    "scaleX",
    "originX"
  ], Km = [
    "y",
    "scaleY",
    "originY"
  ];
  function hr(e, t, n, i) {
    dr(e.x, t, Dm, n ? n.x : void 0, i ? i.x : void 0), dr(e.y, t, Km, n ? n.y : void 0, i ? i.y : void 0);
  }
  function mr(e) {
    return e.translate === 0 && e.scale === 1;
  }
  function Il(e) {
    return mr(e.x) && mr(e.y);
  }
  function pr(e, t) {
    return e.min === t.min && e.max === t.max;
  }
  function Wm(e, t) {
    return pr(e.x, t.x) && pr(e.y, t.y);
  }
  function gr(e, t) {
    return Math.round(e.min) === Math.round(t.min) && Math.round(e.max) === Math.round(t.max);
  }
  function Zl(e, t) {
    return gr(e.x, t.x) && gr(e.y, t.y);
  }
  function Ar(e) {
    return oe(e.x) / oe(e.y);
  }
  function yr(e, t) {
    return e.translate === t.translate && e.scale === t.scale && e.originPoint === t.originPoint;
  }
  function qe(e) {
    return [
      e("x"),
      e("y")
    ];
  }
  function zm(e, t, n) {
    let i = "";
    const o = e.x.translate / t.x, s = e.y.translate / t.y, r = (n == null ? void 0 : n.z) || 0;
    if ((o || s || r) && (i = `translate3d(${o}px, ${s}px, ${r}px) `), (t.x !== 1 || t.y !== 1) && (i += `scale(${1 / t.x}, ${1 / t.y}) `), n) {
      const { transformPerspective: c, rotate: u, rotateX: f, rotateY: d, skewX: m, skewY: p } = n;
      c && (i = `perspective(${c}px) ${i}`), u && (i += `rotate(${u}deg) `), f && (i += `rotateX(${f}deg) `), d && (i += `rotateY(${d}deg) `), m && (i += `skewX(${m}deg) `), p && (i += `skewY(${p}deg) `);
    }
    const a = e.x.scale * t.x, l = e.y.scale * t.y;
    return (a !== 1 || l !== 1) && (i += `scale(${a}, ${l})`), i || "none";
  }
  const Jl = [
    "borderTopLeftRadius",
    "borderTopRightRadius",
    "borderBottomLeftRadius",
    "borderBottomRightRadius"
  ], Hm = Jl.length, xr = (e) => typeof e == "string" ? parseFloat(e) : e, Pr = (e) => typeof e == "number" || E.test(e);
  function Mm(e, t, n, i, o, s) {
    o ? (e.opacity = J(0, n.opacity ?? 1, km(i)), e.opacityExit = J(t.opacity ?? 1, 0, Im(i))) : s && (e.opacity = J(t.opacity ?? 1, n.opacity ?? 1, i));
    for (let r = 0; r < Hm; r++) {
      const a = Jl[r];
      let l = wr(t, a), c = wr(n, a);
      if (l === void 0 && c === void 0) continue;
      l || (l = 0), c || (c = 0), l === 0 || c === 0 || Pr(l) === Pr(c) ? (e[a] = Math.max(J(xr(l), xr(c), i), 0), (Se.test(c) || Se.test(l)) && (e[a] += "%")) : e[a] = c;
    }
    (t.rotate || n.rotate) && (e.rotate = J(t.rotate || 0, n.rotate || 0, i));
  }
  function wr(e, t) {
    return e[t] !== void 0 ? e[t] : e.borderRadius;
  }
  const km = Ol(0, 0.5, Da), Im = Ol(0.5, 0.95, ue);
  function Ol(e, t, n) {
    return (i) => i < e ? 0 : i > t ? 1 : n(Ht(e, t, i));
  }
  function Zm(e, t, n) {
    const i = ne(e) ? e : At(e);
    return i.start(Mo("", i, t, n)), i.animation;
  }
  function It(e, t, n, i = {
    passive: true
  }) {
    return e.addEventListener(t, n, i), () => e.removeEventListener(t, n);
  }
  const Jm = (e, t) => e.depth - t.depth;
  class Om {
    constructor() {
      this.children = [], this.isDirty = false;
    }
    add(t) {
      bo(this.children, t), this.isDirty = true;
    }
    remove(t) {
      Sn(this.children, t), this.isDirty = true;
    }
    forEach(t) {
      this.isDirty && this.children.sort(Jm), this.isDirty = false, this.children.forEach(t);
    }
  }
  function Ym(e, t) {
    const n = ie.now(), i = ({ timestamp: o }) => {
      const s = o - n;
      s >= t && (Me(i), e(s - t));
    };
    return M.setup(i, true), () => Me(i);
  }
  function wn(e) {
    return ne(e) ? e.get() : e;
  }
  class Bm {
    constructor() {
      this.members = [];
    }
    add(t) {
      bo(this.members, t);
      for (let n = this.members.length - 1; n >= 0; n--) {
        const i = this.members[n];
        if (i === t || i === this.lead || i === this.prevLead) continue;
        const o = i.instance;
        (!o || o.isConnected === false) && !i.snapshot && (Sn(this.members, i), i.unmount());
      }
      t.scheduleRender();
    }
    remove(t) {
      if (Sn(this.members, t), t === this.prevLead && (this.prevLead = void 0), t === this.lead) {
        const n = this.members[this.members.length - 1];
        n && this.promote(n);
      }
    }
    relegate(t) {
      var _a3;
      for (let n = this.members.indexOf(t) - 1; n >= 0; n--) {
        const i = this.members[n];
        if (i.isPresent !== false && ((_a3 = i.instance) == null ? void 0 : _a3.isConnected) !== false) return this.promote(i), true;
      }
      return false;
    }
    promote(t, n) {
      var _a3;
      const i = this.lead;
      if (t !== i && (this.prevLead = i, this.lead = t, t.show(), i)) {
        i.updateSnapshot(), t.scheduleRender();
        const { layoutDependency: o } = i.options, { layoutDependency: s } = t.options;
        (o === void 0 || o !== s) && (t.resumeFrom = i, n && (i.preserveOpacity = true), i.snapshot && (t.snapshot = i.snapshot, t.snapshot.latestValues = i.animationValues || i.latestValues), ((_a3 = t.root) == null ? void 0 : _a3.isUpdating) && (t.isLayoutDirty = true)), t.options.crossfade === false && i.hide();
      }
    }
    exitAnimationComplete() {
      this.members.forEach((t) => {
        var _a3, _b, _c2, _d2, _e2;
        (_b = (_a3 = t.options).onExitComplete) == null ? void 0 : _b.call(_a3), (_e2 = (_c2 = t.resumingFrom) == null ? void 0 : (_d2 = _c2.options).onExitComplete) == null ? void 0 : _e2.call(_d2);
      });
    }
    scheduleRender() {
      this.members.forEach((t) => t.instance && t.scheduleRender(false));
    }
    removeLeadSnapshot() {
      var _a3;
      ((_a3 = this.lead) == null ? void 0 : _a3.snapshot) && (this.lead.snapshot = void 0);
    }
  }
  const qn = {
    hasAnimatedSinceResize: true,
    hasEverUpdated: false
  }, xi = [
    "",
    "X",
    "Y",
    "Z"
  ], jm = 1e3;
  let Xm = 0;
  function Pi(e, t, n, i) {
    const { latestValues: o } = t;
    o[e] && (n[e] = o[e], t.setStaticValue(e, 0), i && (i[e] = 0));
  }
  function Yl(e) {
    if (e.hasCheckedOptimisedAppear = true, e.root === e) return;
    const { visualElement: t } = e.options;
    if (!t) return;
    const n = dl(t);
    if (window.MotionHasOptimisedAnimation(n, "transform")) {
      const { layout: o, layoutId: s } = e.options;
      window.MotionCancelOptimisedAnimation(n, "transform", M, !(o || s));
    }
    const { parent: i } = e;
    i && !i.hasCheckedOptimisedAppear && Yl(i);
  }
  function Bl({ attachResizeListener: e, defaultParent: t, measureScroll: n, checkIsScrollRoot: i, resetTransform: o }) {
    return class {
      constructor(r = {}, a = t == null ? void 0 : t()) {
        this.id = Xm++, this.animationId = 0, this.animationCommitId = 0, this.children = /* @__PURE__ */ new Set(), this.options = {}, this.isTreeAnimating = false, this.isAnimationBlocked = false, this.isLayoutDirty = false, this.isProjectionDirty = false, this.isSharedProjectionDirty = false, this.isTransformDirty = false, this.updateManuallyBlocked = false, this.updateBlockedByResize = false, this.isUpdating = false, this.isSVG = false, this.needsReset = false, this.shouldResetTransform = false, this.hasCheckedOptimisedAppear = false, this.treeScale = {
          x: 1,
          y: 1
        }, this.eventHandlers = /* @__PURE__ */ new Map(), this.hasTreeAnimated = false, this.layoutVersion = 0, this.updateScheduled = false, this.scheduleUpdate = () => this.update(), this.projectionUpdateScheduled = false, this.checkUpdateFailed = () => {
          this.isUpdating && (this.isUpdating = false, this.clearAllSnapshots());
        }, this.updateProjection = () => {
          this.projectionUpdateScheduled = false, this.nodes.forEach(_m), this.nodes.forEach(op), this.nodes.forEach(sp), this.nodes.forEach($m);
        }, this.resolvedRelativeTargetAt = 0, this.linkedParentVersion = 0, this.hasProjected = false, this.isVisible = true, this.animationProgress = 0, this.sharedNodes = /* @__PURE__ */ new Map(), this.latestValues = r, this.root = a ? a.root || a : this, this.path = a ? [
          ...a.path,
          a
        ] : [], this.parent = a, this.depth = a ? a.depth + 1 : 0;
        for (let l = 0; l < this.path.length; l++) this.path[l].shouldResetTransform = true;
        this.root === this && (this.nodes = new Om());
      }
      addEventListener(r, a) {
        return this.eventHandlers.has(r) || this.eventHandlers.set(r, new Fo()), this.eventHandlers.get(r).add(a);
      }
      notifyListeners(r, ...a) {
        const l = this.eventHandlers.get(r);
        l && l.notify(...a);
      }
      hasListeners(r) {
        return this.eventHandlers.has(r);
      }
      mount(r) {
        if (this.instance) return;
        this.isSVG = Yo(r) && !_h(r), this.instance = r;
        const { layoutId: a, layout: l, visualElement: c } = this.options;
        if (c && !c.current && c.mount(r), this.root.nodes.add(this), this.parent && this.parent.children.add(this), this.root.hasTreeAnimated && (l || a) && (this.isLayoutDirty = true), e) {
          let u, f = 0;
          const d = () => this.root.updateBlockedByResize = false;
          M.read(() => {
            f = window.innerWidth;
          }), e(r, () => {
            const m = window.innerWidth;
            m !== f && (f = m, this.root.updateBlockedByResize = true, u && u(), u = Ym(d, 250), qn.hasAnimatedSinceResize && (qn.hasAnimatedSinceResize = false, this.nodes.forEach(vr)));
          });
        }
        a && this.root.registerSharedNode(a, this), this.options.animate !== false && c && (a || l) && this.addEventListener("didUpdate", ({ delta: u, hasLayoutChanged: f, hasRelativeLayoutChanged: d, layout: m }) => {
          if (this.isTreeAnimationBlocked()) {
            this.target = void 0, this.relativeTarget = void 0;
            return;
          }
          const p = this.options.transition || c.getDefaultTransition() || up, { onLayoutAnimationStart: h, onLayoutAnimationComplete: g } = c.getProps(), y = !this.targetLayout || !Zl(this.targetLayout, m), x = !f && d;
          if (this.options.layoutRoot || this.resumeFrom || x || f && (y || !this.currentAnimation)) {
            this.resumeFrom && (this.resumingFrom = this.resumeFrom, this.resumingFrom.resumingFrom = void 0);
            const w = {
              ...Ho(p, "layout"),
              onPlay: h,
              onComplete: g
            };
            (c.shouldReduceMotion || this.options.layoutRoot) && (w.delay = 0, w.type = false), this.startAnimation(w), this.setAnimationOrigin(u, x);
          } else f || vr(this), this.isLead() && this.options.onExitComplete && this.options.onExitComplete();
          this.targetLayout = m;
        });
      }
      unmount() {
        this.options.layoutId && this.willUpdate(), this.root.nodes.remove(this);
        const r = this.getStack();
        r && r.remove(this), this.parent && this.parent.children.delete(this), this.instance = void 0, this.eventHandlers.clear(), Me(this.updateProjection);
      }
      blockUpdate() {
        this.updateManuallyBlocked = true;
      }
      unblockUpdate() {
        this.updateManuallyBlocked = false;
      }
      isUpdateBlocked() {
        return this.updateManuallyBlocked || this.updateBlockedByResize;
      }
      isTreeAnimationBlocked() {
        return this.isAnimationBlocked || this.parent && this.parent.isTreeAnimationBlocked() || false;
      }
      startUpdate() {
        this.isUpdateBlocked() || (this.isUpdating = true, this.nodes && this.nodes.forEach(rp), this.animationId++);
      }
      getTransformTemplate() {
        const { visualElement: r } = this.options;
        return r && r.getProps().transformTemplate;
      }
      willUpdate(r = true) {
        if (this.root.hasTreeAnimated = true, this.root.isUpdateBlocked()) {
          this.options.onExitComplete && this.options.onExitComplete();
          return;
        }
        if (window.MotionCancelOptimisedAnimation && !this.hasCheckedOptimisedAppear && Yl(this), !this.root.isUpdating && this.root.startUpdate(), this.isLayoutDirty) return;
        this.isLayoutDirty = true;
        for (let u = 0; u < this.path.length; u++) {
          const f = this.path[u];
          f.shouldResetTransform = true, (typeof f.latestValues.x == "string" || typeof f.latestValues.y == "string") && (f.isLayoutDirty = true), f.updateScroll("snapshot"), f.options.layoutRoot && f.willUpdate(false);
        }
        const { layoutId: a, layout: l } = this.options;
        if (a === void 0 && !l) return;
        const c = this.getTransformTemplate();
        this.prevTransformTemplateValue = c ? c(this.latestValues, "") : void 0, this.updateSnapshot(), r && this.notifyListeners("willUpdate");
      }
      update() {
        if (this.updateScheduled = false, this.isUpdateBlocked()) {
          const l = this.updateBlockedByResize;
          this.unblockUpdate(), this.updateBlockedByResize = false, this.clearAllSnapshots(), l && this.nodes.forEach(tp), this.nodes.forEach(qr);
          return;
        }
        if (this.animationId <= this.animationCommitId) {
          this.nodes.forEach(Rr);
          return;
        }
        this.animationCommitId = this.animationId, this.isUpdating ? (this.isUpdating = false, this.nodes.forEach(np), this.nodes.forEach(ip), this.nodes.forEach(Qm), this.nodes.forEach(Gm)) : this.nodes.forEach(Rr), this.clearAllSnapshots();
        const a = ie.now();
        te.delta = be(0, 1e3 / 60, a - te.timestamp), te.timestamp = a, te.isProcessing = true, fi.update.process(te), fi.preRender.process(te), fi.render.process(te), te.isProcessing = false;
      }
      didUpdate() {
        this.updateScheduled || (this.updateScheduled = true, Jo.read(this.scheduleUpdate));
      }
      clearAllSnapshots() {
        this.nodes.forEach(ep), this.sharedNodes.forEach(ap);
      }
      scheduleUpdateProjection() {
        this.projectionUpdateScheduled || (this.projectionUpdateScheduled = true, M.preRender(this.updateProjection, false, true));
      }
      scheduleCheckAfterUnmount() {
        M.postRender(() => {
          this.isLayoutDirty ? this.root.didUpdate() : this.root.checkUpdateFailed();
        });
      }
      updateSnapshot() {
        this.snapshot || !this.instance || (this.snapshot = this.measure(), this.snapshot && !oe(this.snapshot.measuredBox.x) && !oe(this.snapshot.measuredBox.y) && (this.snapshot = void 0));
      }
      updateLayout() {
        if (!this.instance || (this.updateScroll(), !(this.options.alwaysMeasureLayout && this.isLead()) && !this.isLayoutDirty)) return;
        if (this.resumeFrom && !this.resumeFrom.instance) for (let l = 0; l < this.path.length; l++) this.path[l].updateScroll();
        const r = this.layout;
        this.layout = this.measure(false), this.layoutVersion++, this.layoutCorrected || (this.layoutCorrected = _()), this.isLayoutDirty = false, this.projectionDelta = void 0, this.notifyListeners("measure", this.layout.layoutBox);
        const { visualElement: a } = this.options;
        a && a.notify("LayoutMeasure", this.layout.layoutBox, r ? r.layoutBox : void 0);
      }
      updateScroll(r = "measure") {
        let a = !!(this.options.layoutScroll && this.instance);
        if (this.scroll && this.scroll.animationId === this.root.animationId && this.scroll.phase === r && (a = false), a && this.instance) {
          const l = i(this.instance);
          this.scroll = {
            animationId: this.root.animationId,
            phase: r,
            isRoot: l,
            offset: n(this.instance),
            wasRoot: this.scroll ? this.scroll.isRoot : l
          };
        }
      }
      resetTransform() {
        if (!o) return;
        const r = this.isLayoutDirty || this.shouldResetTransform || this.options.alwaysMeasureLayout, a = this.projectionDelta && !Il(this.projectionDelta), l = this.getTransformTemplate(), c = l ? l(this.latestValues, "") : void 0, u = c !== this.prevTransformTemplateValue;
        r && this.instance && (a || Ge(this.latestValues) || u) && (o(this.instance, c), this.shouldResetTransform = false, this.scheduleRender());
      }
      measure(r = true) {
        const a = this.measurePageBox();
        let l = this.removeElementScroll(a);
        return r && (l = this.removeTransform(l)), fp(l), {
          animationId: this.root.animationId,
          measuredBox: a,
          layoutBox: l,
          latestValues: {},
          source: this.id
        };
      }
      measurePageBox() {
        var _a3;
        const { visualElement: r } = this.options;
        if (!r) return _();
        const a = r.measureViewportBox();
        if (!(((_a3 = this.scroll) == null ? void 0 : _a3.wasRoot) || this.path.some(dp))) {
          const { scroll: c } = this.root;
          c && (Re(a.x, c.offset.x), Re(a.y, c.offset.y));
        }
        return a;
      }
      removeElementScroll(r) {
        var _a3;
        const a = _();
        if (fe(a, r), (_a3 = this.scroll) == null ? void 0 : _a3.wasRoot) return a;
        for (let l = 0; l < this.path.length; l++) {
          const c = this.path[l], { scroll: u, options: f } = c;
          c !== this.root && u && f.layoutScroll && (u.wasRoot && fe(a, r), Re(a.x, u.offset.x), Re(a.y, u.offset.y));
        }
        return a;
      }
      applyTransform(r, a = false, l) {
        var _a3, _b;
        const c = l || _();
        fe(c, r);
        for (let u = 0; u < this.path.length; u++) {
          const f = this.path[u];
          !a && f.options.layoutScroll && f.scroll && f !== f.root && (Re(c.x, -f.scroll.offset.x), Re(c.y, -f.scroll.offset.y)), Ge(f.latestValues) && Pn(c, f.latestValues, (_a3 = f.layout) == null ? void 0 : _a3.layoutBox);
        }
        return Ge(this.latestValues) && Pn(c, this.latestValues, (_b = this.layout) == null ? void 0 : _b.layoutBox), c;
      }
      removeTransform(r) {
        var _a3;
        const a = _();
        fe(a, r);
        for (let l = 0; l < this.path.length; l++) {
          const c = this.path[l];
          if (!Ge(c.latestValues)) continue;
          let u;
          c.instance && (so(c.latestValues) && c.updateSnapshot(), u = _(), fe(u, c.measurePageBox())), hr(a, c.latestValues, (_a3 = c.snapshot) == null ? void 0 : _a3.layoutBox, u);
        }
        return Ge(this.latestValues) && hr(a, this.latestValues), a;
      }
      setTargetDelta(r) {
        this.targetDelta = r, this.root.scheduleUpdateProjection(), this.isProjectionDirty = true;
      }
      setOptions(r) {
        this.options = {
          ...this.options,
          ...r,
          crossfade: r.crossfade !== void 0 ? r.crossfade : true
        };
      }
      clearMeasurements() {
        this.scroll = void 0, this.layout = void 0, this.snapshot = void 0, this.prevTransformTemplateValue = void 0, this.targetDelta = void 0, this.target = void 0, this.isLayoutDirty = false;
      }
      forceRelativeParentToResolveTarget() {
        this.relativeParent && this.relativeParent.resolvedRelativeTargetAt !== te.timestamp && this.relativeParent.resolveTargetDelta(true);
      }
      resolveTargetDelta(r = false) {
        var _a3;
        const a = this.getLead();
        this.isProjectionDirty || (this.isProjectionDirty = a.isProjectionDirty), this.isTransformDirty || (this.isTransformDirty = a.isTransformDirty), this.isSharedProjectionDirty || (this.isSharedProjectionDirty = a.isSharedProjectionDirty);
        const l = !!this.resumingFrom || this !== a;
        if (!(r || l && this.isSharedProjectionDirty || this.isProjectionDirty || ((_a3 = this.parent) == null ? void 0 : _a3.isProjectionDirty) || this.attemptToResolveRelativeTarget || this.root.updateBlockedByResize)) return;
        const { layout: u, layoutId: f } = this.options;
        if (!this.layout || !(u || f)) return;
        this.resolvedRelativeTargetAt = te.timestamp;
        const d = this.getClosestProjectingParent();
        d && this.linkedParentVersion !== d.layoutVersion && !d.options.layoutRoot && this.removeRelativeTarget(), !this.targetDelta && !this.relativeTarget && (this.options.layoutAnchor !== false && d && d.layout ? this.createRelativeTarget(d, this.layout.layoutBox, d.layout.layoutBox) : this.removeRelativeTarget()), !(!this.relativeTarget && !this.targetDelta) && (this.target || (this.target = _(), this.targetWithTransforms = _()), this.relativeTarget && this.relativeTargetOrigin && this.relativeParent && this.relativeParent.target ? (this.forceRelativeParentToResolveTarget(), Lm(this.target, this.relativeTarget, this.relativeParent.target, this.options.layoutAnchor || void 0)) : this.targetDelta ? (this.resumingFrom ? this.applyTransform(this.layout.layoutBox, false, this.target) : fe(this.target, this.layout.layoutBox), El(this.target, this.targetDelta)) : fe(this.target, this.layout.layoutBox), this.attemptToResolveRelativeTarget && (this.attemptToResolveRelativeTarget = false, this.options.layoutAnchor !== false && d && !!d.resumingFrom == !!this.resumingFrom && !d.options.layoutScroll && d.target && this.animationProgress !== 1 ? this.createRelativeTarget(d, this.target, d.target) : this.relativeParent = this.relativeTarget = void 0));
      }
      getClosestProjectingParent() {
        if (!(!this.parent || so(this.parent.latestValues) || Fl(this.parent.latestValues))) return this.parent.isProjecting() ? this.parent : this.parent.getClosestProjectingParent();
      }
      isProjecting() {
        return !!((this.relativeTarget || this.targetDelta || this.options.layoutRoot) && this.layout);
      }
      createRelativeTarget(r, a, l) {
        this.relativeParent = r, this.linkedParentVersion = r.layoutVersion, this.forceRelativeParentToResolveTarget(), this.relativeTarget = _(), this.relativeTargetOrigin = _(), Nn(this.relativeTargetOrigin, a, l, this.options.layoutAnchor || void 0), fe(this.relativeTarget, this.relativeTargetOrigin);
      }
      removeRelativeTarget() {
        this.relativeParent = this.relativeTarget = void 0;
      }
      calcProjection() {
        var _a3;
        const r = this.getLead(), a = !!this.resumingFrom || this !== r;
        let l = true;
        if ((this.isProjectionDirty || ((_a3 = this.parent) == null ? void 0 : _a3.isProjectionDirty)) && (l = false), a && (this.isSharedProjectionDirty || this.isTransformDirty) && (l = false), this.resolvedRelativeTargetAt === te.timestamp && (l = false), l) return;
        const { layout: c, layoutId: u } = this.options;
        if (this.isTreeAnimating = !!(this.parent && this.parent.isTreeAnimating || this.currentAnimation || this.pendingAnimation), this.isTreeAnimating || (this.targetDelta = this.relativeTarget = void 0), !this.layout || !(c || u)) return;
        fe(this.layoutCorrected, this.layout.layoutBox);
        const f = this.treeScale.x, d = this.treeScale.y;
        lm(this.layoutCorrected, this.treeScale, this.path, a), r.layout && !r.target && (this.treeScale.x !== 1 || this.treeScale.y !== 1) && (r.target = r.layout.layoutBox, r.targetWithTransforms = _());
        const { target: m } = r;
        if (!m) {
          this.prevProjectionDelta && (this.createProjectionDeltas(), this.scheduleRender());
          return;
        }
        !this.projectionDelta || !this.prevProjectionDelta ? this.createProjectionDeltas() : (ar(this.prevProjectionDelta.x, this.projectionDelta.x), ar(this.prevProjectionDelta.y, this.projectionDelta.y)), Dt(this.projectionDelta, this.layoutCorrected, m, this.latestValues), (this.treeScale.x !== f || this.treeScale.y !== d || !yr(this.projectionDelta.x, this.prevProjectionDelta.x) || !yr(this.projectionDelta.y, this.prevProjectionDelta.y)) && (this.hasProjected = true, this.scheduleRender(), this.notifyListeners("projectionUpdate", m));
      }
      hide() {
        this.isVisible = false;
      }
      show() {
        this.isVisible = true;
      }
      scheduleRender(r = true) {
        var _a3;
        if ((_a3 = this.options.visualElement) == null ? void 0 : _a3.scheduleRender(), r) {
          const a = this.getStack();
          a && a.scheduleRender();
        }
        this.resumingFrom && !this.resumingFrom.instance && (this.resumingFrom = void 0);
      }
      createProjectionDeltas() {
        this.prevProjectionDelta = dt(), this.projectionDelta = dt(), this.projectionDeltaWithTransform = dt();
      }
      setAnimationOrigin(r, a = false) {
        const l = this.snapshot, c = l ? l.latestValues : {}, u = {
          ...this.latestValues
        }, f = dt();
        (!this.relativeParent || !this.relativeParent.options.layoutRoot) && (this.relativeTarget = this.relativeTargetOrigin = void 0), this.attemptToResolveRelativeTarget = !a;
        const d = _(), m = l ? l.source : void 0, p = this.layout ? this.layout.source : void 0, h = m !== p, g = this.getStack(), y = !g || g.members.length <= 1, x = !!(h && !y && this.options.crossfade === true && !this.path.some(cp));
        this.animationProgress = 0;
        let w;
        this.mixTargetDelta = (q) => {
          const R = q / 1e3;
          Vr(f.x, r.x, R), Vr(f.y, r.y, R), this.setTargetDelta(f), this.relativeTarget && this.relativeTargetOrigin && this.layout && this.relativeParent && this.relativeParent.layout && (Nn(d, this.layout.layoutBox, this.relativeParent.layout.layoutBox, this.options.layoutAnchor || void 0), lp(this.relativeTarget, this.relativeTargetOrigin, d, R), w && Wm(this.relativeTarget, w) && (this.isProjectionDirty = false), w || (w = _()), fe(w, this.relativeTarget)), h && (this.animationValues = u, Mm(u, c, this.latestValues, R, x, y)), this.root.scheduleUpdateProjection(), this.scheduleRender(), this.animationProgress = R;
        }, this.mixTargetDelta(this.options.layoutRoot ? 1e3 : 0);
      }
      startAnimation(r) {
        var _a3, _b, _c2;
        this.notifyListeners("animationStart"), (_a3 = this.currentAnimation) == null ? void 0 : _a3.stop(), (_c2 = (_b = this.resumingFrom) == null ? void 0 : _b.currentAnimation) == null ? void 0 : _c2.stop(), this.pendingAnimation && (Me(this.pendingAnimation), this.pendingAnimation = void 0), this.pendingAnimation = M.update(() => {
          qn.hasAnimatedSinceResize = true, this.motionValue || (this.motionValue = At(0)), this.motionValue.jump(0, false), this.currentAnimation = Zm(this.motionValue, [
            0,
            1e3
          ], {
            ...r,
            velocity: 0,
            isSync: true,
            onUpdate: (a) => {
              this.mixTargetDelta(a), r.onUpdate && r.onUpdate(a);
            },
            onStop: () => {
            },
            onComplete: () => {
              r.onComplete && r.onComplete(), this.completeAnimation();
            }
          }), this.resumingFrom && (this.resumingFrom.currentAnimation = this.currentAnimation), this.pendingAnimation = void 0;
        });
      }
      completeAnimation() {
        this.resumingFrom && (this.resumingFrom.currentAnimation = void 0, this.resumingFrom.preserveOpacity = void 0);
        const r = this.getStack();
        r && r.exitAnimationComplete(), this.resumingFrom = this.currentAnimation = this.animationValues = void 0, this.notifyListeners("animationComplete");
      }
      finishAnimation() {
        this.currentAnimation && (this.mixTargetDelta && this.mixTargetDelta(jm), this.currentAnimation.stop()), this.completeAnimation();
      }
      applyTransformsToTarget() {
        const r = this.getLead();
        let { targetWithTransforms: a, target: l, layout: c, latestValues: u } = r;
        if (!(!a || !l || !c)) {
          if (this !== r && this.layout && c && jl(this.options.animationType, this.layout.layoutBox, c.layoutBox)) {
            l = this.target || _();
            const f = oe(this.layout.layoutBox.x);
            l.x.min = r.target.x.min, l.x.max = l.x.min + f;
            const d = oe(this.layout.layoutBox.y);
            l.y.min = r.target.y.min, l.y.max = l.y.min + d;
          }
          fe(a, l), Pn(a, u), Dt(this.projectionDeltaWithTransform, this.layoutCorrected, a, u);
        }
      }
      registerSharedNode(r, a) {
        this.sharedNodes.has(r) || this.sharedNodes.set(r, new Bm()), this.sharedNodes.get(r).add(a);
        const c = a.options.initialPromotionConfig;
        a.promote({
          transition: c ? c.transition : void 0,
          preserveFollowOpacity: c && c.shouldPreserveFollowOpacity ? c.shouldPreserveFollowOpacity(a) : void 0
        });
      }
      isLead() {
        const r = this.getStack();
        return r ? r.lead === this : true;
      }
      getLead() {
        var _a3;
        const { layoutId: r } = this.options;
        return r ? ((_a3 = this.getStack()) == null ? void 0 : _a3.lead) || this : this;
      }
      getPrevLead() {
        var _a3;
        const { layoutId: r } = this.options;
        return r ? (_a3 = this.getStack()) == null ? void 0 : _a3.prevLead : void 0;
      }
      getStack() {
        const { layoutId: r } = this.options;
        if (r) return this.root.sharedNodes.get(r);
      }
      promote({ needsReset: r, transition: a, preserveFollowOpacity: l } = {}) {
        const c = this.getStack();
        c && c.promote(this, l), r && (this.projectionDelta = void 0, this.needsReset = true), a && this.setOptions({
          transition: a
        });
      }
      relegate() {
        const r = this.getStack();
        return r ? r.relegate(this) : false;
      }
      resetSkewAndRotation() {
        const { visualElement: r } = this.options;
        if (!r) return;
        let a = false;
        const { latestValues: l } = r;
        if ((l.z || l.rotate || l.rotateX || l.rotateY || l.rotateZ || l.skewX || l.skewY) && (a = true), !a) return;
        const c = {};
        l.z && Pi("z", r, c, this.animationValues);
        for (let u = 0; u < xi.length; u++) Pi(`rotate${xi[u]}`, r, c, this.animationValues), Pi(`skew${xi[u]}`, r, c, this.animationValues);
        r.render();
        for (const u in c) r.setStaticValue(u, c[u]), this.animationValues && (this.animationValues[u] = c[u]);
        r.scheduleRender();
      }
      applyProjectionStyles(r, a) {
        if (!this.instance || this.isSVG) return;
        if (!this.isVisible) {
          r.visibility = "hidden";
          return;
        }
        const l = this.getTransformTemplate();
        if (this.needsReset) {
          this.needsReset = false, r.visibility = "", r.opacity = "", r.pointerEvents = wn(a == null ? void 0 : a.pointerEvents) || "", r.transform = l ? l(this.latestValues, "") : "none";
          return;
        }
        const c = this.getLead();
        if (!this.projectionDelta || !this.layout || !c.target) {
          this.options.layoutId && (r.opacity = this.latestValues.opacity !== void 0 ? this.latestValues.opacity : 1, r.pointerEvents = wn(a == null ? void 0 : a.pointerEvents) || ""), this.hasProjected && !Ge(this.latestValues) && (r.transform = l ? l({}, "") : "none", this.hasProjected = false);
          return;
        }
        r.visibility = "";
        const u = c.animationValues || c.latestValues;
        this.applyTransformsToTarget();
        let f = zm(this.projectionDeltaWithTransform, this.treeScale, u);
        l && (f = l(u, f)), r.transform = f;
        const { x: d, y: m } = this.projectionDelta;
        r.transformOrigin = `${d.origin * 100}% ${m.origin * 100}% 0`, c.animationValues ? r.opacity = c === this ? u.opacity ?? this.latestValues.opacity ?? 1 : this.preserveOpacity ? this.latestValues.opacity : u.opacityExit : r.opacity = c === this ? u.opacity !== void 0 ? u.opacity : "" : u.opacityExit !== void 0 ? u.opacityExit : 0;
        for (const p in ao) {
          if (u[p] === void 0) continue;
          const { correct: h, applyTo: g, isCSSVariable: y } = ao[p], x = f === "none" ? u[p] : h(u[p], c);
          if (g) {
            const w = g.length;
            for (let q = 0; q < w; q++) r[g[q]] = x;
          } else y ? this.options.visualElement.renderState.vars[p] = x : r[p] = x;
        }
        this.options.layoutId && (r.pointerEvents = c === this ? wn(a == null ? void 0 : a.pointerEvents) || "" : "none");
      }
      clearSnapshot() {
        this.resumeFrom = this.snapshot = void 0;
      }
      resetTree() {
        this.root.nodes.forEach((r) => {
          var _a3;
          return (_a3 = r.currentAnimation) == null ? void 0 : _a3.stop();
        }), this.root.nodes.forEach(qr), this.root.sharedNodes.clear();
      }
    };
  }
  function Qm(e) {
    e.updateLayout();
  }
  function Gm(e) {
    var _a3;
    const t = ((_a3 = e.resumeFrom) == null ? void 0 : _a3.snapshot) || e.snapshot;
    if (e.isLead() && e.layout && t && e.hasListeners("didUpdate")) {
      const { layoutBox: n, measuredBox: i } = e.layout, { animationType: o } = e.options, s = t.source !== e.layout.source;
      if (o === "size") qe((u) => {
        const f = s ? t.measuredBox[u] : t.layoutBox[u], d = oe(f);
        f.min = n[u].min, f.max = f.min + d;
      });
      else if (o === "x" || o === "y") {
        const u = o === "x" ? "y" : "x";
        lo(s ? t.measuredBox[u] : t.layoutBox[u], n[u]);
      } else jl(o, t.layoutBox, n) && qe((u) => {
        const f = s ? t.measuredBox[u] : t.layoutBox[u], d = oe(n[u]);
        f.max = f.min + d, e.relativeTarget && !e.currentAnimation && (e.isProjectionDirty = true, e.relativeTarget[u].max = e.relativeTarget[u].min + d);
      });
      const r = dt();
      Dt(r, n, t.layoutBox);
      const a = dt();
      s ? Dt(a, e.applyTransform(i, true), t.measuredBox) : Dt(a, n, t.layoutBox);
      const l = !Il(r);
      let c = false;
      if (!e.resumeFrom) {
        const u = e.getClosestProjectingParent();
        if (u && !u.resumeFrom) {
          const { snapshot: f, layout: d } = u;
          if (f && d) {
            const m = e.options.layoutAnchor || void 0, p = _();
            Nn(p, t.layoutBox, f.layoutBox, m);
            const h = _();
            Nn(h, n, d.layoutBox, m), Zl(p, h) || (c = true), u.options.layoutRoot && (e.relativeTarget = h, e.relativeTargetOrigin = p, e.relativeParent = u);
          }
        }
      }
      e.notifyListeners("didUpdate", {
        layout: n,
        snapshot: t,
        delta: a,
        layoutDelta: r,
        hasLayoutChanged: l,
        hasRelativeLayoutChanged: c
      });
    } else if (e.isLead()) {
      const { onExitComplete: n } = e.options;
      n && n();
    }
    e.options.transition = void 0;
  }
  function _m(e) {
    e.parent && (e.isProjecting() || (e.isProjectionDirty = e.parent.isProjectionDirty), e.isSharedProjectionDirty || (e.isSharedProjectionDirty = !!(e.isProjectionDirty || e.parent.isProjectionDirty || e.parent.isSharedProjectionDirty)), e.isTransformDirty || (e.isTransformDirty = e.parent.isTransformDirty));
  }
  function $m(e) {
    e.isProjectionDirty = e.isSharedProjectionDirty = e.isTransformDirty = false;
  }
  function ep(e) {
    e.clearSnapshot();
  }
  function qr(e) {
    e.clearMeasurements();
  }
  function tp(e) {
    e.isLayoutDirty = true, e.updateLayout();
  }
  function Rr(e) {
    e.isLayoutDirty = false;
  }
  function np(e) {
    e.isAnimationBlocked && e.layout && !e.isLayoutDirty && (e.snapshot = e.layout, e.isLayoutDirty = true);
  }
  function ip(e) {
    const { visualElement: t } = e.options;
    t && t.getProps().onBeforeLayoutMeasure && t.notify("BeforeLayoutMeasure"), e.resetTransform();
  }
  function vr(e) {
    e.finishAnimation(), e.targetDelta = e.relativeTarget = e.target = void 0, e.isProjectionDirty = true;
  }
  function op(e) {
    e.resolveTargetDelta();
  }
  function sp(e) {
    e.calcProjection();
  }
  function rp(e) {
    e.resetSkewAndRotation();
  }
  function ap(e) {
    e.removeLeadSnapshot();
  }
  function Vr(e, t, n) {
    e.translate = J(t.translate, 0, n), e.scale = J(t.scale, 1, n), e.origin = t.origin, e.originPoint = t.originPoint;
  }
  function Sr(e, t, n, i) {
    e.min = J(t.min, n.min, i), e.max = J(t.max, n.max, i);
  }
  function lp(e, t, n, i) {
    Sr(e.x, t.x, n.x, i), Sr(e.y, t.y, n.y, i);
  }
  function cp(e) {
    return e.animationValues && e.animationValues.opacityExit !== void 0;
  }
  const up = {
    duration: 0.45,
    ease: [
      0.4,
      0,
      0.1,
      1
    ]
  }, Tr = (e) => typeof navigator < "u" && navigator.userAgent && navigator.userAgent.toLowerCase().includes(e), br = Tr("applewebkit/") && !Tr("chrome/") ? Math.round : ue;
  function Fr(e) {
    e.min = br(e.min), e.max = br(e.max);
  }
  function fp(e) {
    Fr(e.x), Fr(e.y);
  }
  function jl(e, t, n) {
    return e === "position" || e === "preserve-aspect" && !Um(Ar(t), Ar(n), 0.2);
  }
  function dp(e) {
    var _a3;
    return e !== e.root && ((_a3 = e.scroll) == null ? void 0 : _a3.wasRoot);
  }
  const hp = Bl({
    attachResizeListener: (e, t) => It(e, "resize", t),
    measureScroll: () => {
      var _a3, _b;
      return {
        x: document.documentElement.scrollLeft || ((_a3 = document.body) == null ? void 0 : _a3.scrollLeft) || 0,
        y: document.documentElement.scrollTop || ((_b = document.body) == null ? void 0 : _b.scrollTop) || 0
      };
    },
    checkIsScrollRoot: () => true
  }), mp = (e) => !e.isLayoutDirty && e.willUpdate(false);
  function Er() {
    const e = /* @__PURE__ */ new Set(), t = /* @__PURE__ */ new WeakMap(), n = () => e.forEach(mp);
    return {
      add: (i) => {
        e.add(i), t.set(i, i.addEventListener("willUpdate", n));
      },
      remove: (i) => {
        e.delete(i);
        const o = t.get(i);
        o && (o(), t.delete(i)), n();
      },
      dirty: n
    };
  }
  const wi = {
    current: void 0
  }, Xl = Bl({
    measureScroll: (e) => ({
      x: e.scrollLeft,
      y: e.scrollTop
    }),
    defaultParent: () => {
      if (!wi.current) {
        const e = new hp({});
        e.mount(window), e.setOptions({
          layoutScroll: true
        }), wi.current = e;
      }
      return wi.current;
    },
    resetTransform: (e, t) => {
      e.style.transform = t !== void 0 ? t : "none";
    },
    checkIsScrollRoot: (e) => window.getComputedStyle(e).position === "fixed"
  }), _o = Ne({
    transformPagePoint: (e) => e,
    isStatic: false,
    reducedMotion: "never"
  });
  function Cr(e, t) {
    if (typeof e == "function") return e(t);
    e != null && (e.current = t);
  }
  function pp(...e) {
    return (t) => {
      let n = false;
      const i = e.map((o) => {
        const s = Cr(o, t);
        return !n && typeof s == "function" && (n = true), s;
      });
      if (n) return () => {
        for (let o = 0; o < i.length; o++) {
          const s = i[o];
          typeof s == "function" ? s() : Cr(e[o], null);
        }
      };
    };
  }
  function gp(...e) {
    return A.useCallback(pp(...e), e);
  }
  class Ap extends A.Component {
    getSnapshotBeforeUpdate(t) {
      const n = this.props.childRef.current;
      if (pn(n) && t.isPresent && !this.props.isPresent && this.props.pop !== false) {
        const i = n.offsetParent, o = pn(i) && i.offsetWidth || 0, s = pn(i) && i.offsetHeight || 0, r = getComputedStyle(n), a = this.props.sizeRef.current;
        a.height = parseFloat(r.height), a.width = parseFloat(r.width), a.top = n.offsetTop, a.left = n.offsetLeft, a.right = o - a.width - a.left, a.bottom = s - a.height - a.top;
      }
      return null;
    }
    componentDidUpdate() {
    }
    render() {
      return this.props.children;
    }
  }
  function yp({ children: e, isPresent: t, anchorX: n, anchorY: i, root: o, pop: s }) {
    var _a3;
    const r = Pt(), a = O(null), l = O({
      width: 0,
      height: 0,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }), { nonce: c } = G(_o), u = ((_a3 = e.props) == null ? void 0 : _a3.ref) ?? (e == null ? void 0 : e.ref), f = gp(a, u);
    return qo(() => {
      const { width: d, height: m, top: p, left: h, right: g, bottom: y } = l.current;
      if (t || s === false || !a.current || !d || !m) return;
      const x = n === "left" ? `left: ${h}` : `right: ${g}`, w = i === "bottom" ? `bottom: ${y}` : `top: ${p}`;
      a.current.dataset.motionPopId = r;
      const q = document.createElement("style");
      c && (q.nonce = c);
      const R = o ?? document.head;
      return R.appendChild(q), q.sheet && q.sheet.insertRule(`
          [data-motion-pop-id="${r}"] {
            position: absolute !important;
            width: ${d}px !important;
            height: ${m}px !important;
            ${x}px !important;
            ${w}px !important;
          }
        `), () => {
        var _a4;
        (_a4 = a.current) == null ? void 0 : _a4.removeAttribute("data-motion-pop-id"), R.contains(q) && R.removeChild(q);
      };
    }, [
      t
    ]), P(Ap, {
      isPresent: t,
      childRef: a,
      sizeRef: l,
      pop: s,
      children: s === false ? e : A.cloneElement(e, {
        ref: f
      })
    });
  }
  const xp = ({ children: e, initial: t, isPresent: n, onExitComplete: i, custom: o, presenceAffectsLayout: s, mode: r, anchorX: a, anchorY: l, root: c }) => {
    const u = So(Pp), f = Pt();
    let d = true, m = pe(() => (d = false, {
      id: f,
      initial: t,
      isPresent: n,
      custom: o,
      onExitComplete: (p) => {
        u.set(p, true);
        for (const h of u.values()) if (!h) return;
        i && i();
      },
      register: (p) => (u.set(p, false), () => u.delete(p))
    }), [
      n,
      u,
      i
    ]);
    return s && d && (m = {
      ...m
    }), pe(() => {
      u.forEach((p, h) => u.set(h, false));
    }, [
      n
    ]), A.useEffect(() => {
      !n && !u.size && i && i();
    }, [
      n
    ]), e = P(yp, {
      pop: r === "popLayout",
      isPresent: n,
      anchorX: a,
      anchorY: l,
      root: c,
      children: e
    }), P(Jn.Provider, {
      value: m,
      children: e
    });
  };
  function Pp() {
    return /* @__PURE__ */ new Map();
  }
  function Ql(e = true) {
    const t = G(Jn);
    if (t === null) return [
      true,
      null
    ];
    const { isPresent: n, onExitComplete: i, register: o } = t, s = Pt();
    le(() => {
      if (e) return o(s);
    }, [
      e
    ]);
    const r = pt(() => e && i && i(s), [
      s,
      i,
      e
    ]);
    return !n && i ? [
      false,
      r
    ] : [
      true
    ];
  }
  const sn = (e) => e.key || "";
  function Ur(e) {
    const t = [];
    return ff.forEach(e, (n) => {
      df(n) && t.push(n);
    }), t;
  }
  const Vt = ({ children: e, custom: t, initial: n = true, onExitComplete: i, presenceAffectsLayout: o = true, mode: s = "sync", propagate: r = false, anchorX: a = "left", anchorY: l = "top", root: c }) => {
    const [u, f] = Ql(r), d = pe(() => Ur(e), [
      e
    ]), m = r && !u ? [] : d.map(sn), p = O(true), h = O(d), g = So(() => /* @__PURE__ */ new Map()), y = O(/* @__PURE__ */ new Set()), [x, w] = ge(d), [q, R] = ge(d);
    To(() => {
      p.current = false, h.current = d;
      for (let V = 0; V < q.length; V++) {
        const b = sn(q[V]);
        m.includes(b) ? (g.delete(b), y.current.delete(b)) : g.get(b) !== true && g.set(b, false);
      }
    }, [
      q,
      m.length,
      m.join("-")
    ]);
    const v = [];
    if (d !== x) {
      let V = [
        ...d
      ];
      for (let b = 0; b < q.length; b++) {
        const T = q[b], F = sn(T);
        m.includes(F) || (V.splice(b, 0, T), v.push(T));
      }
      return s === "wait" && v.length && (V = v), R(Ur(V)), w(d), null;
    }
    process.env.NODE_ENV !== "production" && s === "wait" && q.length > 1 && console.warn(`You're attempting to animate multiple children within AnimatePresence, but its mode is set to "wait". This will lead to odd visual behaviour.`);
    const { forceRender: S } = G(zt);
    return P(Mi, {
      children: q.map((V) => {
        const b = sn(V), T = r && !u ? false : d === q || m.includes(b), F = () => {
          if (y.current.has(b)) return;
          if (g.has(b)) y.current.add(b), g.set(b, true);
          else return;
          let U = true;
          g.forEach((D) => {
            D || (U = false);
          }), U && (S == null ? void 0 : S(), R(h.current), r && (f == null ? void 0 : f()), i && i());
        };
        return P(xp, {
          isPresent: T,
          initial: !p.current || n ? void 0 : false,
          custom: t,
          presenceAffectsLayout: o,
          mode: s,
          root: c,
          onExitComplete: T ? void 0 : F,
          anchorX: a,
          anchorY: l,
          children: V
        }, b);
      })
    });
  }, wp = Ne(null);
  function qp() {
    const e = O(false);
    return To(() => (e.current = true, () => {
      e.current = false;
    }), []), e;
  }
  function Rp() {
    const e = qp(), [t, n] = ge(0), i = pt(() => {
      e.current && n(t + 1);
    }, [
      t
    ]);
    return [
      pt(() => M.postRender(i), [
        i
      ]),
      t
    ];
  }
  const Gl = (e) => e === true, vp = (e) => Gl(e === true) || e === "id", Vp = ({ children: e, id: t, inherit: n = true }) => {
    const i = G(zt), o = G(wp), [s, r] = Rp(), a = O(null), l = i.id || o;
    a.current === null && (vp(n) && l && (t = t ? l + "-" + t : l), a.current = {
      id: t,
      group: Gl(n) && i.group || Er()
    });
    const c = pe(() => ({
      ...a.current,
      forceRender: s
    }), [
      r
    ]);
    return P(zt.Provider, {
      value: c,
      children: e
    });
  }, _l = Ne({
    strict: false
  }), Lr = {
    animation: [
      "animate",
      "variants",
      "whileHover",
      "whileTap",
      "exit",
      "whileInView",
      "whileFocus",
      "whileDrag"
    ],
    exit: [
      "exit"
    ],
    drag: [
      "drag",
      "dragControls"
    ],
    focus: [
      "whileFocus"
    ],
    hover: [
      "whileHover",
      "onHoverStart",
      "onHoverEnd"
    ],
    tap: [
      "whileTap",
      "onTap",
      "onTapStart",
      "onTapCancel"
    ],
    pan: [
      "onPan",
      "onPanStart",
      "onPanSessionStart",
      "onPanEnd"
    ],
    inView: [
      "whileInView",
      "onViewportEnter",
      "onViewportLeave"
    ],
    layout: [
      "layout",
      "layoutId"
    ]
  };
  let Nr = false;
  function Sp() {
    if (Nr) return;
    const e = {};
    for (const t in Lr) e[t] = {
      isEnabled: (n) => Lr[t].some((i) => !!n[i])
    };
    Sl(e), Nr = true;
  }
  function $l() {
    return Sp(), om();
  }
  function Tp(e) {
    const t = $l();
    for (const n in e) t[n] = {
      ...t[n],
      ...e[n]
    };
    Sl(t);
  }
  const bp = /* @__PURE__ */ new Set([
    "animate",
    "exit",
    "variants",
    "initial",
    "style",
    "values",
    "variants",
    "transition",
    "transformTemplate",
    "custom",
    "inherit",
    "onBeforeLayoutMeasure",
    "onAnimationStart",
    "onAnimationComplete",
    "onUpdate",
    "onDragStart",
    "onDrag",
    "onDragEnd",
    "onMeasureDragConstraints",
    "onDirectionLock",
    "onDragTransitionEnd",
    "_dragX",
    "_dragY",
    "onHoverStart",
    "onHoverEnd",
    "onViewportEnter",
    "onViewportLeave",
    "globalTapTarget",
    "propagate",
    "ignoreStrict",
    "viewport"
  ]);
  function Dn(e) {
    return e.startsWith("while") || e.startsWith("drag") && e !== "draggable" || e.startsWith("layout") || e.startsWith("onTap") || e.startsWith("onPan") || e.startsWith("onLayout") || bp.has(e);
  }
  let ec = (e) => !Dn(e);
  function Fp(e) {
    typeof e == "function" && (ec = (t) => t.startsWith("on") ? !Dn(t) : e(t));
  }
  try {
    Fp(require("@emotion/is-prop-valid").default);
  } catch {
  }
  function Ep(e, t, n) {
    const i = {};
    for (const o in e) o === "values" && typeof e.values == "object" || ne(e[o]) || (ec(o) || n === true && Dn(o) || !t && !Dn(o) || e.draggable && o.startsWith("onDrag")) && (i[o] = e[o]);
    return i;
  }
  const Xn = Ne({});
  function Cp(e, t) {
    if (jn(e)) {
      const { initial: n, animate: i } = e;
      return {
        initial: n === false || kt(n) ? n : void 0,
        animate: kt(i) ? i : void 0
      };
    }
    return e.inherit !== false ? t : {};
  }
  function Up(e) {
    const { initial: t, animate: n } = Cp(e, G(Xn));
    return pe(() => ({
      initial: t,
      animate: n
    }), [
      Dr(t),
      Dr(n)
    ]);
  }
  function Dr(e) {
    return Array.isArray(e) ? e.join(" ") : e;
  }
  const $o = () => ({
    style: {},
    transform: {},
    transformOrigin: {},
    vars: {}
  });
  function tc(e, t, n) {
    for (const i in t) !ne(t[i]) && !Ll(i, n) && (e[i] = t[i]);
  }
  function Lp({ transformTemplate: e }, t) {
    return pe(() => {
      const n = $o();
      return Qo(n, t, e), Object.assign({}, n.vars, n.style);
    }, [
      t
    ]);
  }
  function Np(e, t) {
    const n = e.style || {}, i = {};
    return tc(i, n, e), Object.assign(i, Lp(e, t)), i;
  }
  function Dp(e, t) {
    const n = {}, i = Np(e, t);
    return e.drag && e.dragListener !== false && (n.draggable = false, i.userSelect = i.WebkitUserSelect = i.WebkitTouchCallout = "none", i.touchAction = e.drag === true ? "none" : `pan-${e.drag === "x" ? "y" : "x"}`), e.tabIndex === void 0 && (e.onTap || e.onTapStart || e.whileTap) && (n.tabIndex = 0), n.style = i, n;
  }
  const nc = () => ({
    ...$o(),
    attrs: {}
  });
  function Kp(e, t, n, i) {
    const o = pe(() => {
      const s = nc();
      return Nl(s, t, Kl(i), e.transformTemplate, e.style), {
        ...s.attrs,
        style: {
          ...s.style
        }
      };
    }, [
      t
    ]);
    if (e.style) {
      const s = {};
      tc(s, e.style, e), o.style = {
        ...s,
        ...o.style
      };
    }
    return o;
  }
  const Wp = [
    "animate",
    "circle",
    "defs",
    "desc",
    "ellipse",
    "g",
    "image",
    "line",
    "filter",
    "marker",
    "mask",
    "metadata",
    "path",
    "pattern",
    "polygon",
    "polyline",
    "rect",
    "stop",
    "switch",
    "symbol",
    "svg",
    "text",
    "tspan",
    "use",
    "view"
  ];
  function es(e) {
    return typeof e != "string" || e.includes("-") ? false : !!(Wp.indexOf(e) > -1 || /[A-Z]/u.test(e));
  }
  function zp(e, t, n, { latestValues: i }, o, s = false, r) {
    const l = (r ?? es(e) ? Kp : Dp)(t, i, o, e), c = Ep(t, typeof e == "string", s), u = e !== wa ? {
      ...c,
      ...l,
      ref: n
    } : {}, { children: f } = t, d = pe(() => ne(f) ? f.get() : f, [
      f
    ]);
    return hf(e, {
      ...u,
      children: d
    });
  }
  function Hp({ scrapeMotionValuesFromProps: e, createRenderState: t }, n, i, o) {
    return {
      latestValues: Mp(n, i, o, e),
      renderState: t()
    };
  }
  function Mp(e, t, n, i) {
    const o = {}, s = i(e, {});
    for (const d in s) o[d] = wn(s[d]);
    let { initial: r, animate: a } = e;
    const l = jn(e), c = vl(e);
    t && c && !l && e.inherit !== false && (r === void 0 && (r = t.initial), a === void 0 && (a = t.animate));
    let u = n ? n.initial === false : false;
    u = u || r === false;
    const f = u ? a : r;
    if (f && typeof f != "boolean" && !Bn(f)) {
      const d = Array.isArray(f) ? f : [
        f
      ];
      for (let m = 0; m < d.length; m++) {
        const p = ko(e, d[m]);
        if (p) {
          const { transitionEnd: h, transition: g, ...y } = p;
          for (const x in y) {
            let w = y[x];
            if (Array.isArray(w)) {
              const q = u ? w.length - 1 : 0;
              w = w[q];
            }
            w !== null && (o[x] = w);
          }
          for (const x in h) o[x] = h[x];
        }
      }
    }
    return o;
  }
  const ic = (e) => (t, n) => {
    const i = G(Xn), o = G(Jn), s = () => Hp(e, t, i, o);
    return n ? s() : So(s);
  }, kp = ic({
    scrapeMotionValuesFromProps: Go,
    createRenderState: $o
  }), Ip = ic({
    scrapeMotionValuesFromProps: Wl,
    createRenderState: nc
  }), Zp = Symbol.for("motionComponentSymbol");
  function Jp(e, t, n) {
    const i = O(n);
    qo(() => {
      i.current = n;
    });
    const o = O(null);
    return pt((s) => {
      var _a3;
      s && ((_a3 = e.onMount) == null ? void 0 : _a3.call(e, s));
      const r = i.current;
      if (typeof r == "function") if (s) {
        const a = r(s);
        typeof a == "function" && (o.current = a);
      } else o.current ? (o.current(), o.current = null) : r(s);
      else r && (r.current = s);
      t && (s ? t.mount(s) : t.unmount());
    }, [
      t
    ]);
  }
  const oc = Ne({});
  function lt(e) {
    return e && typeof e == "object" && Object.prototype.hasOwnProperty.call(e, "current");
  }
  function Op(e, t, n, i, o, s) {
    var _a3, _b;
    const { visualElement: r } = G(Xn), a = G(_l), l = G(Jn), c = G(_o), u = c.reducedMotion, f = c.skipAnimations, d = O(null), m = O(false);
    i = i || a.renderer, !d.current && i && (d.current = i(e, {
      visualState: t,
      parent: r,
      props: n,
      presenceContext: l,
      blockInitialAnimation: l ? l.initial === false : false,
      reducedMotionConfig: u,
      skipAnimations: f,
      isSVG: s
    }), m.current && d.current && (d.current.manuallyAnimateOnMount = true));
    const p = d.current, h = G(oc);
    p && !p.projection && o && (p.type === "html" || p.type === "svg") && Yp(d.current, n, o, h);
    const g = O(false);
    qo(() => {
      p && g.current && p.update(n, l);
    });
    const y = n[fl], x = O(!!y && typeof window < "u" && !((_a3 = window.MotionHandoffIsComplete) == null ? void 0 : _a3.call(window, y)) && ((_b = window.MotionHasOptimisedAnimation) == null ? void 0 : _b.call(window, y)));
    return To(() => {
      m.current = true, p && (g.current = true, window.MotionIsMounted = true, p.updateFeatures(), p.scheduleRenderMicrotask(), x.current && p.animationState && p.animationState.animateChanges());
    }), le(() => {
      p && (!x.current && p.animationState && p.animationState.animateChanges(), x.current && (queueMicrotask(() => {
        var _a4;
        (_a4 = window.MotionHandoffMarkAsComplete) == null ? void 0 : _a4.call(window, y);
      }), x.current = false), p.enteringChildren = void 0);
    }), p;
  }
  function Yp(e, t, n, i) {
    const { layoutId: o, layout: s, drag: r, dragConstraints: a, layoutScroll: l, layoutRoot: c, layoutAnchor: u, layoutCrossfade: f } = t;
    e.projection = new n(e.latestValues, t["data-framer-portal-id"] ? void 0 : sc(e.parent)), e.projection.setOptions({
      layoutId: o,
      layout: s,
      alwaysMeasureLayout: !!r || a && lt(a),
      visualElement: e,
      animationType: typeof s == "string" ? s : "both",
      initialPromotionConfig: i,
      crossfade: f,
      layoutScroll: l,
      layoutRoot: c,
      layoutAnchor: u
    });
  }
  function sc(e) {
    if (e) return e.options.allowProjection !== false ? e.projection : sc(e.parent);
  }
  function qi(e, { forwardMotionProps: t = false, type: n } = {}, i, o) {
    i && Tp(i);
    const s = n ? n === "svg" : es(e), r = s ? Ip : kp;
    function a(c, u) {
      let f;
      const d = {
        ...G(_o),
        ...c,
        layoutId: Bp(c)
      }, { isStatic: m } = d, p = Up(c), h = r(c, m);
      if (!m && typeof window < "u") {
        jp(d, i);
        const g = Xp(d);
        f = g.MeasureLayout, p.visualElement = Op(e, h, d, o, g.ProjectionNode, s);
      }
      return j(Xn.Provider, {
        value: p,
        children: [
          f && p.visualElement ? P(f, {
            visualElement: p.visualElement,
            ...d
          }) : null,
          zp(e, c, Jp(h, p.visualElement, u), h, m, t, s)
        ]
      });
    }
    a.displayName = `motion.${typeof e == "string" ? e : `create(${e.displayName ?? e.name ?? ""})`}`;
    const l = mf(a);
    return l[Zp] = e, l;
  }
  function Bp({ layoutId: e }) {
    const t = G(zt).id;
    return t && e !== void 0 ? t + "-" + e : e;
  }
  function jp(e, t) {
    const n = G(_l).strict;
    if (process.env.NODE_ENV !== "production" && t && n) {
      const i = "You have rendered a `motion` component within a `LazyMotion` component. This will break tree shaking. Import and render a `m` component instead.";
      e.ignoreStrict ? wt(false, i, "lazy-strict-mode") : Ce(false, i, "lazy-strict-mode");
    }
  }
  function Xp(e) {
    const t = $l(), { drag: n, layout: i } = t;
    if (!n && !i) return {};
    const o = {
      ...n,
      ...i
    };
    return {
      MeasureLayout: (n == null ? void 0 : n.isEnabled(e)) || (i == null ? void 0 : i.isEnabled(e)) ? o.MeasureLayout : void 0,
      ProjectionNode: o.ProjectionNode
    };
  }
  function Qp(e, t) {
    if (typeof Proxy > "u") return qi;
    const n = /* @__PURE__ */ new Map(), i = (s, r) => qi(s, r, e, t), o = (s, r) => (process.env.NODE_ENV !== "production" && On(false, "motion() is deprecated. Use motion.create() instead."), i(s, r));
    return new Proxy(o, {
      get: (s, r) => r === "create" ? i : (n.has(r) || n.set(r, qi(r, void 0, e, t)), n.get(r))
    });
  }
  const Gp = (e, t) => t.isSVG ?? es(e) ? new wm(t) : new pm(t, {
    allowProjection: e !== wa
  });
  class _p extends Je {
    constructor(t) {
      super(t), t.animationState || (t.animationState = Sm(t));
    }
    updateAnimationControlsSubscription() {
      const { animate: t } = this.node.getProps();
      Bn(t) && (this.unmountControls = t.subscribe(this.node));
    }
    mount() {
      this.updateAnimationControlsSubscription();
    }
    update() {
      const { animate: t } = this.node.getProps(), { animate: n } = this.node.prevProps || {};
      t !== n && this.updateAnimationControlsSubscription();
    }
    unmount() {
      var _a3;
      this.node.animationState.reset(), (_a3 = this.unmountControls) == null ? void 0 : _a3.call(this);
    }
  }
  let $p = 0;
  class eg extends Je {
    constructor() {
      super(...arguments), this.id = $p++, this.isExitComplete = false;
    }
    update() {
      var _a3;
      if (!this.node.presenceContext) return;
      const { isPresent: t, onExitComplete: n } = this.node.presenceContext, { isPresent: i } = this.node.prevPresenceContext || {};
      if (!this.node.animationState || t === i) return;
      if (t && i === false) {
        if (this.isExitComplete) {
          const { initial: s, custom: r } = this.node.getProps();
          if (typeof s == "string") {
            const a = tt(this.node, s, r);
            if (a) {
              const { transition: l, transitionEnd: c, ...u } = a;
              for (const f in u) (_a3 = this.node.getValue(f)) == null ? void 0 : _a3.jump(u[f]);
            }
          }
          this.node.animationState.reset(), this.node.animationState.animateChanges();
        } else this.node.animationState.setActive("exit", false);
        this.isExitComplete = false;
        return;
      }
      const o = this.node.animationState.setActive("exit", !t);
      n && !t && o.then(() => {
        this.isExitComplete = true, n(this.id);
      });
    }
    mount() {
      const { register: t, onExitComplete: n } = this.node.presenceContext || {};
      n && n(this.id), t && (this.unmount = t(this.id));
    }
    unmount() {
    }
  }
  const tg = {
    animation: {
      Feature: _p
    },
    exit: {
      Feature: eg
    }
  };
  function Xt(e) {
    return {
      point: {
        x: e.pageX,
        y: e.pageY
      }
    };
  }
  const ng = (e) => (t) => Oo(t) && e(t, Xt(t));
  function Kt(e, t, n, i) {
    return It(e, t, ng(n), i);
  }
  const rc = ({ current: e }) => e ? e.ownerDocument.defaultView : null, Kr = (e, t) => Math.abs(e - t);
  function ig(e, t) {
    const n = Kr(e.x, t.x), i = Kr(e.y, t.y);
    return Math.sqrt(n ** 2 + i ** 2);
  }
  const Wr = /* @__PURE__ */ new Set([
    "auto",
    "scroll"
  ]);
  class ac {
    constructor(t, n, { transformPagePoint: i, contextWindow: o = window, dragSnapToOrigin: s = false, distanceThreshold: r = 3, element: a } = {}) {
      if (this.startEvent = null, this.lastMoveEvent = null, this.lastMoveEventInfo = null, this.lastRawMoveEventInfo = null, this.handlers = {}, this.contextWindow = window, this.scrollPositions = /* @__PURE__ */ new Map(), this.removeScrollListeners = null, this.onElementScroll = (m) => {
        this.handleScroll(m.target);
      }, this.onWindowScroll = () => {
        this.handleScroll(window);
      }, this.updatePoint = () => {
        if (!(this.lastMoveEvent && this.lastMoveEventInfo)) return;
        this.lastRawMoveEventInfo && (this.lastMoveEventInfo = rn(this.lastRawMoveEventInfo, this.transformPagePoint));
        const m = Ri(this.lastMoveEventInfo, this.history), p = this.startEvent !== null, h = ig(m.offset, {
          x: 0,
          y: 0
        }) >= this.distanceThreshold;
        if (!p && !h) return;
        const { point: g } = m, { timestamp: y } = te;
        this.history.push({
          ...g,
          timestamp: y
        });
        const { onStart: x, onMove: w } = this.handlers;
        p || (x && x(this.lastMoveEvent, m), this.startEvent = this.lastMoveEvent), w && w(this.lastMoveEvent, m);
      }, this.handlePointerMove = (m, p) => {
        this.lastMoveEvent = m, this.lastRawMoveEventInfo = p, this.lastMoveEventInfo = rn(p, this.transformPagePoint), M.update(this.updatePoint, true);
      }, this.handlePointerUp = (m, p) => {
        this.end();
        const { onEnd: h, onSessionEnd: g, resumeAnimation: y } = this.handlers;
        if ((this.dragSnapToOrigin || !this.startEvent) && y && y(), !(this.lastMoveEvent && this.lastMoveEventInfo)) return;
        const x = Ri(m.type === "pointercancel" ? this.lastMoveEventInfo : rn(p, this.transformPagePoint), this.history);
        this.startEvent && h && h(m, x), g && g(m, x);
      }, !Oo(t)) return;
      this.dragSnapToOrigin = s, this.handlers = n, this.transformPagePoint = i, this.distanceThreshold = r, this.contextWindow = o || window;
      const l = Xt(t), c = rn(l, this.transformPagePoint), { point: u } = c, { timestamp: f } = te;
      this.history = [
        {
          ...u,
          timestamp: f
        }
      ];
      const { onSessionStart: d } = n;
      d && d(t, Ri(c, this.history)), this.removeListeners = Yt(Kt(this.contextWindow, "pointermove", this.handlePointerMove), Kt(this.contextWindow, "pointerup", this.handlePointerUp), Kt(this.contextWindow, "pointercancel", this.handlePointerUp)), a && this.startScrollTracking(a);
    }
    startScrollTracking(t) {
      let n = t.parentElement;
      for (; n; ) {
        const i = getComputedStyle(n);
        (Wr.has(i.overflowX) || Wr.has(i.overflowY)) && this.scrollPositions.set(n, {
          x: n.scrollLeft,
          y: n.scrollTop
        }), n = n.parentElement;
      }
      this.scrollPositions.set(window, {
        x: window.scrollX,
        y: window.scrollY
      }), window.addEventListener("scroll", this.onElementScroll, {
        capture: true
      }), window.addEventListener("scroll", this.onWindowScroll), this.removeScrollListeners = () => {
        window.removeEventListener("scroll", this.onElementScroll, {
          capture: true
        }), window.removeEventListener("scroll", this.onWindowScroll);
      };
    }
    handleScroll(t) {
      const n = this.scrollPositions.get(t);
      if (!n) return;
      const i = t === window, o = i ? {
        x: window.scrollX,
        y: window.scrollY
      } : {
        x: t.scrollLeft,
        y: t.scrollTop
      }, s = {
        x: o.x - n.x,
        y: o.y - n.y
      };
      s.x === 0 && s.y === 0 || (i ? this.lastMoveEventInfo && (this.lastMoveEventInfo.point.x += s.x, this.lastMoveEventInfo.point.y += s.y) : this.history.length > 0 && (this.history[0].x -= s.x, this.history[0].y -= s.y), this.scrollPositions.set(t, o), M.update(this.updatePoint, true));
    }
    updateHandlers(t) {
      this.handlers = t;
    }
    end() {
      this.removeListeners && this.removeListeners(), this.removeScrollListeners && this.removeScrollListeners(), this.scrollPositions.clear(), Me(this.updatePoint);
    }
  }
  function rn(e, t) {
    return t ? {
      point: t(e.point)
    } : e;
  }
  function zr(e, t) {
    return {
      x: e.x - t.x,
      y: e.y - t.y
    };
  }
  function Ri({ point: e }, t) {
    return {
      point: e,
      delta: zr(e, lc(t)),
      offset: zr(e, og(t)),
      velocity: sg(t, 0.1)
    };
  }
  function og(e) {
    return e[0];
  }
  function lc(e) {
    return e[e.length - 1];
  }
  function sg(e, t) {
    if (e.length < 2) return {
      x: 0,
      y: 0
    };
    let n = e.length - 1, i = null;
    const o = lc(e);
    for (; n >= 0 && (i = e[n], !(o.timestamp - i.timestamp > se(t))); ) n--;
    if (!i) return {
      x: 0,
      y: 0
    };
    i === e[0] && e.length > 2 && o.timestamp - i.timestamp > se(t) * 2 && (i = e[1]);
    const s = ce(o.timestamp - i.timestamp);
    if (s === 0) return {
      x: 0,
      y: 0
    };
    const r = {
      x: (o.x - i.x) / s,
      y: (o.y - i.y) / s
    };
    return r.x === 1 / 0 && (r.x = 0), r.y === 1 / 0 && (r.y = 0), r;
  }
  function rg(e, { min: t, max: n }, i) {
    return t !== void 0 && e < t ? e = i ? J(t, e, i.min) : Math.max(e, t) : n !== void 0 && e > n && (e = i ? J(n, e, i.max) : Math.min(e, n)), e;
  }
  function Hr(e, t, n) {
    return {
      min: t !== void 0 ? e.min + t : void 0,
      max: n !== void 0 ? e.max + n - (e.max - e.min) : void 0
    };
  }
  function ag(e, { top: t, left: n, bottom: i, right: o }) {
    return {
      x: Hr(e.x, n, o),
      y: Hr(e.y, t, i)
    };
  }
  function Mr(e, t) {
    let n = t.min - e.min, i = t.max - e.max;
    return t.max - t.min < e.max - e.min && ([n, i] = [
      i,
      n
    ]), {
      min: n,
      max: i
    };
  }
  function lg(e, t) {
    return {
      x: Mr(e.x, t.x),
      y: Mr(e.y, t.y)
    };
  }
  function cg(e, t) {
    let n = 0.5;
    const i = oe(e), o = oe(t);
    return o > i ? n = Ht(t.min, t.max - i, e.min) : i > o && (n = Ht(e.min, e.max - o, t.min)), be(0, 1, n);
  }
  function ug(e, t) {
    const n = {};
    return t.min !== void 0 && (n.min = t.min - e.min), t.max !== void 0 && (n.max = t.max - e.min), n;
  }
  const co = 0.35;
  function fg(e = co) {
    return e === false ? e = 0 : e === true && (e = co), {
      x: kr(e, "left", "right"),
      y: kr(e, "top", "bottom")
    };
  }
  function kr(e, t, n) {
    return {
      min: Ir(e, t),
      max: Ir(e, n)
    };
  }
  function Ir(e, t) {
    return typeof e == "number" ? e : e[t] || 0;
  }
  const dg = /* @__PURE__ */ new WeakMap();
  class hg {
    constructor(t) {
      this.openDragLock = null, this.isDragging = false, this.currentDirection = null, this.originPoint = {
        x: 0,
        y: 0
      }, this.constraints = false, this.hasMutatedConstraints = false, this.elastic = _(), this.latestPointerEvent = null, this.latestPanInfo = null, this.visualElement = t;
    }
    start(t, { snapToCursor: n = false, distanceThreshold: i } = {}) {
      const { presenceContext: o } = this.visualElement;
      if (o && o.isPresent === false) return;
      const s = (f) => {
        n && this.snapToCursor(Xt(f).point), this.stopAnimation();
      }, r = (f, d) => {
        const { drag: m, dragPropagation: p, onDragStart: h } = this.getProps();
        if (m && !p && (this.openDragLock && this.openDragLock(), this.openDragLock = Dh(m), !this.openDragLock)) return;
        this.latestPointerEvent = f, this.latestPanInfo = d, this.isDragging = true, this.currentDirection = null, this.resolveConstraints(), this.visualElement.projection && (this.visualElement.projection.isAnimationBlocked = true, this.visualElement.projection.target = void 0), qe((y) => {
          let x = this.getAxisMotionValue(y).get() || 0;
          if (Se.test(x)) {
            const { projection: w } = this.visualElement;
            if (w && w.layout) {
              const q = w.layout.layoutBox[y];
              q && (x = oe(q) * (parseFloat(x) / 100));
            }
          }
          this.originPoint[y] = x;
        }), h && M.update(() => h(f, d), false, true), to(this.visualElement, "transform");
        const { animationState: g } = this.visualElement;
        g && g.setActive("whileDrag", true);
      }, a = (f, d) => {
        this.latestPointerEvent = f, this.latestPanInfo = d;
        const { dragPropagation: m, dragDirectionLock: p, onDirectionLock: h, onDrag: g } = this.getProps();
        if (!m && !this.openDragLock) return;
        const { offset: y } = d;
        if (p && this.currentDirection === null) {
          this.currentDirection = pg(y), this.currentDirection !== null && h && h(this.currentDirection);
          return;
        }
        this.updateAxis("x", d.point, y), this.updateAxis("y", d.point, y), this.visualElement.render(), g && M.update(() => g(f, d), false, true);
      }, l = (f, d) => {
        this.latestPointerEvent = f, this.latestPanInfo = d, this.stop(f, d), this.latestPointerEvent = null, this.latestPanInfo = null;
      }, c = () => {
        const { dragSnapToOrigin: f } = this.getProps();
        (f || this.constraints) && this.startAnimation({
          x: 0,
          y: 0
        });
      }, { dragSnapToOrigin: u } = this.getProps();
      this.panSession = new ac(t, {
        onSessionStart: s,
        onStart: r,
        onMove: a,
        onSessionEnd: l,
        resumeAnimation: c
      }, {
        transformPagePoint: this.visualElement.getTransformPagePoint(),
        dragSnapToOrigin: u,
        distanceThreshold: i,
        contextWindow: rc(this.visualElement),
        element: this.visualElement.current
      });
    }
    stop(t, n) {
      const i = t || this.latestPointerEvent, o = n || this.latestPanInfo, s = this.isDragging;
      if (this.cancel(), !s || !o || !i) return;
      const { velocity: r } = o;
      this.startAnimation(r);
      const { onDragEnd: a } = this.getProps();
      a && M.postRender(() => a(i, o));
    }
    cancel() {
      this.isDragging = false;
      const { projection: t, animationState: n } = this.visualElement;
      t && (t.isAnimationBlocked = false), this.endPanSession();
      const { dragPropagation: i } = this.getProps();
      !i && this.openDragLock && (this.openDragLock(), this.openDragLock = null), n && n.setActive("whileDrag", false);
    }
    endPanSession() {
      this.panSession && this.panSession.end(), this.panSession = void 0;
    }
    updateAxis(t, n, i) {
      const { drag: o } = this.getProps();
      if (!i || !an(t, o, this.currentDirection)) return;
      const s = this.getAxisMotionValue(t);
      let r = this.originPoint[t] + i[t];
      this.constraints && this.constraints[t] && (r = rg(r, this.constraints[t], this.elastic[t])), s.set(r);
    }
    resolveConstraints() {
      var _a3;
      const { dragConstraints: t, dragElastic: n } = this.getProps(), i = this.visualElement.projection && !this.visualElement.projection.layout ? this.visualElement.projection.measure(false) : (_a3 = this.visualElement.projection) == null ? void 0 : _a3.layout, o = this.constraints;
      t && lt(t) ? this.constraints || (this.constraints = this.resolveRefConstraints()) : t && i ? this.constraints = ag(i.layoutBox, t) : this.constraints = false, this.elastic = fg(n), o !== this.constraints && !lt(t) && i && this.constraints && !this.hasMutatedConstraints && qe((s) => {
        this.constraints !== false && this.getAxisMotionValue(s) && (this.constraints[s] = ug(i.layoutBox[s], this.constraints[s]));
      });
    }
    resolveRefConstraints() {
      const { dragConstraints: t, onMeasureDragConstraints: n } = this.getProps();
      if (!t || !lt(t)) return false;
      const i = t.current;
      Ce(i !== null, "If `dragConstraints` is set as a React ref, that ref must be passed to another component's `ref` prop.", "drag-constraints-ref");
      const { projection: o } = this.visualElement;
      if (!o || !o.layout) return false;
      const s = cm(i, o.root, this.visualElement.getTransformPagePoint());
      let r = lg(o.layout.layoutBox, s);
      if (n) {
        const a = n(rm(r));
        this.hasMutatedConstraints = !!a, a && (r = bl(a));
      }
      return r;
    }
    startAnimation(t) {
      const { drag: n, dragMomentum: i, dragElastic: o, dragTransition: s, dragSnapToOrigin: r, onDragTransitionEnd: a } = this.getProps(), l = this.constraints || {}, c = qe((u) => {
        if (!an(u, n, this.currentDirection)) return;
        let f = l && l[u] || {};
        (r === true || r === u) && (f = {
          min: 0,
          max: 0
        });
        const d = o ? 200 : 1e6, m = o ? 40 : 1e7, p = {
          type: "inertia",
          velocity: i ? t[u] : 0,
          bounceStiffness: d,
          bounceDamping: m,
          timeConstant: 750,
          restDelta: 1,
          restSpeed: 10,
          ...s,
          ...f
        };
        return this.startAxisValueAnimation(u, p);
      });
      return Promise.all(c).then(a);
    }
    startAxisValueAnimation(t, n) {
      const i = this.getAxisMotionValue(t);
      return to(this.visualElement, t), i.start(Mo(t, i, 0, n, this.visualElement, false));
    }
    stopAnimation() {
      qe((t) => this.getAxisMotionValue(t).stop());
    }
    getAxisMotionValue(t) {
      const n = `_drag${t.toUpperCase()}`, i = this.visualElement.getProps(), o = i[n];
      return o || this.visualElement.getValue(t, (i.initial ? i.initial[t] : void 0) || 0);
    }
    snapToCursor(t) {
      qe((n) => {
        const { drag: i } = this.getProps();
        if (!an(n, i, this.currentDirection)) return;
        const { projection: o } = this.visualElement, s = this.getAxisMotionValue(n);
        if (o && o.layout) {
          const { min: r, max: a } = o.layout.layoutBox[n], l = s.get() || 0;
          s.set(t[n] - J(r, a, 0.5) + l);
        }
      });
    }
    scalePositionWithinConstraints() {
      if (!this.visualElement.current) return;
      const { drag: t, dragConstraints: n } = this.getProps(), { projection: i } = this.visualElement;
      if (!lt(n) || !i || !this.constraints) return;
      this.stopAnimation();
      const o = {
        x: 0,
        y: 0
      };
      qe((r) => {
        const a = this.getAxisMotionValue(r);
        if (a && this.constraints !== false) {
          const l = a.get();
          o[r] = cg({
            min: l,
            max: l
          }, this.constraints[r]);
        }
      });
      const { transformTemplate: s } = this.visualElement.getProps();
      this.visualElement.current.style.transform = s ? s({}, "") : "none", i.root && i.root.updateScroll(), i.updateLayout(), this.constraints = false, this.resolveConstraints(), qe((r) => {
        if (!an(r, t, null)) return;
        const a = this.getAxisMotionValue(r), { min: l, max: c } = this.constraints[r];
        a.set(J(l, c, o[r]));
      }), this.visualElement.render();
    }
    addListeners() {
      if (!this.visualElement.current) return;
      dg.set(this.visualElement, this);
      const t = this.visualElement.current, n = Kt(t, "pointerdown", (c) => {
        const { drag: u, dragListener: f = true } = this.getProps(), d = c.target, m = d !== t && kh(d);
        u && f && !m && this.start(c);
      });
      let i;
      const o = () => {
        const { dragConstraints: c } = this.getProps();
        lt(c) && c.current && (this.constraints = this.resolveRefConstraints(), i || (i = mg(t, c.current, () => this.scalePositionWithinConstraints())));
      }, { projection: s } = this.visualElement, r = s.addEventListener("measure", o);
      s && !s.layout && (s.root && s.root.updateScroll(), s.updateLayout()), M.read(o);
      const a = It(window, "resize", () => this.scalePositionWithinConstraints()), l = s.addEventListener("didUpdate", (({ delta: c, hasLayoutChanged: u }) => {
        this.isDragging && u && (qe((f) => {
          const d = this.getAxisMotionValue(f);
          d && (this.originPoint[f] += c[f].translate, d.set(d.get() + c[f].translate));
        }), this.visualElement.render());
      }));
      return () => {
        a(), n(), r(), l && l(), i && i();
      };
    }
    getProps() {
      const t = this.visualElement.getProps(), { drag: n = false, dragDirectionLock: i = false, dragPropagation: o = false, dragConstraints: s = false, dragElastic: r = co, dragMomentum: a = true } = t;
      return {
        ...t,
        drag: n,
        dragDirectionLock: i,
        dragPropagation: o,
        dragConstraints: s,
        dragElastic: r,
        dragMomentum: a
      };
    }
  }
  function Zr(e) {
    let t = true;
    return () => {
      if (t) {
        t = false;
        return;
      }
      e();
    };
  }
  function mg(e, t, n) {
    const i = Xs(e, Zr(n)), o = Xs(t, Zr(n));
    return () => {
      i(), o();
    };
  }
  function an(e, t, n) {
    return (t === true || t === e) && (n === null || n === e);
  }
  function pg(e, t = 10) {
    let n = null;
    return Math.abs(e.y) > t ? n = "y" : Math.abs(e.x) > t && (n = "x"), n;
  }
  class gg extends Je {
    constructor(t) {
      super(t), this.removeGroupControls = ue, this.removeListeners = ue, this.controls = new hg(t);
    }
    mount() {
      const { dragControls: t } = this.node.getProps();
      t && (this.removeGroupControls = t.subscribe(this.controls)), this.removeListeners = this.controls.addListeners() || ue;
    }
    update() {
      const { dragControls: t } = this.node.getProps(), { dragControls: n } = this.node.prevProps || {};
      t !== n && (this.removeGroupControls(), t && (this.removeGroupControls = t.subscribe(this.controls)));
    }
    unmount() {
      this.removeGroupControls(), this.removeListeners(), this.controls.isDragging || this.controls.endPanSession();
    }
  }
  const vi = (e) => (t, n) => {
    e && M.update(() => e(t, n), false, true);
  };
  class Ag extends Je {
    constructor() {
      super(...arguments), this.removePointerDownListener = ue;
    }
    onPointerDown(t) {
      this.session = new ac(t, this.createPanHandlers(), {
        transformPagePoint: this.node.getTransformPagePoint(),
        contextWindow: rc(this.node)
      });
    }
    createPanHandlers() {
      const { onPanSessionStart: t, onPanStart: n, onPan: i, onPanEnd: o } = this.node.getProps();
      return {
        onSessionStart: vi(t),
        onStart: vi(n),
        onMove: vi(i),
        onEnd: (s, r) => {
          delete this.session, o && M.postRender(() => o(s, r));
        }
      };
    }
    mount() {
      this.removePointerDownListener = Kt(this.node.current, "pointerdown", (t) => this.onPointerDown(t));
    }
    update() {
      this.session && this.session.updateHandlers(this.createPanHandlers());
    }
    unmount() {
      this.removePointerDownListener(), this.session && this.session.end();
    }
  }
  let Vi = false;
  class yg extends pf {
    componentDidMount() {
      const { visualElement: t, layoutGroup: n, switchLayoutGroup: i, layoutId: o } = this.props, { projection: s } = t;
      s && (n.group && n.group.add(s), i && i.register && o && i.register(s), Vi && s.root.didUpdate(), s.addEventListener("animationComplete", () => {
        this.safeToRemove();
      }), s.setOptions({
        ...s.options,
        layoutDependency: this.props.layoutDependency,
        onExitComplete: () => this.safeToRemove()
      })), qn.hasEverUpdated = true;
    }
    getSnapshotBeforeUpdate(t) {
      const { layoutDependency: n, visualElement: i, drag: o, isPresent: s } = this.props, { projection: r } = i;
      return r && (r.isPresent = s, t.layoutDependency !== n && r.setOptions({
        ...r.options,
        layoutDependency: n
      }), Vi = true, o || t.layoutDependency !== n || n === void 0 || t.isPresent !== s ? r.willUpdate() : this.safeToRemove(), t.isPresent !== s && (s ? r.promote() : r.relegate() || M.postRender(() => {
        const a = r.getStack();
        (!a || !a.members.length) && this.safeToRemove();
      }))), null;
    }
    componentDidUpdate() {
      const { visualElement: t, layoutAnchor: n } = this.props, { projection: i } = t;
      i && (i.options.layoutAnchor = n, i.root.didUpdate(), Jo.postRender(() => {
        !i.currentAnimation && i.isLead() && this.safeToRemove();
      }));
    }
    componentWillUnmount() {
      const { visualElement: t, layoutGroup: n, switchLayoutGroup: i } = this.props, { projection: o } = t;
      Vi = true, o && (o.scheduleCheckAfterUnmount(), n && n.group && n.group.remove(o), i && i.deregister && i.deregister(o));
    }
    safeToRemove() {
      const { safeToRemove: t } = this.props;
      t && t();
    }
    render() {
      return null;
    }
  }
  function cc(e) {
    const [t, n] = Ql(), i = G(zt);
    return P(yg, {
      ...e,
      layoutGroup: i,
      switchLayoutGroup: G(oc),
      isPresent: t,
      safeToRemove: n
    });
  }
  const xg = {
    pan: {
      Feature: Ag
    },
    drag: {
      Feature: gg,
      ProjectionNode: Xl,
      MeasureLayout: cc
    }
  };
  function Jr(e, t, n) {
    const { props: i } = e;
    e.animationState && i.whileHover && e.animationState.setActive("whileHover", n === "Start");
    const o = "onHover" + n, s = i[o];
    s && M.postRender(() => s(t, Xt(t)));
  }
  class Pg extends Je {
    mount() {
      const { current: t } = this.node;
      t && (this.unmount = Wh(t, (n, i) => (Jr(this.node, i, "Start"), (o) => Jr(this.node, o, "End"))));
    }
    unmount() {
    }
  }
  class wg extends Je {
    constructor() {
      super(...arguments), this.isActive = false;
    }
    onFocus() {
      let t = false;
      try {
        t = this.node.current.matches(":focus-visible");
      } catch {
        t = true;
      }
      !t || !this.node.animationState || (this.node.animationState.setActive("whileFocus", true), this.isActive = true);
    }
    onBlur() {
      !this.isActive || !this.node.animationState || (this.node.animationState.setActive("whileFocus", false), this.isActive = false);
    }
    mount() {
      this.unmount = Yt(It(this.node.current, "focus", () => this.onFocus()), It(this.node.current, "blur", () => this.onBlur()));
    }
    unmount() {
    }
  }
  function Or(e, t, n) {
    const { props: i } = e;
    if (e.current instanceof HTMLButtonElement && e.current.disabled) return;
    e.animationState && i.whileTap && e.animationState.setActive("whileTap", n === "Start");
    const o = "onTap" + (n === "End" ? "" : n), s = i[o];
    s && M.postRender(() => s(t, Xt(t)));
  }
  class qg extends Je {
    mount() {
      const { current: t } = this.node;
      if (!t) return;
      const { globalTapTarget: n, propagate: i } = this.node.props;
      this.unmount = Zh(t, (o, s) => (Or(this.node, s, "Start"), (r, { success: a }) => Or(this.node, r, a ? "End" : "Cancel")), {
        useGlobalTarget: n,
        stopPropagation: (i == null ? void 0 : i.tap) === false
      });
    }
    unmount() {
    }
  }
  const uo = /* @__PURE__ */ new WeakMap(), Si = /* @__PURE__ */ new WeakMap(), Rg = (e) => {
    const t = uo.get(e.target);
    t && t(e);
  }, vg = (e) => {
    e.forEach(Rg);
  };
  function Vg({ root: e, ...t }) {
    const n = e || document;
    Si.has(n) || Si.set(n, {});
    const i = Si.get(n), o = JSON.stringify(t);
    return i[o] || (i[o] = new IntersectionObserver(vg, {
      root: e,
      ...t
    })), i[o];
  }
  function Sg(e, t, n) {
    const i = Vg(t);
    return uo.set(e, n), i.observe(e), () => {
      uo.delete(e), i.unobserve(e);
    };
  }
  const Tg = {
    some: 0,
    all: 1
  };
  class bg extends Je {
    constructor() {
      super(...arguments), this.hasEnteredView = false, this.isInView = false;
    }
    startObserver() {
      var _a3;
      (_a3 = this.stopObserver) == null ? void 0 : _a3.call(this);
      const { viewport: t = {} } = this.node.getProps(), { root: n, margin: i, amount: o = "some", once: s } = t, r = {
        root: n ? n.current : void 0,
        rootMargin: i,
        threshold: typeof o == "number" ? o : Tg[o]
      }, a = (l) => {
        const { isIntersecting: c } = l;
        if (this.isInView === c || (this.isInView = c, s && !c && this.hasEnteredView)) return;
        c && (this.hasEnteredView = true), this.node.animationState && this.node.animationState.setActive("whileInView", c);
        const { onViewportEnter: u, onViewportLeave: f } = this.node.getProps(), d = c ? u : f;
        d && d(l);
      };
      this.stopObserver = Sg(this.node.current, r, a);
    }
    mount() {
      this.startObserver();
    }
    update() {
      if (typeof IntersectionObserver > "u") return;
      const { props: t, prevProps: n } = this.node;
      [
        "amount",
        "margin",
        "root"
      ].some(Fg(t, n)) && this.startObserver();
    }
    unmount() {
      var _a3;
      (_a3 = this.stopObserver) == null ? void 0 : _a3.call(this), this.hasEnteredView = false, this.isInView = false;
    }
  }
  function Fg({ viewport: e = {} }, { viewport: t = {} } = {}) {
    return (n) => e[n] !== t[n];
  }
  const Eg = {
    inView: {
      Feature: bg
    },
    tap: {
      Feature: qg
    },
    focus: {
      Feature: wg
    },
    hover: {
      Feature: Pg
    }
  }, Cg = {
    layout: {
      ProjectionNode: Xl,
      MeasureLayout: cc
    }
  }, Ug = {
    ...tg,
    ...Eg,
    ...xg,
    ...Cg
  }, me = Qp(Ug, Gp);
  function ts() {
    !Xo.current && Vl();
    const [e] = ge(Cn.current);
    return process.env.NODE_ENV !== "production" && On(e !== true, "You have Reduced Motion enabled on your device. Animations may not appear as expected.", "reduced-motion-disabled"), e;
  }
  const uc = Ne(null);
  DP = function() {
    return G(uc);
  };
  fo = function({ children: e, id: t, defaultGlassProps: n, animate: i = true, style: o, className: s }) {
    const r = Pt(), a = t ?? r, l = pe(() => ({
      containerId: a,
      defaultProps: n
    }), [
      a,
      n
    ]), c = i ? P(Vt, {
      children: e
    }) : e;
    return P(uc.Provider, {
      value: l,
      children: P("div", {
        style: o,
        className: s,
        children: c
      })
    });
  };
  function k(e, t, { checkForDefaultPrevented: n = true } = {}) {
    return function(o) {
      if (e == null ? void 0 : e(o), n === false || !o.defaultPrevented) return t == null ? void 0 : t(o);
    };
  }
  function Yr(e, t) {
    if (typeof e == "function") return e(t);
    e != null && (e.current = t);
  }
  function fc(...e) {
    return (t) => {
      let n = false;
      const i = e.map((o) => {
        const s = Yr(o, t);
        return !n && typeof s == "function" && (n = true), s;
      });
      if (n) return () => {
        for (let o = 0; o < i.length; o++) {
          const s = i[o];
          typeof s == "function" ? s() : Yr(e[o], null);
        }
      };
    };
  }
  function B(...e) {
    return A.useCallback(fc(...e), e);
  }
  function Lg(e, t) {
    const n = A.createContext(t), i = (s) => {
      const { children: r, ...a } = s, l = A.useMemo(() => a, Object.values(a));
      return P(n.Provider, {
        value: l,
        children: r
      });
    };
    i.displayName = e + "Provider";
    function o(s) {
      const r = A.useContext(n);
      if (r) return r;
      if (t !== void 0) return t;
      throw new Error(`\`${s}\` must be used within \`${e}\``);
    }
    return [
      i,
      o
    ];
  }
  function Oe(e, t = []) {
    let n = [];
    function i(s, r) {
      const a = A.createContext(r), l = n.length;
      n = [
        ...n,
        r
      ];
      const c = (f) => {
        var _a3;
        const { scope: d, children: m, ...p } = f, h = ((_a3 = d == null ? void 0 : d[e]) == null ? void 0 : _a3[l]) || a, g = A.useMemo(() => p, Object.values(p));
        return P(h.Provider, {
          value: g,
          children: m
        });
      };
      c.displayName = s + "Provider";
      function u(f, d) {
        var _a3;
        const m = ((_a3 = d == null ? void 0 : d[e]) == null ? void 0 : _a3[l]) || a, p = A.useContext(m);
        if (p) return p;
        if (r !== void 0) return r;
        throw new Error(`\`${f}\` must be used within \`${s}\``);
      }
      return [
        c,
        u
      ];
    }
    const o = () => {
      const s = n.map((r) => A.createContext(r));
      return function(a) {
        const l = (a == null ? void 0 : a[e]) || s;
        return A.useMemo(() => ({
          [`__scope${e}`]: {
            ...a,
            [e]: l
          }
        }), [
          a,
          l
        ]);
      };
    };
    return o.scopeName = e, [
      i,
      Ng(o, ...t)
    ];
  }
  function Ng(...e) {
    const t = e[0];
    if (e.length === 1) return t;
    const n = () => {
      const i = e.map((o) => ({
        useScope: o(),
        scopeName: o.scopeName
      }));
      return function(s) {
        const r = i.reduce((a, { useScope: l, scopeName: c }) => {
          const f = l(s)[`__scope${c}`];
          return {
            ...a,
            ...f
          };
        }, {});
        return A.useMemo(() => ({
          [`__scope${t.scopeName}`]: r
        }), [
          r
        ]);
      };
    };
    return n.scopeName = t.scopeName, n;
  }
  var ke = (globalThis == null ? void 0 : globalThis.document) ? A.useLayoutEffect : () => {
  }, Dg = A[" useInsertionEffect ".trim().toString()] || ke;
  function Ye({ prop: e, defaultProp: t, onChange: n = () => {
  }, caller: i }) {
    const [o, s, r] = Kg({
      defaultProp: t,
      onChange: n
    }), a = e !== void 0, l = a ? e : o;
    {
      const u = A.useRef(e !== void 0);
      A.useEffect(() => {
        const f = u.current;
        f !== a && console.warn(`${i} is changing from ${f ? "controlled" : "uncontrolled"} to ${a ? "controlled" : "uncontrolled"}. Components should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled value for the lifetime of the component.`), u.current = a;
      }, [
        a,
        i
      ]);
    }
    const c = A.useCallback((u) => {
      var _a3;
      if (a) {
        const f = Wg(u) ? u(e) : u;
        f !== e && ((_a3 = r.current) == null ? void 0 : _a3.call(r, f));
      } else s(u);
    }, [
      a,
      e,
      s,
      r
    ]);
    return [
      l,
      c
    ];
  }
  function Kg({ defaultProp: e, onChange: t }) {
    const [n, i] = A.useState(e), o = A.useRef(n), s = A.useRef(t);
    return Dg(() => {
      s.current = t;
    }, [
      t
    ]), A.useEffect(() => {
      var _a3;
      o.current !== n && ((_a3 = s.current) == null ? void 0 : _a3.call(s, n), o.current = n);
    }, [
      n,
      o
    ]), [
      n,
      i,
      s
    ];
  }
  function Wg(e) {
    return typeof e == "function";
  }
  function dc(e) {
    const t = A.useRef({
      value: e,
      previous: e
    });
    return A.useMemo(() => (t.current.value !== e && (t.current.previous = t.current.value, t.current.value = e), t.current.previous), [
      e
    ]);
  }
  function ns(e) {
    const [t, n] = A.useState(void 0);
    return ke(() => {
      if (e) {
        n({
          width: e.offsetWidth,
          height: e.offsetHeight
        });
        const i = new ResizeObserver((o) => {
          if (!Array.isArray(o) || !o.length) return;
          const s = o[0];
          let r, a;
          if ("borderBoxSize" in s) {
            const l = s.borderBoxSize, c = Array.isArray(l) ? l[0] : l;
            r = c.inlineSize, a = c.blockSize;
          } else r = e.offsetWidth, a = e.offsetHeight;
          n({
            width: r,
            height: a
          });
        });
        return i.observe(e, {
          box: "border-box"
        }), () => i.unobserve(e);
      } else n(void 0);
    }, [
      e
    ]), t;
  }
  function Zt(e) {
    const t = zg(e), n = A.forwardRef((i, o) => {
      const { children: s, ...r } = i, a = A.Children.toArray(s), l = a.find(Mg);
      if (l) {
        const c = l.props.children, u = a.map((f) => f === l ? A.Children.count(c) > 1 ? A.Children.only(null) : A.isValidElement(c) ? c.props.children : null : f);
        return P(t, {
          ...r,
          ref: o,
          children: A.isValidElement(c) ? A.cloneElement(c, void 0, u) : null
        });
      }
      return P(t, {
        ...r,
        ref: o,
        children: s
      });
    });
    return n.displayName = `${e}.Slot`, n;
  }
  function zg(e) {
    const t = A.forwardRef((n, i) => {
      const { children: o, ...s } = n;
      if (A.isValidElement(o)) {
        const r = Ig(o), a = kg(s, o.props);
        return o.type !== A.Fragment && (a.ref = i ? fc(i, r) : r), A.cloneElement(o, a);
      }
      return A.Children.count(o) > 1 ? A.Children.only(null) : null;
    });
    return t.displayName = `${e}.SlotClone`, t;
  }
  var Hg = Symbol("radix.slottable");
  function Mg(e) {
    return A.isValidElement(e) && typeof e.type == "function" && "__radixId" in e.type && e.type.__radixId === Hg;
  }
  function kg(e, t) {
    const n = {
      ...t
    };
    for (const i in t) {
      const o = e[i], s = t[i];
      /^on[A-Z]/.test(i) ? o && s ? n[i] = (...a) => {
        const l = s(...a);
        return o(...a), l;
      } : o && (n[i] = o) : i === "style" ? n[i] = {
        ...o,
        ...s
      } : i === "className" && (n[i] = [
        o,
        s
      ].filter(Boolean).join(" "));
    }
    return {
      ...e,
      ...n
    };
  }
  function Ig(e) {
    var _a3, _b;
    let t = (_a3 = Object.getOwnPropertyDescriptor(e.props, "ref")) == null ? void 0 : _a3.get, n = t && "isReactWarning" in t && t.isReactWarning;
    return n ? e.ref : (t = (_b = Object.getOwnPropertyDescriptor(e, "ref")) == null ? void 0 : _b.get, n = t && "isReactWarning" in t && t.isReactWarning, n ? e.props.ref : e.props.ref || e.ref);
  }
  var Zg = [
    "a",
    "button",
    "div",
    "form",
    "h2",
    "h3",
    "img",
    "input",
    "label",
    "li",
    "nav",
    "ol",
    "p",
    "select",
    "span",
    "svg",
    "ul"
  ], Z = Zg.reduce((e, t) => {
    const n = Zt(`Primitive.${t}`), i = A.forwardRef((o, s) => {
      const { asChild: r, ...a } = o, l = r ? n : t;
      return typeof window < "u" && (window[Symbol.for("radix-ui")] = true), P(l, {
        ...a,
        ref: s
      });
    });
    return i.displayName = `Primitive.${t}`, {
      ...e,
      [t]: i
    };
  }, {});
  function Jg(e, t) {
    e && qa.flushSync(() => e.dispatchEvent(t));
  }
  var Qn = "Switch", [Og] = Oe(Qn), [Yg, Bg] = Og(Qn), hc = A.forwardRef((e, t) => {
    const { __scopeSwitch: n, name: i, checked: o, defaultChecked: s, required: r, disabled: a, value: l = "on", onCheckedChange: c, form: u, ...f } = e, [d, m] = A.useState(null), p = B(t, (w) => m(w)), h = A.useRef(false), g = d ? u || !!d.closest("form") : true, [y, x] = Ye({
      prop: o,
      defaultProp: s ?? false,
      onChange: c,
      caller: Qn
    });
    return j(Yg, {
      scope: n,
      checked: y,
      disabled: a,
      children: [
        P(Z.button, {
          type: "button",
          role: "switch",
          "aria-checked": y,
          "aria-required": r,
          "data-state": Ac(y),
          "data-disabled": a ? "" : void 0,
          disabled: a,
          value: l,
          ...f,
          ref: p,
          onClick: k(e.onClick, (w) => {
            x((q) => !q), g && (h.current = w.isPropagationStopped(), h.current || w.stopPropagation());
          })
        }),
        g && P(gc, {
          control: d,
          bubbles: !h.current,
          name: i,
          value: l,
          checked: y,
          required: r,
          disabled: a,
          form: u,
          style: {
            transform: "translateX(-100%)"
          }
        })
      ]
    });
  });
  hc.displayName = Qn;
  var mc = "SwitchThumb", pc = A.forwardRef((e, t) => {
    const { __scopeSwitch: n, ...i } = e, o = Bg(mc, n);
    return P(Z.span, {
      "data-state": Ac(o.checked),
      "data-disabled": o.disabled ? "" : void 0,
      ...i,
      ref: t
    });
  });
  pc.displayName = mc;
  var jg = "SwitchBubbleInput", gc = A.forwardRef(({ __scopeSwitch: e, control: t, checked: n, bubbles: i = true, ...o }, s) => {
    const r = A.useRef(null), a = B(r, s), l = dc(n), c = ns(t);
    return A.useEffect(() => {
      const u = r.current;
      if (!u) return;
      const f = window.HTMLInputElement.prototype, m = Object.getOwnPropertyDescriptor(f, "checked").set;
      if (l !== n && m) {
        const p = new Event("click", {
          bubbles: i
        });
        m.call(u, n), u.dispatchEvent(p);
      }
    }, [
      l,
      n,
      i
    ]), P("input", {
      type: "checkbox",
      "aria-hidden": true,
      defaultChecked: n,
      ...o,
      tabIndex: -1,
      ref: a,
      style: {
        ...o.style,
        ...c,
        position: "absolute",
        pointerEvents: "none",
        opacity: 0,
        margin: 0
      }
    });
  });
  gc.displayName = jg;
  function Ac(e) {
    return e ? "checked" : "unchecked";
  }
  var Xg = hc, Qg = pc;
  const { toggleWidth: Br, toggleHeight: Gg, toggleThumbSize: Ti, toggleThumbInset: bi, minTapTarget: jr } = ee, _g = {
    type: "spring",
    stiffness: 500,
    damping: 30,
    mass: 0.8
  }, $g = {
    type: "tween",
    duration: 0
  };
  KP = function({ checked: e, onCheckedChange: t, label: n, disabled: i = false, className: o, style: s }) {
    const a = ts() ? $g : _g, l = e ? Br - Ti - bi : bi;
    return P(Xg, {
      checked: e,
      onCheckedChange: t,
      disabled: i,
      "aria-label": n,
      className: o,
      style: {
        all: "unset",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: jr,
        minWidth: jr,
        cursor: i ? "default" : "pointer",
        ...s
      },
      children: P("div", {
        style: {
          width: Br,
          height: Gg,
          position: "relative",
          flexShrink: 0,
          borderRadius: I.pill,
          background: e ? "rgba(52, 199, 89, 0.65)" : "rgba(120, 120, 130, 0.4)",
          border: "1px solid rgba(255,255,255,0.15)",
          transition: "background 0.2s"
        },
        "data-testid": "toggle-active-overlay",
        children: P(Qg, {
          asChild: true,
          children: P(me.div, {
            layout: true,
            transition: a,
            style: {
              position: "absolute",
              top: bi,
              left: l,
              width: Ti,
              height: Ti,
              borderRadius: I.pill,
              background: "rgba(255, 255, 255, 0.95)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)"
            }
          })
        })
      })
    });
  };
  function yc(e, [t, n]) {
    return Math.min(n, Math.max(t, e));
  }
  var eA = A.createContext(void 0);
  function is(e) {
    const t = A.useContext(eA);
    return e || t || "ltr";
  }
  function xc(e) {
    const t = e + "CollectionProvider", [n, i] = Oe(t), [o, s] = n(t, {
      collectionRef: {
        current: null
      },
      itemMap: /* @__PURE__ */ new Map()
    }), r = (h) => {
      const { scope: g, children: y } = h, x = $.useRef(null), w = $.useRef(/* @__PURE__ */ new Map()).current;
      return P(o, {
        scope: g,
        itemMap: w,
        collectionRef: x,
        children: y
      });
    };
    r.displayName = t;
    const a = e + "CollectionSlot", l = Zt(a), c = $.forwardRef((h, g) => {
      const { scope: y, children: x } = h, w = s(a, y), q = B(g, w.collectionRef);
      return P(l, {
        ref: q,
        children: x
      });
    });
    c.displayName = a;
    const u = e + "CollectionItemSlot", f = "data-radix-collection-item", d = Zt(u), m = $.forwardRef((h, g) => {
      const { scope: y, children: x, ...w } = h, q = $.useRef(null), R = B(g, q), v = s(u, y);
      return $.useEffect(() => (v.itemMap.set(q, {
        ref: q,
        ...w
      }), () => void v.itemMap.delete(q))), P(d, {
        [f]: "",
        ref: R,
        children: x
      });
    });
    m.displayName = u;
    function p(h) {
      const g = s(e + "CollectionConsumer", h);
      return $.useCallback(() => {
        const x = g.collectionRef.current;
        if (!x) return [];
        const w = Array.from(x.querySelectorAll(`[${f}]`));
        return Array.from(g.itemMap.values()).sort((v, S) => w.indexOf(v.ref.current) - w.indexOf(S.ref.current));
      }, [
        g.collectionRef,
        g.itemMap
      ]);
    }
    return [
      {
        Provider: r,
        Slot: c,
        ItemSlot: m
      },
      p,
      i
    ];
  }
  var Pc = [
    "PageUp",
    "PageDown"
  ], wc = [
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight"
  ], qc = {
    "from-left": [
      "Home",
      "PageDown",
      "ArrowDown",
      "ArrowLeft"
    ],
    "from-right": [
      "Home",
      "PageDown",
      "ArrowDown",
      "ArrowRight"
    ],
    "from-bottom": [
      "Home",
      "PageDown",
      "ArrowDown",
      "ArrowLeft"
    ],
    "from-top": [
      "Home",
      "PageDown",
      "ArrowUp",
      "ArrowLeft"
    ]
  }, St = "Slider", [ho, tA, nA] = xc(St), [Rc] = Oe(St, [
    nA
  ]), [iA, Gn] = Rc(St), vc = A.forwardRef((e, t) => {
    const { name: n, min: i = 0, max: o = 100, step: s = 1, orientation: r = "horizontal", disabled: a = false, minStepsBetweenThumbs: l = 0, defaultValue: c = [
      i
    ], value: u, onValueChange: f = () => {
    }, onValueCommit: d = () => {
    }, inverted: m = false, form: p, ...h } = e, g = A.useRef(/* @__PURE__ */ new Set()), y = A.useRef(0), w = r === "horizontal" ? oA : sA, [q = [], R] = Ye({
      prop: u,
      defaultProp: c,
      onChange: (F) => {
        var _a3;
        (_a3 = [
          ...g.current
        ][y.current]) == null ? void 0 : _a3.focus(), f(F);
      }
    }), v = A.useRef(q);
    function S(F) {
      const U = uA(q, F);
      T(F, U);
    }
    function V(F) {
      T(F, y.current);
    }
    function b() {
      const F = v.current[y.current];
      q[y.current] !== F && d(q);
    }
    function T(F, U, { commit: D } = {
      commit: false
    }) {
      const K = mA(s), z = pA(Math.round((F - i) / s) * s + i, K), N = yc(z, [
        i,
        o
      ]);
      R((H = []) => {
        const C = lA(H, N, U);
        if (hA(C, l * s)) {
          y.current = C.indexOf(N);
          const W = String(C) !== String(H);
          return W && D && d(C), W ? C : H;
        } else return H;
      });
    }
    return P(iA, {
      scope: e.__scopeSlider,
      name: n,
      disabled: a,
      min: i,
      max: o,
      valueIndexToChangeRef: y,
      thumbs: g.current,
      values: q,
      orientation: r,
      form: p,
      children: P(ho.Provider, {
        scope: e.__scopeSlider,
        children: P(ho.Slot, {
          scope: e.__scopeSlider,
          children: P(w, {
            "aria-disabled": a,
            "data-disabled": a ? "" : void 0,
            ...h,
            ref: t,
            onPointerDown: k(h.onPointerDown, () => {
              a || (v.current = q);
            }),
            min: i,
            max: o,
            inverted: m,
            onSlideStart: a ? void 0 : S,
            onSlideMove: a ? void 0 : V,
            onSlideEnd: a ? void 0 : b,
            onHomeKeyDown: () => !a && T(i, 0, {
              commit: true
            }),
            onEndKeyDown: () => !a && T(o, q.length - 1, {
              commit: true
            }),
            onStepKeyDown: ({ event: F, direction: U }) => {
              if (!a) {
                const z = Pc.includes(F.key) || F.shiftKey && wc.includes(F.key) ? 10 : 1, N = y.current, H = q[N], C = s * z * U;
                T(H + C, N, {
                  commit: true
                });
              }
            }
          })
        })
      })
    });
  });
  vc.displayName = St;
  var [Vc, Sc] = Rc(St, {
    startEdge: "left",
    endEdge: "right",
    size: "width",
    direction: 1
  }), oA = A.forwardRef((e, t) => {
    const { min: n, max: i, dir: o, inverted: s, onSlideStart: r, onSlideMove: a, onSlideEnd: l, onStepKeyDown: c, ...u } = e, [f, d] = A.useState(null), m = B(t, (w) => d(w)), p = A.useRef(void 0), h = is(o), g = h === "ltr", y = g && !s || !g && s;
    function x(w) {
      const q = p.current || f.getBoundingClientRect(), R = [
        0,
        q.width
      ], S = os(R, y ? [
        n,
        i
      ] : [
        i,
        n
      ]);
      return p.current = q, S(w - q.left);
    }
    return P(Vc, {
      scope: e.__scopeSlider,
      startEdge: y ? "left" : "right",
      endEdge: y ? "right" : "left",
      direction: y ? 1 : -1,
      size: "width",
      children: P(Tc, {
        dir: h,
        "data-orientation": "horizontal",
        ...u,
        ref: m,
        style: {
          ...u.style,
          "--radix-slider-thumb-transform": "translateX(-50%)"
        },
        onSlideStart: (w) => {
          const q = x(w.clientX);
          r == null ? void 0 : r(q);
        },
        onSlideMove: (w) => {
          const q = x(w.clientX);
          a == null ? void 0 : a(q);
        },
        onSlideEnd: () => {
          p.current = void 0, l == null ? void 0 : l();
        },
        onStepKeyDown: (w) => {
          const R = qc[y ? "from-left" : "from-right"].includes(w.key);
          c == null ? void 0 : c({
            event: w,
            direction: R ? -1 : 1
          });
        }
      })
    });
  }), sA = A.forwardRef((e, t) => {
    const { min: n, max: i, inverted: o, onSlideStart: s, onSlideMove: r, onSlideEnd: a, onStepKeyDown: l, ...c } = e, u = A.useRef(null), f = B(t, u), d = A.useRef(void 0), m = !o;
    function p(h) {
      const g = d.current || u.current.getBoundingClientRect(), y = [
        0,
        g.height
      ], w = os(y, m ? [
        i,
        n
      ] : [
        n,
        i
      ]);
      return d.current = g, w(h - g.top);
    }
    return P(Vc, {
      scope: e.__scopeSlider,
      startEdge: m ? "bottom" : "top",
      endEdge: m ? "top" : "bottom",
      size: "height",
      direction: m ? 1 : -1,
      children: P(Tc, {
        "data-orientation": "vertical",
        ...c,
        ref: f,
        style: {
          ...c.style,
          "--radix-slider-thumb-transform": "translateY(50%)"
        },
        onSlideStart: (h) => {
          const g = p(h.clientY);
          s == null ? void 0 : s(g);
        },
        onSlideMove: (h) => {
          const g = p(h.clientY);
          r == null ? void 0 : r(g);
        },
        onSlideEnd: () => {
          d.current = void 0, a == null ? void 0 : a();
        },
        onStepKeyDown: (h) => {
          const y = qc[m ? "from-bottom" : "from-top"].includes(h.key);
          l == null ? void 0 : l({
            event: h,
            direction: y ? -1 : 1
          });
        }
      })
    });
  }), Tc = A.forwardRef((e, t) => {
    const { __scopeSlider: n, onSlideStart: i, onSlideMove: o, onSlideEnd: s, onHomeKeyDown: r, onEndKeyDown: a, onStepKeyDown: l, ...c } = e, u = Gn(St, n);
    return P(Z.span, {
      ...c,
      ref: t,
      onKeyDown: k(e.onKeyDown, (f) => {
        f.key === "Home" ? (r(f), f.preventDefault()) : f.key === "End" ? (a(f), f.preventDefault()) : Pc.concat(wc).includes(f.key) && (l(f), f.preventDefault());
      }),
      onPointerDown: k(e.onPointerDown, (f) => {
        const d = f.target;
        d.setPointerCapture(f.pointerId), f.preventDefault(), u.thumbs.has(d) ? d.focus() : i(f);
      }),
      onPointerMove: k(e.onPointerMove, (f) => {
        f.target.hasPointerCapture(f.pointerId) && o(f);
      }),
      onPointerUp: k(e.onPointerUp, (f) => {
        const d = f.target;
        d.hasPointerCapture(f.pointerId) && (d.releasePointerCapture(f.pointerId), s(f));
      })
    });
  }), bc = "SliderTrack", Fc = A.forwardRef((e, t) => {
    const { __scopeSlider: n, ...i } = e, o = Gn(bc, n);
    return P(Z.span, {
      "data-disabled": o.disabled ? "" : void 0,
      "data-orientation": o.orientation,
      ...i,
      ref: t
    });
  });
  Fc.displayName = bc;
  var mo = "SliderRange", Ec = A.forwardRef((e, t) => {
    const { __scopeSlider: n, ...i } = e, o = Gn(mo, n), s = Sc(mo, n), r = A.useRef(null), a = B(t, r), l = o.values.length, c = o.values.map((d) => Lc(d, o.min, o.max)), u = l > 1 ? Math.min(...c) : 0, f = 100 - Math.max(...c);
    return P(Z.span, {
      "data-orientation": o.orientation,
      "data-disabled": o.disabled ? "" : void 0,
      ...i,
      ref: a,
      style: {
        ...e.style,
        [s.startEdge]: u + "%",
        [s.endEdge]: f + "%"
      }
    });
  });
  Ec.displayName = mo;
  var po = "SliderThumb", Cc = A.forwardRef((e, t) => {
    const n = tA(e.__scopeSlider), [i, o] = A.useState(null), s = B(t, (a) => o(a)), r = A.useMemo(() => i ? n().findIndex((a) => a.ref.current === i) : -1, [
      n,
      i
    ]);
    return P(rA, {
      ...e,
      ref: s,
      index: r
    });
  }), rA = A.forwardRef((e, t) => {
    const { __scopeSlider: n, index: i, name: o, ...s } = e, r = Gn(po, n), a = Sc(po, n), [l, c] = A.useState(null), u = B(t, (x) => c(x)), f = l ? r.form || !!l.closest("form") : true, d = ns(l), m = r.values[i], p = m === void 0 ? 0 : Lc(m, r.min, r.max), h = cA(i, r.values.length), g = d == null ? void 0 : d[a.size], y = g ? fA(g, p, a.direction) : 0;
    return A.useEffect(() => {
      if (l) return r.thumbs.add(l), () => {
        r.thumbs.delete(l);
      };
    }, [
      l,
      r.thumbs
    ]), j("span", {
      style: {
        transform: "var(--radix-slider-thumb-transform)",
        position: "absolute",
        [a.startEdge]: `calc(${p}% + ${y}px)`
      },
      children: [
        P(ho.ItemSlot, {
          scope: e.__scopeSlider,
          children: P(Z.span, {
            role: "slider",
            "aria-label": e["aria-label"] || h,
            "aria-valuemin": r.min,
            "aria-valuenow": m,
            "aria-valuemax": r.max,
            "aria-orientation": r.orientation,
            "data-orientation": r.orientation,
            "data-disabled": r.disabled ? "" : void 0,
            tabIndex: r.disabled ? void 0 : 0,
            ...s,
            ref: u,
            style: m === void 0 ? {
              display: "none"
            } : e.style,
            onFocus: k(e.onFocus, () => {
              r.valueIndexToChangeRef.current = i;
            })
          })
        }),
        f && P(Uc, {
          name: o ?? (r.name ? r.name + (r.values.length > 1 ? "[]" : "") : void 0),
          form: r.form,
          value: m
        }, i)
      ]
    });
  });
  Cc.displayName = po;
  var aA = "RadioBubbleInput", Uc = A.forwardRef(({ __scopeSlider: e, value: t, ...n }, i) => {
    const o = A.useRef(null), s = B(o, i), r = dc(t);
    return A.useEffect(() => {
      const a = o.current;
      if (!a) return;
      const l = window.HTMLInputElement.prototype, u = Object.getOwnPropertyDescriptor(l, "value").set;
      if (r !== t && u) {
        const f = new Event("input", {
          bubbles: true
        });
        u.call(a, t), a.dispatchEvent(f);
      }
    }, [
      r,
      t
    ]), P(Z.input, {
      style: {
        display: "none"
      },
      ...n,
      ref: s,
      defaultValue: t
    });
  });
  Uc.displayName = aA;
  function lA(e = [], t, n) {
    const i = [
      ...e
    ];
    return i[n] = t, i.sort((o, s) => o - s);
  }
  function Lc(e, t, n) {
    const s = 100 / (n - t) * (e - t);
    return yc(s, [
      0,
      100
    ]);
  }
  function cA(e, t) {
    return t > 2 ? `Value ${e + 1} of ${t}` : t === 2 ? [
      "Minimum",
      "Maximum"
    ][e] : void 0;
  }
  function uA(e, t) {
    if (e.length === 1) return 0;
    const n = e.map((o) => Math.abs(o - t)), i = Math.min(...n);
    return n.indexOf(i);
  }
  function fA(e, t, n) {
    const i = e / 2, s = os([
      0,
      50
    ], [
      0,
      i
    ]);
    return (i - s(t) * n) * n;
  }
  function dA(e) {
    return e.slice(0, -1).map((t, n) => e[n + 1] - t);
  }
  function hA(e, t) {
    if (t > 0) {
      const n = dA(e);
      return Math.min(...n) >= t;
    }
    return true;
  }
  function os(e, t) {
    return (n) => {
      if (e[0] === e[1] || t[0] === t[1]) return t[0];
      const i = (t[1] - t[0]) / (e[1] - e[0]);
      return t[0] + i * (n - e[0]);
    };
  }
  function mA(e) {
    return (String(e).split(".")[1] || "").length;
  }
  function pA(e, t) {
    const n = Math.pow(10, t);
    return Math.round(e * n) / n;
  }
  var gA = vc, AA = Fc, yA = Ec, xA = Cc;
  const { sliderTrackHeight: WP, sliderThumbSize: Xr, minTapTarget: PA } = ee;
  zP = function({ value: e, onValueChange: t, min: n = 0, max: i = 100, step: o = 1, label: s, disabled: r = false, className: a, style: l }) {
    return j(gA, {
      value: [
        e
      ],
      onValueChange: ([c]) => t(c),
      min: n,
      max: i,
      step: o,
      "aria-label": s,
      disabled: r,
      className: a,
      style: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: PA,
        touchAction: "none",
        userSelect: "none",
        ...l
      },
      children: [
        P(AA, {
          style: {
            display: "block",
            position: "relative",
            flexGrow: 1,
            height: 6,
            borderRadius: I.pill,
            background: "rgba(255, 255, 255, 0.15)",
            border: "1px solid rgba(255,255,255,0.1)"
          },
          children: P(yA, {
            style: {
              display: "block",
              position: "absolute",
              height: "100%",
              borderRadius: I.pill,
              background: "rgba(255, 255, 255, 0.5)"
            }
          })
        }),
        P(xA, {
          "aria-label": s,
          style: {
            display: "block",
            width: Xr,
            height: Xr,
            borderRadius: I.pill,
            outline: "none",
            background: "rgba(255, 255, 255, 0.9)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.2)",
            cursor: "pointer"
          }
        })
      ]
    });
  };
  var wA = A[" useId ".trim().toString()] || (() => {
  }), qA = 0;
  function Wt(e) {
    const [t, n] = A.useState(wA());
    return ke(() => {
      n((i) => i ?? String(qA++));
    }, [
      e
    ]), e || (t ? `radix-${t}` : "");
  }
  function nt(e) {
    const t = A.useRef(e);
    return A.useEffect(() => {
      t.current = e;
    }), A.useMemo(() => (...n) => {
      var _a3;
      return (_a3 = t.current) == null ? void 0 : _a3.call(t, ...n);
    }, []);
  }
  var Fi = "rovingFocusGroup.onEntryFocus", RA = {
    bubbles: false,
    cancelable: true
  }, Qt = "RovingFocusGroup", [go, Nc, vA] = xc(Qt), [VA, Dc] = Oe(Qt, [
    vA
  ]), [SA, TA] = VA(Qt), Kc = A.forwardRef((e, t) => P(go.Provider, {
    scope: e.__scopeRovingFocusGroup,
    children: P(go.Slot, {
      scope: e.__scopeRovingFocusGroup,
      children: P(bA, {
        ...e,
        ref: t
      })
    })
  }));
  Kc.displayName = Qt;
  var bA = A.forwardRef((e, t) => {
    const { __scopeRovingFocusGroup: n, orientation: i, loop: o = false, dir: s, currentTabStopId: r, defaultCurrentTabStopId: a, onCurrentTabStopIdChange: l, onEntryFocus: c, preventScrollOnEntryFocus: u = false, ...f } = e, d = A.useRef(null), m = B(t, d), p = is(s), [h, g] = Ye({
      prop: r,
      defaultProp: a ?? null,
      onChange: l,
      caller: Qt
    }), [y, x] = A.useState(false), w = nt(c), q = Nc(n), R = A.useRef(false), [v, S] = A.useState(0);
    return A.useEffect(() => {
      const V = d.current;
      if (V) return V.addEventListener(Fi, w), () => V.removeEventListener(Fi, w);
    }, [
      w
    ]), P(SA, {
      scope: n,
      orientation: i,
      dir: p,
      loop: o,
      currentTabStopId: h,
      onItemFocus: A.useCallback((V) => g(V), [
        g
      ]),
      onItemShiftTab: A.useCallback(() => x(true), []),
      onFocusableItemAdd: A.useCallback(() => S((V) => V + 1), []),
      onFocusableItemRemove: A.useCallback(() => S((V) => V - 1), []),
      children: P(Z.div, {
        tabIndex: y || v === 0 ? -1 : 0,
        "data-orientation": i,
        ...f,
        ref: m,
        style: {
          outline: "none",
          ...e.style
        },
        onMouseDown: k(e.onMouseDown, () => {
          R.current = true;
        }),
        onFocus: k(e.onFocus, (V) => {
          const b = !R.current;
          if (V.target === V.currentTarget && b && !y) {
            const T = new CustomEvent(Fi, RA);
            if (V.currentTarget.dispatchEvent(T), !T.defaultPrevented) {
              const F = q().filter((N) => N.focusable), U = F.find((N) => N.active), D = F.find((N) => N.id === h), z = [
                U,
                D,
                ...F
              ].filter(Boolean).map((N) => N.ref.current);
              Hc(z, u);
            }
          }
          R.current = false;
        }),
        onBlur: k(e.onBlur, () => x(false))
      })
    });
  }), Wc = "RovingFocusGroupItem", zc = A.forwardRef((e, t) => {
    const { __scopeRovingFocusGroup: n, focusable: i = true, active: o = false, tabStopId: s, children: r, ...a } = e, l = Wt(), c = s || l, u = TA(Wc, n), f = u.currentTabStopId === c, d = Nc(n), { onFocusableItemAdd: m, onFocusableItemRemove: p, currentTabStopId: h } = u;
    return A.useEffect(() => {
      if (i) return m(), () => p();
    }, [
      i,
      m,
      p
    ]), P(go.ItemSlot, {
      scope: n,
      id: c,
      focusable: i,
      active: o,
      children: P(Z.span, {
        tabIndex: f ? 0 : -1,
        "data-orientation": u.orientation,
        ...a,
        ref: t,
        onMouseDown: k(e.onMouseDown, (g) => {
          i ? u.onItemFocus(c) : g.preventDefault();
        }),
        onFocus: k(e.onFocus, () => u.onItemFocus(c)),
        onKeyDown: k(e.onKeyDown, (g) => {
          if (g.key === "Tab" && g.shiftKey) {
            u.onItemShiftTab();
            return;
          }
          if (g.target !== g.currentTarget) return;
          const y = CA(g, u.orientation, u.dir);
          if (y !== void 0) {
            if (g.metaKey || g.ctrlKey || g.altKey || g.shiftKey) return;
            g.preventDefault();
            let w = d().filter((q) => q.focusable).map((q) => q.ref.current);
            if (y === "last") w.reverse();
            else if (y === "prev" || y === "next") {
              y === "prev" && w.reverse();
              const q = w.indexOf(g.currentTarget);
              w = u.loop ? UA(w, q + 1) : w.slice(q + 1);
            }
            setTimeout(() => Hc(w));
          }
        }),
        children: typeof r == "function" ? r({
          isCurrentTabStop: f,
          hasTabStop: h != null
        }) : r
      })
    });
  });
  zc.displayName = Wc;
  var FA = {
    ArrowLeft: "prev",
    ArrowUp: "prev",
    ArrowRight: "next",
    ArrowDown: "next",
    PageUp: "first",
    Home: "first",
    PageDown: "last",
    End: "last"
  };
  function EA(e, t) {
    return t !== "rtl" ? e : e === "ArrowLeft" ? "ArrowRight" : e === "ArrowRight" ? "ArrowLeft" : e;
  }
  function CA(e, t, n) {
    const i = EA(e.key, n);
    if (!(t === "vertical" && [
      "ArrowLeft",
      "ArrowRight"
    ].includes(i)) && !(t === "horizontal" && [
      "ArrowUp",
      "ArrowDown"
    ].includes(i))) return FA[i];
  }
  function Hc(e, t = false) {
    const n = document.activeElement;
    for (const i of e) if (i === n || (i.focus({
      preventScroll: t
    }), document.activeElement !== n)) return;
  }
  function UA(e, t) {
    return e.map((n, i) => e[(t + i) % e.length]);
  }
  var LA = Kc, NA = zc, Mc = "Toggle", kc = A.forwardRef((e, t) => {
    const { pressed: n, defaultPressed: i, onPressedChange: o, ...s } = e, [r, a] = Ye({
      prop: n,
      onChange: o,
      defaultProp: i ?? false,
      caller: Mc
    });
    return P(Z.button, {
      type: "button",
      "aria-pressed": r,
      "data-state": r ? "on" : "off",
      "data-disabled": e.disabled ? "" : void 0,
      ...s,
      ref: t,
      onClick: k(e.onClick, () => {
        e.disabled || a(!r);
      })
    });
  });
  kc.displayName = Mc;
  var Be = "ToggleGroup", [Ic] = Oe(Be, [
    Dc
  ]), Zc = Dc(), ss = $.forwardRef((e, t) => {
    const { type: n, ...i } = e;
    if (n === "single") return P(DA, {
      ...i,
      ref: t
    });
    if (n === "multiple") return P(KA, {
      ...i,
      ref: t
    });
    throw new Error(`Missing prop \`type\` expected on \`${Be}\``);
  });
  ss.displayName = Be;
  var [Jc, Oc] = Ic(Be), DA = $.forwardRef((e, t) => {
    const { value: n, defaultValue: i, onValueChange: o = () => {
    }, ...s } = e, [r, a] = Ye({
      prop: n,
      defaultProp: i ?? "",
      onChange: o,
      caller: Be
    });
    return P(Jc, {
      scope: e.__scopeToggleGroup,
      type: "single",
      value: $.useMemo(() => r ? [
        r
      ] : [], [
        r
      ]),
      onItemActivate: a,
      onItemDeactivate: $.useCallback(() => a(""), [
        a
      ]),
      children: P(Yc, {
        ...s,
        ref: t
      })
    });
  }), KA = $.forwardRef((e, t) => {
    const { value: n, defaultValue: i, onValueChange: o = () => {
    }, ...s } = e, [r, a] = Ye({
      prop: n,
      defaultProp: i ?? [],
      onChange: o,
      caller: Be
    }), l = $.useCallback((u) => a((f = []) => [
      ...f,
      u
    ]), [
      a
    ]), c = $.useCallback((u) => a((f = []) => f.filter((d) => d !== u)), [
      a
    ]);
    return P(Jc, {
      scope: e.__scopeToggleGroup,
      type: "multiple",
      value: r,
      onItemActivate: l,
      onItemDeactivate: c,
      children: P(Yc, {
        ...s,
        ref: t
      })
    });
  });
  ss.displayName = Be;
  var [WA, zA] = Ic(Be), Yc = $.forwardRef((e, t) => {
    const { __scopeToggleGroup: n, disabled: i = false, rovingFocus: o = true, orientation: s, dir: r, loop: a = true, ...l } = e, c = Zc(n), u = is(r), f = {
      role: "group",
      dir: u,
      ...l
    };
    return P(WA, {
      scope: n,
      rovingFocus: o,
      disabled: i,
      children: o ? P(LA, {
        asChild: true,
        ...c,
        orientation: s,
        dir: u,
        loop: a,
        children: P(Z.div, {
          ...f,
          ref: t
        })
      }) : P(Z.div, {
        ...f,
        ref: t
      })
    });
  }), Kn = "ToggleGroupItem", Bc = $.forwardRef((e, t) => {
    const n = Oc(Kn, e.__scopeToggleGroup), i = zA(Kn, e.__scopeToggleGroup), o = Zc(e.__scopeToggleGroup), s = n.value.includes(e.value), r = i.disabled || e.disabled, a = {
      ...e,
      pressed: s,
      disabled: r
    }, l = $.useRef(null);
    return i.rovingFocus ? P(NA, {
      asChild: true,
      ...o,
      focusable: !r,
      active: s,
      ref: l,
      children: P(Qr, {
        ...a,
        ref: t
      })
    }) : P(Qr, {
      ...a,
      ref: t
    });
  });
  Bc.displayName = Kn;
  var Qr = $.forwardRef((e, t) => {
    const { __scopeToggleGroup: n, value: i, ...o } = e, s = Oc(Kn, n), r = {
      role: "radio",
      "aria-checked": e.pressed,
      "aria-pressed": void 0
    }, a = s.type === "single" ? r : void 0;
    return P(kc, {
      ...a,
      ...o,
      ref: t,
      onPressedChange: (l) => {
        l ? s.onItemActivate(i) : s.onItemDeactivate(i);
      }
    });
  }), jc = ss, Xc = Bc;
  HP = function({ value: e, onValueChange: t, segments: n, className: i, style: o }) {
    const r = `${Pt()}-seg-indicator`, l = ts() ? {
      type: "tween",
      duration: 0
    } : {
      type: "spring",
      stiffness: 400,
      damping: 35
    };
    return P(Vp, {
      children: P(jc, {
        type: "single",
        value: e,
        onValueChange: (c) => {
          c && t(c);
        },
        className: i,
        style: {
          display: "flex",
          height: ee.segmentedHeight,
          padding: ee.segmentedPadding,
          borderRadius: I.pill,
          background: "rgba(80, 80, 100, 0.5)",
          border: "1px solid rgba(255,255,255,0.1)",
          position: "relative",
          ...o
        },
        children: n.map((c) => j(Xc, {
          value: c.value,
          style: {
            position: "relative",
            flex: 1,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "inherit",
            zIndex: 1,
            padding: `0 ${L.sm}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          },
          children: [
            e === c.value && P(me.div, {
              layoutId: r,
              transition: l,
              style: {
                position: "absolute",
                inset: 0,
                borderRadius: I.pill,
                zIndex: 0
              },
              children: P(Pe, {
                cornerRadius: I.pill,
                opacity: 0.35,
                specular: 0.25,
                style: {
                  width: "100%",
                  height: "100%"
                }
              })
            }),
            P("span", {
              style: {
                position: "relative",
                zIndex: 1,
                fontSize: 13,
                fontWeight: 500,
                whiteSpace: "nowrap"
              },
              children: c.label
            })
          ]
        }, c.value))
      })
    });
  };
  MP = function({ label: e, selected: t, onToggle: n, disabled: i, className: o, style: s }) {
    const r = t ? 0.2 : void 0, a = t ? 0.2 : void 0;
    return P(Ae, {
      cornerRadius: I.pill,
      opacity: t ? 0.4 : 0.15,
      specular: r,
      rim: a,
      "aria-pressed": t,
      onClick: () => n(!t),
      disabled: i,
      className: o,
      style: {
        paddingInline: L.md,
        paddingBlock: L.sm,
        minHeight: ee.minTapTarget,
        minWidth: 64,
        fontSize: 14,
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: t ? "1.5px solid rgba(255, 255, 255, 0.5)" : "1px solid rgba(255, 255, 255, 0.15)",
        background: t ? "rgba(255, 255, 255, 0.12)" : "none",
        ...s
      },
      children: e
    });
  };
  kP = function({ value: e, onChange: t, min: n = 0, max: i = 10, step: o = 1, label: s, disabled: r, className: a, style: l }) {
    const c = () => t(Math.max(n, e - o)), u = () => t(Math.min(i, e + o));
    return j("div", {
      role: "group",
      "aria-label": s ?? "Stepper",
      className: a,
      style: {
        display: "flex",
        alignItems: "center",
        gap: L.xs,
        ...l
      },
      children: [
        P(Ae, {
          cornerRadius: I.pill,
          opacity: 0.2,
          "aria-label": "Decrease",
          disabled: r || e <= n,
          onClick: c,
          style: {
            width: ee.minTapTarget,
            height: ee.minTapTarget,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            fontWeight: 500,
            border: "1px solid rgba(255,255,255,0.15)"
          },
          children: "\u2212"
        }),
        P("output", {
          "aria-label": `${s ?? "Value"}: ${e}`,
          "aria-atomic": "true",
          children: P(Pe, {
            cornerRadius: I.sm,
            style: {
              minWidth: 48,
              minHeight: 36,
              textAlign: "center",
              padding: `${L.sm}px ${L.md}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 17,
              fontWeight: 500
            },
            children: e
          })
        }),
        P(Ae, {
          cornerRadius: I.pill,
          opacity: 0.2,
          "aria-label": "Increase",
          disabled: r || e >= i,
          onClick: u,
          style: {
            width: ee.minTapTarget,
            height: ee.minTapTarget,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            fontWeight: 500,
            border: "1px solid rgba(255,255,255,0.15)"
          },
          children: "+"
        })
      ]
    });
  };
  IP = function({ label: e, value: t, onChange: n, placeholder: i, type: o = "text", id: s, disabled: r, className: a, style: l }) {
    const c = Pt(), u = s ?? c, [f, d] = ge(false), m = f ? 0.22 : void 0, p = f ? 0.28 : void 0;
    return j("div", {
      className: a,
      style: l,
      children: [
        e && P("label", {
          htmlFor: u,
          style: {
            display: "block",
            marginBottom: L.xs,
            fontSize: 14
          },
          children: e
        }),
        P(Pe, {
          cornerRadius: I.md,
          opacity: 0.2,
          specular: m,
          rim: p,
          style: {
            display: "flex",
            alignItems: "center",
            minHeight: ee.minTapTarget,
            padding: `${L.sm}px ${L.md}px`,
            border: f ? "1px solid rgba(255,255,255,0.4)" : "1px solid rgba(255,255,255,0.15)",
            outline: f ? "2px solid rgba(255,255,255,0.65)" : "2px solid transparent",
            outlineOffset: 2,
            transition: "outline-color 0.15s, border-color 0.15s"
          },
          children: P("input", {
            id: u,
            type: o,
            value: t,
            onChange: (h) => n(h.target.value),
            placeholder: i,
            disabled: r,
            onFocus: () => d(true),
            onBlur: () => d(false),
            style: {
              background: "transparent",
              border: "none",
              outline: "none",
              width: "100%",
              color: "inherit",
              font: "inherit",
              fontSize: 16,
              padding: 0
            }
          })
        })
      ]
    });
  };
  ZP = function({ title: e, onBack: t, actions: n, style: i, className: o }) {
    return P("nav", {
      role: "navigation",
      className: o,
      style: i,
      children: j(Pe, {
        cornerRadius: 0,
        style: {
          width: "100%",
          height: 44,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          paddingLeft: L.md,
          paddingRight: L.md
        },
        children: [
          t && P(fo, {
            id: "navbar-back",
            animate: false,
            children: P(Ae, {
              cornerRadius: I.md,
              "aria-label": "Back",
              onClick: t,
              style: {
                minWidth: ee.minTapTarget,
                minHeight: ee.minTapTarget,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0
              },
              children: P("span", {
                style: {
                  fontSize: 24,
                  lineHeight: 1
                },
                children: "\u2039"
              })
            })
          }),
          P("span", {
            style: {
              flex: 1,
              textAlign: "left",
              fontWeight: 600,
              fontSize: 17,
              paddingLeft: L.sm,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            },
            children: e
          }),
          n && n.length > 0 && P(fo, {
            id: "navbar-actions",
            animate: false,
            children: P("div", {
              style: {
                display: "flex",
                flexDirection: "row",
                gap: L.xs
              },
              children: n.map((s) => P(Ae, {
                cornerRadius: I.md,
                "aria-label": s.label,
                onClick: () => s.onPress(),
                refractionMode: s.primary ? "prominent" : void 0,
                style: {
                  minWidth: ee.minTapTarget,
                  minHeight: ee.minTapTarget,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0
                },
                children: P("span", {
                  style: {
                    fontSize: 20,
                    lineHeight: 1
                  },
                  children: s.icon
                })
              }, s.id))
            })
          })
        ]
      })
    });
  };
  JP = function({ actions: e, style: t, className: n }) {
    return P("div", {
      role: "toolbar",
      "aria-label": "Toolbar",
      className: n,
      style: t,
      children: P(Pe, {
        cornerRadius: 0,
        style: {
          width: "100%",
          height: 44,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: L.sm,
          paddingLeft: L.md,
          paddingRight: L.md
        },
        children: P(fo, {
          id: "toolbar-actions",
          animate: false,
          children: P("div", {
            style: {
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: L.sm
            },
            children: e.map((i) => P(Ae, {
              cornerRadius: I.md,
              "aria-label": i.label,
              onClick: () => i.onPress(),
              refractionMode: i.primary ? "prominent" : void 0,
              "data-primary": i.primary ? "true" : void 0,
              style: {
                minWidth: ee.minTapTarget,
                minHeight: ee.minTapTarget,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0
              },
              children: P("span", {
                style: {
                  fontSize: 20,
                  lineHeight: 1
                },
                children: i.icon
              })
            }, i.id))
          })
        })
      })
    });
  };
  OP = function({ value: e, onValueChange: t, onCancel: n, placeholder: i = "Search", label: o = "Search", disabled: s, style: r, className: a }) {
    const [l, c] = ge(false), u = O(null), f = () => {
      var _a3;
      t(""), (_a3 = u.current) == null ? void 0 : _a3.focus();
    }, d = () => {
      var _a3;
      t(""), n == null ? void 0 : n(), (_a3 = u.current) == null ? void 0 : _a3.blur();
    };
    return j("div", {
      className: a,
      style: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: L.sm,
        ...r
      },
      children: [
        j(Pe, {
          cornerRadius: I.pill,
          specular: l ? 0.35 : void 0,
          rim: l ? 0.3 : void 0,
          style: {
            display: "flex",
            alignItems: "center",
            height: 36,
            paddingLeft: L.sm,
            paddingRight: L.xs,
            flex: 1
          },
          children: [
            P("span", {
              style: {
                fontSize: 14,
                opacity: 0.5,
                marginRight: L.xs,
                lineHeight: 1,
                flexShrink: 0
              },
              "aria-hidden": "true",
              children: "\u{1F50D}"
            }),
            P("input", {
              ref: u,
              type: "text",
              value: e,
              onChange: (m) => t(m.target.value),
              onFocus: () => c(true),
              onBlur: () => c(false),
              placeholder: i,
              "aria-label": o,
              disabled: s,
              style: {
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontSize: 15,
                color: "inherit",
                padding: 0,
                minWidth: 0
              }
            }),
            e.length > 0 && P(Ae, {
              cornerRadius: I.sm,
              "aria-label": "Clear search",
              onClick: f,
              style: {
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                fontSize: 12,
                flexShrink: 0
              },
              children: "\xD7"
            })
          ]
        }),
        P(Vt, {
          children: l && P(me.div, {
            initial: {
              opacity: 0,
              x: 20
            },
            animate: {
              opacity: 1,
              x: 0
            },
            exit: {
              opacity: 0,
              x: 20
            },
            transition: {
              type: "spring",
              stiffness: 400,
              damping: 30
            },
            children: P("div", {
              onMouseDown: (m) => {
                m.preventDefault();
              },
              children: P(Ae, {
                cornerRadius: I.md,
                onClick: d,
                style: {
                  fontSize: 15,
                  padding: `${L.xs}px ${L.sm}px`,
                  whiteSpace: "nowrap"
                },
                children: "Cancel"
              })
            })
          })
        })
      ]
    });
  };
  YP = function({ value: e, onValueChange: t, tabs: n, scrollMinimize: i, className: o, style: s }) {
    const [r, a] = ge(false), l = ts();
    return le(() => {
      if (!i) return;
      const c = {
        current: 0
      }, u = () => {
        const f = window.scrollY;
        f > c.current && f > 50 ? a(true) : a(false), c.current = f;
      };
      return window.addEventListener("scroll", u, {
        passive: true
      }), () => window.removeEventListener("scroll", u);
    }, [
      i
    ]), P("nav", {
      role: "navigation",
      className: o,
      style: s,
      children: P(Pe, {
        cornerRadius: 0,
        style: {
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: r ? 49 : 83,
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: l ? "none" : "height 0.3s ease",
          paddingBottom: r ? 0 : 34
        },
        children: P(jc, {
          type: "single",
          value: e,
          onValueChange: (c) => {
            c && t(c);
          },
          "aria-label": "Navigation tabs",
          style: {
            display: "flex",
            flexDirection: "row",
            gap: L.xs
          },
          children: n.map((c) => P(Xc, {
            value: c.value,
            asChild: true,
            children: j(Ae, {
              cornerRadius: I.md,
              refractionMode: e === c.value ? "prominent" : void 0,
              "aria-label": c.label,
              style: {
                minWidth: ee.minTapTarget,
                minHeight: ee.minTapTarget,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2
              },
              children: [
                c.icon,
                !r && P("span", {
                  style: {
                    fontSize: 10
                  },
                  children: c.label
                })
              ]
            })
          }, c.value))
        })
      })
    });
  };
  function HA(e, t = globalThis == null ? void 0 : globalThis.document) {
    const n = nt(e);
    A.useEffect(() => {
      const i = (o) => {
        o.key === "Escape" && n(o);
      };
      return t.addEventListener("keydown", i, {
        capture: true
      }), () => t.removeEventListener("keydown", i, {
        capture: true
      });
    }, [
      n,
      t
    ]);
  }
  var MA = "DismissableLayer", Ao = "dismissableLayer.update", kA = "dismissableLayer.pointerDownOutside", IA = "dismissableLayer.focusOutside", Gr, Qc = A.createContext({
    layers: /* @__PURE__ */ new Set(),
    layersWithOutsidePointerEventsDisabled: /* @__PURE__ */ new Set(),
    branches: /* @__PURE__ */ new Set()
  }), rs = A.forwardRef((e, t) => {
    const { disableOutsidePointerEvents: n = false, onEscapeKeyDown: i, onPointerDownOutside: o, onFocusOutside: s, onInteractOutside: r, onDismiss: a, ...l } = e, c = A.useContext(Qc), [u, f] = A.useState(null), d = (u == null ? void 0 : u.ownerDocument) ?? (globalThis == null ? void 0 : globalThis.document), [, m] = A.useState({}), p = B(t, (S) => f(S)), h = Array.from(c.layers), [g] = [
      ...c.layersWithOutsidePointerEventsDisabled
    ].slice(-1), y = h.indexOf(g), x = u ? h.indexOf(u) : -1, w = c.layersWithOutsidePointerEventsDisabled.size > 0, q = x >= y, R = OA((S) => {
      const V = S.target, b = [
        ...c.branches
      ].some((T) => T.contains(V));
      !q || b || (o == null ? void 0 : o(S), r == null ? void 0 : r(S), S.defaultPrevented || (a == null ? void 0 : a()));
    }, d), v = YA((S) => {
      const V = S.target;
      [
        ...c.branches
      ].some((T) => T.contains(V)) || (s == null ? void 0 : s(S), r == null ? void 0 : r(S), S.defaultPrevented || (a == null ? void 0 : a()));
    }, d);
    return HA((S) => {
      x === c.layers.size - 1 && (i == null ? void 0 : i(S), !S.defaultPrevented && a && (S.preventDefault(), a()));
    }, d), A.useEffect(() => {
      if (u) return n && (c.layersWithOutsidePointerEventsDisabled.size === 0 && (Gr = d.body.style.pointerEvents, d.body.style.pointerEvents = "none"), c.layersWithOutsidePointerEventsDisabled.add(u)), c.layers.add(u), _r(), () => {
        n && c.layersWithOutsidePointerEventsDisabled.size === 1 && (d.body.style.pointerEvents = Gr);
      };
    }, [
      u,
      d,
      n,
      c
    ]), A.useEffect(() => () => {
      u && (c.layers.delete(u), c.layersWithOutsidePointerEventsDisabled.delete(u), _r());
    }, [
      u,
      c
    ]), A.useEffect(() => {
      const S = () => m({});
      return document.addEventListener(Ao, S), () => document.removeEventListener(Ao, S);
    }, []), P(Z.div, {
      ...l,
      ref: p,
      style: {
        pointerEvents: w ? q ? "auto" : "none" : void 0,
        ...e.style
      },
      onFocusCapture: k(e.onFocusCapture, v.onFocusCapture),
      onBlurCapture: k(e.onBlurCapture, v.onBlurCapture),
      onPointerDownCapture: k(e.onPointerDownCapture, R.onPointerDownCapture)
    });
  });
  rs.displayName = MA;
  var ZA = "DismissableLayerBranch", JA = A.forwardRef((e, t) => {
    const n = A.useContext(Qc), i = A.useRef(null), o = B(t, i);
    return A.useEffect(() => {
      const s = i.current;
      if (s) return n.branches.add(s), () => {
        n.branches.delete(s);
      };
    }, [
      n.branches
    ]), P(Z.div, {
      ...e,
      ref: o
    });
  });
  JA.displayName = ZA;
  function OA(e, t = globalThis == null ? void 0 : globalThis.document) {
    const n = nt(e), i = A.useRef(false), o = A.useRef(() => {
    });
    return A.useEffect(() => {
      const s = (a) => {
        if (a.target && !i.current) {
          let l = function() {
            Gc(kA, n, c, {
              discrete: true
            });
          };
          const c = {
            originalEvent: a
          };
          a.pointerType === "touch" ? (t.removeEventListener("click", o.current), o.current = l, t.addEventListener("click", o.current, {
            once: true
          })) : l();
        } else t.removeEventListener("click", o.current);
        i.current = false;
      }, r = window.setTimeout(() => {
        t.addEventListener("pointerdown", s);
      }, 0);
      return () => {
        window.clearTimeout(r), t.removeEventListener("pointerdown", s), t.removeEventListener("click", o.current);
      };
    }, [
      t,
      n
    ]), {
      onPointerDownCapture: () => i.current = true
    };
  }
  function YA(e, t = globalThis == null ? void 0 : globalThis.document) {
    const n = nt(e), i = A.useRef(false);
    return A.useEffect(() => {
      const o = (s) => {
        s.target && !i.current && Gc(IA, n, {
          originalEvent: s
        }, {
          discrete: false
        });
      };
      return t.addEventListener("focusin", o), () => t.removeEventListener("focusin", o);
    }, [
      t,
      n
    ]), {
      onFocusCapture: () => i.current = true,
      onBlurCapture: () => i.current = false
    };
  }
  function _r() {
    const e = new CustomEvent(Ao);
    document.dispatchEvent(e);
  }
  function Gc(e, t, n, { discrete: i }) {
    const o = n.originalEvent.target, s = new CustomEvent(e, {
      bubbles: false,
      cancelable: true,
      detail: n
    });
    t && o.addEventListener(e, t, {
      once: true
    }), i ? Jg(o, s) : o.dispatchEvent(s);
  }
  var Ei = "focusScope.autoFocusOnMount", Ci = "focusScope.autoFocusOnUnmount", $r = {
    bubbles: false,
    cancelable: true
  }, BA = "FocusScope", as = A.forwardRef((e, t) => {
    const { loop: n = false, trapped: i = false, onMountAutoFocus: o, onUnmountAutoFocus: s, ...r } = e, [a, l] = A.useState(null), c = nt(o), u = nt(s), f = A.useRef(null), d = B(t, (h) => l(h)), m = A.useRef({
      paused: false,
      pause() {
        this.paused = true;
      },
      resume() {
        this.paused = false;
      }
    }).current;
    A.useEffect(() => {
      if (i) {
        let h = function(w) {
          if (m.paused || !a) return;
          const q = w.target;
          a.contains(q) ? f.current = q : We(f.current, {
            select: true
          });
        }, g = function(w) {
          if (m.paused || !a) return;
          const q = w.relatedTarget;
          q !== null && (a.contains(q) || We(f.current, {
            select: true
          }));
        }, y = function(w) {
          if (document.activeElement === document.body) for (const R of w) R.removedNodes.length > 0 && We(a);
        };
        document.addEventListener("focusin", h), document.addEventListener("focusout", g);
        const x = new MutationObserver(y);
        return a && x.observe(a, {
          childList: true,
          subtree: true
        }), () => {
          document.removeEventListener("focusin", h), document.removeEventListener("focusout", g), x.disconnect();
        };
      }
    }, [
      i,
      a,
      m.paused
    ]), A.useEffect(() => {
      if (a) {
        ta.add(m);
        const h = document.activeElement;
        if (!a.contains(h)) {
          const y = new CustomEvent(Ei, $r);
          a.addEventListener(Ei, c), a.dispatchEvent(y), y.defaultPrevented || (jA($A(_c(a)), {
            select: true
          }), document.activeElement === h && We(a));
        }
        return () => {
          a.removeEventListener(Ei, c), setTimeout(() => {
            const y = new CustomEvent(Ci, $r);
            a.addEventListener(Ci, u), a.dispatchEvent(y), y.defaultPrevented || We(h ?? document.body, {
              select: true
            }), a.removeEventListener(Ci, u), ta.remove(m);
          }, 0);
        };
      }
    }, [
      a,
      c,
      u,
      m
    ]);
    const p = A.useCallback((h) => {
      if (!n && !i || m.paused) return;
      const g = h.key === "Tab" && !h.altKey && !h.ctrlKey && !h.metaKey, y = document.activeElement;
      if (g && y) {
        const x = h.currentTarget, [w, q] = XA(x);
        w && q ? !h.shiftKey && y === q ? (h.preventDefault(), n && We(w, {
          select: true
        })) : h.shiftKey && y === w && (h.preventDefault(), n && We(q, {
          select: true
        })) : y === x && h.preventDefault();
      }
    }, [
      n,
      i,
      m.paused
    ]);
    return P(Z.div, {
      tabIndex: -1,
      ...r,
      ref: d,
      onKeyDown: p
    });
  });
  as.displayName = BA;
  function jA(e, { select: t = false } = {}) {
    const n = document.activeElement;
    for (const i of e) if (We(i, {
      select: t
    }), document.activeElement !== n) return;
  }
  function XA(e) {
    const t = _c(e), n = ea(t, e), i = ea(t.reverse(), e);
    return [
      n,
      i
    ];
  }
  function _c(e) {
    const t = [], n = document.createTreeWalker(e, NodeFilter.SHOW_ELEMENT, {
      acceptNode: (i) => {
        const o = i.tagName === "INPUT" && i.type === "hidden";
        return i.disabled || i.hidden || o ? NodeFilter.FILTER_SKIP : i.tabIndex >= 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      }
    });
    for (; n.nextNode(); ) t.push(n.currentNode);
    return t;
  }
  function ea(e, t) {
    for (const n of e) if (!QA(n, {
      upTo: t
    })) return n;
  }
  function QA(e, { upTo: t }) {
    if (getComputedStyle(e).visibility === "hidden") return true;
    for (; e; ) {
      if (t !== void 0 && e === t) return false;
      if (getComputedStyle(e).display === "none") return true;
      e = e.parentElement;
    }
    return false;
  }
  function GA(e) {
    return e instanceof HTMLInputElement && "select" in e;
  }
  function We(e, { select: t = false } = {}) {
    if (e && e.focus) {
      const n = document.activeElement;
      e.focus({
        preventScroll: true
      }), e !== n && GA(e) && t && e.select();
    }
  }
  var ta = _A();
  function _A() {
    let e = [];
    return {
      add(t) {
        const n = e[0];
        t !== n && (n == null ? void 0 : n.pause()), e = na(e, t), e.unshift(t);
      },
      remove(t) {
        var _a3;
        e = na(e, t), (_a3 = e[0]) == null ? void 0 : _a3.resume();
      }
    };
  }
  function na(e, t) {
    const n = [
      ...e
    ], i = n.indexOf(t);
    return i !== -1 && n.splice(i, 1), n;
  }
  function $A(e) {
    return e.filter((t) => t.tagName !== "A");
  }
  var ey = "Portal", ls = A.forwardRef((e, t) => {
    var _a3;
    const { container: n, ...i } = e, [o, s] = A.useState(false);
    ke(() => s(true), []);
    const r = n || o && ((_a3 = globalThis == null ? void 0 : globalThis.document) == null ? void 0 : _a3.body);
    return r ? gf.createPortal(P(Z.div, {
      ...i,
      ref: t
    }), r) : null;
  });
  ls.displayName = ey;
  function ty(e, t) {
    return A.useReducer((n, i) => t[n][i] ?? n, e);
  }
  var Tt = (e) => {
    const { present: t, children: n } = e, i = ny(t), o = typeof n == "function" ? n({
      present: i.isPresent
    }) : A.Children.only(n), s = B(i.ref, iy(o));
    return typeof n == "function" || i.isPresent ? A.cloneElement(o, {
      ref: s
    }) : null;
  };
  Tt.displayName = "Presence";
  function ny(e) {
    const [t, n] = A.useState(), i = A.useRef(null), o = A.useRef(e), s = A.useRef("none"), r = e ? "mounted" : "unmounted", [a, l] = ty(r, {
      mounted: {
        UNMOUNT: "unmounted",
        ANIMATION_OUT: "unmountSuspended"
      },
      unmountSuspended: {
        MOUNT: "mounted",
        ANIMATION_END: "unmounted"
      },
      unmounted: {
        MOUNT: "mounted"
      }
    });
    return A.useEffect(() => {
      const c = ln(i.current);
      s.current = a === "mounted" ? c : "none";
    }, [
      a
    ]), ke(() => {
      const c = i.current, u = o.current;
      if (u !== e) {
        const d = s.current, m = ln(c);
        e ? l("MOUNT") : m === "none" || (c == null ? void 0 : c.display) === "none" ? l("UNMOUNT") : l(u && d !== m ? "ANIMATION_OUT" : "UNMOUNT"), o.current = e;
      }
    }, [
      e,
      l
    ]), ke(() => {
      if (t) {
        let c;
        const u = t.ownerDocument.defaultView ?? window, f = (m) => {
          const h = ln(i.current).includes(CSS.escape(m.animationName));
          if (m.target === t && h && (l("ANIMATION_END"), !o.current)) {
            const g = t.style.animationFillMode;
            t.style.animationFillMode = "forwards", c = u.setTimeout(() => {
              t.style.animationFillMode === "forwards" && (t.style.animationFillMode = g);
            });
          }
        }, d = (m) => {
          m.target === t && (s.current = ln(i.current));
        };
        return t.addEventListener("animationstart", d), t.addEventListener("animationcancel", f), t.addEventListener("animationend", f), () => {
          u.clearTimeout(c), t.removeEventListener("animationstart", d), t.removeEventListener("animationcancel", f), t.removeEventListener("animationend", f);
        };
      } else l("ANIMATION_END");
    }, [
      t,
      l
    ]), {
      isPresent: [
        "mounted",
        "unmountSuspended"
      ].includes(a),
      ref: A.useCallback((c) => {
        i.current = c ? getComputedStyle(c) : null, n(c);
      }, [])
    };
  }
  function ln(e) {
    return (e == null ? void 0 : e.animationName) || "none";
  }
  function iy(e) {
    var _a3, _b;
    let t = (_a3 = Object.getOwnPropertyDescriptor(e.props, "ref")) == null ? void 0 : _a3.get, n = t && "isReactWarning" in t && t.isReactWarning;
    return n ? e.ref : (t = (_b = Object.getOwnPropertyDescriptor(e, "ref")) == null ? void 0 : _b.get, n = t && "isReactWarning" in t && t.isReactWarning, n ? e.props.ref : e.props.ref || e.ref);
  }
  var Ui = 0;
  function $c() {
    A.useEffect(() => {
      const e = document.querySelectorAll("[data-radix-focus-guard]");
      return document.body.insertAdjacentElement("afterbegin", e[0] ?? ia()), document.body.insertAdjacentElement("beforeend", e[1] ?? ia()), Ui++, () => {
        Ui === 1 && document.querySelectorAll("[data-radix-focus-guard]").forEach((t) => t.remove()), Ui--;
      };
    }, []);
  }
  function ia() {
    const e = document.createElement("span");
    return e.setAttribute("data-radix-focus-guard", ""), e.tabIndex = 0, e.style.outline = "none", e.style.opacity = "0", e.style.position = "fixed", e.style.pointerEvents = "none", e;
  }
  var ve = function() {
    return ve = Object.assign || function(t) {
      for (var n, i = 1, o = arguments.length; i < o; i++) {
        n = arguments[i];
        for (var s in n) Object.prototype.hasOwnProperty.call(n, s) && (t[s] = n[s]);
      }
      return t;
    }, ve.apply(this, arguments);
  };
  function eu(e, t) {
    var n = {};
    for (var i in e) Object.prototype.hasOwnProperty.call(e, i) && t.indexOf(i) < 0 && (n[i] = e[i]);
    if (e != null && typeof Object.getOwnPropertySymbols == "function") for (var o = 0, i = Object.getOwnPropertySymbols(e); o < i.length; o++) t.indexOf(i[o]) < 0 && Object.prototype.propertyIsEnumerable.call(e, i[o]) && (n[i[o]] = e[i[o]]);
    return n;
  }
  function oy(e, t, n) {
    if (n || arguments.length === 2) for (var i = 0, o = t.length, s; i < o; i++) (s || !(i in t)) && (s || (s = Array.prototype.slice.call(t, 0, i)), s[i] = t[i]);
    return e.concat(s || Array.prototype.slice.call(t));
  }
  var Rn = "right-scroll-bar-position", vn = "width-before-scroll-bar", sy = "with-scroll-bars-hidden", ry = "--removed-body-scroll-bar-size";
  function Li(e, t) {
    return typeof e == "function" ? e(t) : e && (e.current = t), e;
  }
  function ay(e, t) {
    var n = ge(function() {
      return {
        value: e,
        callback: t,
        facade: {
          get current() {
            return n.value;
          },
          set current(i) {
            var o = n.value;
            o !== i && (n.value = i, n.callback(i, o));
          }
        }
      };
    })[0];
    return n.callback = t, n.facade;
  }
  var ly = typeof window < "u" ? A.useLayoutEffect : A.useEffect, oa = /* @__PURE__ */ new WeakMap();
  function cy(e, t) {
    var n = ay(null, function(i) {
      return e.forEach(function(o) {
        return Li(o, i);
      });
    });
    return ly(function() {
      var i = oa.get(n);
      if (i) {
        var o = new Set(i), s = new Set(e), r = n.current;
        o.forEach(function(a) {
          s.has(a) || Li(a, null);
        }), s.forEach(function(a) {
          o.has(a) || Li(a, r);
        });
      }
      oa.set(n, e);
    }, [
      e
    ]), n;
  }
  function uy(e) {
    return e;
  }
  function fy(e, t) {
    t === void 0 && (t = uy);
    var n = [], i = false, o = {
      read: function() {
        if (i) throw new Error("Sidecar: could not `read` from an `assigned` medium. `read` could be used only with `useMedium`.");
        return n.length ? n[n.length - 1] : e;
      },
      useMedium: function(s) {
        var r = t(s, i);
        return n.push(r), function() {
          n = n.filter(function(a) {
            return a !== r;
          });
        };
      },
      assignSyncMedium: function(s) {
        for (i = true; n.length; ) {
          var r = n;
          n = [], r.forEach(s);
        }
        n = {
          push: function(a) {
            return s(a);
          },
          filter: function() {
            return n;
          }
        };
      },
      assignMedium: function(s) {
        i = true;
        var r = [];
        if (n.length) {
          var a = n;
          n = [], a.forEach(s), r = n;
        }
        var l = function() {
          var u = r;
          r = [], u.forEach(s);
        }, c = function() {
          return Promise.resolve().then(l);
        };
        c(), n = {
          push: function(u) {
            r.push(u), c();
          },
          filter: function(u) {
            return r = r.filter(u), n;
          }
        };
      }
    };
    return o;
  }
  function dy(e) {
    e === void 0 && (e = {});
    var t = fy(null);
    return t.options = ve({
      async: true,
      ssr: false
    }, e), t;
  }
  var tu = function(e) {
    var t = e.sideCar, n = eu(e, [
      "sideCar"
    ]);
    if (!t) throw new Error("Sidecar: please provide `sideCar` property to import the right car");
    var i = t.read();
    if (!i) throw new Error("Sidecar medium not found");
    return A.createElement(i, ve({}, n));
  };
  tu.isSideCarExport = true;
  function hy(e, t) {
    return e.useMedium(t), tu;
  }
  var nu = dy(), Ni = function() {
  }, _n = A.forwardRef(function(e, t) {
    var n = A.useRef(null), i = A.useState({
      onScrollCapture: Ni,
      onWheelCapture: Ni,
      onTouchMoveCapture: Ni
    }), o = i[0], s = i[1], r = e.forwardProps, a = e.children, l = e.className, c = e.removeScrollBar, u = e.enabled, f = e.shards, d = e.sideCar, m = e.noRelative, p = e.noIsolation, h = e.inert, g = e.allowPinchZoom, y = e.as, x = y === void 0 ? "div" : y, w = e.gapMode, q = eu(e, [
      "forwardProps",
      "children",
      "className",
      "removeScrollBar",
      "enabled",
      "shards",
      "sideCar",
      "noRelative",
      "noIsolation",
      "inert",
      "allowPinchZoom",
      "as",
      "gapMode"
    ]), R = d, v = cy([
      n,
      t
    ]), S = ve(ve({}, q), o);
    return A.createElement(A.Fragment, null, u && A.createElement(R, {
      sideCar: nu,
      removeScrollBar: c,
      shards: f,
      noRelative: m,
      noIsolation: p,
      inert: h,
      setCallbacks: s,
      allowPinchZoom: !!g,
      lockRef: n,
      gapMode: w
    }), r ? A.cloneElement(A.Children.only(a), ve(ve({}, S), {
      ref: v
    })) : A.createElement(x, ve({}, S, {
      className: l,
      ref: v
    }), a));
  });
  _n.defaultProps = {
    enabled: true,
    removeScrollBar: true,
    inert: false
  };
  _n.classNames = {
    fullWidth: vn,
    zeroRight: Rn
  };
  var my = function() {
    if (typeof __webpack_nonce__ < "u") return __webpack_nonce__;
  };
  function py() {
    if (!document) return null;
    var e = document.createElement("style");
    e.type = "text/css";
    var t = my();
    return t && e.setAttribute("nonce", t), e;
  }
  function gy(e, t) {
    e.styleSheet ? e.styleSheet.cssText = t : e.appendChild(document.createTextNode(t));
  }
  function Ay(e) {
    var t = document.head || document.getElementsByTagName("head")[0];
    t.appendChild(e);
  }
  var yy = function() {
    var e = 0, t = null;
    return {
      add: function(n) {
        e == 0 && (t = py()) && (gy(t, n), Ay(t)), e++;
      },
      remove: function() {
        e--, !e && t && (t.parentNode && t.parentNode.removeChild(t), t = null);
      }
    };
  }, xy = function() {
    var e = yy();
    return function(t, n) {
      A.useEffect(function() {
        return e.add(t), function() {
          e.remove();
        };
      }, [
        t && n
      ]);
    };
  }, iu = function() {
    var e = xy(), t = function(n) {
      var i = n.styles, o = n.dynamic;
      return e(i, o), null;
    };
    return t;
  }, Py = {
    left: 0,
    top: 0,
    right: 0,
    gap: 0
  }, Di = function(e) {
    return parseInt(e || "", 10) || 0;
  }, wy = function(e) {
    var t = window.getComputedStyle(document.body), n = t[e === "padding" ? "paddingLeft" : "marginLeft"], i = t[e === "padding" ? "paddingTop" : "marginTop"], o = t[e === "padding" ? "paddingRight" : "marginRight"];
    return [
      Di(n),
      Di(i),
      Di(o)
    ];
  }, qy = function(e) {
    if (e === void 0 && (e = "margin"), typeof window > "u") return Py;
    var t = wy(e), n = document.documentElement.clientWidth, i = window.innerWidth;
    return {
      left: t[0],
      top: t[1],
      right: t[2],
      gap: Math.max(0, i - n + t[2] - t[0])
    };
  }, Ry = iu(), ht = "data-scroll-locked", vy = function(e, t, n, i) {
    var o = e.left, s = e.top, r = e.right, a = e.gap;
    return n === void 0 && (n = "margin"), `
  .`.concat(sy, ` {
   overflow: hidden `).concat(i, `;
   padding-right: `).concat(a, "px ").concat(i, `;
  }
  body[`).concat(ht, `] {
    overflow: hidden `).concat(i, `;
    overscroll-behavior: contain;
    `).concat([
      t && "position: relative ".concat(i, ";"),
      n === "margin" && `
    padding-left: `.concat(o, `px;
    padding-top: `).concat(s, `px;
    padding-right: `).concat(r, `px;
    margin-left:0;
    margin-top:0;
    margin-right: `).concat(a, "px ").concat(i, `;
    `),
      n === "padding" && "padding-right: ".concat(a, "px ").concat(i, ";")
    ].filter(Boolean).join(""), `
  }
  
  .`).concat(Rn, ` {
    right: `).concat(a, "px ").concat(i, `;
  }
  
  .`).concat(vn, ` {
    margin-right: `).concat(a, "px ").concat(i, `;
  }
  
  .`).concat(Rn, " .").concat(Rn, ` {
    right: 0 `).concat(i, `;
  }
  
  .`).concat(vn, " .").concat(vn, ` {
    margin-right: 0 `).concat(i, `;
  }
  
  body[`).concat(ht, `] {
    `).concat(ry, ": ").concat(a, `px;
  }
`);
  }, sa = function() {
    var e = parseInt(document.body.getAttribute(ht) || "0", 10);
    return isFinite(e) ? e : 0;
  }, Vy = function() {
    A.useEffect(function() {
      return document.body.setAttribute(ht, (sa() + 1).toString()), function() {
        var e = sa() - 1;
        e <= 0 ? document.body.removeAttribute(ht) : document.body.setAttribute(ht, e.toString());
      };
    }, []);
  }, Sy = function(e) {
    var t = e.noRelative, n = e.noImportant, i = e.gapMode, o = i === void 0 ? "margin" : i;
    Vy();
    var s = A.useMemo(function() {
      return qy(o);
    }, [
      o
    ]);
    return A.createElement(Ry, {
      styles: vy(s, !t, o, n ? "" : "!important")
    });
  }, yo = false;
  if (typeof window < "u") try {
    var cn = Object.defineProperty({}, "passive", {
      get: function() {
        return yo = true, true;
      }
    });
    window.addEventListener("test", cn, cn), window.removeEventListener("test", cn, cn);
  } catch {
    yo = false;
  }
  var st = yo ? {
    passive: false
  } : false, Ty = function(e) {
    return e.tagName === "TEXTAREA";
  }, ou = function(e, t) {
    if (!(e instanceof Element)) return false;
    var n = window.getComputedStyle(e);
    return n[t] !== "hidden" && !(n.overflowY === n.overflowX && !Ty(e) && n[t] === "visible");
  }, by = function(e) {
    return ou(e, "overflowY");
  }, Fy = function(e) {
    return ou(e, "overflowX");
  }, ra = function(e, t) {
    var n = t.ownerDocument, i = t;
    do {
      typeof ShadowRoot < "u" && i instanceof ShadowRoot && (i = i.host);
      var o = su(e, i);
      if (o) {
        var s = ru(e, i), r = s[1], a = s[2];
        if (r > a) return true;
      }
      i = i.parentNode;
    } while (i && i !== n.body);
    return false;
  }, Ey = function(e) {
    var t = e.scrollTop, n = e.scrollHeight, i = e.clientHeight;
    return [
      t,
      n,
      i
    ];
  }, Cy = function(e) {
    var t = e.scrollLeft, n = e.scrollWidth, i = e.clientWidth;
    return [
      t,
      n,
      i
    ];
  }, su = function(e, t) {
    return e === "v" ? by(t) : Fy(t);
  }, ru = function(e, t) {
    return e === "v" ? Ey(t) : Cy(t);
  }, Uy = function(e, t) {
    return e === "h" && t === "rtl" ? -1 : 1;
  }, Ly = function(e, t, n, i, o) {
    var s = Uy(e, window.getComputedStyle(t).direction), r = s * i, a = n.target, l = t.contains(a), c = false, u = r > 0, f = 0, d = 0;
    do {
      if (!a) break;
      var m = ru(e, a), p = m[0], h = m[1], g = m[2], y = h - g - s * p;
      (p || y) && su(e, a) && (f += y, d += p);
      var x = a.parentNode;
      a = x && x.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? x.host : x;
    } while (!l && a !== document.body || l && (t.contains(a) || t === a));
    return (u && Math.abs(f) < 1 || !u && Math.abs(d) < 1) && (c = true), c;
  }, un = function(e) {
    return "changedTouches" in e ? [
      e.changedTouches[0].clientX,
      e.changedTouches[0].clientY
    ] : [
      0,
      0
    ];
  }, aa = function(e) {
    return [
      e.deltaX,
      e.deltaY
    ];
  }, la = function(e) {
    return e && "current" in e ? e.current : e;
  }, Ny = function(e, t) {
    return e[0] === t[0] && e[1] === t[1];
  }, Dy = function(e) {
    return `
  .block-interactivity-`.concat(e, ` {pointer-events: none;}
  .allow-interactivity-`).concat(e, ` {pointer-events: all;}
`);
  }, Ky = 0, rt = [];
  function Wy(e) {
    var t = A.useRef([]), n = A.useRef([
      0,
      0
    ]), i = A.useRef(), o = A.useState(Ky++)[0], s = A.useState(iu)[0], r = A.useRef(e);
    A.useEffect(function() {
      r.current = e;
    }, [
      e
    ]), A.useEffect(function() {
      if (e.inert) {
        document.body.classList.add("block-interactivity-".concat(o));
        var h = oy([
          e.lockRef.current
        ], (e.shards || []).map(la), true).filter(Boolean);
        return h.forEach(function(g) {
          return g.classList.add("allow-interactivity-".concat(o));
        }), function() {
          document.body.classList.remove("block-interactivity-".concat(o)), h.forEach(function(g) {
            return g.classList.remove("allow-interactivity-".concat(o));
          });
        };
      }
    }, [
      e.inert,
      e.lockRef.current,
      e.shards
    ]);
    var a = A.useCallback(function(h, g) {
      if ("touches" in h && h.touches.length === 2 || h.type === "wheel" && h.ctrlKey) return !r.current.allowPinchZoom;
      var y = un(h), x = n.current, w = "deltaX" in h ? h.deltaX : x[0] - y[0], q = "deltaY" in h ? h.deltaY : x[1] - y[1], R, v = h.target, S = Math.abs(w) > Math.abs(q) ? "h" : "v";
      if ("touches" in h && S === "h" && v.type === "range") return false;
      var V = window.getSelection(), b = V && V.anchorNode, T = b ? b === v || b.contains(v) : false;
      if (T) return false;
      var F = ra(S, v);
      if (!F) return true;
      if (F ? R = S : (R = S === "v" ? "h" : "v", F = ra(S, v)), !F) return false;
      if (!i.current && "changedTouches" in h && (w || q) && (i.current = R), !R) return true;
      var U = i.current || R;
      return Ly(U, g, h, U === "h" ? w : q);
    }, []), l = A.useCallback(function(h) {
      var g = h;
      if (!(!rt.length || rt[rt.length - 1] !== s)) {
        var y = "deltaY" in g ? aa(g) : un(g), x = t.current.filter(function(R) {
          return R.name === g.type && (R.target === g.target || g.target === R.shadowParent) && Ny(R.delta, y);
        })[0];
        if (x && x.should) {
          g.cancelable && g.preventDefault();
          return;
        }
        if (!x) {
          var w = (r.current.shards || []).map(la).filter(Boolean).filter(function(R) {
            return R.contains(g.target);
          }), q = w.length > 0 ? a(g, w[0]) : !r.current.noIsolation;
          q && g.cancelable && g.preventDefault();
        }
      }
    }, []), c = A.useCallback(function(h, g, y, x) {
      var w = {
        name: h,
        delta: g,
        target: y,
        should: x,
        shadowParent: zy(y)
      };
      t.current.push(w), setTimeout(function() {
        t.current = t.current.filter(function(q) {
          return q !== w;
        });
      }, 1);
    }, []), u = A.useCallback(function(h) {
      n.current = un(h), i.current = void 0;
    }, []), f = A.useCallback(function(h) {
      c(h.type, aa(h), h.target, a(h, e.lockRef.current));
    }, []), d = A.useCallback(function(h) {
      c(h.type, un(h), h.target, a(h, e.lockRef.current));
    }, []);
    A.useEffect(function() {
      return rt.push(s), e.setCallbacks({
        onScrollCapture: f,
        onWheelCapture: f,
        onTouchMoveCapture: d
      }), document.addEventListener("wheel", l, st), document.addEventListener("touchmove", l, st), document.addEventListener("touchstart", u, st), function() {
        rt = rt.filter(function(h) {
          return h !== s;
        }), document.removeEventListener("wheel", l, st), document.removeEventListener("touchmove", l, st), document.removeEventListener("touchstart", u, st);
      };
    }, []);
    var m = e.removeScrollBar, p = e.inert;
    return A.createElement(A.Fragment, null, p ? A.createElement(s, {
      styles: Dy(o)
    }) : null, m ? A.createElement(Sy, {
      noRelative: e.noRelative,
      gapMode: e.gapMode
    }) : null);
  }
  function zy(e) {
    for (var t = null; e !== null; ) e instanceof ShadowRoot && (t = e.host, e = e.host), e = e.parentNode;
    return t;
  }
  const Hy = hy(nu, Wy);
  var cs = A.forwardRef(function(e, t) {
    return A.createElement(_n, ve({}, e, {
      ref: t,
      sideCar: Hy
    }));
  });
  cs.classNames = _n.classNames;
  var My = function(e) {
    if (typeof document > "u") return null;
    var t = Array.isArray(e) ? e[0] : e;
    return t.ownerDocument.body;
  }, at = /* @__PURE__ */ new WeakMap(), fn = /* @__PURE__ */ new WeakMap(), dn = {}, Ki = 0, au = function(e) {
    return e && (e.host || au(e.parentNode));
  }, ky = function(e, t) {
    return t.map(function(n) {
      if (e.contains(n)) return n;
      var i = au(n);
      return i && e.contains(i) ? i : (console.error("aria-hidden", n, "in not contained inside", e, ". Doing nothing"), null);
    }).filter(function(n) {
      return !!n;
    });
  }, Iy = function(e, t, n, i) {
    var o = ky(t, Array.isArray(e) ? e : [
      e
    ]);
    dn[n] || (dn[n] = /* @__PURE__ */ new WeakMap());
    var s = dn[n], r = [], a = /* @__PURE__ */ new Set(), l = new Set(o), c = function(f) {
      !f || a.has(f) || (a.add(f), c(f.parentNode));
    };
    o.forEach(c);
    var u = function(f) {
      !f || l.has(f) || Array.prototype.forEach.call(f.children, function(d) {
        if (a.has(d)) u(d);
        else try {
          var m = d.getAttribute(i), p = m !== null && m !== "false", h = (at.get(d) || 0) + 1, g = (s.get(d) || 0) + 1;
          at.set(d, h), s.set(d, g), r.push(d), h === 1 && p && fn.set(d, true), g === 1 && d.setAttribute(n, "true"), p || d.setAttribute(i, "true");
        } catch (y) {
          console.error("aria-hidden: cannot operate on ", d, y);
        }
      });
    };
    return u(t), a.clear(), Ki++, function() {
      r.forEach(function(f) {
        var d = at.get(f) - 1, m = s.get(f) - 1;
        at.set(f, d), s.set(f, m), d || (fn.has(f) || f.removeAttribute(i), fn.delete(f)), m || f.removeAttribute(n);
      }), Ki--, Ki || (at = /* @__PURE__ */ new WeakMap(), at = /* @__PURE__ */ new WeakMap(), fn = /* @__PURE__ */ new WeakMap(), dn = {});
    };
  }, lu = function(e, t, n) {
    n === void 0 && (n = "data-aria-hidden");
    var i = Array.from(Array.isArray(e) ? e : [
      e
    ]), o = My(e);
    return o ? (i.push.apply(i, Array.from(o.querySelectorAll("[aria-live], script"))), Iy(i, o, n, "aria-hidden")) : function() {
      return null;
    };
  }, $n = "Dialog", [cu] = Oe($n), [Zy, we] = cu($n), uu = (e) => {
    const { __scopeDialog: t, children: n, open: i, defaultOpen: o, onOpenChange: s, modal: r = true } = e, a = A.useRef(null), l = A.useRef(null), [c, u] = Ye({
      prop: i,
      defaultProp: o ?? false,
      onChange: s,
      caller: $n
    });
    return P(Zy, {
      scope: t,
      triggerRef: a,
      contentRef: l,
      contentId: Wt(),
      titleId: Wt(),
      descriptionId: Wt(),
      open: c,
      onOpenChange: u,
      onOpenToggle: A.useCallback(() => u((f) => !f), [
        u
      ]),
      modal: r,
      children: n
    });
  };
  uu.displayName = $n;
  var fu = "DialogTrigger", Jy = A.forwardRef((e, t) => {
    const { __scopeDialog: n, ...i } = e, o = we(fu, n), s = B(t, o.triggerRef);
    return P(Z.button, {
      type: "button",
      "aria-haspopup": "dialog",
      "aria-expanded": o.open,
      "aria-controls": o.contentId,
      "data-state": ds(o.open),
      ...i,
      ref: s,
      onClick: k(e.onClick, o.onOpenToggle)
    });
  });
  Jy.displayName = fu;
  var us = "DialogPortal", [Oy, du] = cu(us, {
    forceMount: void 0
  }), hu = (e) => {
    const { __scopeDialog: t, forceMount: n, children: i, container: o } = e, s = we(us, t);
    return P(Oy, {
      scope: t,
      forceMount: n,
      children: A.Children.map(i, (r) => P(Tt, {
        present: n || s.open,
        children: P(ls, {
          asChild: true,
          container: o,
          children: r
        })
      }))
    });
  };
  hu.displayName = us;
  var Wn = "DialogOverlay", mu = A.forwardRef((e, t) => {
    const n = du(Wn, e.__scopeDialog), { forceMount: i = n.forceMount, ...o } = e, s = we(Wn, e.__scopeDialog);
    return s.modal ? P(Tt, {
      present: i || s.open,
      children: P(By, {
        ...o,
        ref: t
      })
    }) : null;
  });
  mu.displayName = Wn;
  var Yy = Zt("DialogOverlay.RemoveScroll"), By = A.forwardRef((e, t) => {
    const { __scopeDialog: n, ...i } = e, o = we(Wn, n);
    return P(cs, {
      as: Yy,
      allowPinchZoom: true,
      shards: [
        o.contentRef
      ],
      children: P(Z.div, {
        "data-state": ds(o.open),
        ...i,
        ref: t,
        style: {
          pointerEvents: "auto",
          ...i.style
        }
      })
    });
  }), it = "DialogContent", pu = A.forwardRef((e, t) => {
    const n = du(it, e.__scopeDialog), { forceMount: i = n.forceMount, ...o } = e, s = we(it, e.__scopeDialog);
    return P(Tt, {
      present: i || s.open,
      children: s.modal ? P(jy, {
        ...o,
        ref: t
      }) : P(Xy, {
        ...o,
        ref: t
      })
    });
  });
  pu.displayName = it;
  var jy = A.forwardRef((e, t) => {
    const n = we(it, e.__scopeDialog), i = A.useRef(null), o = B(t, n.contentRef, i);
    return A.useEffect(() => {
      const s = i.current;
      if (s) return lu(s);
    }, []), P(gu, {
      ...e,
      ref: o,
      trapFocus: n.open,
      disableOutsidePointerEvents: true,
      onCloseAutoFocus: k(e.onCloseAutoFocus, (s) => {
        var _a3;
        s.preventDefault(), (_a3 = n.triggerRef.current) == null ? void 0 : _a3.focus();
      }),
      onPointerDownOutside: k(e.onPointerDownOutside, (s) => {
        const r = s.detail.originalEvent, a = r.button === 0 && r.ctrlKey === true;
        (r.button === 2 || a) && s.preventDefault();
      }),
      onFocusOutside: k(e.onFocusOutside, (s) => s.preventDefault())
    });
  }), Xy = A.forwardRef((e, t) => {
    const n = we(it, e.__scopeDialog), i = A.useRef(false), o = A.useRef(false);
    return P(gu, {
      ...e,
      ref: t,
      trapFocus: false,
      disableOutsidePointerEvents: false,
      onCloseAutoFocus: (s) => {
        var _a3, _b;
        (_a3 = e.onCloseAutoFocus) == null ? void 0 : _a3.call(e, s), s.defaultPrevented || (i.current || ((_b = n.triggerRef.current) == null ? void 0 : _b.focus()), s.preventDefault()), i.current = false, o.current = false;
      },
      onInteractOutside: (s) => {
        var _a3, _b;
        (_a3 = e.onInteractOutside) == null ? void 0 : _a3.call(e, s), s.defaultPrevented || (i.current = true, s.detail.originalEvent.type === "pointerdown" && (o.current = true));
        const r = s.target;
        ((_b = n.triggerRef.current) == null ? void 0 : _b.contains(r)) && s.preventDefault(), s.detail.originalEvent.type === "focusin" && o.current && s.preventDefault();
      }
    });
  }), gu = A.forwardRef((e, t) => {
    const { __scopeDialog: n, trapFocus: i, onOpenAutoFocus: o, onCloseAutoFocus: s, ...r } = e, a = we(it, n), l = A.useRef(null), c = B(t, l);
    return $c(), j(Mi, {
      children: [
        P(as, {
          asChild: true,
          loop: true,
          trapped: i,
          onMountAutoFocus: o,
          onUnmountAutoFocus: s,
          children: P(rs, {
            role: "dialog",
            id: a.contentId,
            "aria-describedby": a.descriptionId,
            "aria-labelledby": a.titleId,
            "data-state": ds(a.open),
            ...r,
            ref: c,
            onDismiss: () => a.onOpenChange(false)
          })
        }),
        j(Mi, {
          children: [
            P(Gy, {
              titleId: a.titleId
            }),
            P($y, {
              contentRef: l,
              descriptionId: a.descriptionId
            })
          ]
        })
      ]
    });
  }), fs = "DialogTitle", Au = A.forwardRef((e, t) => {
    const { __scopeDialog: n, ...i } = e, o = we(fs, n);
    return P(Z.h2, {
      id: o.titleId,
      ...i,
      ref: t
    });
  });
  Au.displayName = fs;
  var yu = "DialogDescription", xu = A.forwardRef((e, t) => {
    const { __scopeDialog: n, ...i } = e, o = we(yu, n);
    return P(Z.p, {
      id: o.descriptionId,
      ...i,
      ref: t
    });
  });
  xu.displayName = yu;
  var Pu = "DialogClose", Qy = A.forwardRef((e, t) => {
    const { __scopeDialog: n, ...i } = e, o = we(Pu, n);
    return P(Z.button, {
      type: "button",
      ...i,
      ref: t,
      onClick: k(e.onClick, () => o.onOpenChange(false))
    });
  });
  Qy.displayName = Pu;
  function ds(e) {
    return e ? "open" : "closed";
  }
  var wu = "DialogTitleWarning", [BP, qu] = Lg(wu, {
    contentName: it,
    titleName: fs,
    docsSlug: "dialog"
  }), Gy = ({ titleId: e }) => {
    const t = qu(wu), n = `\`${t.contentName}\` requires a \`${t.titleName}\` for the component to be accessible for screen reader users.

If you want to hide the \`${t.titleName}\`, you can wrap it with our VisuallyHidden component.

For more information, see https://radix-ui.com/primitives/docs/components/${t.docsSlug}`;
    return A.useEffect(() => {
      e && (document.getElementById(e) || console.error(n));
    }, [
      n,
      e
    ]), null;
  }, _y = "DialogDescriptionWarning", $y = ({ contentRef: e, descriptionId: t }) => {
    const i = `Warning: Missing \`Description\` or \`aria-describedby={undefined}\` for {${qu(_y).contentName}}.`;
    return A.useEffect(() => {
      var _a3;
      const o = (_a3 = e.current) == null ? void 0 : _a3.getAttribute("aria-describedby");
      t && o && (document.getElementById(t) || console.warn(i));
    }, [
      i,
      e,
      t
    ]), null;
  }, hs = uu, ms = hu, ps = mu, gs = pu, Jt = Au, zn = xu;
  jP = function({ open: e, onOpenChange: t, title: n, message: i, actions: o, className: s, style: r }) {
    return P(hs, {
      open: e,
      onOpenChange: t,
      modal: true,
      children: P(Vt, {
        children: e && j(ms, {
          forceMount: true,
          children: [
            P(ps, {
              asChild: true,
              forceMount: true,
              children: P(me.div, {
                initial: {
                  opacity: 0
                },
                animate: {
                  opacity: 1
                },
                exit: {
                  opacity: 0
                },
                style: {
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.4)",
                  zIndex: 9998
                }
              })
            }),
            P(gs, {
              asChild: true,
              forceMount: true,
              children: P(me.div, {
                initial: {
                  opacity: 0,
                  scale: 0.95
                },
                animate: {
                  opacity: 1,
                  scale: 1
                },
                exit: {
                  opacity: 0,
                  scale: 0.95
                },
                className: s,
                style: {
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 9999,
                  minWidth: 280,
                  maxWidth: 400,
                  ...r
                },
                children: P(Pe, {
                  cornerRadius: I.xl,
                  opacity: 0.4,
                  blur: 0.5,
                  style: {
                    width: "100%"
                  },
                  children: j("div", {
                    style: {
                      padding: L.xl,
                      textAlign: "center"
                    },
                    children: [
                      P(Jt, {
                        style: {
                          fontSize: 17,
                          fontWeight: 600,
                          margin: 0,
                          marginBottom: i ? L.sm : L.md
                        },
                        children: n
                      }),
                      i ? P(zn, {
                        style: {
                          fontSize: 13,
                          opacity: 0.7,
                          margin: 0,
                          marginBottom: L.md
                        },
                        children: i
                      }) : P(zn, {
                        style: {
                          position: "absolute",
                          width: 1,
                          height: 1,
                          padding: 0,
                          margin: -1,
                          overflow: "hidden",
                          clip: "rect(0, 0, 0, 0)",
                          whiteSpace: "nowrap",
                          borderWidth: 0
                        },
                        children: n
                      }),
                      P("div", {
                        style: {
                          height: 1,
                          background: "rgba(255,255,255,0.12)",
                          margin: `0 -${L.xl}px`
                        }
                      }),
                      P("div", {
                        style: {
                          display: "flex",
                          flexDirection: "column",
                          margin: `0 -${L.xl}px`,
                          marginBottom: -L.xl
                        },
                        children: o.map((a, l) => P("button", {
                          onClick: () => {
                            a.onPress(), t(false);
                          },
                          style: {
                            background: "none",
                            border: "none",
                            borderTop: l > 0 ? "1px solid rgba(255,255,255,0.12)" : "none",
                            cursor: "pointer",
                            width: "100%",
                            padding: `${L.md}px ${L.lg}px`,
                            fontSize: 17,
                            fontWeight: a.style === "destructive" || a.style === "primary" ? 600 : 400,
                            color: a.style === "destructive" ? "#ff453a" : "rgba(255, 255, 255, 0.95)",
                            textShadow: a.style === "destructive" ? "0 0 12px rgba(255, 69, 58, 0.4)" : "0 1px 3px rgba(0,0,0,0.6)",
                            fontFamily: "inherit"
                          },
                          children: a.label
                        }, a.label))
                      })
                    ]
                  })
                })
              })
            })
          ]
        })
      })
    });
  };
  XP = function({ open: e, onOpenChange: t, title: n, actions: i, cancelLabel: o = "Cancel", className: s, style: r }) {
    return P(hs, {
      open: e,
      onOpenChange: t,
      modal: true,
      children: P(Vt, {
        children: e && j(ms, {
          forceMount: true,
          children: [
            P(ps, {
              asChild: true,
              forceMount: true,
              children: P(me.div, {
                initial: {
                  opacity: 0
                },
                animate: {
                  opacity: 1
                },
                exit: {
                  opacity: 0
                },
                style: {
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.4)",
                  zIndex: 9998
                }
              })
            }),
            P(gs, {
              asChild: true,
              forceMount: true,
              children: P(me.div, {
                initial: {
                  y: "100%"
                },
                animate: {
                  y: 0
                },
                exit: {
                  y: "100%"
                },
                transition: {
                  type: "spring",
                  damping: 30,
                  stiffness: 300
                },
                className: s,
                style: {
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 9999,
                  padding: L.sm,
                  ...r
                },
                children: P(Pe, {
                  cornerRadius: I.xl,
                  opacity: 0.4,
                  blur: 0.5,
                  style: {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    width: "100%"
                  },
                  children: j("div", {
                    style: {
                      padding: L.lg,
                      paddingBottom: L.md
                    },
                    children: [
                      n ? P(Jt, {
                        style: {
                          fontSize: 13,
                          fontWeight: 600,
                          textAlign: "center",
                          opacity: 0.6,
                          margin: 0,
                          marginBottom: L.md,
                          textTransform: "uppercase",
                          letterSpacing: 0.5
                        },
                        children: n
                      }) : P(Jt, {
                        style: {
                          position: "absolute",
                          width: 1,
                          height: 1,
                          padding: 0,
                          margin: -1,
                          overflow: "hidden",
                          clip: "rect(0, 0, 0, 0)",
                          whiteSpace: "nowrap",
                          borderWidth: 0
                        },
                        children: "Action sheet"
                      }),
                      P(zn, {
                        style: {
                          position: "absolute",
                          width: 1,
                          height: 1,
                          padding: 0,
                          margin: -1,
                          overflow: "hidden",
                          clip: "rect(0, 0, 0, 0)",
                          whiteSpace: "nowrap",
                          borderWidth: 0
                        },
                        children: n ? `${n} actions` : "Action sheet"
                      }),
                      P("div", {
                        style: {
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          borderRadius: I.md,
                          overflow: "hidden"
                        },
                        children: i.map((a, l) => P(Ae, {
                          cornerRadius: 0,
                          tint: a.style === "destructive" ? [
                            0.8,
                            0.1,
                            0.1
                          ] : void 0,
                          onClick: () => {
                            a.onPress(), t(false);
                          },
                          style: {
                            width: "100%",
                            padding: `${L.md}px ${L.lg}px`,
                            fontSize: 17,
                            fontWeight: a.style === "primary" ? 600 : 400,
                            ...a.style === "destructive" ? {
                              color: "#ff453a",
                              fontWeight: 600,
                              textShadow: "0 0 12px rgba(255, 69, 58, 0.4)"
                            } : {},
                            ...l < i.length - 1 ? {
                              borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
                            } : {}
                          },
                          children: a.label
                        }, a.label))
                      }),
                      P("div", {
                        style: {
                          marginTop: L.md,
                          paddingTop: L.sm
                        },
                        children: P(Ae, {
                          cornerRadius: I.pill,
                          onClick: () => t(false),
                          style: {
                            width: "100%",
                            padding: `${L.md}px ${L.lg}px`,
                            fontSize: 17,
                            fontWeight: 600
                          },
                          children: o
                        })
                      })
                    ]
                  })
                })
              })
            })
          ]
        })
      })
    });
  };
  QP = function({ open: e, onOpenChange: t, children: n, height: i = "half", showDragHandle: o = true, title: s, className: r, style: a }) {
    return P(hs, {
      open: e,
      onOpenChange: t,
      modal: true,
      children: P(Vt, {
        children: e && j(ms, {
          forceMount: true,
          children: [
            P(ps, {
              asChild: true,
              forceMount: true,
              children: P(me.div, {
                initial: {
                  opacity: 0
                },
                animate: {
                  opacity: 1
                },
                exit: {
                  opacity: 0
                },
                style: {
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.4)",
                  zIndex: 9998
                },
                onClick: () => t(false)
              })
            }),
            P(gs, {
              asChild: true,
              forceMount: true,
              children: P(me.div, {
                initial: {
                  y: "100%"
                },
                animate: {
                  y: 0
                },
                exit: {
                  y: "100%"
                },
                transition: {
                  type: "spring",
                  damping: 30,
                  stiffness: 300
                },
                className: r,
                style: {
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 9999,
                  height: i === "full" ? "100vh" : "50vh",
                  ...a
                },
                children: j(Pe, {
                  cornerRadius: I.xl,
                  style: {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0
                  },
                  children: [
                    o && P(me.div, {
                      drag: "y",
                      dragConstraints: {
                        top: 0
                      },
                      dragElastic: {
                        top: 0,
                        bottom: 0.3
                      },
                      onDragEnd: (l, c) => {
                        (c.offset.y > 100 || c.velocity.y > 500) && t(false);
                      },
                      style: {
                        padding: L.md,
                        cursor: "grab",
                        touchAction: "none"
                      },
                      "data-testid": "glass-sheet-drag-handle",
                      children: P("div", {
                        style: {
                          width: 36,
                          height: 5,
                          borderRadius: 2.5,
                          background: "rgba(255,255,255,0.3)",
                          margin: "0 auto"
                        }
                      })
                    }),
                    s ? P(Jt, {
                      style: {
                        fontSize: 17,
                        fontWeight: 600,
                        margin: 0,
                        padding: `0 ${L.xl}px ${L.sm}px`,
                        textAlign: "center"
                      },
                      children: s
                    }) : P(Jt, {
                      style: {
                        position: "absolute",
                        width: 1,
                        height: 1,
                        padding: 0,
                        margin: -1,
                        overflow: "hidden",
                        clip: "rect(0, 0, 0, 0)",
                        whiteSpace: "nowrap",
                        borderWidth: 0
                      },
                      children: "Sheet"
                    }),
                    P(zn, {
                      style: {
                        position: "absolute",
                        width: 1,
                        height: 1,
                        padding: 0,
                        margin: -1,
                        overflow: "hidden",
                        clip: "rect(0, 0, 0, 0)",
                        whiteSpace: "nowrap",
                        borderWidth: 0
                      },
                      children: "Modal sheet"
                    }),
                    P("div", {
                      style: {
                        flex: 1,
                        overflow: "auto",
                        padding: `0 ${L.xl}px ${L.xl}px`
                      },
                      children: n
                    })
                  ]
                })
              })
            })
          ]
        })
      })
    });
  };
  const ex = [
    "top",
    "right",
    "bottom",
    "left"
  ], Ie = Math.min, re = Math.max, Hn = Math.round, hn = Math.floor, Te = (e) => ({
    x: e,
    y: e
  }), tx = {
    left: "right",
    right: "left",
    bottom: "top",
    top: "bottom"
  };
  function xo(e, t, n) {
    return re(e, Ie(t, n));
  }
  function Ue(e, t) {
    return typeof e == "function" ? e(t) : e;
  }
  function Le(e) {
    return e.split("-")[0];
  }
  function bt(e) {
    return e.split("-")[1];
  }
  function As(e) {
    return e === "x" ? "y" : "x";
  }
  function ys(e) {
    return e === "y" ? "height" : "width";
  }
  function Ve(e) {
    const t = e[0];
    return t === "t" || t === "b" ? "y" : "x";
  }
  function xs(e) {
    return As(Ve(e));
  }
  function nx(e, t, n) {
    n === void 0 && (n = false);
    const i = bt(e), o = xs(e), s = ys(o);
    let r = o === "x" ? i === (n ? "end" : "start") ? "right" : "left" : i === "start" ? "bottom" : "top";
    return t.reference[s] > t.floating[s] && (r = Mn(r)), [
      r,
      Mn(r)
    ];
  }
  function ix(e) {
    const t = Mn(e);
    return [
      Po(e),
      t,
      Po(t)
    ];
  }
  function Po(e) {
    return e.includes("start") ? e.replace("start", "end") : e.replace("end", "start");
  }
  const ca = [
    "left",
    "right"
  ], ua = [
    "right",
    "left"
  ], ox = [
    "top",
    "bottom"
  ], sx = [
    "bottom",
    "top"
  ];
  function rx(e, t, n) {
    switch (e) {
      case "top":
      case "bottom":
        return n ? t ? ua : ca : t ? ca : ua;
      case "left":
      case "right":
        return t ? ox : sx;
      default:
        return [];
    }
  }
  function ax(e, t, n, i) {
    const o = bt(e);
    let s = rx(Le(e), n === "start", i);
    return o && (s = s.map((r) => r + "-" + o), t && (s = s.concat(s.map(Po)))), s;
  }
  function Mn(e) {
    const t = Le(e);
    return tx[t] + e.slice(t.length);
  }
  function lx(e) {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      ...e
    };
  }
  function Ru(e) {
    return typeof e != "number" ? lx(e) : {
      top: e,
      right: e,
      bottom: e,
      left: e
    };
  }
  function kn(e) {
    const { x: t, y: n, width: i, height: o } = e;
    return {
      width: i,
      height: o,
      top: n,
      left: t,
      right: t + i,
      bottom: n + o,
      x: t,
      y: n
    };
  }
  function fa(e, t, n) {
    let { reference: i, floating: o } = e;
    const s = Ve(t), r = xs(t), a = ys(r), l = Le(t), c = s === "y", u = i.x + i.width / 2 - o.width / 2, f = i.y + i.height / 2 - o.height / 2, d = i[a] / 2 - o[a] / 2;
    let m;
    switch (l) {
      case "top":
        m = {
          x: u,
          y: i.y - o.height
        };
        break;
      case "bottom":
        m = {
          x: u,
          y: i.y + i.height
        };
        break;
      case "right":
        m = {
          x: i.x + i.width,
          y: f
        };
        break;
      case "left":
        m = {
          x: i.x - o.width,
          y: f
        };
        break;
      default:
        m = {
          x: i.x,
          y: i.y
        };
    }
    switch (bt(t)) {
      case "start":
        m[r] -= d * (n && c ? -1 : 1);
        break;
      case "end":
        m[r] += d * (n && c ? -1 : 1);
        break;
    }
    return m;
  }
  async function cx(e, t) {
    var n;
    t === void 0 && (t = {});
    const { x: i, y: o, platform: s, rects: r, elements: a, strategy: l } = e, { boundary: c = "clippingAncestors", rootBoundary: u = "viewport", elementContext: f = "floating", altBoundary: d = false, padding: m = 0 } = Ue(t, e), p = Ru(m), g = a[d ? f === "floating" ? "reference" : "floating" : f], y = kn(await s.getClippingRect({
      element: (n = await (s.isElement == null ? void 0 : s.isElement(g))) == null || n ? g : g.contextElement || await (s.getDocumentElement == null ? void 0 : s.getDocumentElement(a.floating)),
      boundary: c,
      rootBoundary: u,
      strategy: l
    })), x = f === "floating" ? {
      x: i,
      y: o,
      width: r.floating.width,
      height: r.floating.height
    } : r.reference, w = await (s.getOffsetParent == null ? void 0 : s.getOffsetParent(a.floating)), q = await (s.isElement == null ? void 0 : s.isElement(w)) ? await (s.getScale == null ? void 0 : s.getScale(w)) || {
      x: 1,
      y: 1
    } : {
      x: 1,
      y: 1
    }, R = kn(s.convertOffsetParentRelativeRectToViewportRelativeRect ? await s.convertOffsetParentRelativeRectToViewportRelativeRect({
      elements: a,
      rect: x,
      offsetParent: w,
      strategy: l
    }) : x);
    return {
      top: (y.top - R.top + p.top) / q.y,
      bottom: (R.bottom - y.bottom + p.bottom) / q.y,
      left: (y.left - R.left + p.left) / q.x,
      right: (R.right - y.right + p.right) / q.x
    };
  }
  const ux = 50, fx = async (e, t, n) => {
    const { placement: i = "bottom", strategy: o = "absolute", middleware: s = [], platform: r } = n, a = r.detectOverflow ? r : {
      ...r,
      detectOverflow: cx
    }, l = await (r.isRTL == null ? void 0 : r.isRTL(t));
    let c = await r.getElementRects({
      reference: e,
      floating: t,
      strategy: o
    }), { x: u, y: f } = fa(c, i, l), d = i, m = 0;
    const p = {};
    for (let h = 0; h < s.length; h++) {
      const g = s[h];
      if (!g) continue;
      const { name: y, fn: x } = g, { x: w, y: q, data: R, reset: v } = await x({
        x: u,
        y: f,
        initialPlacement: i,
        placement: d,
        strategy: o,
        middlewareData: p,
        rects: c,
        platform: a,
        elements: {
          reference: e,
          floating: t
        }
      });
      u = w ?? u, f = q ?? f, p[y] = {
        ...p[y],
        ...R
      }, v && m < ux && (m++, typeof v == "object" && (v.placement && (d = v.placement), v.rects && (c = v.rects === true ? await r.getElementRects({
        reference: e,
        floating: t,
        strategy: o
      }) : v.rects), { x: u, y: f } = fa(c, d, l)), h = -1);
    }
    return {
      x: u,
      y: f,
      placement: d,
      strategy: o,
      middlewareData: p
    };
  }, dx = (e) => ({
    name: "arrow",
    options: e,
    async fn(t) {
      const { x: n, y: i, placement: o, rects: s, platform: r, elements: a, middlewareData: l } = t, { element: c, padding: u = 0 } = Ue(e, t) || {};
      if (c == null) return {};
      const f = Ru(u), d = {
        x: n,
        y: i
      }, m = xs(o), p = ys(m), h = await r.getDimensions(c), g = m === "y", y = g ? "top" : "left", x = g ? "bottom" : "right", w = g ? "clientHeight" : "clientWidth", q = s.reference[p] + s.reference[m] - d[m] - s.floating[p], R = d[m] - s.reference[m], v = await (r.getOffsetParent == null ? void 0 : r.getOffsetParent(c));
      let S = v ? v[w] : 0;
      (!S || !await (r.isElement == null ? void 0 : r.isElement(v))) && (S = a.floating[w] || s.floating[p]);
      const V = q / 2 - R / 2, b = S / 2 - h[p] / 2 - 1, T = Ie(f[y], b), F = Ie(f[x], b), U = T, D = S - h[p] - F, K = S / 2 - h[p] / 2 + V, z = xo(U, K, D), N = !l.arrow && bt(o) != null && K !== z && s.reference[p] / 2 - (K < U ? T : F) - h[p] / 2 < 0, H = N ? K < U ? K - U : K - D : 0;
      return {
        [m]: d[m] + H,
        data: {
          [m]: z,
          centerOffset: K - z - H,
          ...N && {
            alignmentOffset: H
          }
        },
        reset: N
      };
    }
  }), hx = function(e) {
    return e === void 0 && (e = {}), {
      name: "flip",
      options: e,
      async fn(t) {
        var n, i;
        const { placement: o, middlewareData: s, rects: r, initialPlacement: a, platform: l, elements: c } = t, { mainAxis: u = true, crossAxis: f = true, fallbackPlacements: d, fallbackStrategy: m = "bestFit", fallbackAxisSideDirection: p = "none", flipAlignment: h = true, ...g } = Ue(e, t);
        if ((n = s.arrow) != null && n.alignmentOffset) return {};
        const y = Le(o), x = Ve(a), w = Le(a) === a, q = await (l.isRTL == null ? void 0 : l.isRTL(c.floating)), R = d || (w || !h ? [
          Mn(a)
        ] : ix(a)), v = p !== "none";
        !d && v && R.push(...ax(a, h, p, q));
        const S = [
          a,
          ...R
        ], V = await l.detectOverflow(t, g), b = [];
        let T = ((i = s.flip) == null ? void 0 : i.overflows) || [];
        if (u && b.push(V[y]), f) {
          const K = nx(o, r, q);
          b.push(V[K[0]], V[K[1]]);
        }
        if (T = [
          ...T,
          {
            placement: o,
            overflows: b
          }
        ], !b.every((K) => K <= 0)) {
          var F, U;
          const K = (((F = s.flip) == null ? void 0 : F.index) || 0) + 1, z = S[K];
          if (z && (!(f === "alignment" ? x !== Ve(z) : false) || T.every((C) => Ve(C.placement) === x ? C.overflows[0] > 0 : true))) return {
            data: {
              index: K,
              overflows: T
            },
            reset: {
              placement: z
            }
          };
          let N = (U = T.filter((H) => H.overflows[0] <= 0).sort((H, C) => H.overflows[1] - C.overflows[1])[0]) == null ? void 0 : U.placement;
          if (!N) switch (m) {
            case "bestFit": {
              var D;
              const H = (D = T.filter((C) => {
                if (v) {
                  const W = Ve(C.placement);
                  return W === x || W === "y";
                }
                return true;
              }).map((C) => [
                C.placement,
                C.overflows.filter((W) => W > 0).reduce((W, X) => W + X, 0)
              ]).sort((C, W) => C[1] - W[1])[0]) == null ? void 0 : D[0];
              H && (N = H);
              break;
            }
            case "initialPlacement":
              N = a;
              break;
          }
          if (o !== N) return {
            reset: {
              placement: N
            }
          };
        }
        return {};
      }
    };
  };
  function da(e, t) {
    return {
      top: e.top - t.height,
      right: e.right - t.width,
      bottom: e.bottom - t.height,
      left: e.left - t.width
    };
  }
  function ha(e) {
    return ex.some((t) => e[t] >= 0);
  }
  const mx = function(e) {
    return e === void 0 && (e = {}), {
      name: "hide",
      options: e,
      async fn(t) {
        const { rects: n, platform: i } = t, { strategy: o = "referenceHidden", ...s } = Ue(e, t);
        switch (o) {
          case "referenceHidden": {
            const r = await i.detectOverflow(t, {
              ...s,
              elementContext: "reference"
            }), a = da(r, n.reference);
            return {
              data: {
                referenceHiddenOffsets: a,
                referenceHidden: ha(a)
              }
            };
          }
          case "escaped": {
            const r = await i.detectOverflow(t, {
              ...s,
              altBoundary: true
            }), a = da(r, n.floating);
            return {
              data: {
                escapedOffsets: a,
                escaped: ha(a)
              }
            };
          }
          default:
            return {};
        }
      }
    };
  }, vu = /* @__PURE__ */ new Set([
    "left",
    "top"
  ]);
  async function px(e, t) {
    const { placement: n, platform: i, elements: o } = e, s = await (i.isRTL == null ? void 0 : i.isRTL(o.floating)), r = Le(n), a = bt(n), l = Ve(n) === "y", c = vu.has(r) ? -1 : 1, u = s && l ? -1 : 1, f = Ue(t, e);
    let { mainAxis: d, crossAxis: m, alignmentAxis: p } = typeof f == "number" ? {
      mainAxis: f,
      crossAxis: 0,
      alignmentAxis: null
    } : {
      mainAxis: f.mainAxis || 0,
      crossAxis: f.crossAxis || 0,
      alignmentAxis: f.alignmentAxis
    };
    return a && typeof p == "number" && (m = a === "end" ? p * -1 : p), l ? {
      x: m * u,
      y: d * c
    } : {
      x: d * c,
      y: m * u
    };
  }
  const gx = function(e) {
    return e === void 0 && (e = 0), {
      name: "offset",
      options: e,
      async fn(t) {
        var n, i;
        const { x: o, y: s, placement: r, middlewareData: a } = t, l = await px(t, e);
        return r === ((n = a.offset) == null ? void 0 : n.placement) && (i = a.arrow) != null && i.alignmentOffset ? {} : {
          x: o + l.x,
          y: s + l.y,
          data: {
            ...l,
            placement: r
          }
        };
      }
    };
  }, Ax = function(e) {
    return e === void 0 && (e = {}), {
      name: "shift",
      options: e,
      async fn(t) {
        const { x: n, y: i, placement: o, platform: s } = t, { mainAxis: r = true, crossAxis: a = false, limiter: l = {
          fn: (y) => {
            let { x, y: w } = y;
            return {
              x,
              y: w
            };
          }
        }, ...c } = Ue(e, t), u = {
          x: n,
          y: i
        }, f = await s.detectOverflow(t, c), d = Ve(Le(o)), m = As(d);
        let p = u[m], h = u[d];
        if (r) {
          const y = m === "y" ? "top" : "left", x = m === "y" ? "bottom" : "right", w = p + f[y], q = p - f[x];
          p = xo(w, p, q);
        }
        if (a) {
          const y = d === "y" ? "top" : "left", x = d === "y" ? "bottom" : "right", w = h + f[y], q = h - f[x];
          h = xo(w, h, q);
        }
        const g = l.fn({
          ...t,
          [m]: p,
          [d]: h
        });
        return {
          ...g,
          data: {
            x: g.x - n,
            y: g.y - i,
            enabled: {
              [m]: r,
              [d]: a
            }
          }
        };
      }
    };
  }, yx = function(e) {
    return e === void 0 && (e = {}), {
      options: e,
      fn(t) {
        const { x: n, y: i, placement: o, rects: s, middlewareData: r } = t, { offset: a = 0, mainAxis: l = true, crossAxis: c = true } = Ue(e, t), u = {
          x: n,
          y: i
        }, f = Ve(o), d = As(f);
        let m = u[d], p = u[f];
        const h = Ue(a, t), g = typeof h == "number" ? {
          mainAxis: h,
          crossAxis: 0
        } : {
          mainAxis: 0,
          crossAxis: 0,
          ...h
        };
        if (l) {
          const w = d === "y" ? "height" : "width", q = s.reference[d] - s.floating[w] + g.mainAxis, R = s.reference[d] + s.reference[w] - g.mainAxis;
          m < q ? m = q : m > R && (m = R);
        }
        if (c) {
          var y, x;
          const w = d === "y" ? "width" : "height", q = vu.has(Le(o)), R = s.reference[f] - s.floating[w] + (q && ((y = r.offset) == null ? void 0 : y[f]) || 0) + (q ? 0 : g.crossAxis), v = s.reference[f] + s.reference[w] + (q ? 0 : ((x = r.offset) == null ? void 0 : x[f]) || 0) - (q ? g.crossAxis : 0);
          p < R ? p = R : p > v && (p = v);
        }
        return {
          [d]: m,
          [f]: p
        };
      }
    };
  }, xx = function(e) {
    return e === void 0 && (e = {}), {
      name: "size",
      options: e,
      async fn(t) {
        var n, i;
        const { placement: o, rects: s, platform: r, elements: a } = t, { apply: l = () => {
        }, ...c } = Ue(e, t), u = await r.detectOverflow(t, c), f = Le(o), d = bt(o), m = Ve(o) === "y", { width: p, height: h } = s.floating;
        let g, y;
        f === "top" || f === "bottom" ? (g = f, y = d === (await (r.isRTL == null ? void 0 : r.isRTL(a.floating)) ? "start" : "end") ? "left" : "right") : (y = f, g = d === "end" ? "top" : "bottom");
        const x = h - u.top - u.bottom, w = p - u.left - u.right, q = Ie(h - u[g], x), R = Ie(p - u[y], w), v = !t.middlewareData.shift;
        let S = q, V = R;
        if ((n = t.middlewareData.shift) != null && n.enabled.x && (V = w), (i = t.middlewareData.shift) != null && i.enabled.y && (S = x), v && !d) {
          const T = re(u.left, 0), F = re(u.right, 0), U = re(u.top, 0), D = re(u.bottom, 0);
          m ? V = p - 2 * (T !== 0 || F !== 0 ? T + F : re(u.left, u.right)) : S = h - 2 * (U !== 0 || D !== 0 ? U + D : re(u.top, u.bottom));
        }
        await l({
          ...t,
          availableWidth: V,
          availableHeight: S
        });
        const b = await r.getDimensions(a.floating);
        return p !== b.width || h !== b.height ? {
          reset: {
            rects: true
          }
        } : {};
      }
    };
  };
  function ei() {
    return typeof window < "u";
  }
  function Ft(e) {
    return Vu(e) ? (e.nodeName || "").toLowerCase() : "#document";
  }
  function ae(e) {
    var t;
    return (e == null || (t = e.ownerDocument) == null ? void 0 : t.defaultView) || window;
  }
  function Fe(e) {
    var t;
    return (t = (Vu(e) ? e.ownerDocument : e.document) || window.document) == null ? void 0 : t.documentElement;
  }
  function Vu(e) {
    return ei() ? e instanceof Node || e instanceof ae(e).Node : false;
  }
  function ye(e) {
    return ei() ? e instanceof Element || e instanceof ae(e).Element : false;
  }
  function De(e) {
    return ei() ? e instanceof HTMLElement || e instanceof ae(e).HTMLElement : false;
  }
  function ma(e) {
    return !ei() || typeof ShadowRoot > "u" ? false : e instanceof ShadowRoot || e instanceof ae(e).ShadowRoot;
  }
  function Gt(e) {
    const { overflow: t, overflowX: n, overflowY: i, display: o } = xe(e);
    return /auto|scroll|overlay|hidden|clip/.test(t + i + n) && o !== "inline" && o !== "contents";
  }
  function Px(e) {
    return /^(table|td|th)$/.test(Ft(e));
  }
  function ti(e) {
    try {
      if (e.matches(":popover-open")) return true;
    } catch {
    }
    try {
      return e.matches(":modal");
    } catch {
      return false;
    }
  }
  const wx = /transform|translate|scale|rotate|perspective|filter/, qx = /paint|layout|strict|content/, Qe = (e) => !!e && e !== "none";
  let Wi;
  function Ps(e) {
    const t = ye(e) ? xe(e) : e;
    return Qe(t.transform) || Qe(t.translate) || Qe(t.scale) || Qe(t.rotate) || Qe(t.perspective) || !ws() && (Qe(t.backdropFilter) || Qe(t.filter)) || wx.test(t.willChange || "") || qx.test(t.contain || "");
  }
  function Rx(e) {
    let t = Ze(e);
    for (; De(t) && !yt(t); ) {
      if (Ps(t)) return t;
      if (ti(t)) return null;
      t = Ze(t);
    }
    return null;
  }
  function ws() {
    return Wi == null && (Wi = typeof CSS < "u" && CSS.supports && CSS.supports("-webkit-backdrop-filter", "none")), Wi;
  }
  function yt(e) {
    return /^(html|body|#document)$/.test(Ft(e));
  }
  function xe(e) {
    return ae(e).getComputedStyle(e);
  }
  function ni(e) {
    return ye(e) ? {
      scrollLeft: e.scrollLeft,
      scrollTop: e.scrollTop
    } : {
      scrollLeft: e.scrollX,
      scrollTop: e.scrollY
    };
  }
  function Ze(e) {
    if (Ft(e) === "html") return e;
    const t = e.assignedSlot || e.parentNode || ma(e) && e.host || Fe(e);
    return ma(t) ? t.host : t;
  }
  function Su(e) {
    const t = Ze(e);
    return yt(t) ? e.ownerDocument ? e.ownerDocument.body : e.body : De(t) && Gt(t) ? t : Su(t);
  }
  function Ot(e, t, n) {
    var i;
    t === void 0 && (t = []), n === void 0 && (n = true);
    const o = Su(e), s = o === ((i = e.ownerDocument) == null ? void 0 : i.body), r = ae(o);
    if (s) {
      const a = wo(r);
      return t.concat(r, r.visualViewport || [], Gt(o) ? o : [], a && n ? Ot(a) : []);
    } else return t.concat(o, Ot(o, [], n));
  }
  function wo(e) {
    return e.parent && Object.getPrototypeOf(e.parent) ? e.frameElement : null;
  }
  function Tu(e) {
    const t = xe(e);
    let n = parseFloat(t.width) || 0, i = parseFloat(t.height) || 0;
    const o = De(e), s = o ? e.offsetWidth : n, r = o ? e.offsetHeight : i, a = Hn(n) !== s || Hn(i) !== r;
    return a && (n = s, i = r), {
      width: n,
      height: i,
      $: a
    };
  }
  function qs(e) {
    return ye(e) ? e : e.contextElement;
  }
  function mt(e) {
    const t = qs(e);
    if (!De(t)) return Te(1);
    const n = t.getBoundingClientRect(), { width: i, height: o, $: s } = Tu(t);
    let r = (s ? Hn(n.width) : n.width) / i, a = (s ? Hn(n.height) : n.height) / o;
    return (!r || !Number.isFinite(r)) && (r = 1), (!a || !Number.isFinite(a)) && (a = 1), {
      x: r,
      y: a
    };
  }
  const vx = Te(0);
  function bu(e) {
    const t = ae(e);
    return !ws() || !t.visualViewport ? vx : {
      x: t.visualViewport.offsetLeft,
      y: t.visualViewport.offsetTop
    };
  }
  function Vx(e, t, n) {
    return t === void 0 && (t = false), !n || t && n !== ae(e) ? false : t;
  }
  function ot(e, t, n, i) {
    t === void 0 && (t = false), n === void 0 && (n = false);
    const o = e.getBoundingClientRect(), s = qs(e);
    let r = Te(1);
    t && (i ? ye(i) && (r = mt(i)) : r = mt(e));
    const a = Vx(s, n, i) ? bu(s) : Te(0);
    let l = (o.left + a.x) / r.x, c = (o.top + a.y) / r.y, u = o.width / r.x, f = o.height / r.y;
    if (s) {
      const d = ae(s), m = i && ye(i) ? ae(i) : i;
      let p = d, h = wo(p);
      for (; h && i && m !== p; ) {
        const g = mt(h), y = h.getBoundingClientRect(), x = xe(h), w = y.left + (h.clientLeft + parseFloat(x.paddingLeft)) * g.x, q = y.top + (h.clientTop + parseFloat(x.paddingTop)) * g.y;
        l *= g.x, c *= g.y, u *= g.x, f *= g.y, l += w, c += q, p = ae(h), h = wo(p);
      }
    }
    return kn({
      width: u,
      height: f,
      x: l,
      y: c
    });
  }
  function ii(e, t) {
    const n = ni(e).scrollLeft;
    return t ? t.left + n : ot(Fe(e)).left + n;
  }
  function Fu(e, t) {
    const n = e.getBoundingClientRect(), i = n.left + t.scrollLeft - ii(e, n), o = n.top + t.scrollTop;
    return {
      x: i,
      y: o
    };
  }
  function Sx(e) {
    let { elements: t, rect: n, offsetParent: i, strategy: o } = e;
    const s = o === "fixed", r = Fe(i), a = t ? ti(t.floating) : false;
    if (i === r || a && s) return n;
    let l = {
      scrollLeft: 0,
      scrollTop: 0
    }, c = Te(1);
    const u = Te(0), f = De(i);
    if ((f || !f && !s) && ((Ft(i) !== "body" || Gt(r)) && (l = ni(i)), f)) {
      const m = ot(i);
      c = mt(i), u.x = m.x + i.clientLeft, u.y = m.y + i.clientTop;
    }
    const d = r && !f && !s ? Fu(r, l) : Te(0);
    return {
      width: n.width * c.x,
      height: n.height * c.y,
      x: n.x * c.x - l.scrollLeft * c.x + u.x + d.x,
      y: n.y * c.y - l.scrollTop * c.y + u.y + d.y
    };
  }
  function Tx(e) {
    return Array.from(e.getClientRects());
  }
  function bx(e) {
    const t = Fe(e), n = ni(e), i = e.ownerDocument.body, o = re(t.scrollWidth, t.clientWidth, i.scrollWidth, i.clientWidth), s = re(t.scrollHeight, t.clientHeight, i.scrollHeight, i.clientHeight);
    let r = -n.scrollLeft + ii(e);
    const a = -n.scrollTop;
    return xe(i).direction === "rtl" && (r += re(t.clientWidth, i.clientWidth) - o), {
      width: o,
      height: s,
      x: r,
      y: a
    };
  }
  const pa = 25;
  function Fx(e, t) {
    const n = ae(e), i = Fe(e), o = n.visualViewport;
    let s = i.clientWidth, r = i.clientHeight, a = 0, l = 0;
    if (o) {
      s = o.width, r = o.height;
      const u = ws();
      (!u || u && t === "fixed") && (a = o.offsetLeft, l = o.offsetTop);
    }
    const c = ii(i);
    if (c <= 0) {
      const u = i.ownerDocument, f = u.body, d = getComputedStyle(f), m = u.compatMode === "CSS1Compat" && parseFloat(d.marginLeft) + parseFloat(d.marginRight) || 0, p = Math.abs(i.clientWidth - f.clientWidth - m);
      p <= pa && (s -= p);
    } else c <= pa && (s += c);
    return {
      width: s,
      height: r,
      x: a,
      y: l
    };
  }
  function Ex(e, t) {
    const n = ot(e, true, t === "fixed"), i = n.top + e.clientTop, o = n.left + e.clientLeft, s = De(e) ? mt(e) : Te(1), r = e.clientWidth * s.x, a = e.clientHeight * s.y, l = o * s.x, c = i * s.y;
    return {
      width: r,
      height: a,
      x: l,
      y: c
    };
  }
  function ga(e, t, n) {
    let i;
    if (t === "viewport") i = Fx(e, n);
    else if (t === "document") i = bx(Fe(e));
    else if (ye(t)) i = Ex(t, n);
    else {
      const o = bu(e);
      i = {
        x: t.x - o.x,
        y: t.y - o.y,
        width: t.width,
        height: t.height
      };
    }
    return kn(i);
  }
  function Eu(e, t) {
    const n = Ze(e);
    return n === t || !ye(n) || yt(n) ? false : xe(n).position === "fixed" || Eu(n, t);
  }
  function Cx(e, t) {
    const n = t.get(e);
    if (n) return n;
    let i = Ot(e, [], false).filter((a) => ye(a) && Ft(a) !== "body"), o = null;
    const s = xe(e).position === "fixed";
    let r = s ? Ze(e) : e;
    for (; ye(r) && !yt(r); ) {
      const a = xe(r), l = Ps(r);
      !l && a.position === "fixed" && (o = null), (s ? !l && !o : !l && a.position === "static" && !!o && (o.position === "absolute" || o.position === "fixed") || Gt(r) && !l && Eu(e, r)) ? i = i.filter((u) => u !== r) : o = a, r = Ze(r);
    }
    return t.set(e, i), i;
  }
  function Ux(e) {
    let { element: t, boundary: n, rootBoundary: i, strategy: o } = e;
    const r = [
      ...n === "clippingAncestors" ? ti(t) ? [] : Cx(t, this._c) : [].concat(n),
      i
    ], a = ga(t, r[0], o);
    let l = a.top, c = a.right, u = a.bottom, f = a.left;
    for (let d = 1; d < r.length; d++) {
      const m = ga(t, r[d], o);
      l = re(m.top, l), c = Ie(m.right, c), u = Ie(m.bottom, u), f = re(m.left, f);
    }
    return {
      width: c - f,
      height: u - l,
      x: f,
      y: l
    };
  }
  function Lx(e) {
    const { width: t, height: n } = Tu(e);
    return {
      width: t,
      height: n
    };
  }
  function Nx(e, t, n) {
    const i = De(t), o = Fe(t), s = n === "fixed", r = ot(e, true, s, t);
    let a = {
      scrollLeft: 0,
      scrollTop: 0
    };
    const l = Te(0);
    function c() {
      l.x = ii(o);
    }
    if (i || !i && !s) if ((Ft(t) !== "body" || Gt(o)) && (a = ni(t)), i) {
      const m = ot(t, true, s, t);
      l.x = m.x + t.clientLeft, l.y = m.y + t.clientTop;
    } else o && c();
    s && !i && o && c();
    const u = o && !i && !s ? Fu(o, a) : Te(0), f = r.left + a.scrollLeft - l.x - u.x, d = r.top + a.scrollTop - l.y - u.y;
    return {
      x: f,
      y: d,
      width: r.width,
      height: r.height
    };
  }
  function zi(e) {
    return xe(e).position === "static";
  }
  function Aa(e, t) {
    if (!De(e) || xe(e).position === "fixed") return null;
    if (t) return t(e);
    let n = e.offsetParent;
    return Fe(e) === n && (n = n.ownerDocument.body), n;
  }
  function Cu(e, t) {
    const n = ae(e);
    if (ti(e)) return n;
    if (!De(e)) {
      let o = Ze(e);
      for (; o && !yt(o); ) {
        if (ye(o) && !zi(o)) return o;
        o = Ze(o);
      }
      return n;
    }
    let i = Aa(e, t);
    for (; i && Px(i) && zi(i); ) i = Aa(i, t);
    return i && yt(i) && zi(i) && !Ps(i) ? n : i || Rx(e) || n;
  }
  const Dx = async function(e) {
    const t = this.getOffsetParent || Cu, n = this.getDimensions, i = await n(e.floating);
    return {
      reference: Nx(e.reference, await t(e.floating), e.strategy),
      floating: {
        x: 0,
        y: 0,
        width: i.width,
        height: i.height
      }
    };
  };
  function Kx(e) {
    return xe(e).direction === "rtl";
  }
  const Wx = {
    convertOffsetParentRelativeRectToViewportRelativeRect: Sx,
    getDocumentElement: Fe,
    getClippingRect: Ux,
    getOffsetParent: Cu,
    getElementRects: Dx,
    getClientRects: Tx,
    getDimensions: Lx,
    getScale: mt,
    isElement: ye,
    isRTL: Kx
  };
  function Uu(e, t) {
    return e.x === t.x && e.y === t.y && e.width === t.width && e.height === t.height;
  }
  function zx(e, t) {
    let n = null, i;
    const o = Fe(e);
    function s() {
      var a;
      clearTimeout(i), (a = n) == null || a.disconnect(), n = null;
    }
    function r(a, l) {
      a === void 0 && (a = false), l === void 0 && (l = 1), s();
      const c = e.getBoundingClientRect(), { left: u, top: f, width: d, height: m } = c;
      if (a || t(), !d || !m) return;
      const p = hn(f), h = hn(o.clientWidth - (u + d)), g = hn(o.clientHeight - (f + m)), y = hn(u), w = {
        rootMargin: -p + "px " + -h + "px " + -g + "px " + -y + "px",
        threshold: re(0, Ie(1, l)) || 1
      };
      let q = true;
      function R(v) {
        const S = v[0].intersectionRatio;
        if (S !== l) {
          if (!q) return r();
          S ? r(false, S) : i = setTimeout(() => {
            r(false, 1e-7);
          }, 1e3);
        }
        S === 1 && !Uu(c, e.getBoundingClientRect()) && r(), q = false;
      }
      try {
        n = new IntersectionObserver(R, {
          ...w,
          root: o.ownerDocument
        });
      } catch {
        n = new IntersectionObserver(R, w);
      }
      n.observe(e);
    }
    return r(true), s;
  }
  function Hx(e, t, n, i) {
    i === void 0 && (i = {});
    const { ancestorScroll: o = true, ancestorResize: s = true, elementResize: r = typeof ResizeObserver == "function", layoutShift: a = typeof IntersectionObserver == "function", animationFrame: l = false } = i, c = qs(e), u = o || s ? [
      ...c ? Ot(c) : [],
      ...t ? Ot(t) : []
    ] : [];
    u.forEach((y) => {
      o && y.addEventListener("scroll", n, {
        passive: true
      }), s && y.addEventListener("resize", n);
    });
    const f = c && a ? zx(c, n) : null;
    let d = -1, m = null;
    r && (m = new ResizeObserver((y) => {
      let [x] = y;
      x && x.target === c && m && t && (m.unobserve(t), cancelAnimationFrame(d), d = requestAnimationFrame(() => {
        var w;
        (w = m) == null || w.observe(t);
      })), n();
    }), c && !l && m.observe(c), t && m.observe(t));
    let p, h = l ? ot(e) : null;
    l && g();
    function g() {
      const y = ot(e);
      h && !Uu(h, y) && n(), h = y, p = requestAnimationFrame(g);
    }
    return n(), () => {
      var y;
      u.forEach((x) => {
        o && x.removeEventListener("scroll", n), s && x.removeEventListener("resize", n);
      }), f == null ? void 0 : f(), (y = m) == null || y.disconnect(), m = null, l && cancelAnimationFrame(p);
    };
  }
  const Mx = gx, kx = Ax, Ix = hx, Zx = xx, Jx = mx, ya = dx, Ox = yx, Yx = (e, t, n) => {
    const i = /* @__PURE__ */ new Map(), o = {
      platform: Wx,
      ...n
    }, s = {
      ...o.platform,
      _c: i
    };
    return fx(e, t, {
      ...o,
      platform: s
    });
  };
  var Bx = typeof document < "u", jx = function() {
  }, Vn = Bx ? Pa : jx;
  function In(e, t) {
    if (e === t) return true;
    if (typeof e != typeof t) return false;
    if (typeof e == "function" && e.toString() === t.toString()) return true;
    let n, i, o;
    if (e && t && typeof e == "object") {
      if (Array.isArray(e)) {
        if (n = e.length, n !== t.length) return false;
        for (i = n; i-- !== 0; ) if (!In(e[i], t[i])) return false;
        return true;
      }
      if (o = Object.keys(e), n = o.length, n !== Object.keys(t).length) return false;
      for (i = n; i-- !== 0; ) if (!{}.hasOwnProperty.call(t, o[i])) return false;
      for (i = n; i-- !== 0; ) {
        const s = o[i];
        if (!(s === "_owner" && e.$$typeof) && !In(e[s], t[s])) return false;
      }
      return true;
    }
    return e !== e && t !== t;
  }
  function Lu(e) {
    return typeof window > "u" ? 1 : (e.ownerDocument.defaultView || window).devicePixelRatio || 1;
  }
  function xa(e, t) {
    const n = Lu(e);
    return Math.round(t * n) / n;
  }
  function Hi(e) {
    const t = A.useRef(e);
    return Vn(() => {
      t.current = e;
    }), t;
  }
  function Xx(e) {
    e === void 0 && (e = {});
    const { placement: t = "bottom", strategy: n = "absolute", middleware: i = [], platform: o, elements: { reference: s, floating: r } = {}, transform: a = true, whileElementsMounted: l, open: c } = e, [u, f] = A.useState({
      x: 0,
      y: 0,
      strategy: n,
      placement: t,
      middlewareData: {},
      isPositioned: false
    }), [d, m] = A.useState(i);
    In(d, i) || m(i);
    const [p, h] = A.useState(null), [g, y] = A.useState(null), x = A.useCallback((C) => {
      C !== v.current && (v.current = C, h(C));
    }, []), w = A.useCallback((C) => {
      C !== S.current && (S.current = C, y(C));
    }, []), q = s || p, R = r || g, v = A.useRef(null), S = A.useRef(null), V = A.useRef(u), b = l != null, T = Hi(l), F = Hi(o), U = Hi(c), D = A.useCallback(() => {
      if (!v.current || !S.current) return;
      const C = {
        placement: t,
        strategy: n,
        middleware: d
      };
      F.current && (C.platform = F.current), Yx(v.current, S.current, C).then((W) => {
        const X = {
          ...W,
          isPositioned: U.current !== false
        };
        K.current && !In(V.current, X) && (V.current = X, qa.flushSync(() => {
          f(X);
        }));
      });
    }, [
      d,
      t,
      n,
      F,
      U
    ]);
    Vn(() => {
      c === false && V.current.isPositioned && (V.current.isPositioned = false, f((C) => ({
        ...C,
        isPositioned: false
      })));
    }, [
      c
    ]);
    const K = A.useRef(false);
    Vn(() => (K.current = true, () => {
      K.current = false;
    }), []), Vn(() => {
      if (q && (v.current = q), R && (S.current = R), q && R) {
        if (T.current) return T.current(q, R, D);
        D();
      }
    }, [
      q,
      R,
      D,
      T,
      b
    ]);
    const z = A.useMemo(() => ({
      reference: v,
      floating: S,
      setReference: x,
      setFloating: w
    }), [
      x,
      w
    ]), N = A.useMemo(() => ({
      reference: q,
      floating: R
    }), [
      q,
      R
    ]), H = A.useMemo(() => {
      const C = {
        position: n,
        left: 0,
        top: 0
      };
      if (!N.floating) return C;
      const W = xa(N.floating, u.x), X = xa(N.floating, u.y);
      return a ? {
        ...C,
        transform: "translate(" + W + "px, " + X + "px)",
        ...Lu(N.floating) >= 1.5 && {
          willChange: "transform"
        }
      } : {
        position: n,
        left: W,
        top: X
      };
    }, [
      n,
      a,
      N.floating,
      u.x,
      u.y
    ]);
    return A.useMemo(() => ({
      ...u,
      update: D,
      refs: z,
      elements: N,
      floatingStyles: H
    }), [
      u,
      D,
      z,
      N,
      H
    ]);
  }
  const Qx = (e) => {
    function t(n) {
      return {}.hasOwnProperty.call(n, "current");
    }
    return {
      name: "arrow",
      options: e,
      fn(n) {
        const { element: i, padding: o } = typeof e == "function" ? e(n) : e;
        return i && t(i) ? i.current != null ? ya({
          element: i.current,
          padding: o
        }).fn(n) : {} : i ? ya({
          element: i,
          padding: o
        }).fn(n) : {};
      }
    };
  }, Gx = (e, t) => {
    const n = Mx(e);
    return {
      name: n.name,
      fn: n.fn,
      options: [
        e,
        t
      ]
    };
  }, _x = (e, t) => {
    const n = kx(e);
    return {
      name: n.name,
      fn: n.fn,
      options: [
        e,
        t
      ]
    };
  }, $x = (e, t) => ({
    fn: Ox(e).fn,
    options: [
      e,
      t
    ]
  }), eP = (e, t) => {
    const n = Ix(e);
    return {
      name: n.name,
      fn: n.fn,
      options: [
        e,
        t
      ]
    };
  }, tP = (e, t) => {
    const n = Zx(e);
    return {
      name: n.name,
      fn: n.fn,
      options: [
        e,
        t
      ]
    };
  }, nP = (e, t) => {
    const n = Jx(e);
    return {
      name: n.name,
      fn: n.fn,
      options: [
        e,
        t
      ]
    };
  }, iP = (e, t) => {
    const n = Qx(e);
    return {
      name: n.name,
      fn: n.fn,
      options: [
        e,
        t
      ]
    };
  };
  var oP = "Arrow", Nu = A.forwardRef((e, t) => {
    const { children: n, width: i = 10, height: o = 5, ...s } = e;
    return P(Z.svg, {
      ...s,
      ref: t,
      width: i,
      height: o,
      viewBox: "0 0 30 10",
      preserveAspectRatio: "none",
      children: e.asChild ? n : P("polygon", {
        points: "0,0 30,0 15,10"
      })
    });
  });
  Nu.displayName = oP;
  var sP = Nu, Rs = "Popper", [Du, Ku] = Oe(Rs), [rP, Wu] = Du(Rs), zu = (e) => {
    const { __scopePopper: t, children: n } = e, [i, o] = A.useState(null);
    return P(rP, {
      scope: t,
      anchor: i,
      onAnchorChange: o,
      children: n
    });
  };
  zu.displayName = Rs;
  var Hu = "PopperAnchor", Mu = A.forwardRef((e, t) => {
    const { __scopePopper: n, virtualRef: i, ...o } = e, s = Wu(Hu, n), r = A.useRef(null), a = B(t, r), l = A.useRef(null);
    return A.useEffect(() => {
      const c = l.current;
      l.current = (i == null ? void 0 : i.current) || r.current, c !== l.current && s.onAnchorChange(l.current);
    }), i ? null : P(Z.div, {
      ...o,
      ref: a
    });
  });
  Mu.displayName = Hu;
  var vs = "PopperContent", [aP, lP] = Du(vs), ku = A.forwardRef((e, t) => {
    var _a3, _b, _c2, _d2, _e2, _f2;
    const { __scopePopper: n, side: i = "bottom", sideOffset: o = 0, align: s = "center", alignOffset: r = 0, arrowPadding: a = 0, avoidCollisions: l = true, collisionBoundary: c = [], collisionPadding: u = 0, sticky: f = "partial", hideWhenDetached: d = false, updatePositionStrategy: m = "optimized", onPlaced: p, ...h } = e, g = Wu(vs, n), [y, x] = A.useState(null), w = B(t, (Et) => x(Et)), [q, R] = A.useState(null), v = ns(q), S = (v == null ? void 0 : v.width) ?? 0, V = (v == null ? void 0 : v.height) ?? 0, b = i + (s !== "center" ? "-" + s : ""), T = typeof u == "number" ? u : {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      ...u
    }, F = Array.isArray(c) ? c : [
      c
    ], U = F.length > 0, D = {
      padding: T,
      boundary: F.filter(uP),
      altBoundary: U
    }, { refs: K, floatingStyles: z, placement: N, isPositioned: H, middlewareData: C } = Xx({
      strategy: "fixed",
      placement: b,
      whileElementsMounted: (...Et) => Hx(...Et, {
        animationFrame: m === "always"
      }),
      elements: {
        reference: g.anchor
      },
      middleware: [
        Gx({
          mainAxis: o + V,
          alignmentAxis: r
        }),
        l && _x({
          mainAxis: true,
          crossAxis: false,
          limiter: f === "partial" ? $x() : void 0,
          ...D
        }),
        l && eP({
          ...D
        }),
        tP({
          ...D,
          apply: ({ elements: Et, rects: Ss, availableWidth: af, availableHeight: lf }) => {
            const { width: cf, height: uf } = Ss.reference, $t = Et.floating.style;
            $t.setProperty("--radix-popper-available-width", `${af}px`), $t.setProperty("--radix-popper-available-height", `${lf}px`), $t.setProperty("--radix-popper-anchor-width", `${cf}px`), $t.setProperty("--radix-popper-anchor-height", `${uf}px`);
          }
        }),
        q && iP({
          element: q,
          padding: a
        }),
        fP({
          arrowWidth: S,
          arrowHeight: V
        }),
        d && nP({
          strategy: "referenceHidden",
          ...D
        })
      ]
    }), [W, X] = Ju(N), Ee = nt(p);
    ke(() => {
      H && (Ee == null ? void 0 : Ee());
    }, [
      H,
      Ee
    ]);
    const si = (_a3 = C.arrow) == null ? void 0 : _a3.x, nf = (_b = C.arrow) == null ? void 0 : _b.y, of = ((_c2 = C.arrow) == null ? void 0 : _c2.centerOffset) !== 0, [sf, rf] = A.useState();
    return ke(() => {
      y && rf(window.getComputedStyle(y).zIndex);
    }, [
      y
    ]), P("div", {
      ref: K.setFloating,
      "data-radix-popper-content-wrapper": "",
      style: {
        ...z,
        transform: H ? z.transform : "translate(0, -200%)",
        minWidth: "max-content",
        zIndex: sf,
        "--radix-popper-transform-origin": [
          (_d2 = C.transformOrigin) == null ? void 0 : _d2.x,
          (_e2 = C.transformOrigin) == null ? void 0 : _e2.y
        ].join(" "),
        ...((_f2 = C.hide) == null ? void 0 : _f2.referenceHidden) && {
          visibility: "hidden",
          pointerEvents: "none"
        }
      },
      dir: e.dir,
      children: P(aP, {
        scope: n,
        placedSide: W,
        onArrowChange: R,
        arrowX: si,
        arrowY: nf,
        shouldHideArrow: of,
        children: P(Z.div, {
          "data-side": W,
          "data-align": X,
          ...h,
          ref: w,
          style: {
            ...h.style,
            animation: H ? void 0 : "none"
          }
        })
      })
    });
  });
  ku.displayName = vs;
  var Iu = "PopperArrow", cP = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right"
  }, Zu = A.forwardRef(function(t, n) {
    const { __scopePopper: i, ...o } = t, s = lP(Iu, i), r = cP[s.placedSide];
    return P("span", {
      ref: s.onArrowChange,
      style: {
        position: "absolute",
        left: s.arrowX,
        top: s.arrowY,
        [r]: 0,
        transformOrigin: {
          top: "",
          right: "0 0",
          bottom: "center 0",
          left: "100% 0"
        }[s.placedSide],
        transform: {
          top: "translateY(100%)",
          right: "translateY(50%) rotate(90deg) translateX(-50%)",
          bottom: "rotate(180deg)",
          left: "translateY(50%) rotate(-90deg) translateX(50%)"
        }[s.placedSide],
        visibility: s.shouldHideArrow ? "hidden" : void 0
      },
      children: P(sP, {
        ...o,
        ref: n,
        style: {
          ...o.style,
          display: "block"
        }
      })
    });
  });
  Zu.displayName = Iu;
  function uP(e) {
    return e !== null;
  }
  var fP = (e) => ({
    name: "transformOrigin",
    options: e,
    fn(t) {
      var _a3, _b, _c2;
      const { placement: n, rects: i, middlewareData: o } = t, r = ((_a3 = o.arrow) == null ? void 0 : _a3.centerOffset) !== 0, a = r ? 0 : e.arrowWidth, l = r ? 0 : e.arrowHeight, [c, u] = Ju(n), f = {
        start: "0%",
        center: "50%",
        end: "100%"
      }[u], d = (((_b = o.arrow) == null ? void 0 : _b.x) ?? 0) + a / 2, m = (((_c2 = o.arrow) == null ? void 0 : _c2.y) ?? 0) + l / 2;
      let p = "", h = "";
      return c === "bottom" ? (p = r ? f : `${d}px`, h = `${-l}px`) : c === "top" ? (p = r ? f : `${d}px`, h = `${i.floating.height + l}px`) : c === "right" ? (p = `${-l}px`, h = r ? f : `${m}px`) : c === "left" && (p = `${i.floating.width + l}px`, h = r ? f : `${m}px`), {
        data: {
          x: p,
          y: h
        }
      };
    }
  });
  function Ju(e) {
    const [t, n = "center"] = e.split("-");
    return [
      t,
      n
    ];
  }
  var dP = zu, Ou = Mu, hP = ku, mP = Zu, oi = "Popover", [Yu] = Oe(oi, [
    Ku
  ]), _t = Ku(), [pP, je] = Yu(oi), Bu = (e) => {
    const { __scopePopover: t, children: n, open: i, defaultOpen: o, onOpenChange: s, modal: r = false } = e, a = _t(t), l = A.useRef(null), [c, u] = A.useState(false), [f, d] = Ye({
      prop: i,
      defaultProp: o ?? false,
      onChange: s,
      caller: oi
    });
    return P(dP, {
      ...a,
      children: P(pP, {
        scope: t,
        contentId: Wt(),
        triggerRef: l,
        open: f,
        onOpenChange: d,
        onOpenToggle: A.useCallback(() => d((m) => !m), [
          d
        ]),
        hasCustomAnchor: c,
        onCustomAnchorAdd: A.useCallback(() => u(true), []),
        onCustomAnchorRemove: A.useCallback(() => u(false), []),
        modal: r,
        children: n
      })
    });
  };
  Bu.displayName = oi;
  var ju = "PopoverAnchor", gP = A.forwardRef((e, t) => {
    const { __scopePopover: n, ...i } = e, o = je(ju, n), s = _t(n), { onCustomAnchorAdd: r, onCustomAnchorRemove: a } = o;
    return A.useEffect(() => (r(), () => a()), [
      r,
      a
    ]), P(Ou, {
      ...s,
      ...i,
      ref: t
    });
  });
  gP.displayName = ju;
  var Xu = "PopoverTrigger", Qu = A.forwardRef((e, t) => {
    const { __scopePopover: n, ...i } = e, o = je(Xu, n), s = _t(n), r = B(t, o.triggerRef), a = P(Z.button, {
      type: "button",
      "aria-haspopup": "dialog",
      "aria-expanded": o.open,
      "aria-controls": o.contentId,
      "data-state": tf(o.open),
      ...i,
      ref: r,
      onClick: k(e.onClick, o.onOpenToggle)
    });
    return o.hasCustomAnchor ? a : P(Ou, {
      asChild: true,
      ...s,
      children: a
    });
  });
  Qu.displayName = Xu;
  var Vs = "PopoverPortal", [AP, yP] = Yu(Vs, {
    forceMount: void 0
  }), Gu = (e) => {
    const { __scopePopover: t, forceMount: n, children: i, container: o } = e, s = je(Vs, t);
    return P(AP, {
      scope: t,
      forceMount: n,
      children: P(Tt, {
        present: n || s.open,
        children: P(ls, {
          asChild: true,
          container: o,
          children: i
        })
      })
    });
  };
  Gu.displayName = Vs;
  var xt = "PopoverContent", _u = A.forwardRef((e, t) => {
    const n = yP(xt, e.__scopePopover), { forceMount: i = n.forceMount, ...o } = e, s = je(xt, e.__scopePopover);
    return P(Tt, {
      present: i || s.open,
      children: s.modal ? P(PP, {
        ...o,
        ref: t
      }) : P(wP, {
        ...o,
        ref: t
      })
    });
  });
  _u.displayName = xt;
  var xP = Zt("PopoverContent.RemoveScroll"), PP = A.forwardRef((e, t) => {
    const n = je(xt, e.__scopePopover), i = A.useRef(null), o = B(t, i), s = A.useRef(false);
    return A.useEffect(() => {
      const r = i.current;
      if (r) return lu(r);
    }, []), P(cs, {
      as: xP,
      allowPinchZoom: true,
      children: P($u, {
        ...e,
        ref: o,
        trapFocus: n.open,
        disableOutsidePointerEvents: true,
        onCloseAutoFocus: k(e.onCloseAutoFocus, (r) => {
          var _a3;
          r.preventDefault(), s.current || ((_a3 = n.triggerRef.current) == null ? void 0 : _a3.focus());
        }),
        onPointerDownOutside: k(e.onPointerDownOutside, (r) => {
          const a = r.detail.originalEvent, l = a.button === 0 && a.ctrlKey === true, c = a.button === 2 || l;
          s.current = c;
        }, {
          checkForDefaultPrevented: false
        }),
        onFocusOutside: k(e.onFocusOutside, (r) => r.preventDefault(), {
          checkForDefaultPrevented: false
        })
      })
    });
  }), wP = A.forwardRef((e, t) => {
    const n = je(xt, e.__scopePopover), i = A.useRef(false), o = A.useRef(false);
    return P($u, {
      ...e,
      ref: t,
      trapFocus: false,
      disableOutsidePointerEvents: false,
      onCloseAutoFocus: (s) => {
        var _a3, _b;
        (_a3 = e.onCloseAutoFocus) == null ? void 0 : _a3.call(e, s), s.defaultPrevented || (i.current || ((_b = n.triggerRef.current) == null ? void 0 : _b.focus()), s.preventDefault()), i.current = false, o.current = false;
      },
      onInteractOutside: (s) => {
        var _a3, _b;
        (_a3 = e.onInteractOutside) == null ? void 0 : _a3.call(e, s), s.defaultPrevented || (i.current = true, s.detail.originalEvent.type === "pointerdown" && (o.current = true));
        const r = s.target;
        ((_b = n.triggerRef.current) == null ? void 0 : _b.contains(r)) && s.preventDefault(), s.detail.originalEvent.type === "focusin" && o.current && s.preventDefault();
      }
    });
  }), $u = A.forwardRef((e, t) => {
    const { __scopePopover: n, trapFocus: i, onOpenAutoFocus: o, onCloseAutoFocus: s, disableOutsidePointerEvents: r, onEscapeKeyDown: a, onPointerDownOutside: l, onFocusOutside: c, onInteractOutside: u, ...f } = e, d = je(xt, n), m = _t(n);
    return $c(), P(as, {
      asChild: true,
      loop: true,
      trapped: i,
      onMountAutoFocus: o,
      onUnmountAutoFocus: s,
      children: P(rs, {
        asChild: true,
        disableOutsidePointerEvents: r,
        onInteractOutside: u,
        onEscapeKeyDown: a,
        onPointerDownOutside: l,
        onFocusOutside: c,
        onDismiss: () => d.onOpenChange(false),
        children: P(hP, {
          "data-state": tf(d.open),
          role: "dialog",
          id: d.contentId,
          ...m,
          ...f,
          ref: t,
          style: {
            ...f.style,
            "--radix-popover-content-transform-origin": "var(--radix-popper-transform-origin)",
            "--radix-popover-content-available-width": "var(--radix-popper-available-width)",
            "--radix-popover-content-available-height": "var(--radix-popper-available-height)",
            "--radix-popover-trigger-width": "var(--radix-popper-anchor-width)",
            "--radix-popover-trigger-height": "var(--radix-popper-anchor-height)"
          }
        })
      })
    });
  }), ef = "PopoverClose", qP = A.forwardRef((e, t) => {
    const { __scopePopover: n, ...i } = e, o = je(ef, n);
    return P(Z.button, {
      type: "button",
      ...i,
      ref: t,
      onClick: k(e.onClick, () => o.onOpenChange(false))
    });
  });
  qP.displayName = ef;
  var RP = "PopoverArrow", vP = A.forwardRef((e, t) => {
    const { __scopePopover: n, ...i } = e, o = _t(n);
    return P(mP, {
      ...o,
      ...i,
      ref: t
    });
  });
  vP.displayName = RP;
  function tf(e) {
    return e ? "open" : "closed";
  }
  var VP = Bu, SP = Qu, TP = Gu, bP = _u;
  GP = function({ open: e, onOpenChange: t, trigger: n, children: i, side: o = "bottom", align: s = "center", sideOffset: r = 8, className: a, style: l }) {
    return j(VP, {
      open: e,
      onOpenChange: t,
      children: [
        P(SP, {
          asChild: true,
          children: n
        }),
        P(Vt, {
          children: e && P(TP, {
            forceMount: true,
            children: P(bP, {
              asChild: true,
              forceMount: true,
              side: o,
              align: s,
              sideOffset: r,
              avoidCollisions: true,
              children: P(me.div, {
                initial: {
                  opacity: 0
                },
                animate: {
                  opacity: 1
                },
                exit: {
                  opacity: 0
                },
                className: a,
                style: l,
                children: P(Pe, {
                  cornerRadius: I.lg,
                  children: P("div", {
                    style: {
                      padding: L.md
                    },
                    children: i
                  })
                })
              })
            })
          })
        })
      ]
    });
  };
})();
export {
  ee as APPLE_CONTROL_SIZES,
  I as APPLE_RADII,
  L as APPLE_SPACING,
  XP as GlassActionSheet,
  jP as GlassAlert,
  Ae as GlassButton,
  LP as GlassCard,
  MP as GlassChip,
  Ra as GlassContext,
  fo as GlassEffectContainer,
  IP as GlassInput,
  ZP as GlassNavigationBar,
  Pe as GlassPanel,
  GP as GlassPopover,
  UP as GlassProvider,
  qf as GlassRenderer,
  OP as GlassSearchBar,
  HP as GlassSegmentedControl,
  QP as GlassSheet,
  zP as GlassSlider,
  kP as GlassStepper,
  YP as GlassTabBar,
  KP as GlassToggle,
  JP as GlassToolbar,
  __tla,
  DP as useGlassEffect,
  Zn as useGlassEngine
};
